import * as i1 from '@angular/common/http';
import { HttpRequest, HttpClientModule } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable, Inject, SecurityContext, ComponentFactoryResolver, NgModule } from '@angular/core';
import { __awaiter, __decorate, __metadata, __asyncValues } from 'tslib';
import { SharedUrlRoutes, UtilHttpStatus, AccessManager, UtilType, UtilArray, UtilDocumentId, VersionManager, UtilSequenceProcess, UtilError, DocumentUpdater, UtilDocument, UtilSystemHeader, UtilUploads, UtilFileReference, UtilIncludeRuleSet, SearchResultsProcessor, RulesetError, ESQueryUtils, RuleHelper, UtilConflict, UtilValidation, UtilRules, UtilString, DocumentIdValidator } from '@formbird/shared';
import { Observable, ReplaySubject, Subject, Subscription, from, BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter, map, concatMap, switchMap } from 'rxjs/operators';
import { cloneDeep, merge, toLower, isEmpty, extend, isEqual, chunk, nth, isArray, get, includes, isPlainObject, map as map$1, orderBy, isString, isFunction, isObject, startsWith, isDate, isError } from 'lodash';
import * as Dexie from 'dexie';
import * as Handlebars from 'handlebars';
import { SharedConstants, FtError, ChangeSourceType } from '@formbird/types';
import { Notyf } from 'notyf';
import cloneDeep$1 from 'lodash.clonedeep';
import { combineReducers, createStore } from 'redux';
import { DOCUMENT } from '@angular/common';
import loadjs from 'loadjs';
import * as async from 'async';
import * as mime from 'mime-types';
import * as i12 from '@angular/platform-browser';
import * as lodashString from 'lodash/string';
import * as i2 from '@angular/router';
import * as io from 'socket.io-client';
import { createCustomElement } from '@angular/elements';

/**
 * This is the public interface of @formbird/services. It wraps the global
 * redux store and adds a few other add on methods. It's what you'll inject
 * into your Angular application as a service.
 */
class AppStore {
}
/** @hidden, @deprecated */
AppStore.instance = undefined;

/**
 * OPTIONS_KEY: this is per-class (static) and holds the config from the
 * @SubStore decorator.
 */
const OPTIONS_KEY = '@angular-redux::substore::class::options';
/**
 * INSTANCE_SUBSTORE_KEY, INSTANCE_SELECTIONS_KEY: these are per-instance
 * (non-static) and holds references to the substores/selected observables
 * to be used by an instance of a decorated class. I'm not using
 * reflect-metadata here because I want
 *
 * 1. different instances to have different substores in the case where
 * `basePathMethodName` is dynamic.
 * 2. the instance substore to be garbage collected when the instance is no
 * longer reachable.
 * This is therefore an own-property on the actual instance of the decorated
 * class.
 */
const INSTANCE_SUBSTORE_KEY = '@angular-redux::substore::instance::store';
const INSTANCE_SELECTIONS_KEY = '@angular-redux::substore::instance::selections';
/**
 * Used to detect when the base path changes - this allows components to
 * dynamically adjust their selections if necessary.
 */
const INSTANCE_BASE_PATH_KEY = '@angular-redux::substore::instance::basepath';
const getClassOptions = (decoratedInstance) => decoratedInstance.constructor[OPTIONS_KEY];
/** @hidden */
const setClassOptions = (decoratedClassConstructor, options) => {
    decoratedClassConstructor[OPTIONS_KEY] = options;
};
// I want the store to be saved on the actual instance so
// 1. different instances can have distinct substores if necessary
// 2. the substore/selections will be marked for garbage collection when the
//    instance is destroyed.
const setInstanceStore = (decoratedInstance, store) => (decoratedInstance[INSTANCE_SUBSTORE_KEY] = store);
const getInstanceStore = (decoratedInstance) => decoratedInstance[INSTANCE_SUBSTORE_KEY];
const getInstanceSelectionMap = (decoratedInstance) => {
    const map = decoratedInstance[INSTANCE_SELECTIONS_KEY] || {};
    decoratedInstance[INSTANCE_SELECTIONS_KEY] = map;
    return map;
};
const hasBasePathChanged = (decoratedInstance, basePath) => decoratedInstance[INSTANCE_BASE_PATH_KEY] !== (basePath || []).toString();
const setInstanceBasePath = (decoratedInstance, basePath) => {
    decoratedInstance[INSTANCE_BASE_PATH_KEY] = (basePath || []).toString();
};
const clearInstanceState = (decoratedInstance) => {
    decoratedInstance[INSTANCE_SELECTIONS_KEY] = null;
    decoratedInstance[INSTANCE_SUBSTORE_KEY] = null;
    decoratedInstance[INSTANCE_BASE_PATH_KEY] = null;
};
/**
 * Gets the store associated with a decorated instance (e.g. a
 * component or service)
 * @hidden
 */
const getBaseStore = (decoratedInstance) => {
    // The root store hasn't been set up yet.
    if (!AppStore.instance) {
        return undefined;
    }
    const options = getClassOptions(decoratedInstance);
    // This is not decorated with `@WithSubStore`. Return the root store.
    if (!options) {
        return AppStore.instance;
    }
    // Dynamic base path support:
    const basePath = decoratedInstance[options.basePathMethodName]();
    if (hasBasePathChanged(decoratedInstance, basePath)) {
        clearInstanceState(decoratedInstance);
        setInstanceBasePath(decoratedInstance, basePath);
    }
    if (!basePath) {
        return AppStore.instance;
    }
    const store = getInstanceStore(decoratedInstance);
    if (!store) {
        setInstanceStore(decoratedInstance, AppStore.instance.configureSubStore(basePath, options.localReducer));
    }
    return getInstanceStore(decoratedInstance);
};
/**
 * Creates an Observable from the given selection parameters,
 * rooted at decoratedInstance's store, and caches it on the
 * instance for future use.
 * @hidden
 */
const getInstanceSelection = (decoratedInstance, key, selector, transformer, comparator) => {
    const store = getBaseStore(decoratedInstance);
    if (store) {
        const selections = getInstanceSelectionMap(decoratedInstance);
        selections[key] =
            selections[key] ||
                (!transformer
                    ? store.select(selector, comparator)
                    : store.select(selector).pipe(obs$ => transformer(obs$, decoratedInstance), distinctUntilChanged(comparator)));
        return selections[key];
    }
    return undefined;
};

/**
 * Selects an observable from the store, and attaches it to the decorated
 * property.
 *
 * ```ts
 *  import { select } from '@formbird/services';
 *
 *  class SomeClass {
 *    @select(['foo','bar']) foo$: Observable<string>
 * }
 * ```
 *
 * @param selector
 * A selector function, property name string, or property name path
 * (array of strings/array indices) that locates the store data to be
 * selected
 *
 * @param comparator Function used to determine if this selector has changed.
 */
function select(selector, comparator) {
    return (target, key) => {
        const adjustedSelector = selector
            ? selector
            : String(key).lastIndexOf('$') === String(key).length - 1
                ? String(key).substring(0, String(key).length - 1)
                : key;
        decorate(adjustedSelector, undefined, comparator)(target, key);
    };
}
/**
 * Selects an observable using the given path selector, and runs it through the
 * given transformer function. A transformer function takes the store
 * observable as an input and returns a derived observable from it. That derived
 *  observable is run through distinctUntilChanges with the given optional
 * comparator and attached to the store property.
 *
 * Think of a Transformer as a FunctionSelector that operates on observables
 * instead of values.
 *
 * ```ts
 * import { select$ } from 'angular-redux/store';
 *
 * export const debounceAndTriple = obs$ => obs$
 *  .debounce(300)
 *  .map(x => 3 * x);
 *
 * class Foo {
 *  @select$(['foo', 'bar'], debounceAndTriple)
 *  readonly debouncedFooBar$: Observable<number>;
 * }
 * ```
 */
function select$(selector, transformer, comparator) {
    return decorate(selector, transformer, comparator);
}
function decorate(selector, transformer, comparator) {
    return function decorator(target, key) {
        function getter() {
            return getInstanceSelection(this, key, selector, transformer, comparator);
        }
        // Replace decorated property with a getter that returns the observable.
        if (delete target[key]) {
            Object.defineProperty(target, key, {
                get: getter,
                enumerable: true,
                configurable: true,
            });
        }
    };
}

/**
 * constants for local storage keys
 */
const LocalStorageKeys = {
    LOGGED_IN_USER_ID: 'LoggedInUserID',
    LOGGED_IN_USER: 'LoggedInUser',
    LOGGED_IN_ACCOUNT_CONTROL_DOCUMENT: 'LoggedInAccountControlDocument',
    USER_SETTINGS: 'UserSettings',
    COMMAND_BAR_CSS: 'commandBarCSS',
    APP_VERSION: 'AppVersion',
    CHECK_PUBLIC_USER: "CheckPublicUser"
};

const logger$v = console;
const USER_SET_USER = 'USER_SET_USER';
function userSetUser(user) {
    return {
        type: USER_SET_USER,
        payload: {
            user
        }
    };
}

'use strict';
/**
 * A constants object for constants that are used on the client-side offline for service worker
 */
const OfflineConstants = {
    LOGGED_IN_USER_GROUP_CONFIG: 'LoggedInUserGroupConfig',
    DEFAULT_MAX_UPLOAD_SIZE: 50,
    DOCUMENT_FIELD_NAME_UPLOAD_FILE: 'imageFileNo96',
    OFFLINE_PENDING_DOCUMENT_COUNT: 'offlinePendingDocumentsCount'
};

class SynchronousStorageService {
    constructor(urlBasePathService) {
        this.urlBasePathService = urlBasePathService;
    }
    setItem(key, value) {
        key = this.urlBasePathService.getBasePath() + key;
        return this.storage.setItem(key, value);
    }
    getItem(key) {
        key = this.urlBasePathService.getBasePath() + key;
        return this.storage.getItem(key);
    }
    removeItem(key) {
        key = this.urlBasePathService.getBasePath() + key;
        return this.storage.removeItem(key);
    }
}

let basePath = '';
const baseTag = document.getElementsByTagName('base')[0];
if (baseTag) {
    basePath = baseTag.href.split('/')[3] || '';
}
const UrlBasePathUtil = {
    basePath: basePath,
    basePathWithSlashPrefix: basePath !== '' ? '/' + basePath : ''
};
class UrlBasePathService {
    getBasePath() {
        return UrlBasePathUtil.basePath;
    }
}
/** @nocollapse */ /** @nocollapse */ UrlBasePathService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: UrlBasePathService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ UrlBasePathService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: UrlBasePathService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: UrlBasePathService, decorators: [{
            type: Injectable
        }] });

class LocalStorageService extends SynchronousStorageService {
    constructor(urlBasePathService) {
        super(urlBasePathService);
        this.urlBasePathService = urlBasePathService;
        this.storage = localStorage;
    }
}
/** @nocollapse */ /** @nocollapse */ LocalStorageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: LocalStorageService, deps: [{ token: UrlBasePathService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ LocalStorageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: LocalStorageService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: LocalStorageService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: UrlBasePathService }]; } });

const logger$u = console;
let pageVisibilityService$;
class PageVisibilityService {
    constructor() {
        // main snippet source: https://www.html5rocks.com/en/tutorials/pagevisibility/intro/
        this.visibilityListeners = [];
        this.evtname = null;
        pageVisibilityService$ = this;
    }
    getHiddenProp() {
        const prefixes = ['webkit', 'moz', 'ms', 'o'];
        // if 'hidden' is natively supported just return it
        if ('hidden' in document) {
            return 'hidden';
        }
        // otherwise loop over all the known prefixes until we find one
        for (let i = 0; i < prefixes.length; i++) {
            if ((prefixes[i] + 'Hidden') in document) {
                return prefixes[i] + 'Hidden';
            }
        }
        // otherwise it's not supported
        return null;
    }
    isHidden() {
        const prop = pageVisibilityService$.getHiddenProp();
        if (!prop) {
            return false;
        }
        return document[prop];
    }
    visChange() {
        const hidden = pageVisibilityService$.isHidden();
        for (let x = 0; x < pageVisibilityService$.visibilityListeners.length; x++) {
            pageVisibilityService$.visibilityListeners[x](hidden);
        }
    }
    startListeningForPageVisibility() {
        if (this.evtname !== null) { // done init, skip.
            return;
        }
        // use the property name to generate the prefixed event name
        const visProp = pageVisibilityService$.getHiddenProp();
        if (visProp) {
            this.evtname = visProp.replace(/[H|h]idden/, '') + 'visibilitychange';
            document.addEventListener(this.evtname, this.visChange);
        }
        else {
            logger$u.error('Visibility API not supported by browser.');
        }
    }
    stopListeningForPageVisibility() {
        document.removeEventListener(this.evtname, this.visChange);
        this.evtname = null; // reset so we can enable it back via startListening..
    }
    /**
     * Listeners should execute as fast as possible.
     *
     * It is the responsibility of the listener to implement its own debounce logic so to avoid unnecessary repetitive
     * execution.
     *
     * At the moment, debounce will not be implemented here to allow the listeners to react in the shortest
     * possible time.
     * @param listener the listener
     */
    registerVisibilityListener(listener) {
        this.visibilityListeners.push(listener);
    }
    removeVisibilityListener(listener) {
        const index = this.visibilityListeners.indexOf(listener);
        if (index > -1) {
            this.visibilityListeners.splice(index, 1);
        }
    }
}
/** @nocollapse */ /** @nocollapse */ PageVisibilityService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: PageVisibilityService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ PageVisibilityService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: PageVisibilityService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: PageVisibilityService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

const logger$t = console;
class KeyValueStorageService {
    constructor(urlBasePathService, localStorageService) {
        this.urlBasePathService = urlBasePathService;
        this.localStorageService = localStorageService;
        this.dbName = 'keyValueStorage';
        this.dbVersion = 1;
        this.objectName = 'keyValues';
        this.idb = null;
        this.openFailedError = false;
    }
    setItem(key, value) {
        const self = this;
        if (self.openFailedError) {
            self.localStorageService.setItem(key, value);
            return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
            this.getDatabase().then(function success(db) {
                db.transaction('rw', db[self.objectName], (result) => {
                    db[self.objectName].put({ key: key, value: value }).then((data) => {
                        resolve(result);
                    }, (e) => {
                        logger$t.error('Error on IndexedDBService.saveEntry:');
                        logger$t.error(e);
                        reject(e);
                    });
                }).catch(e => {
                    if (e.name === 'OpenFailedError') {
                        logger$t.error('Error adding entry to IndexedDB, using localStorage instead.');
                        self.localStorageService.setItem(key, value);
                        self.openFailedError = true;
                        resolve(value);
                    }
                    else {
                        logger$t.error('Error adding entry to IndexedDB: ' + e.stack || e);
                        reject(e);
                    }
                });
            });
        });
    }
    getItem(key) {
        key = this.urlBasePathService.getBasePath() + key;
        const self = this;
        if (self.openFailedError) {
            const value = self.localStorageService.getItem(key);
            return Promise.resolve(value);
        }
        return new Promise((resolve, reject) => {
            this.getDatabase().then(function success(db) {
                let value;
                db[self.objectName].where('key').equals(key).each(results => {
                    value = results.value;
                }).then(() => {
                    resolve(value);
                }).catch(function (error) {
                    if (error.name === 'OpenFailedError') {
                        logger$t.error('Error getting dexie entries, using localStorage instead.');
                        const value = self.localStorageService.getItem(key);
                        self.openFailedError = true;
                        resolve(value);
                    }
                    else {
                        logger$t.error('Error getting dexie entries: ' + error.message);
                        reject(error);
                    }
                });
            });
        });
    }
    removeItem(key) {
        key = this.urlBasePathService.getBasePath() + key;
        const self = this;
        if (self.openFailedError) {
            self.localStorageService.removeItem(key);
            return Promise.resolve(1);
        }
        return new Promise((resolve, reject) => {
            this.getDatabase().then(function success(db) {
                db[self.objectName].where('key').equals(key).delete().then(deleteCount => {
                    resolve(deleteCount);
                }).catch(function (error) {
                    if (error.name === 'OpenFailedError') {
                        logger$t.error('Error removing dexie entries, using localStorage instead.');
                        self.localStorageService.removeItem(key);
                        self.openFailedError = true;
                        resolve(1);
                    }
                    else {
                        logger$t.error('Error removing dexie entries: ' + error.message);
                        reject(error);
                    }
                });
            });
        });
    }
    getDatabase() {
        const self = this;
        return new Promise(function (resolve, reject) {
            if (self.idb) {
                resolve(self.idb);
            }
            else {
                const db = new Dexie.default(self.dbName);
                db.version(self.dbVersion).stores({
                    keyValues: 'key'
                });
                self.idb = db;
                resolve(self.idb);
            }
        });
    }
}
/** @nocollapse */ /** @nocollapse */ KeyValueStorageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: KeyValueStorageService, deps: [{ token: UrlBasePathService }, { token: LocalStorageService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ KeyValueStorageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: KeyValueStorageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: KeyValueStorageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: UrlBasePathService }, { type: LocalStorageService }]; } });

const serverRoutes$5 = SharedUrlRoutes.serverRoutes;
const logger$s = console;
let LoggedInUserService$;
class LoggedInUserService {
    constructor(http, localStorageService, pageVisibilityService, appStore, keyValueStorageService) {
        this.http = http;
        this.localStorageService = localStorageService;
        this.pageVisibilityService = pageVisibilityService;
        this.appStore = appStore;
        this.keyValueStorageService = keyValueStorageService;
        this.user = {
            welcomeMsg: '',
            account: null,
            filteredAccount: null,
            menu: null,
            publicAccount: false,
            accountPublicURL: null,
            accountControlDocument: null,
            accountGroupConfigDocument: null
        };
        this.userConfigItemListeners = []; // listeners for 'set' events in the user config.
        LoggedInUserService$ = this;
        this.pageVisibilityService.registerVisibilityListener((isPageHidden) => {
            if (isPageHidden) {
                return;
            }
            const trueUserId = this.localStorageService.getItem(LocalStorageKeys.LOGGED_IN_USER_ID);
            const registeredUserId = this.user.account == null ? null : this.user.account.documentId;
            if (trueUserId !== registeredUserId) { // user changed
                window.location.reload(); // just reload the page so that everything is checked from its initial state.
            }
        });
        this.user$.subscribe((user) => this.user = user);
    }
    /**
     * get the id of the logged in user
     */
    getUserId() {
        return this.user.account != null ? this.user.account.documentId : this.localStorageService.getItem(LocalStorageKeys.LOGGED_IN_USER_ID);
    }
    /**
     * set the user on login
     * @param account - the account to set
     * @param setAsOfflineUser - whether to set this user as the offline user
     */
    setUser(account) {
        return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            if (account) {
                this.user.filteredAccount = this.filterAcct(cloneDeep(account));
                this.user.account = account;
                this.user.menu = account.menu;
                if (account.systemHeader) {
                    this.user.welcomeMsg = account.systemHeader.summaryName;
                }
                this.localStorageService.setItem(LocalStorageKeys.LOGGED_IN_USER_ID, account.documentId);
                yield this.keyValueStorageService.setItem(LocalStorageKeys.LOGGED_IN_USER_ID, account.documentId);
                try {
                    const accountGroupConfigDocument = yield this.loggedUserAccountGroupConfigDocument();
                    this.setUserAccountGroupConfigDocument(accountGroupConfigDocument);
                }
                catch (err) {
                    logger$s.error("Error on getting accountGroupConfig document " + err.message);
                }
                this.loggedUserAccountControlDocument().subscribe(resp => {
                    this.setUserControlDocument(resp);
                    resolve();
                });
            }
            else {
                resolve();
            }
        }));
    }
    setUserControlDocument(controlDocument) {
        if (controlDocument) {
            this.user.accountControlDocument = controlDocument;
            if (controlDocument.publicAccount) {
                this.user.publicAccount = controlDocument.publicAccount;
                this.user.accountPublicURL = controlDocument.accountPublicURL;
            }
        }
        this.appStore.dispatch(userSetUser(this.user));
    }
    getUserControlDocument() {
        return this.user.accountControlDocument;
    }
    loggedUserAccountControlDocument() {
        const url = serverRoutes$5.loggedUserAccountControlDocument;
        return this.http.get(url);
    }
    setUserAccountGroupConfigDocument(groupConfigDocument) {
        if (groupConfigDocument) {
            this.user.accountGroupConfigDocument = groupConfigDocument;
        }
        this.appStore.dispatch(userSetUser(this.user));
    }
    loggedUserAccountGroupConfigDocument() {
        const url = serverRoutes$5.loggedUserAccountGroupConfigDocument;
        return this.http.get(url);
    }
    getUserAccountGroupConfigDocument() {
        return this.user.accountGroupConfigDocument;
    }
    /**
     * remove the user on logout
     * @param account the account
     */
    logoutUser() {
        this.resetUser();
        localStorage.removeItem(LocalStorageKeys.LOGGED_IN_USER_ID);
        this.userConfigItemListeners = [];
    }
    filterAcct(account) {
        const fieldsToRemove = [
            'accessKeys',
            'attachKeys',
            'components',
            'menu',
            'provider'
        ];
        for (let i = 0; i < fieldsToRemove.length; i++) {
            delete account[fieldsToRemove[i]];
        }
        return account;
    }
    resetUser() {
        // reset only values. See http://stackoverflow.com/questions/19744462/update-scope-value-when-service-data-is-changed
        this.user.welcomeMsg = '';
        this.user.account = null;
        this.user.filteredAccount = null;
        this.user.menu = null;
    }
    /**
     * Custom user configuration is saved in local storage so that they survive across logins.
     * To that end, they should only contain flags or basic values keeping in mind the
     * limited storage capacity of local storage.
     */
    getUserConfig() {
        const userId = this.getUserId();
        if (userId == null) {
            logger$s.error('Requesting for user config with no user logged in.');
            return;
        }
        let userConfig;
        userConfig = this.localStorageService.getItem(userId);
        if (userConfig == null) {
            // add default user config here
            userConfig = {
                cachingEnabled: false,
                idbCurrentVersion: 0,
                searchIndexes: [],
                registeredSearchIndexes: [],
                lastSyncDate: 0 // last time we synched offline
            };
            this.localStorageService.setItem(userId, JSON.stringify(userConfig));
            this.keyValueStorageService.setItem(userId, JSON.stringify(userConfig));
        }
        else {
            userConfig = JSON.parse(userConfig);
        }
        return userConfig;
    }
    getUserConfigItem(key) {
        if (key == null) {
            logger$s.error('Missing key on requesting user config.');
            return;
        }
        const userConfig = this.getUserConfig();
        if (userConfig == null) {
            return;
        }
        return userConfig[key];
    }
    setUserConfigItem(key, value) {
        if (key == null) {
            logger$s.error('Missing key on setting user config.');
            return;
        }
        let userConfig;
        userConfig = this.getUserConfig();
        if (userConfig == null) {
            return;
        }
        userConfig[key] = value;
        this.localStorageService.setItem(this.getUserId(), JSON.stringify(userConfig));
        this.keyValueStorageService.setItem(this.getUserId(), JSON.stringify(userConfig));
        for (let x = 0; x < this.userConfigItemListeners.length; x++) {
            const listener = this.userConfigItemListeners[x];
            if (listener.key === key) {
                listener.listener(value); // informs the listener of the new value
            }
        }
    }
    /**
     * Custom implementation for listening to changes in user config items. Originally this should
     * have been done via using StorageEventListener but unfortunately it fires events only if other
     * tabs had modified the offline storage.
     * @param key the key
     * @param listener the listener
     */
    addUserConfigItemListener(key, listener) {
        if (key == null || listener == null) {
            return;
        }
        this.userConfigItemListeners.push({ key: key, listener: listener });
    }
    isUserOfflineMode() {
        return this.getUserConfigItem('cachingEnabled') === true;
    }
    getLoggedInUser() {
        return this.user.account;
    }
    loadLoggedUser() {
        const _self = this;
        const url = serverRoutes$5.loggedUserData;
        return new Promise((resolve, reject) => {
            _self.http.get(url).subscribe(response => {
                const userBean = response;
                _self.setUserControlDocument(response.controlDoc);
                _self.setUserAccountGroupConfigDocument(response.groupConfigDoc);
                _self.appStore.dispatch(userSetUser(_self.user));
                return resolve(userBean);
            }, error => {
                if (_self.isUserOfflineMode() && error.status === UtilHttpStatus.SERVER_UNRESPONSIVE) { // account will be retrieved
                    resolve({ user: _self.user.account });
                }
                else {
                    reject(error);
                }
            });
        });
    }
    setLoggedUser() {
        const _self = this;
        const url = serverRoutes$5.loggedUserData;
        return new Promise((resolve, reject) => {
            _self.http.get(url).subscribe((response) => __awaiter(this, void 0, void 0, function* () {
                const userBean = response;
                const account = userBean.user;
                _self.user.filteredAccount = _self.filterAcct(cloneDeep(account));
                _self.user.account = account;
                _self.user.menu = account.menu;
                if (account.systemHeader) {
                    _self.user.welcomeMsg = account.systemHeader.summaryName;
                }
                _self.localStorageService.setItem(LocalStorageKeys.LOGGED_IN_USER_ID, account.documentId);
                yield this.keyValueStorageService.setItem(LocalStorageKeys.LOGGED_IN_USER_ID, account.documentId);
                _self.setUserControlDocument(userBean.controlDoc);
                _self.setUserAccountGroupConfigDocument(userBean.groupConfigDoc);
                yield this.keyValueStorageService.setItem(OfflineConstants.LOGGED_IN_USER_GROUP_CONFIG, userBean.groupConfigDoc);
                resolve(userBean);
            }), error => {
                if (_self.isUserOfflineMode() && error.status === UtilHttpStatus.SERVER_UNRESPONSIVE) { // item will be retried
                    resolve({ user: _self.user.account });
                }
                else {
                    reject(error);
                }
            });
        });
    }
    observableUser() {
        return this.user$;
    }
}
/** @nocollapse */ /** @nocollapse */ LoggedInUserService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: LoggedInUserService, deps: [{ token: i1.HttpClient }, { token: LocalStorageService }, { token: PageVisibilityService }, { token: AppStore }, { token: KeyValueStorageService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ LoggedInUserService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: LoggedInUserService, providedIn: 'root' });
__decorate([
    select(['userState', 'user']),
    __metadata("design:type", Observable)
], LoggedInUserService.prototype, "user$", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: LoggedInUserService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: LocalStorageService }, { type: PageVisibilityService }, { type: AppStore }, { type: KeyValueStorageService }]; }, propDecorators: { user$: [] } });

class ClientAccessService {
    constructor(loggedInUserService) {
        this.loggedInUserService = loggedInUserService;
        this.user$.subscribe((user) => this.user = user);
    }
    checkAccess(targetDocument, operation) {
        if (!targetDocument) {
            return true;
        }
        return AccessManager.checkAccess(targetDocument, this.user.accountControlDocument, operation);
    }
    hasPermission(targetDocument, operations) {
        if (!targetDocument) {
            return true;
        }
        return AccessManager.hasPermission(targetDocument, this.user.accountControlDocument, operations);
    }
    // Document access determined by comparing systemHeader.keyIds against the user's attachKeys.
    // A new document inherits keyIds from its template and from the user that created it.
    writeKeysToDocument(document, template) {
        const userAccountControlDocument = this.user.accountControlDocument;
        AccessManager.writeKeysToDocument(document, template, userAccountControlDocument);
    }
}
/** @nocollapse */ /** @nocollapse */ ClientAccessService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ClientAccessService, deps: [{ token: LoggedInUserService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ ClientAccessService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ClientAccessService, providedIn: 'root' });
__decorate([
    select(['userState', 'user']),
    __metadata("design:type", Observable)
], ClientAccessService.prototype, "user$", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ClientAccessService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: LoggedInUserService }]; }, propDecorators: { user$: [] } });

class BroadcastService {
    constructor() {
        this._eventBus = new ReplaySubject();
    }
    broadcast(key, data) {
        this._eventBus.next({ key, data });
    }
    on(key) {
        return this._eventBus
            .pipe(filter(event => event.key === key))
            .pipe(map(event => event.data));
    }
}
/** @nocollapse */ /** @nocollapse */ BroadcastService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: BroadcastService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ BroadcastService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: BroadcastService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: BroadcastService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

class UtilService {
    static getDataUrl(url, id) {
        let dataUrl = url;
        if (UtilType.hasValue(id)) {
            dataUrl += '/' + id;
        }
        return dataUrl;
    }
    static hasValue(val) {
        return UtilType.hasValue(val);
    }
    static isDefined(val) {
        return UtilType.isDefined(val);
    }
}

const IndexedDBConstants = {
    VERSION: 9,
    FT_DOCUMENTS_DB_NAME_PREFIX: 'ft-documents-',
    FT_PROPERTIES_TABLE_NAME: 'ftProperties',
    FT_PROPERTIES_TABLE_INDEX_KEY: 'key',
    DB_NAME: 'formbird',
    DB_INDEXES: 'indexedDBIndexes',
    // document store constants
    DOCUMENT_TABLE_NAME: 'documents',
    SYSTEM_TYPE_TEMPLATE: 'template',
    DOCUMENT_INDEX_PENDING_OPERATION: 'systemHeader.offlineDetails.status',
    DOCUMENT_INDEX_VERSION_ID: 'systemHeader.versionId',
    DOCUMENT_INDEX_DOCUMENT_ID: 'documentId',
    DOCUMENT_INDEX_SYSTEM_TYPE: 'systemHeader.systemType',
    DOCUMENT_INDEX_SUMMARY_NAME: 'systemHeader.summaryName',
    DOCUMENT_INDEX_NAME: 'name',
    // under offlineFiles store
    OFFLINE_FILE_TABLE_NAME: 'offlineFiles',
    OFFLINE_FILE_INDEX_STATUS: 'status',
    OFFLINE_FILE_INDEX_FILENO: 'fileNo',
    // universal offline statuses
    OFFLINE_STATUS_UPLOADED: 1,
    OFFLINE_STATUS_PENDING: 0,
    OFFLINE_STATUS_UPLOADING: 2,
    OFFLINE_STATUS_FAILED: -1
};
// the systemHeader.versionId needs to be first because it is the primary key
IndexedDBConstants.DOCUMENT_INDEXES = [IndexedDBConstants.DOCUMENT_INDEX_VERSION_ID,
    IndexedDBConstants.DOCUMENT_INDEX_DOCUMENT_ID,
    IndexedDBConstants.DOCUMENT_INDEX_SYSTEM_TYPE,
    IndexedDBConstants.DOCUMENT_INDEX_PENDING_OPERATION,
    IndexedDBConstants.DOCUMENT_INDEX_SUMMARY_NAME,
    IndexedDBConstants.DOCUMENT_INDEX_NAME];

class NotificationService {
    constructor() {
        this.queue = [];
        this.currentMessage = {};
        this._scope = null;
        this.notyf = new Notyf({
            ripple: false,
            types: [
                {
                    type: 'warning',
                    className: 'notyf-warning',
                    icon: {
                        className: 'material-icons',
                        tagName: 'i',
                        text: 'warning'
                    }
                },
                {
                    type: 'info',
                    className: 'notyf-info',
                    dismissible: true,
                    icon: {
                        className: 'material-icons',
                        tagName: 'i',
                        text: 'info'
                    }
                }
            ]
        });
    }
    /**
     * position:
     *     { x: 'right', y: 'top' }
     *     { x: 'center', y: 'top' }
     *     { x: 'left', y: 'top' }
     *     { x: 'right', y: 'bottom' }
     *     { x: 'center', y: 'bottom' }
     *     { x: 'left', y: 'bottom' }
     *      /**
     *(e.g) position: { x: 'right', y: 'top' }
     *     notyf position options
     *     Param |       Type                | Details
     *     x     |  left ; center or right  | x-position
     *     y     | top  ; center or bottom  |  y-position
     */
    printNotyfMessage(message, options, title, position, preventDuplicate, onClickFunction, timeOut, customClassName) {
        let messageType = (options === null || options === void 0 ? void 0 : options.messageType) || SharedConstants.STATUS_INFO;
        if (typeof options !== 'object') {
            messageType = options;
        }
        if (!position || /^toast/.test(position)) {
            position = { x: 'right', y: 'top' };
        }
        preventDuplicate = preventDuplicate ? preventDuplicate : false;
        const notyfMessage = !(!title || title === '') ? '<i class="notyf-message-type">[' + messageType + ']</i><b>' +
            title + '</b> <br/>' + message : message;
        let lowerCaseMessageType = messageType.toLowerCase();
        lowerCaseMessageType = lowerCaseMessageType === 'forbidden' ? SharedConstants.STATUS_ERROR : lowerCaseMessageType;
        /** preventDuplicate condition
         * if value is true, do not display duplicate notification
         * if value is false, display duplicate notification
         **/
        if (preventDuplicate) {
            const notyfTextContent = this.notyf.view.container.textContent;
            let compareMessage = !(!title || title === '') ? '[' + messageType + ']' + title + ' ' + message : message;
            if (lowerCaseMessageType === 'warning') {
                compareMessage = 'warning' + compareMessage;
            }
            else if (lowerCaseMessageType === 'info') {
                compareMessage = 'info' + compareMessage;
            }
            /**The message is duplicate if notyfTextContent.split(compareMessage).length greater than 1
             * The message is duplicate if preventDuplicate value is true and
             * [title, message and messageType] are similar with new notification message
             * */
            if (notyfTextContent.split(compareMessage).length > 1) {
                if (lowerCaseMessageType === 'success') {
                    console.log('%cDuplicate ' + lowerCaseMessageType + ' notification: ' + notyfMessage, `background: #3dc663;color:white`);
                }
                else if (lowerCaseMessageType === 'info') {
                    console.log('%cDuplicate ' + lowerCaseMessageType + ' notification: ' + notyfMessage, `background: blue;color:white`);
                }
                else if (lowerCaseMessageType === 'warning') {
                    console.log('%cDuplicate ' + lowerCaseMessageType + ' notification: ' + notyfMessage, `background: orange;color:white`);
                }
                else if (lowerCaseMessageType === 'error') {
                    console.log('%cDuplicate ' + lowerCaseMessageType + ' notification: ' + notyfMessage, `background: #ed3d3d;color:white`);
                }
                else if (lowerCaseMessageType === 'forbidden') {
                    console.log('%cDuplicate ' + lowerCaseMessageType + ' notification: ' + notyfMessage, `background: #ed3d3d;color:white`);
                }
                return;
            }
        }
        if (customClassName) {
            this.notyf.options.types = this.notyf.options.types.map(obj => {
                return Object.assign(Object.assign({}, obj), { className: obj.className + ' ' + customClassName });
            });
        }
        let notyfOptions = { type: lowerCaseMessageType, message: notyfMessage, duration: timeOut, dismissible: true, position: position };
        console.log(notyfOptions);
        if (options && typeof options === 'object') {
            notyfOptions = Object.assign(Object.assign({}, notyfOptions), (options.notyfOptions || {}));
        }
        const notification = this.notyf.open(notyfOptions);
        notification.on('click', ({ target, event }) => {
            onClickFunction();
        });
        return notification;
    }
    /**
     * position:
     *     { x: 'right', y: 'top' }
     *     { x: 'center', y: 'top' }
     *     { x: 'left', y: 'top' }
     *     { x: 'right', y: 'bottom' }
     *     { x: 'center', y: 'bottom' }
     *     { x: 'left', y: 'bottom' }
     *      /**
     *(e.g) position: { x: 'right', y: 'top' }
     *     notyf position options
     *     Param |       Type                | Details
     *     x     |  left ; center or right  | x-position
     *     y     | top  ; center or bottom  |  y-position
     */
    printHTML5Notification(message, messageType, title, position, preventDuplicate, onClickFunction, timeOut) {
        const doPrintHTML5Notif = () => {
            let icon;
            const lowerCaseMessageType = messageType.toLowerCase();
            switch (lowerCaseMessageType) {
                case SharedConstants.STATUS_SUCCESS:
                    icon = './images/notifications/success.png';
                    break;
                case SharedConstants.STATUS_INFO:
                    icon = './images/notifications/info.png';
                    break;
                case SharedConstants.STATUS_WARNING:
                    icon = './images/notifications/warning.png';
                    break;
                case SharedConstants.STATUS_ERROR:
                    icon = './images/notifications/error.png';
                    break;
                case SharedConstants.STATUS_FORBIDDEN:
                    icon = './images/notifications/warning.png';
                    break;
            }
            const notification = new window.Notification('Formbird', {
                body: message,
                icon: icon,
                dir: 'auto'
            });
            notification.onclick = function () {
                onClickFunction();
            };
            if (timeOut && timeOut !== 0) {
                console.log("weeeew");
                setTimeout(function () {
                    notification.close();
                }, timeOut);
            }
            return notification;
        };
        if (Notification.permission === 'granted') {
            return doPrintHTML5Notif();
        }
        else {
            window.Notification.requestPermission(function (permission) {
                doPrintHTML5Notif();
            });
        }
    }
    /**
     * Display both messages
     */
    printMessage(message, options, title, position, preventDuplicate, displayDesktopNotification, onClickFunction, timeOut, isPermanent, customClassName) {
        if (typeof options === 'object') {
            timeOut = timeOut || options.timeOut;
            displayDesktopNotification = displayDesktopNotification || options.displayDesktopNotification;
            isPermanent = options.isPermanent;
            title = title || options.title;
            position = position || options.position;
            preventDuplicate = preventDuplicate || options.preventDuplicate;
            onClickFunction = onClickFunction || options.onClickFunction;
            customClassName = customClassName || options.customClassName;
        }
        if (isPermanent) {
            timeOut = 0;
        }
        else if (!timeOut) {
            timeOut = 3000;
        }
        if (!onClickFunction) {
            onClickFunction = function () { };
        }
        const notification = {};
        if (displayDesktopNotification) {
            let messageType = (typeof options === 'string' ? options : options.messageType) || SharedConstants.STATUS_INFO;
            notification.desktopNotification = this.printHTML5Notification(message, messageType, title, position, preventDuplicate, onClickFunction, timeOut);
        }
        notification.notyfNotification = this.printNotyfMessage(message, options, title, position, preventDuplicate, onClickFunction, timeOut, customClassName);
        return notification;
    }
    dismiss(notification) {
        if (notification.desktopNotification) {
            notification.desktopNotification.close();
        }
        this.notyf.dismiss(notification === null || notification === void 0 ? void 0 : notification.notyfNotification);
    }
    set(message) {
        const msg = message;
        this.queue.push(msg);
    }
    get() {
        return this.currentMessage;
    }
    error(message, title, displayDesktopNotification) {
        const messageType = SharedConstants.STATUS_ERROR;
        const position = '';
        const preventDuplicate = false;
        const onClickFunction = null; // leave this undefined so the default will be used
        const timeOut = 0;
        const isPermanent = true; // make errors permanent so the user will see them if they miss the temporary popup
        this.printMessage(message, messageType, title, position, preventDuplicate, displayDesktopNotification, onClickFunction, timeOut, isPermanent);
    }
    forbidden(message, title, displayDesktopNotification) {
        if (!title) {
            title = 'Forbidden';
        }
        this.error(message, title, displayDesktopNotification);
    }
    info(message, title, displayDesktopNotification) {
        this.printMessage(message, SharedConstants.STATUS_INFO, title, '', undefined, displayDesktopNotification);
    }
    success(message, title, displayDesktopNotification) {
        this.printMessage(message, SharedConstants.STATUS_SUCCESS, title, '', undefined, displayDesktopNotification);
    }
    warning(message, title, displayDesktopNotification) {
        this.printMessage(message, SharedConstants.STATUS_WARNING, title, '', undefined, displayDesktopNotification);
    }
    setupAlert(scope, maxAlertMessages) {
        this._scope = scope;
        this._scope.MaxAlertMessages = [];
        if (!maxAlertMessages) {
            maxAlertMessages = 5;
        }
        for (let i = 0; i < maxAlertMessages; i++) {
            const alertPlaceholderId = 'alert_placeholder_' + i;
            this._scope.MaxAlertMessages.push(alertPlaceholderId);
        }
    }
    reverseSetupAlert() {
        if (this._scope != null && this._scope.MaxAlertMessages != null) {
            delete this._scope.MaxAlertMessages;
        }
    }
}
/** @nocollapse */ /** @nocollapse */ NotificationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: NotificationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ NotificationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: NotificationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: NotificationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

const logger$r = console;
// Flags used to indicate if we need to wait for onupgradeneeded function to complete. This is to solve 10049#c33176
// retrieve the db without upgrading it (like when the user refreshes the page after db was created)
const GET_DB_EVENT_DEFAULT = 0;
// create the db, perform the default upgrade function and retrieve the db
const GET_DB_EVENT_INIT = 1;
// perform search index upgrade function and retrieve the db
const GET_DB_EVENT_REG_SEARCH_INDEXES = 2;
let IndexedDBConnectorService$;
class IndexedDBConnectorService {
    constructor(loggedInUserService, pageVisibilityService, notificationService, keyValueStorageService) {
        this.loggedInUserService = loggedInUserService;
        this.pageVisibilityService = pageVisibilityService;
        this.notificationService = notificationService;
        this.keyValueStorageService = keyValueStorageService;
        this.offlineDatabase = {
            idb: null,
            idbVersion: 0,
            name: '',
            state: 'closed',
            pendingRequests: [] // requests that arrived while idb is being opened.
        };
        this.pageVisibilityService.registerVisibilityListener((isPageHidden) => {
            if (isPageHidden) {
                return;
            }
            if (this.loggedInUserService.getUserConfigItem('cachingEnabled') === true) {
                this.getDatabase().then(() => {
                    let trueIdbVersion = this.loggedInUserService.getUserConfigItem('idbCurrentVersion'); // shared accross tabs
                    trueIdbVersion = trueIdbVersion == null ? 0 : trueIdbVersion;
                    let registeredIdbVersion = this.offlineDatabase.idbVersion;
                    registeredIdbVersion = registeredIdbVersion == null ? 0 : registeredIdbVersion;
                    // reload since there's a new version of idb due to cache being enabled by another tab, or new search indexes
                    // registered by another tab.
                    if (typeof window !== 'undefined' && trueIdbVersion !== registeredIdbVersion) {
                        window.location.reload();
                    }
                }, (error) => {
                    logger$r.error(error);
                });
            }
        });
        IndexedDBConnectorService$ = this;
    }
    getLoggedInUserService() {
        return this.loggedInUserService;
    }
    getDatabase() {
        const _self = this;
        return new Promise(function (resolve, reject) {
            if (!_self.loggedInUserService.getUserConfigItem('cachingEnabled')) {
                _self.loggedInUserService.setUserConfigItem('idbCurrentVersion', 0);
                reject(new Error('Attempted to access IndexedDB database when offline is not enabled.'));
            }
            else if (_self.offlineDatabase.idb && _self.offlineDatabase.idb.isOpen()) {
                resolve(_self.offlineDatabase.idb);
            }
            else {
                // lazy-load Dexie so it is not loaded until offline is enabled
                // TODO: lazy-load dexie back when undefined dexie's GlobalPromise issue resolved
                _self.tryPersistWithoutPromptingUser().then(function (persist) {
                    switch (persist) {
                        case "never":
                            logger$r.info("Not possible to persist storage");
                            break;
                        case "persisted":
                            logger$r.info("Successfully persisted storage silently");
                            break;
                        case "prompt":
                            logger$r.info("Not persisted, but we may prompt user when we want to.");
                            navigator.storage.persist().then(function (persistent) {
                                if (persistent) {
                                    logger$r.info("Not persisted, but we may prompt user when we want to.");
                                }
                                else {
                                    logger$r.warn("Storage may be cleared by the UA under storage pressure.");
                                    const message = "If your device is low on storage, the offline database may be cleared\ " +
                                        "in this browser. Please choose a browser that does not have this limitation";
                                    _self.notificationService.printMessage(message, 'warn', null, { x: 'right', y: 'top' });
                                }
                            });
                            break;
                    }
                });
                const idbName = IndexedDBConstants.FT_DOCUMENTS_DB_NAME_PREFIX + _self.loggedInUserService.getUserId();
                const db = new Dexie.default(idbName);
                const documentIndexes = IndexedDBConstants.DOCUMENT_INDEXES;
                let indexes = _self.loggedInUserService.getUserConfigItem('searchIndexes');
                indexes = _self.getIndexKeyFromAccountGroupConfig(indexes);
                for (let i = 0; i < indexes.length; i++) {
                    UtilArray.add(documentIndexes, indexes[i], true, null);
                }
                let currentVersion = _self.loggedInUserService.getUserConfigItem('idbCurrentVersion');
                currentVersion = currentVersion ? currentVersion : IndexedDBConstants.VERSION;
                // open the database as a dexie database
                db.version(currentVersion).stores({
                    // each table has a list of indexes assigned. The first index is the primary key,
                    // so systemHeader.versionId
                    // is the primary key for documents
                    documents: documentIndexes.toString(),
                    ftProperties: 'key',
                    offlineFiles: 'fileNo, status'
                });
                db.open();
                _self.offlineDatabase.idb = db;
                _self.offlineDatabase.idbVersion = currentVersion;
                _self.loggedInUserService.setUserConfigItem('searchIndexes', documentIndexes);
                _self.loggedInUserService.setUserConfigItem('idbCurrentVersion', currentVersion);
                resolve(_self.offlineDatabase.idb);
            }
        });
    }
    tryPersistWithoutPromptingUser() {
        return new Promise((resolve, reject) => {
            if (!navigator.storage || !navigator.storage.persisted) {
                resolve("never");
            }
            navigator.storage.persisted().then(function (persisted) {
                if (persisted) {
                    resolve("persisted");
                }
                else {
                    if (!navigator.permissions || !navigator.permissions.query) {
                        resolve("prompt");
                    }
                    navigator.permissions.query({ name: "persistent-storage" }).then(function (permission) {
                        if (permission.state === "granted") {
                            navigator.storage.persist().then(function (persisted) {
                                if (persisted) {
                                    resolve("persisted");
                                }
                                else {
                                    reject(new Error("Failed to persist"));
                                }
                            });
                        }
                        if (permission.state === "prompt") {
                            resolve("prompt");
                        }
                        resolve("never");
                    });
                }
            });
        });
    }
    upgradeOfflineDatabase() {
        const currentVersion = this.loggedInUserService.getUserConfigItem('idbCurrentVersion');
        const nextVersion = Number(currentVersion) + 1;
        const indexes = this.loggedInUserService.getUserConfigItem('searchIndexes');
        const db = this.offlineDatabase.idb;
        if (db) {
            db.close();
            db.version(nextVersion).stores({
                documents: indexes.toString(),
                ftProperties: 'key',
                offlineFiles: 'fileNo, status'
            });
            db.open();
            this.loggedInUserService.setUserConfigItem('idbCurrentVersion', nextVersion);
            this.keyValueStorageService.setItem('idbCurrentVersion', nextVersion);
            this.offlineDatabase.idbVersion = nextVersion;
            this.offlineDatabase.idb = db;
            logger$r.info('Offline DB has been upgraded to version: ' + nextVersion);
        }
    }
    // /**
    //  THIS METHOD IS NOT USED ANY WHERE. WILL BE CONVERTED TO ANGULAR LATER.
    //  * Generic function for opening
    //  * @param version
    //  * @param requestEvent
    //  * @returns {jQuery.promise|promise|*}
    //  */
    // function openOfflineDatabase(version, requestEvent){
    //   var deferred = $q.defer();
    //
    //   var onsuccessComplete = false;
    //   //we should wait for upgrade in two cases: [1]upon creating the database & [2]upon registering search indexes
    //   var dontWaitForUpgrade = requestEvent === GET_DB_EVENT_DEFAULT;
    //   var onupgradeComplete = dontWaitForUpgrade;//regard as completed if don't wait
    //
    //   /**
    //    * Chrome and Firefox do not behave the same way when it comes to the order/timing which IDBDatabase.onsuccess
    //    * and IDBDatabase.onupgradeneeded functions. resolveOpenRequest will make sure that onupgradeneeded function
    //    * completes before we return the IDBDatabase reference to those who request it via the getDatabase function.
    //    *
    //    * See 10049#c33176
    //    */
    //   var resolveOpenDBRequest = function(IDBDatabase){
    //     if(onsuccessComplete && onupgradeComplete){
    //       deferred.resolve(IDBDatabase);
    //       $rootScope.$digest();
    //       return deferred.promise;
    //     }
    //   };
    //
    //   var idbName = LoggedInUserService.getUserId();
    //
    //   var dbRequest = indexedDB.open(idbName, parseInt(version));
    //
    //   dbRequest.onblocked = function(event) {
    //     logger.info("IndexedDB Event: indexedDB blocked. ");
    //   };
    //
    //   dbRequest.onerror = function (event) {
    //     var msg = "Error opening indexedDB: " + event.target.error.message;
    //     logger.error(msg);
    //     if (event.target.error.name === "VersionError") {
    //
    //       var nextVersion = parseInt(version) + 1;
    //       LoggedInUserService.setUserConfigItem('idbCurrentVersion', nextVersion);
    //       logger.info("Reloading page to open IDB with version: " + nextVersion);
    //
    //       $window.location.reload();
    //
    //     } else if (event.target.error.name === "AbortError") {
    //       logger.error("Error: AbortError.");
    //     } else {
    //       logger.error("Error: " + event.target.error.name);
    //       deferred.reject(event.target.error);
    //     }
    //   };
    //
    //   dbRequest.onsuccess = function (event) {
    //     // only set the database version when the upgrade is successful
    //     LoggedInUserService.setUserConfigItem('idbCurrentVersion', version);
    //
    //     logger.info('IndexedDB Event: ' + indexedDBConstants.DB_NAME + ' storage opened successfully. Version: ' + version);
    //
    //     var database = dbRequest.result;
    //
    //     database.onclose = function (evt) {
    //       logger.info("Closing indexedDB database: " + database.name);
    //       $window.alert("Offline database closed.");
    //     };
    //
    //     database.onabort = function (evt) {
    //       logger.error("IndexedDB abort occured: " + evt.target.error.message);
    //       logger.error(evt);
    //     };
    //
    //     database.onversionchange = function (evt) {//another tab might have upgraded the database
    //       logger.info('IndexedDB Event: database version changed.');
    //
    //       ModalService.showDialog('A new version for the offline database is available. Please reload.',
    //         'WARNING',
    //         function () {
    //           $window.location.reload();
    //         },
    //         function () {
    //
    //         });
    //     };
    //
    //     database.onerror = function(evt){
    //       logger.error('IDBDatabase error occurred: ' + evt.target.error.message);
    //       logger.error(evt);
    //     };
    //
    //     onsuccessComplete = true;
    //     resolveOpenDBRequest(database);
    //
    //   };
    //
    //   var defaultUpgradeFunc =  function (event) {
    //
    //     var database = event.target.result;
    //     //If the an objectStore's structure is modified in a future version, make sure to remove
    //     //the old version of the objectStore (if it exists)
    //
    //     //recreate tables to prep for whatever structural changes in the indexes
    //     var tables = [
    //       {
    //         name: indexedDBConstants.DOCUMENT_TABLE_NAME,
    //         param: { keyPath: "systemHeader.versionId", autoIncrement: false},
    //         indexes : [
    //           {
    //             name:  indexedDBConstants.DOCUMENT_INDEX_VERSION_ID,
    //             path: "systemHeader.versionId",
    //             unique: true
    //           },
    //           {
    //             name: indexedDBConstants.DOCUMENT_INDEX_DOCUMENT_ID,
    //             path: "documentId",
    //             unique: false
    //           },
    //           {
    //             name: indexedDBConstants.DOCUMENT_INDEX_PENDING_OPERATION,
    //             path: "systemHeader.offlineDetails.status",
    //             unique: false
    //           },
    //           {
    //             name: indexedDBConstants.DOCUMENT_INDEX_SYSTEM_TYPE,
    //             path: "systemHeader.systemType",
    //             unique: false
    //           }
    //         ]
    //       },
    //       {
    //         name: indexedDBConstants.FT_PROPERTIES_TABLE_NAME,
    //         param:{ keyPath: "key", autoIncrement: false},
    //         indexes : [
    //           { name: "key", path: "key", unique: true }
    //         ]
    //       },
    //       {
    //         name: indexedDBConstants.OFFLINE_FILE_TABLE_NAME,
    //         param: { keyPath: 'fileNo', autoIncrement : false},
    //         indexes:[
    //           { name: "fileNo", path: "fileNo", unique: true },
    //           { name: "status", path: "status", unique: false }
    //         ]
    //       }
    //     ];
    //
    //     //recreate tables for new structural changes in the indexes
    //     for(var i = 0 ; i < tables.length ; i++){
    //       var tbl = tables[i];
    //       //Do not add recreate already existing objectStores else an ConstraintError error will be thrown.
    //       if(database.objectStoreNames.contains(tbl.name)){
    //         database.deleteObjectStore(tbl.name);
    //       }
    //       var store = database.createObjectStore(tbl.name, tbl.param);
    //       for(var z = 0 ; z < tbl.indexes.length; z++){
    //         var index = tbl.indexes[z];
    //         createIndex(store, index.name, index.path, index.unique);
    //       }
    //     }
    //
    //     logger.info("IndexedDB Event: Default offline database indexes set.");
    //
    //     onupgradeComplete = true;
    //     resolveOpenDBRequest(database);
    //
    //   };
    //
    //   var registerSearchIndexesFunc = function (event) {
    //
    //     var searchIndexes = LoggedInUserService.getUserConfigItem('searchIndexes');
    //     var objectStore = event.target.transaction.objectStore(indexedDBConstants.DOCUMENT_TABLE_NAME);
    //
    //     for(var x = 0 ; x < searchIndexes.length ; x++){
    //       var value = searchIndexes[x].replace(".", "___");
    //       createIndex(objectStore, value, value, false);
    //     }
    //
    //     //record all registered indexes
    //     var registered = LoggedInUserService.getUserConfigItem('registeredSearchIndexes');
    //     LoggedInUserService.setUserConfigItem('registeredSearchIndexes', angular.extend(registered, searchIndexes));
    //
    //     logger.info("Search indexes registered." + searchIndexes);
    //
    //     onupgradeComplete = true;
    //     var database = event.target.result;
    //     resolveOpenDBRequest(database);
    //   };
    //
    //   var regSearchIndexes = requestEvent === GET_DB_EVENT_REG_SEARCH_INDEXES;
    //
    //   dbRequest.onupgradeneeded = regSearchIndexes ? registerSearchIndexesFunc: defaultUpgradeFunc;
    //
    //   return deferred.promise;
    //
    // }
    upgradeIndexedDB() {
        const searchIndexes = this.loggedInUserService.getUserConfigItem('searchIndexes');
        const registered = this.loggedInUserService.getUserConfigItem('registeredSearchIndexes');
        let hasUnregisteredIndex = false;
        for (let k = 0; k < searchIndexes.length; k++) {
            hasUnregisteredIndex = registered.indexOf(searchIndexes[k]) === -1;
            if (hasUnregisteredIndex) {
                break;
            }
        }
        if (!hasUnregisteredIndex) {
            return Promise.resolve(); // just return a resolved promise
        }
        return this.getDatabase();
    }
    /**
     * open an IndexedDB cursor
     * @param cursorIndexName - the name of the index to open a cursor on
     * @param cursorRange - an IndexedDBKeyRange (part of the IndexedDB api) of the cursor to open
     * @returns promise.promise|*|jQuery.promise the promise
     */
    openCursor(cursorIndexName, cursorRange) {
        const _self = this;
        return new Promise((resolve, reject) => {
            _self.getDatabase().then(function (db) {
                const tbl = IndexedDBConstants.DOCUMENT_TABLE_NAME;
                const transaction = db.transaction(tbl, 'readwrite');
                const store = transaction.objectStore(tbl);
                const index = store.index(cursorIndexName);
                const request = index.openCursor(cursorRange);
                request.onsuccess = function () {
                    const cursor = request.result;
                    resolve(cursor);
                };
                request.onerror = function () {
                    reject('Error opening indexedDB cursor in table: ' + tbl);
                };
            }, (error) => {
                reject(error);
            });
        });
    }
    createIndex(objectStore, indexName, keyPath, isUnique) {
        if (!UtilArray.contains(objectStore.indexNames, indexName)) {
            objectStore.createIndex(indexName, keyPath, { unique: !!isUnique });
        }
    }
    closeDatabase() {
        if (this.offlineDatabase.idb != null) {
            this.offlineDatabase.idb.delete();
            this.offlineDatabase.idb = null;
            this.offlineDatabase.idbVersion = null;
            this.offlineDatabase.state = 'closed';
        }
        return Promise.resolve();
    }
    getIndexKeyFromAccountGroupConfig(indexes) {
        let accountConfig = this.loggedInUserService.getUserAccountGroupConfigDocument();
        if (accountConfig && accountConfig.indexedDBIndexes && accountConfig.indexedDBIndexes.length > 0) {
            indexes = UtilArray.union(indexes, accountConfig.indexedDBIndexes);
        }
        return indexes;
    }
}
/** @nocollapse */ /** @nocollapse */ IndexedDBConnectorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: IndexedDBConnectorService, deps: [{ token: LoggedInUserService }, { token: PageVisibilityService }, { token: NotificationService }, { token: KeyValueStorageService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ IndexedDBConnectorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: IndexedDBConnectorService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: IndexedDBConnectorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: LoggedInUserService }, { type: PageVisibilityService }, { type: NotificationService }, { type: KeyValueStorageService }]; } });

// type of message sent from a web worker to the main thread
var WebWorkerMessageType;
(function (WebWorkerMessageType) {
    WebWorkerMessageType[WebWorkerMessageType["ADD_TO_MAX_CACHE_COUNT"] = 0] = "ADD_TO_MAX_CACHE_COUNT";
    WebWorkerMessageType[WebWorkerMessageType["ADD_TO_CURRENT_CACHE_COUNT"] = 1] = "ADD_TO_CURRENT_CACHE_COUNT";
    WebWorkerMessageType[WebWorkerMessageType["SET_PAGE_DOWNLOAD_PROGRESS"] = 2] = "SET_PAGE_DOWNLOAD_PROGRESS";
    WebWorkerMessageType[WebWorkerMessageType["SET_INITIAL_CACHE_QUERY_SUCCESS"] = 3] = "SET_INITIAL_CACHE_QUERY_SUCCESS";
    WebWorkerMessageType[WebWorkerMessageType["INITIAL_CACHE_COMPLETED"] = 4] = "INITIAL_CACHE_COMPLETED";
    WebWorkerMessageType[WebWorkerMessageType["SET_LAST_SYNC_START_TIME"] = 5] = "SET_LAST_SYNC_START_TIME";
    WebWorkerMessageType[WebWorkerMessageType["PEINDING_DOCUMENT_COUNT"] = 6] = "PEINDING_DOCUMENT_COUNT";
})(WebWorkerMessageType || (WebWorkerMessageType = {}));
;

'use strict';
/**
 * A constants object for constants that are used on the client-side only
 */
const ClientConstants = {
    // Used for the search filter
    FILTER: 'filter',
    FILTERED_QUERY: 'queryToBeFiltered',
    HTTP_STATUS_LOGIN_REJECTED: 401,
    OPERATION_TYPE_POST: 'POST',
    OPERATION_TYPE_PUT: 'PUT',
    OPERATION_TYPE_DELETE: 'DELETE',
    LAST_ENTERED_STATE_URL: 'LastEnteredStateUrl',
    URL_EQUAL_REPLACEMENT_STRING: 'fttf',
    // element ids
    TOGGLE_ID_LEFT: 'left_close',
    // broadcasts
    DOCUMENT_NEW: 'document:new',
    DOCUMENT_RENEW: 'document:renew',
    DOCUMENT_SAVING: 'document:saving',
    DOCUMENT_SAVED: 'document:saved',
    DOCUMENT_CHANGED: 'document:changed',
    DOCUMENT_DESTROYED: 'document:destroyed',
    DOCUMENT_LOADED: 'document:loaded',
    DOCUMENT_LOAD_ERROR: 'document:loadError',
    VALIDATE_FORM_FIELDS: 'document:validateFormFields',
    DOCUMENT_FORM_UPDATED: 'document:formUpdated',
    PRESAVE_DOCUMENT_FORM_RESET: 'document:preSaveDocumentReset',
    FORM_DETECT_CHANGES: 'form:detectChanges',
    URL_CLICKED: 'url:clicked',
    FORM_DIRTY_UPDATED: 'formDirty',
    SPINNER_UPDATED: 'showSpinner',
    FORM_RESET: 'formReset',
    RULES_STATUS_STARTED: 'rules_status_started',
    RULES_STATUS_DONE: 'rules_status_done',
    // validation
    VALIDATION_INTERVAL_TIME: 500,
    VALIDATION_REQUIRED_KEY: 'required',
    VALIDATION_REQUIRED_MESSAGE: 'This field is required.',
    VALIDATION_MIN_LENGTH_KEY: 'hasMinLength',
    VALIDATION_MIN_LENGTH_MESSAGE: 'This field is required at least %s characters long.',
    VALIDATION_MAX_LENGTH_KEY: 'hasMaxLength',
    VALIDATION_MAX_LENGTH_MESSAGE: 'This field is required max %s characters long.',
    CHANGED_DOCUMENT_LIST_ID_KEY: '___DOCUMENT_LIST_ID___',
    FILE_SENT_EVENT_NAME: 'FileSentEventName',
    LOCAL_STORAGE_OFFLINE_STATUS_KEY: 'OfflineStatus',
    BEFORE_INSTALL_PROMPT: 'beforeinstallprompt',
    DOCUMENT_SAVE_STATUS: 'document:save:status',
};

const logger$q = console;
const OFFLINE_SET_OFFLINE_STATUS = 'OFFLINE_SET_OFFLINE_STATUS';
function offlineSetOfflineStatus(offlineStatus) {
    return {
        type: OFFLINE_SET_OFFLINE_STATUS,
        payload: {
            offlineStatus
        }
    };
}

const serverRoutes$4 = SharedUrlRoutes.serverRoutes;
class SessionTestService {
    constructor(http) {
        this.http = http;
    }
    testSession() {
        // returns an observable that shows whether the session to the server is connected
        return this.testSessionObservable().toPromise();
    }
    testSessionObservable() {
        return this.http.get(serverRoutes$4.sessionTest, { responseType: 'text' });
    }
}
/** @nocollapse */ /** @nocollapse */ SessionTestService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: SessionTestService, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ SessionTestService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: SessionTestService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: SessionTestService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }]; } });

class OfflineStatusService {
    constructor(loggedInUserService, appStore, broadcastService, keyValueStorageService, sessionTestService) {
        this.loggedInUserService = loggedInUserService;
        this.appStore = appStore;
        this.broadcastService = broadcastService;
        this.keyValueStorageService = keyValueStorageService;
        this.sessionTestService = sessionTestService;
        this.offlineStatus = {
            unsyncedCount: 0,
            connected: true,
            incomingCacheCount: 0,
            maxCacheCount: 0,
            currentCacheCount: 0,
            cacheLoading: false,
            pendingDocumentCount: 0,
            errorCacheCount: 0,
            cachingApp: false,
            initialCachingComplete: true,
            connectionText: null,
            currentStaticResourceCount: 0,
            totalStaticResourceCount: 0,
            tileServerCacheCount: 0,
            tileServerMaxCacheCount: 0,
            errorCacheAttachedFile: [],
            errorOfflineChangesSyncDocs: [],
            maxCacheAttachedFileCount: 0,
            currentCacheAttachedFileCount: 0
        };
        this.offlineStatusSubject = new Subject();
        this.offlineStatus$ = this.offlineStatusSubject.asObservable();
        this.pageDownloadProgress = {
            pageNumber: 0,
            loaded: 0,
            total: 0
        };
    }
    getLastSyncDate() {
        // initial value is zero see this.loggedInUserService;
        // fetch directly from the User settings so it will still work across logins
        return this.loggedInUserService.getUserConfigItem('lastSyncDate');
    }
    setLastSyncDate(document) {
        // update the last sync date if the date is later than the stored value. This will be used to sync new objects
        // when data is saved or the app is loaded again. The sync will only happen from the last sync date value
        if (document && document.systemHeader && document.systemHeader.serverUpdatedDate) {
            // use the number of values since the epoch (1/1/1970) in milliseconds, as returned by the valueOf() function,
            // so it can be used to easily compare
            const syncDate = new Date(document.systemHeader.serverUpdatedDate).valueOf();
            if (syncDate > this.getLastSyncDate()) {
                this.loggedInUserService.setUserConfigItem('lastSyncDate', syncDate);
            }
        }
    }
    setOfflineStatusToLocalStorage() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.keyValueStorageService.setItem(ClientConstants.LOCAL_STORAGE_OFFLINE_STATUS_KEY, JSON.stringify(this.offlineStatus));
            // publish offline status subject.
            // This can be changed to publish the subject in other tabs when the data changes in localStorage
            this.offlineStatusSubject.next(this.offlineStatus);
            this.appStore.dispatch(offlineSetOfflineStatus(this.offlineStatus));
            this.broadcastService.broadcast('offlineStatus:updated', this.offlineStatus);
        });
    }
    /**
     * Displays the loading cache status in the page
     * @param isCacheLoading
     * returns true if all pending documents had been cached.
     */
    setCacheLoadingStatus(isCacheLoading) {
        this.offlineStatus.cacheLoading = isCacheLoading;
        this.setOfflineStatusToLocalStorage();
        return this.offlineStatus.currentCacheCount === this.offlineStatus.maxCacheCount;
    }
    setConnected() {
        this.offlineStatus.connected = true;
        this.offlineStatus.connectionText = 'Online';
        this.setOfflineStatusToLocalStorage();
    }
    setDisconnected() {
        this.offlineStatus.connected = false;
        this.offlineStatus.connectionText = 'Offline';
        this.setOfflineStatusToLocalStorage();
    }
    addToMaxCacheCount(count) {
        if (count) {
            this.offlineStatus.maxCacheCount += count;
            this.setOfflineStatusToLocalStorage();
        }
    }
    ignoreCacheDocument(doc) {
        this.offlineStatus.currentCacheCount++;
        this.offlineStatus.errorCacheCount++;
        this.setOfflineStatusToLocalStorage();
    }
    getErrorCacheCount() {
        return this.offlineStatus.errorCacheCount;
    }
    initialCacheQuerySuccess(document) {
        this.offlineStatus.currentCacheCount++;
        if (this.offlineStatus.currentCacheCount >= this.offlineStatus.maxCacheCount) {
            this.offlineStatus.currentCacheCount = 0;
            this.offlineStatus.maxCacheCount = 0;
        }
        this.setOfflineStatusToLocalStorage();
        this.setLastSyncDate(document);
    }
    setInitialCachingComplete(complete) {
        this.offlineStatus.initialCachingComplete = (complete === true);
        this.setOfflineStatusToLocalStorage();
    }
    setPendingDocumentCount(pendingCount) {
        this.offlineStatus.pendingDocumentCount = pendingCount;
        this.loggedInUserService.setUserConfigItem('offlinePendingDocumentsCount', pendingCount);
        this.setOfflineStatusToLocalStorage();
    }
    incPendingDocumentCount() {
        this.offlineStatus.pendingDocumentCount++;
        this.loggedInUserService.setUserConfigItem('offlinePendingDocumentsCount', this.offlineStatus.pendingDocumentCount);
        this.setOfflineStatusToLocalStorage();
    }
    decPendingDocumentCount() {
        this.offlineStatus.pendingDocumentCount--;
        this.loggedInUserService.setUserConfigItem('offlinePendingDocumentsCount', this.offlineStatus.pendingDocumentCount);
        this.setOfflineStatusToLocalStorage();
    }
    isConnected() {
        return this.offlineStatus.connected;
    }
    setCachingAppStatus(isCachingApp) {
        this.offlineStatus.cachingApp = isCachingApp;
        this.setOfflineStatusToLocalStorage();
    }
    setCurrentStaticResourceCount(count) {
        this.offlineStatus.currentStaticResourceCount = count;
        this.setOfflineStatusToLocalStorage();
    }
    setTotalStaticResourceCount(count) {
        this.offlineStatus.totalStaticResourceCount = count;
        this.setOfflineStatusToLocalStorage();
    }
    mergeOfflineStatusData(offlineStatus) {
        merge(this.offlineStatus, offlineStatus);
        this.setOfflineStatusToLocalStorage();
    }
    checkServerConnectionStatus() {
        const _self = this;
        return new Promise((resolve, reject) => {
            this.sessionTestService
                .testSession()
                .then(res => {
                if (res === "") {
                    resolve(false);
                }
                else {
                    resolve(true);
                }
            }, err => {
                if (err.status === 401) {
                    resolve(true);
                }
            });
        });
    }
    getOfflineLoginConnectionPollTime() {
        const config = window.ftClientConfig;
        return config.offlineLoginConnectionPollTime || 1000;
    }
    setMaxCacheCount(count) {
        if (count) {
            this.offlineStatus.maxCacheCount = count;
            this.setOfflineStatusToLocalStorage();
        }
    }
}
/** @nocollapse */ /** @nocollapse */ OfflineStatusService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: OfflineStatusService, deps: [{ token: LoggedInUserService }, { token: AppStore }, { token: BroadcastService }, { token: KeyValueStorageService }, { token: SessionTestService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ OfflineStatusService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: OfflineStatusService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: OfflineStatusService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: LoggedInUserService }, { type: AppStore }, { type: BroadcastService }, { type: KeyValueStorageService }, { type: SessionTestService }]; } });

const logger$p = console;
class DexieSearchService {
    constructor(indexedDBConnectorService) {
        this.indexedDBConnectorService = indexedDBConnectorService;
    }
    getDatabase() {
        return this.indexedDBConnectorService.getDatabase();
    }
    search(query) {
        const _self = this;
        return new Promise((resolve, reject) => {
            logger$p.debug('Doing search in DexieSearchService...');
            if (query.dexieSearchFunction) {
                _self.getDatabase().then(function (db) {
                    query.dexieSearchFunction(db, function (err, data) {
                        if (err) {
                            const message = 'Error in Dexie: ' + err;
                            reject(new Error(message));
                        }
                        else {
                            let results;
                            results = {};
                            results.total = data.length;
                            results.statusCode = UtilHttpStatus.OK;
                            results.data = data;
                            results.searchProvier = 'dexie';
                            resolve(results);
                        }
                    });
                }, (error) => {
                    reject(error);
                });
            }
            else {
                const msg = 'Dexie search not specified for offline querying';
                reject(new Error(msg));
            }
        });
    }
}
/** @nocollapse */ /** @nocollapse */ DexieSearchService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DexieSearchService, deps: [{ token: IndexedDBConnectorService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ DexieSearchService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DexieSearchService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DexieSearchService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: IndexedDBConnectorService }]; } });

const logger$o = console;
class IndexedDBService {
    constructor(http, indexedDBConnectorService, offlineStatusService, loggedInUserService, dexieSearchService, clientAccessService, broadcastService, notificationService, keyValueStorageService) {
        this.http = http;
        this.indexedDBConnectorService = indexedDBConnectorService;
        this.offlineStatusService = offlineStatusService;
        this.loggedInUserService = loggedInUserService;
        this.dexieSearchService = dexieSearchService;
        this.clientAccessService = clientAccessService;
        this.broadcastService = broadcastService;
        this.notificationService = notificationService;
        this.keyValueStorageService = keyValueStorageService;
        this.constants = IndexedDBConstants;
        this.checkQuotaExceededError = (error) => {
            if ((error.name === 'QuotaExceededError') || (error.inner && error.inner.name === 'QuotaExceededError')) {
                const message = "The disk is full, please free up disk space. " + error.message;
                logger$o.error(message);
                this.notificationService.printMessage(message, 'error', null, { x: 'right', y: 'top' });
            }
        };
        this.onDocumentUpdated();
        this.userSub = this.loggedInUserService.observableUser().subscribe((user) => this.user = user);
    }
    ngOnDestroy() {
        this.userSub.unsubscribe();
        this.terminateService();
    }
    initialise() {
        return this.getDatabase();
    }
    terminateService() {
        return this.indexedDBConnectorService.closeDatabase();
    }
    search(options) {
        const _self = this;
        if (options.searchOptions && options.searchOptions.dexieSearchFunction) {
            return _self.dexieSearchService.search(options.searchOptions);
        }
        else {
            const msg = 'Dexie search not specified for offline querying';
            return Promise.reject(new Error(msg));
        }
    }
    insert(bean) {
        if (!bean.systemHeader) {
            bean.systemHeader = {};
        }
        // set versionId
        bean.systemHeader.versionId = UtilDocumentId.generateId();
        // set currentVersion for new document
        bean.systemHeader.currentVersion = true;
        // set created date for version
        bean.systemHeader.createdDate = new Date();
        const userId = this.user ? this.user.account.documentId : null;
        if (userId) {
            bean.systemHeader.createdBy = userId;
        }
        if (!bean.systemHeader.documentCreatedDate) {
            bean.systemHeader.documentCreatedDate = new Date();
        }
        if (userId && !bean.systemHeader.documentCreatedBy) {
            bean.systemHeader.documentCreatedBy = userId;
        }
        return this.persist(bean, ClientConstants.OPERATION_TYPE_POST, null);
    }
    destroy(id) {
        const _self = this;
        return new Promise((resolve, reject) => {
            _self.getDocumentCurrentVersion(id, function (originalRecord) {
                const originalBean = originalRecord;
                // save version as an insert
                const versionDoc = VersionManager.createVersion(originalBean);
                _self.cacheDocument(versionDoc, true).then(function successFunc(record) {
                    const newBean = cloneDeep(originalBean);
                    newBean.systemHeader.deleted = true;
                    // perform newBean update
                    // Aside from the differences in data between originalBean and newbean, newBean will always
                    // have its previousVersionId = originalBean's versionId. newBean will also be set as the
                    // current version with a new versionId
                    if (newBean.systemHeader.versionId) {
                        newBean.systemHeader.previousVersionId = originalBean.systemHeader.versionId;
                    }
                    // update createdBy every time because when we update the doc it's a new version
                    newBean.systemHeader.createdBy = _self.user ?
                        _self.user.account.documentId : null;
                    newBean.systemHeader.createdDate = new Date();
                    newBean.systemHeader.versionId = UtilDocumentId.generateId();
                    _self.persist(newBean, ClientConstants.OPERATION_TYPE_DELETE).then(function sucF(rec) {
                        resolve(rec);
                    }, function errF(error) {
                        reject(error);
                    });
                }, function errorFunc(err) {
                    reject(err);
                });
            });
        });
    }
    getDocument(id) {
        const _self = this;
        return new Promise(function (resolve, reject) {
            _self.getDocumentCurrentVersion(id, function (cacheObject) {
                if (cacheObject == null) {
                    reject('Document not available in offline database.');
                }
                else {
                    resolve(cacheObject);
                }
            });
        });
    }
    getDatabase() {
        return this.indexedDBConnectorService.getDatabase();
    }
    closeDatabase() {
        this.getDatabase().then(function (db) {
            db.close();
        }, (error) => {
            logger$o.error(error);
        });
    }
    backupData() {
        const _self = this;
        return new Promise((resolve, reject) => {
            _self.getDatabase().then(function (db) {
                var tblName = IndexedDBConstants.DOCUMENT_TABLE_NAME;
                db.transaction('rw', db[tblName], (result) => {
                    db[tblName].toArray().then((array) => {
                        resolve(array);
                    }).catch(error => resolve(error));
                }).catch(e => {
                    logger$o.error('An error occured while clearing cached documents.');
                    logger$o.error(e.stack || e);
                    reject(e);
                });
            }, (error) => {
                reject(error);
            });
        });
    }
    clearData() {
        const _self = this;
        return new Promise((resolve, reject) => {
            _self.getDatabase().then(function (db) {
                const tblName = IndexedDBConstants.DOCUMENT_TABLE_NAME;
                db.transaction('rw', db[tblName], (result) => {
                    db[tblName].clear().then(() => __awaiter(this, void 0, void 0, function* () {
                        _self.loggedInUserService.setUserConfigItem('cachingEnabled', false);
                        _self.loggedInUserService.setUserConfigItem('lastSyncDate', 0);
                        _self.loggedInUserService.setUserConfigItem('idbCurrentVersion', 0);
                        _self.loggedInUserService.setUserConfigItem('searchIndexes', []);
                        yield _self.keyValueStorageService.removeItem('searchIndexes');
                        yield _self.keyValueStorageService.removeItem('idbCurrentVersion');
                        yield _self.keyValueStorageService.removeItem('MainOfflinePoller');
                        _self.offlineStatusService.offlineStatus.errorCacheAttachedFile = [];
                        _self.offlineStatusService.offlineStatus.maxCacheAttachedFileCount = 0;
                        _self.offlineStatusService.offlineStatus.totalStaticResourceCount = 0;
                        yield _self.offlineStatusService.setOfflineStatusToLocalStorage();
                        resolve(true);
                    }), (e) => {
                        logger$o.error('An error occured while clearing cached documents.');
                        logger$o.error(e.stack || e);
                        resolve(false);
                    });
                }).catch(e => {
                    logger$o.error('An error occured while clearing cached documents.');
                    logger$o.error(e.stack || e);
                    resolve(false);
                });
            }, (error) => {
                logger$o.error('An error occured while clearing cached documents.');
                logger$o.error(error.stack || error);
                resolve(false);
            });
        });
    }
    /**
     * set indexeddb indexes in document stored in IndexedDB so that it can be queried
     * @param document the document
     */
    setIndexedDBIndexes(document) {
        if (!document || !document.systemHeader) {
            return;
        }
        const templateType = document.systemHeader.systemType === SharedConstants.SYSTEM_TYPE_TEMPLATE;
        const withSearchIndexes = document[IndexedDBConstants.DB_INDEXES] && document[IndexedDBConstants.DB_INDEXES].length;
        if (templateType && withSearchIndexes) {
            const templateIndexes = document[IndexedDBConstants.DB_INDEXES];
            let indexes = this.loggedInUserService.getUserConfigItem('searchIndexes');
            indexes = UtilArray.union(indexes, templateIndexes, null);
            this.loggedInUserService.setUserConfigItem('searchIndexes', indexes);
        }
    }
    /**
     * cache a document
     * @param document the document
     * @returns PromiseLike<any> the Promise
     */
    doCacheDocument(document, updateOfflineStatus) {
        const _self = this;
        return new Promise((resolve, reject) => {
            if (!document.documentId) {
                _self.offlineStatusService.ignoreCacheDocument(document);
                const errMsg = "Document has no documentId " + JSON.stringify(document);
                logger$o.error(errMsg);
                reject(new Error(errMsg));
                return;
            }
            _self.setIndexedDBIndexes(document);
            _self.cacheDocument(document, false).then(function successFunc(cacheObject) {
                const doc = cacheObject;
                // update the last sync time in local storage with the time of this record if
                // it is later than the stored value
                if (updateOfflineStatus !== false) {
                    _self.offlineStatusService.initialCacheQuerySuccess(doc);
                }
                resolve(doc);
            }, function errorFunc(err) {
                const errorMsg = 'Error caching document: ' + document.documentId + ': ' + err;
                logger$o.error(errorMsg);
                if (updateOfflineStatus !== false) {
                    _self.offlineStatusService.ignoreCacheDocument(document);
                }
                reject(new Error(errorMsg));
            });
        });
    }
    /**
     * Updates or inserts a given row in the indexedb database under the table. For tables that uses inline keys
     * only (the primary key is on the object).
     * @param tblName - table name
     * @param record - record
     */
    saveEntry(tblName, record, callback) {
        const _self = this;
        if (!record) {
            if (typeof callback !== 'undefined') {
                callback(new Error('Document not specified in IndexedDB saveEntry'));
            }
            return;
        }
        if (record && record.systemHeader && record.systemHeader.versionId) {
            //                logger.info('Saving IndexedDB entry with documentId: ' +
            //                    record.documentId + '. systemHeader.versionId: ' + record.systemHeader.versionId );
        }
        this.getDatabase().then(function success(db) {
            db.transaction('rw', db[tblName], (result) => {
                db[tblName].put(record).then((data) => {
                    if (callback != null) {
                        callback();
                    }
                }, (e) => {
                    _self.checkQuotaExceededError(e);
                    logger$o.error('Error on IndexedDBService.saveEntry:');
                    logger$o.error(e);
                    if (callback != null) {
                        callback(e);
                    }
                });
            }).catch(e => {
                _self.checkQuotaExceededError(e);
                logger$o.error('Error adding entry to IndexedDB: ' + e.stack || e);
                callback(e);
            });
        }, (error) => {
            logger$o.error('Error adding entry to IndexedDB: ' + error.stack || error);
            callback(error);
        });
    }
    /**
     * Request errors will set the callback function's first argument to null.
     * Returns the file entry object as the callback function's first argument.
     * @param fileNo - the file number
     * @param callback - the callback
     */
    getFile(fileNo, callback) {
        const tblName = IndexedDBConstants.OFFLINE_FILE_TABLE_NAME;
        const indexName = IndexedDBConstants.OFFLINE_FILE_INDEX_FILENO;
        const indexValue = fileNo;
        this.getEntries(tblName, indexName, indexValue, function (entriesArr) {
            if (entriesArr.length > 0) {
                callback(entriesArr[0]); // get the first entry -- which is the only entry, fileNos are unique
                return;
            }
            callback(null);
        });
    }
    /**
     * Record format should be:
     *    var record = {
                      fileNo : fileNo,
                      status : status, //'UPLOADED' or 'PENDING'
                      blob: blob,
                      uploadConfig : uploadConfig
                  };
     */
    saveFile(record, status, errMessage, errorCount) {
        record.errorMessage = errMessage;
        record.errorCount = errorCount;
        const tblName = IndexedDBConstants.OFFLINE_FILE_TABLE_NAME;
        this.saveEntry(tblName, record, function (err) {
            if (err) {
                logger$o.error('Failed to save file to offline database: ' + err.message);
            }
        });
    }
    // ***End of Low level functions
    /**
     * Retrieve entries from a table
     * @param tblName - the table name
     * @param indexName - the index to search for
     * @param indexValue - the value to search for in the index
     * @param callback - callback that passes the entries in an array
     */
    getEntries(tblName, indexName, indexValue, callback) {
        this.getDatabase().then(function success(db) {
            db[tblName].where(indexName).equals(indexValue).toArray(results => {
                callback(results);
            }).catch(function (error) {
                logger$o.error('Error getting dexie entries: ' + error.message);
                callback([]);
            });
        }, (error) => {
            logger$o.error('Error getting dexie entries: ' + error.message);
            callback([]);
        });
    }
    /**
     * Delete entries from a table
     * @param tblName - the table name
     * @param indexName - the index to search for
     * @param indexValue - the value to search for in the index
     * @param callback - callback that passes deleted entries in an array. (optional)
     */
    deleteEntries(tblName, indexName, indexValue, callback) {
        this.getDatabase().then(function success(db) {
            db[tblName].where(indexName).equals(indexValue).delete().then(deleteCount => {
                callback(null, deleteCount);
            }).catch(function (error) {
                callback(error, null);
            });
        }, (error) => {
            callback(error, null);
        });
    }
    /**
     * Seeks out the current version of the document in indexedDB document table and passes it via callback
     * @param id - either versionId or documentId. Evaluate it as documentId first, if that fails then treat it as a version id
     * @param callback - first param is the actual document. null if not found
     */
    getDocumentCurrentVersion(id, callback) {
        const _self = this;
        if (id === undefined || id === null) {
            callback(null);
            return;
        }
        const tblName = IndexedDBConstants.DOCUMENT_TABLE_NAME;
        const indexName = IndexedDBConstants.DOCUMENT_INDEX_DOCUMENT_ID;
        const indexValue = id;
        _self.getEntries(tblName, indexName, indexValue, function (cachedObjects) {
            for (let x = 0; x < cachedObjects.length; x++) {
                const cacheObject = cachedObjects[x];
                if (cacheObject.systemHeader.currentVersion === true) {
                    callback(cacheObject);
                    return;
                }
            }
            _self.loadDocByVersion(id, callback);
        });
    }
    loadDocsById(docId, callback) {
        if (docId === undefined || docId === null) {
            callback(null);
            return;
        }
        const tblName = IndexedDBConstants.DOCUMENT_TABLE_NAME;
        const indexName = IndexedDBConstants.DOCUMENT_INDEX_DOCUMENT_ID;
        const indexValue = docId;
        this.getEntries(tblName, indexName, indexValue, function (cachedObjects) {
            callback(cachedObjects);
        });
    }
    /*
     offlineDetails
  
     Once offline, if a document has this object under it, it means that it is subject to an operation yet
     to be submitted to the server.
  
     This object will have the following attributes {type, status, lastRequestStatus, tries}
  
     type - From ClientConstants: OPERATION_TYPE_DELETE, OPERATION_TYPE_PUT, OPERATION_TYPE_POST
     status - IndexedDBConstants: (1)OFFLINE_STATUS_UPLOADED, (2)OFFLINE_STATUS_PENDING,
     (3)OFFLINE_STATUS_UPLOADING (4)OFFLINE_STATUS_FAILED
  
     lastRequestStatus - http status of last attempt to upload this operation
     tries - integer count of the number of times this document's pending operation has been uploaded to the server
  
     Should not be used to cache documents. Use cacheDocument function instead.
     Used for saving documents that needs to be sent to and processed by the server.
  
     */
    persist(cacheObject, operation, options) {
        const self = this;
        cacheObject.systemHeader.offlineDetails = {
            type: operation,
            status: IndexedDBConstants.OFFLINE_STATUS_PENDING,
            lastRequestStatus: '',
            tries: 0,
            deleteOnComplete: false,
            updates: options ? options.updates : null,
            isUpdateFromPreviousVersion: options ? options.isUpdateFromPreviousVersion : false,
            createdDate: new Date(),
            makeCurrentVersion: options ? options.makeCurrentVersion : false,
            importPreviousVersion: options ? options.importPreviousVersion : false
        };
        return new Promise((resolve, reject) => {
            self.saveCacheObject(cacheObject, function onCacheObjectSaved(err, cacheObj) {
                if (err) {
                    reject(err);
                }
                else {
                    self.offlineStatusService.incPendingDocumentCount();
                    resolve(cacheObj);
                }
            });
        });
    }
    getUnprocessedDocs(callback) {
        const tbl = IndexedDBConstants.DOCUMENT_TABLE_NAME;
        const index = IndexedDBConstants.DOCUMENT_INDEX_PENDING_OPERATION;
        const searchValue = IndexedDBConstants.OFFLINE_STATUS_PENDING;
        this.getEntries(tbl, index, searchValue, callback); // function callback(docsArray)
    }
    getProcessedCurrentVersionDocs(callback) {
        const tbl = IndexedDBConstants.DOCUMENT_TABLE_NAME;
        const index = IndexedDBConstants.DOCUMENT_INDEX_PENDING_OPERATION;
        const searchValue = IndexedDBConstants.OFFLINE_STATUS_UPLOADED;
        this.getEntries(tbl, index, searchValue, callback); // function callback(docsArray)
    }
    getUnsentOfflineFilesInQueue(callback) {
        const tbl = IndexedDBConstants.OFFLINE_FILE_TABLE_NAME;
        const index = IndexedDBConstants.OFFLINE_FILE_INDEX_STATUS;
        const searchValue = IndexedDBConstants.OFFLINE_STATUS_PENDING;
        this.getEntries(tbl, index, searchValue, callback);
    }
    /**
     * Cache the document in IndexedDB for offline use
     * @param document - the document to cache
     */
    cacheDocument(document, force) {
        const _self = this;
        return new Promise(function (resolve, reject) {
            _self.loadDocByVersion(document.systemHeader.versionId, docWithVersionFound);
            function docWithVersionFound(err, cacheObject) {
                // Do not cache the document twice unless forced to (when saving a version document. see jsonPatchUpdate)
                if (!force && cacheObject != null) {
                    resolve(document);
                    return;
                }
                _self.saveCacheObject(document, onCacheObjectSaved);
                function onCacheObjectSaved(error, doc) {
                    if (error) {
                        reject(error);
                    }
                    else {
                        resolve(doc);
                    }
                }
            }
        });
    }
    /**
     * cache document received from websocket
     * @param docInfo - the info used to load full document and changed versions
     * @param versions - the versions of document
     * @param updateOfflineStatus - flag indicates that should update offline caching status
     * @returns Promise
     */
    cachePushedDocument(docInfo, versions, updateOfflineStatus) {
        const _self = this;
        let currentVersion = null;
        versions.forEach(function (version) {
            if (version && version.systemHeader && version.systemHeader.currentVersion) {
                currentVersion = version;
            }
        });
        function cacheVersion(version) {
            return new Promise((resolve, reject) => {
                _self.saveCacheObject(version, function (err, savedVersion) {
                    if (err) {
                        reject(err);
                    }
                    else {
                        resolve(savedVersion);
                    }
                });
            });
        }
        function cacheExistingCurrentDoc() {
            // If the currentVersion in indexedDB is before the first prev version in above list so it would not
            // be set to non-currentVersion. This step set the value to false.
            _self.getDocumentCurrentVersion(docInfo.documentId, function (doc) {
                if (doc && currentVersion && doc.systemHeader.versionId !== currentVersion.systemHeader.versionId) {
                    doc.systemHeader.currentVersion = false;
                    _self.saveCacheObject(doc, cacheDone);
                }
            });
        }
        function cacheDone(err, version) {
            if (err) {
                logger$o.error('Caching version error: ' + err);
            }
            else {
                logger$o.info('Caching version done: ' + version.systemHeader.versionId);
            }
        }
        return new Promise((resolve, reject) => {
            const hasOfflineAccess = _self.clientAccessService.checkAccess(currentVersion, SharedConstants.OPERATION_TYPE_OFFLINE);
            if (hasOfflineAccess) {
                _self.setIndexedDBIndexes(currentVersion);
                if (docInfo.versionIds && docInfo.versionIds.length) { // in case of updating versions from pushed document
                    const promises = [];
                    versions.forEach(function (version) {
                        promises.push(cacheVersion(version));
                    });
                    Promise.all(promises).then(function ok(versions2) {
                        cacheExistingCurrentDoc();
                        if (updateOfflineStatus !== false) {
                            _self.offlineStatusService.initialCacheQuerySuccess(currentVersion);
                        }
                        resolve(versions2);
                    }, function fail(error) {
                        const msg = 'Error in caching version of document: ' + docInfo.documentId;
                        logger$o.error(msg);
                        reject(new Error(msg));
                    });
                }
                else { // in case of updating each current version from websocket reconnect event or browser reloading
                    cacheVersion(currentVersion).then(cacheExistingCurrentDoc).then(function () {
                        if (updateOfflineStatus !== false) {
                            _self.offlineStatusService.initialCacheQuerySuccess(currentVersion);
                        }
                        resolve(currentVersion);
                    });
                }
            }
        });
    }
    /**
     * load a document by systemHeader.versionId
     * @param versionId - the version id
     * @param callback - will be called at completion of retrieving data. Parameters are:
     *                   err - an error object
     *                   result - the document found matching systemHeader.versionId
     */
    loadDocByVersion(versionId, callback) {
        const _self = this;
        // if no callback then promisify
        if (!callback) {
            return new Promise((resolve, reject) => {
                _self.loadDocByVersion(versionId, (err, result) => {
                    if (err) {
                        reject(err);
                    }
                    else {
                        resolve(result);
                    }
                });
            });
        }
        if (!versionId) {
            callback(new Error('versionId not specified in loadDocByVersion'));
            return;
        }
        this.getDatabase().then(function success(db) {
            db[IndexedDBConstants.DOCUMENT_TABLE_NAME].where(IndexedDBConstants.DOCUMENT_INDEX_VERSION_ID).equals(versionId)
                .first(result => {
                // return null as error object because there is no error
                callback(null, result);
            }).catch(function (error) {
                logger$o.error('Error getting document version ' + versionId + '. Error: ' + error.message);
                callback(error);
            });
        }, (error) => {
            logger$o.error('Error getting document version ' + versionId + '. Error: ' + error.message);
            callback(error);
        });
    }
    loadCachedDocByVersion(documentId, versionId, callback) {
        const tblName = IndexedDBConstants.DOCUMENT_TABLE_NAME;
        const indexName = IndexedDBConstants.DOCUMENT_INDEX_DOCUMENT_ID;
        const indexValue = documentId;
        this.getEntries(tblName, indexName, indexValue, function (entriesArr) {
            if (entriesArr.length > 0) {
                for (let i = 0; i < entriesArr.length; i++) {
                    if (entriesArr[i].systemHeader.versionId === versionId) {
                        callback(entriesArr[i]);
                        return;
                    }
                }
            }
            logger$o.info('No cached document found for this version: ' + versionId + ' of doc: ' + documentId);
            callback(null);
        });
    }
    updateOfflineStatusForCachingAttachedFiles(updateOfflineStatus, isMaxCount) {
        const _self = this;
        if (updateOfflineStatus !== false) {
            if (isMaxCount) {
                this.offlineStatusService.offlineStatus.maxCacheAttachedFileCount++;
            }
            else {
                this.offlineStatusService.offlineStatus.currentCacheAttachedFileCount++;
            }
        }
    }
    cacheAttachedFilesOffline(document, updateOfflineStatus) {
        return __awaiter(this, void 0, void 0, function* () {
            // Cache file attachments sequence here
            // Searches for a file attachment in the document
            // Just visit every array property and if it contains an item with a property
            // named fileNo then load that file from the server into the cache system
            const properties = Object.keys(document);
            for (let i = 0; i < properties.length; i++) {
                const pValue = document[properties[i]];
                if (pValue instanceof Array) {
                    for (let ix = 0; ix < pValue.length; ix++) {
                        const specimen = pValue[ix];
                        if (specimen && specimen.fileNo && specimen.fileType) { // this is a file attachment by ftUploader
                            try {
                                this.updateOfflineStatusForCachingAttachedFiles(updateOfflineStatus, true);
                                yield this.loadFileAsync(specimen.fileNo);
                                this.updateOfflineStatusForCachingAttachedFiles(updateOfflineStatus, false);
                            }
                            catch (err) {
                                const msg = 'Error getting file ' + err.status + " : " + err.message;
                                if (err.status !== UtilHttpStatus.NOT_FOUND) {
                                    logger$o.error(msg);
                                    logger$o.error(err);
                                }
                                this.updateOfflineStatusForCachingAttachedFiles(updateOfflineStatus, false);
                                this.offlineStatusService.offlineStatus.errorCacheAttachedFile.push(msg);
                            }
                        }
                    }
                }
            }
        });
    }
    toBase64Str(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = function () {
                resolve(reader.result);
            };
            reader.onerror = function () {
                reject('Error converting to base64str blob.');
            };
            reader.readAsDataURL(blob);
        });
    }
    // caches file blob into indexeddb
    loadFileAsync(fileNo) {
        const _self = this;
        return new Promise((resolve, reject) => {
            const url = SharedUrlRoutes.serverRoutes.loadFile + '/' + fileNo;
            _self.http.get(url, { responseType: 'blob' }).toPromise().then(response => {
                const data = response;
                if (data) {
                    _self.toBase64Str(data).then(function (base64str) {
                        const offlineFileRecord = {
                            fileNo: fileNo,
                            status: IndexedDBConstants.OFFLINE_STATUS_UPLOADED,
                            uploadConfig: {
                                data: {
                                    uploadedFile: base64str
                                }
                            }
                        };
                        _self.saveFile(offlineFileRecord);
                        resolve(true);
                    });
                }
                else {
                    logger$o.warn('No data from remote file.');
                    resolve(true);
                }
            }).catch(err => {
                reject(err);
            });
        });
    }
    /**
     *  Removes all versions of this document and leave the current version behind.
     *  'document' here is the current version fetched from the server. See DataService.getDocumentOfflineMode
     */
    documentReset(documentId) {
        const tblName = IndexedDBConstants.DOCUMENT_TABLE_NAME;
        const indexName = IndexedDBConstants.DOCUMENT_INDEX_DOCUMENT_ID;
        const indexValue = documentId;
        this.deleteEntries(tblName, indexName, indexValue, function (err, count) {
            logger$o.info('Document reset cleared ' + count + ' versions of ' + indexValue);
        });
    }
    saveCacheObject(offlineCacheObject, callback) {
        const tbl = IndexedDBConstants.DOCUMENT_TABLE_NAME;
        // versionId is the primary key, hence, redundancy is avoided in cache see IndexedDBConnectorService
        this.saveEntry(tbl, offlineCacheObject, function (err) {
            if (!err) {
                callback(null, offlineCacheObject);
            }
            else {
                callback(err);
            }
        });
        // if there are blobs, then save them into cache too
        if (offlineCacheObject && offlineCacheObject.systemHeader && !offlineCacheObject.systemHeader.offlineDetails) {
            this.cacheAttachedFilesOffline(offlineCacheObject);
        }
    }
    onDocumentUpdated() {
        const _self = this;
        this.broadcastService.on('document:updated').subscribe(data => {
            if (data) {
                const record = data[0];
                if (record && record.systemHeader && record.systemHeader.systemType === SharedConstants.SYSTEM_TYPE_TEMPLATE) {
                    const indexes = _self.loggedInUserService.getUserConfigItem('searchIndexes');
                    if (record.indexedDBIndexes && record.indexedDBIndexes.length) {
                        let needtoUpgraded = false;
                        record.indexedDBIndexes.forEach(function (indexName) {
                            const isUpgraded = UtilArray.add(indexes, indexName, true);
                            needtoUpgraded = isUpgraded ? isUpgraded : needtoUpgraded;
                        });
                        if (needtoUpgraded) {
                            _self.loggedInUserService.setUserConfigItem('searchIndexes', indexes);
                            _self.indexedDBConnectorService.upgradeOfflineDatabase();
                        }
                    }
                }
            }
        });
    }
    jsonPatchUpdate(newBean, originalBean, options) {
        return __awaiter(this, void 0, void 0, function* () {
            // Use the doc saved in indexeddb because original bean has been mutated
            // to not have offlineDetails. See https://mantis.formbird.com/view.php?id=14351
            const orig = yield this.loadDocByVersion(originalBean.systemHeader.versionId);
            const versionDoc = VersionManager.createVersion(orig);
            yield this.cacheDocument(versionDoc, true);
            // perform newBean update
            // Aside from the differences in data between originalBean and newbean, newBean will always have
            // its previousVersionId = originalBean's versionId. newBean will also be set as the current version
            // with a new versionId
            newBean.systemHeader.previousVersionId = versionDoc.systemHeader.versionId;
            // if versionId not yet generated
            if (newBean.systemHeader.versionId === versionDoc.systemHeader.versionId) {
                newBean.systemHeader.versionId = UtilDocumentId.generateId();
            }
            // update createdBy every time because when we update the doc it's a new version
            newBean.systemHeader.createdBy = this.user ?
                this.user.account.documentId : null;
            newBean.systemHeader.createdDate = new Date();
            const savedDocument = yield this.persist(newBean, ClientConstants.OPERATION_TYPE_PUT, options);
            return savedDocument;
        });
    }
    deepDiffUpdate(newBean, originalBean, options) {
        return this.jsonPatchUpdate(newBean, originalBean, options);
    }
    doCacheBulkDocuments(documents, updateOfflineStatus) {
        const _self = this;
        return new Promise((resolve, reject) => {
            this.getDatabase().then(function success(db) {
                const tblName = IndexedDBConstants.DOCUMENT_TABLE_NAME;
                db.transaction('rw', db[tblName], (result) => {
                    db[tblName].bulkPut(documents).then((data) => {
                        if (updateOfflineStatus !== false) {
                            _self.offlineStatusService.offlineStatus.currentCacheCount += (documents.length - 1);
                            _self.offlineStatusService.initialCacheQuerySuccess(documents[documents.length - 1]);
                        }
                        resolve(data);
                    }, (e) => {
                        logger$o.error('Error caching bulk documents: ' + e.name + ": " + e.message);
                        reject(e);
                    });
                }).catch(e => {
                    logger$o.error('Error adding entry to IndexedDB: ' + e.stack || e);
                    reject(e);
                });
            }, (error) => {
                reject(error);
            });
        });
    }
    executeComponentFunction(options) {
        logger$o.info('Executing component function in indexed-db Service');
        const _self = this;
        return new Promise((resolve, reject) => {
            this.indexedDBConnectorService.getDatabase().then(function (db) {
                try {
                    db.documents.where('systemHeader.systemType').equals(SharedConstants.SYSTEM_TYPE_QUERY).toArray(function (documents) {
                        return __awaiter(this, void 0, void 0, function* () {
                            let queryDocument;
                            const results = yield documents.filter(function (document) {
                                return document.name === options.offlineComponentLibraryName && document.systemHeader.currentVersion === true &&
                                    toLower(document.searchProvider) === 'dexie';
                            });
                            if (results && results.length > 0) {
                                queryDocument = results[0];
                                if (queryDocument && !isEmpty(queryDocument.filter)) {
                                    try {
                                        const fun = new Function(queryDocument.filter);
                                        fun(db, function (err, data) {
                                            if (err) {
                                                const message = 'Error in Dexie: ' + err;
                                                reject(new Error(message));
                                            }
                                            else {
                                                resolve(data);
                                            }
                                        });
                                    }
                                    catch (err) {
                                        reject(err);
                                    }
                                }
                            }
                            else {
                                reject(new Error("Offline component Library document not found."));
                            }
                        });
                    });
                }
                catch (err) {
                    reject(err);
                }
            }, (error) => {
                reject(error);
            });
        });
    }
    getTemplateAndDocument(id, templateId, callBack) {
        return __awaiter(this, void 0, void 0, function* () {
            const _self = this;
            _self.getDocumentCurrentVersion(id, function (document) {
                let template;
                if (!templateId && document && document.systemHeader &&
                    document.systemHeader.systemType === SharedConstants.SYSTEM_TYPE_TEMPLATE) {
                    template = document;
                    document = null;
                }
                if (document) {
                    const tplId = templateId || document.systemHeader.templateId;
                    if (tplId) {
                        _self.getDocumentCurrentVersion(tplId, function (template) {
                            if (template == null) {
                                const error = new FtError(`Template for document not found: ${tplId}`);
                                callBack(error, null);
                            }
                            else {
                                callBack(null, { template, document, templateReturnStatus: UtilHttpStatus.OK });
                            }
                        });
                    }
                }
                else if (template) {
                    callBack(null, { template, document, templateReturnStatus: UtilHttpStatus.OK });
                }
                else {
                    const error = new FtError(`Template or document: ${id} not found`);
                    callBack(error, null);
                }
            });
        });
    }
}
/** @nocollapse */ /** @nocollapse */ IndexedDBService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: IndexedDBService, deps: [{ token: i1.HttpClient }, { token: IndexedDBConnectorService }, { token: OfflineStatusService }, { token: LoggedInUserService }, { token: DexieSearchService }, { token: ClientAccessService }, { token: BroadcastService }, { token: NotificationService }, { token: KeyValueStorageService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ IndexedDBService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: IndexedDBService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: IndexedDBService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: IndexedDBConnectorService }, { type: OfflineStatusService }, { type: LoggedInUserService }, { type: DexieSearchService }, { type: ClientAccessService }, { type: BroadcastService }, { type: NotificationService }, { type: KeyValueStorageService }]; } });

const FORM_SETUP_DOCUMENT_LIST = 'FORM_SETUP_DOCUMENT_LIST';
const FORM_CHANGE_DOCUMENT = 'FORM_CHANGE_DOCUMENT';
const FORM_RESET_CHANGE_DOCUMENT = 'FORM_RESET_CHANGE_DOCUMENT';
const FORM_SET_DOCUMENT = 'FORM_SET_DOCUMENT';
const FORM_SET_TEMPLATE = 'FORM_SET_TEMPLATE';
const FORM_DELETE_DOCUMENT = 'FORM_DELETE_DOCUMENT';
const FORM_UPDATE_COMPONENT_DEFINITION = 'FORM_UPDATE_COMPONENT_DEFINITION';
const FORM_RESET_TEMPLATE_COMPONENT_DEFINITION = 'FORM_RESET_TEMPLATE_COMPONENT_DEFINITION';
const FORM_SET_COMPONENT_FLAG_TEMPLATE = 'FORM_SET_COMPONENT_FLAG_TEMPLATE';
const FORM_SET_COMPONENT_PROPS_TEMPLATE = 'FORM_SET_COMPONENT_PROPS_TEMPLATE';
const FORM_SET_LIST_COMPONENTS_FLAG_TEMPLATE = 'FORM_SET_LIST_COMPONENTS_FLAG_TEMPLATE';
const FORM_CLEAR_DOCUMENT = 'FORM_CLEAR_DOCUMENT';
const FORM_RESET_TO_ORG_DOCUMENT = 'FORM_RESET_TO_ORG_DOCUMENT';
const FORM_DOCUMENT_LOADED = 'FORM_DOCUMENT_LOADED';
const FORM_SET_ORG_DOCUMENT = 'FORM_SET_ORG_DOCUMENT';
const FORM_SET_ALT_TEMPLATE_INFO = 'FORM_SET_ALT_TEMPLATE';
const FORM_SET_DOCUMENT_INFO = 'FORM_SET_DOCUMENT_INFO';
const FORM_RESET_DOCUMENT_INFO = 'FORM_RESET_DOCUMENT_INFO';
const FORM_UPDATE_INIT_DOCUMENT_DATA = 'FORM_UPDATE_INIT_DOCUMENT_DATA';
const FORM_RESET_INIT_DOCUMENT_DATA = 'FORM_RESET_INIT_DOCUMENT_DATA';
const FORM_UNDO = 'FORM_UNDO';
const FORM_REDO = 'FORM_REDO';
const FORM_RESET_UNDO_REDO = 'FORM_RESET_UNDO_REDO';
const FORM_SET_DISABLE_SAVE_FIELD_DOCUMENT = 'FORM_SET_DISABLE_SAVE_FIELD_DOCUMENT';
const FORM_SET_DOCUMENT_SESSION = 'FORM_SET_DOCUMENT_SESSION';
const FORM_SET_RULES_STATUS = 'FORM_SET_RULES_STATUS';
function formSetupDocumentList(isNewMainDocList, documentListId) {
    return {
        type: FORM_SETUP_DOCUMENT_LIST,
        payload: {
            isNewMainDocList,
            documentListId
        }
    };
}
function formDocumentChanged(documentChangedInfo) {
    return {
        type: FORM_CHANGE_DOCUMENT,
        payload: {
            documentChangedInfo
        }
    };
}
function formResetDocumentChanged() {
    return {
        type: FORM_RESET_CHANGE_DOCUMENT
    };
}
function formClearDocuments() {
    return {
        type: FORM_CLEAR_DOCUMENT
    };
}
function formSetDocument(document, versionId) {
    return {
        type: FORM_SET_DOCUMENT,
        payload: {
            document,
            versionId
        }
    };
}
function formSetTemplate(document, versionId) {
    return {
        type: FORM_SET_TEMPLATE,
        payload: {
            document,
            versionId
        }
    };
}
function formUpdateComponentDefinition(templateId, fieldName, key, componentDef) {
    return {
        type: FORM_UPDATE_COMPONENT_DEFINITION,
        payload: {
            templateId,
            fieldName,
            key,
            componentDef
        }
    };
}
function formResetTemplateComponentDefinition(templateId, unsavedDocumentListId) {
    return {
        type: FORM_RESET_TEMPLATE_COMPONENT_DEFINITION,
        payload: {
            templateId
        }
    };
}
function formSetComponentPropsTemplate(templateId, fieldName, componentName, propertyValue) {
    return {
        type: FORM_SET_COMPONENT_PROPS_TEMPLATE,
        payload: {
            templateId,
            fieldName,
            componentName,
            propertyValue
        }
    };
}
function formSetComponentFlagTemplate(templateId, fieldName, flagName, flagValue) {
    return {
        type: FORM_SET_COMPONENT_FLAG_TEMPLATE,
        payload: {
            templateId,
            fieldName,
            flagName,
            flagValue
        }
    };
}
function formSetListComponentsFlagTemplate(templateId, fieldNames, flagName, flagValue) {
    return {
        type: FORM_SET_LIST_COMPONENTS_FLAG_TEMPLATE,
        payload: {
            templateId,
            fieldNames,
            flagName,
            flagValue
        }
    };
}
function formSetOrgDocument(document, versionId) {
    return {
        type: FORM_SET_ORG_DOCUMENT,
        payload: {
            document,
            versionId
        }
    };
}
function formDeleteDocument(documentId) {
    return {
        type: FORM_DELETE_DOCUMENT,
        payload: {
            documentId
        }
    };
}
function formResetToOrgDocument(documentId, unsavedDocumentListId) {
    return {
        type: FORM_RESET_TO_ORG_DOCUMENT,
        payload: {
            documentId
        }
    };
}
function formSetDocumentInfo(documentListId, documentInfo) {
    return {
        type: FORM_SET_DOCUMENT_INFO,
        payload: {
            documentInfo,
            unsavedDocumentListId: documentListId
        }
    };
}
function formResetDocumentInfo(unsavedDocumentListId) {
    return {
        type: FORM_RESET_DOCUMENT_INFO,
        payload: {
            unsavedDocumentListId
        }
    };
}
function formUpdateInitDocumentData(unsavedDocumentListId, documentId, fieldName, fieldValue) {
    return {
        type: FORM_UPDATE_INIT_DOCUMENT_DATA,
        payload: {
            unsavedDocumentListId,
            documentId,
            fieldName,
            fieldValue
        }
    };
}
function formResetInitDocumentData(unsavedDocumentListId, documentId) {
    return {
        type: FORM_RESET_INIT_DOCUMENT_DATA,
        payload: {
            unsavedDocumentListId,
            documentId
        }
    };
}
function formDocumentLoaded(documentId, unsavedDocumentListId, isLoaded) {
    return {
        type: FORM_DOCUMENT_LOADED,
        payload: {
            documentId,
            unsavedDocumentListId,
            isLoaded
        }
    };
}
function formUndoDocument() {
    return {
        type: FORM_UNDO
    };
}
function formRedoDocument() {
    return {
        type: FORM_REDO
    };
}
function resetUndoRedoDocument() {
    return {
        type: FORM_RESET_UNDO_REDO
    };
}
function formSetDisableSaveFieldDocument(documentId, disabledFields) {
    return {
        type: FORM_SET_DISABLE_SAVE_FIELD_DOCUMENT,
        payload: {
            documentId,
            disabledFields
        }
    };
}
function formSetDocumentSession(documentSession, documentId) {
    return {
        type: FORM_SET_DOCUMENT_SESSION,
        payload: {
            documentSession,
            documentId
        }
    };
}
function formSetRulesStatus(templateId, status) {
    return {
        type: FORM_SET_RULES_STATUS,
        payload: {
            templateId,
            status
        }
    };
}
function formSetAltTemplateInfo(documentId, templateId) {
    return {
        type: FORM_SET_ALT_TEMPLATE_INFO,
        payload: {
            documentId,
            templateId
        }
    };
}

const logger$n = console;
class DocumentSessionService {
    constructor(appStore) {
        this.appStore = appStore;
        this.subs = new Subscription();
        this.documentSession = {};
        if (this.documentSession$) {
            this.documentSession$.subscribe(documentSession => {
                // logger.info("Changed documentSession value : " + JSON.stringify(documentSession));
            });
        }
    }
    initialize(documentId) {
        this.setDocumentSession(documentId, {});
    }
    setDocumentSession(documentId, document) {
        this.appStore.dispatch(formSetDocumentSession(document, documentId));
    }
    getDocumentSession(documentId) {
        this.documentSession = cloneDeep(this.appStore.getState().formState.documentSession[documentId]);
        return this.documentSession ? this.documentSession : {};
    }
}
/** @nocollapse */ /** @nocollapse */ DocumentSessionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DocumentSessionService, deps: [{ token: AppStore }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ DocumentSessionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DocumentSessionService, providedIn: 'root' });
__decorate([
    select(['formState', 'documentSession']),
    __metadata("design:type", Observable)
], DocumentSessionService.prototype, "documentSession$", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DocumentSessionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: AppStore }]; }, propDecorators: { documentSession$: [] } });

class ConfigService {
    constructor() { }
    clientConfig() {
        return window['ftClientConfig'];
    }
}
/** @nocollapse */ /** @nocollapse */ ConfigService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ConfigService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ ConfigService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ConfigService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ConfigService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

const serverRoutes$3 = SharedUrlRoutes.serverRoutes;
const logger$m = console;
class RestDataService {
    constructor(http, loggedInUserService, offlineStatusService, indexedDBService, documentSessionService, configService) {
        this.http = http;
        this.loggedInUserService = loggedInUserService;
        this.offlineStatusService = offlineStatusService;
        this.indexedDBService = indexedDBService;
        this.documentSessionService = documentSessionService;
        this.configService = configService;
    }
    checkDocumentExists(url, id, includeDeleted) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                let fullUrl = url;
                if (id) {
                    fullUrl += '/' + id;
                }
                if (includeDeleted === true) {
                    fullUrl += '/' + includeDeleted;
                }
                return yield this.http.get(fullUrl).toPromise();
            }
            catch (err) {
                const msg = err ? err.message : 'Something went wrong when checking document exists.';
                const error = new FtError(msg);
                if (err) {
                    error.status = err.status;
                    error.statusText = err.statusText;
                    error.message = err.message;
                }
                throw error;
            }
        });
    }
    getTemplateAndDocument(id, templateId) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                let url = SharedUrlRoutes.serverRoutes.templateAndDocument + '/' + id;
                if (templateId) {
                    url = url + '/' + templateId;
                }
                return yield this.http.get(url).toPromise();
            }
            catch (err) {
                const msg = err ? err.message : 'Something went wrong when getting template and document.';
                const error = new FtError(msg);
                if (err) {
                    error.status = err.status;
                    error.statusText = err.statusText;
                    error.message = err.error;
                    if (err.status === UtilHttpStatus.SERVER_UNRESPONSIVE) {
                        error.message = 'Network connection lost. Could not retrieve.';
                    }
                }
                throw error;
            }
        });
    }
    getDocumentObservable(id, expectedDocumentType) {
        let fullUrl = serverRoutes$3.document + '/' + id;
        if (expectedDocumentType) {
            fullUrl += "?expectedDocumentType=" + expectedDocumentType;
        }
        return this.http.get(fullUrl);
    }
    getDocument(id, expectedDocumentType) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return yield this.getDocumentObservable(id, expectedDocumentType).toPromise();
            }
            catch (err) {
                const msg = err ? err.message : 'Something went wrong when getting document.';
                const error = new FtError(msg);
                if (err) {
                    error.status = err.status;
                    error.statusText = err.statusText;
                    error.message = err.error;
                }
                throw error;
            }
        });
    }
    loadDataCache(syncStartTime, isInitialize) {
        const _self = this;
        return new Promise((resolve, reject) => {
            _self.getCachedDocumentCount(syncStartTime).toPromise().then(res => {
                const data = res;
                logger$m.info('Loading data cache. With info: ', data);
                _self.offlineStatusService.addToMaxCacheCount(data.docCount);
                function loadEach(page) {
                    return _self.loadCachedDocuments(syncStartTime, page, isInitialize);
                }
                UtilSequenceProcess.processRange(1, data.pages, loadEach).then(function allPromiseSuccessFunc(result) {
                    resolve(result);
                }, function errorFunc(err) {
                    reject(err);
                });
            }).catch(err => {
                const msg = 'Error requesting offline document count. Status: ' + err.status;
                logger$m.error(err.message);
                reject(new FtError(msg));
            });
        });
    }
    workerLoadDataCache(syncStartTime, isInitialize) {
        const _self = this;
        return new Promise((resolve, reject) => {
            const workerOptions = {
                syncStartTime,
                isInitialize,
                syncPageSize: 0,
                currentCacheCount: 0
            };
            const offlineUserConfig = _self.configService.clientConfig().offline;
            let syncWorker;
            if ((offlineUserConfig === null || offlineUserConfig === void 0 ? void 0 : offlineUserConfig.syncher) === 'rust') {
                syncWorker = new Worker('./assets/offline-sync-rs.worker.js', { type: 'module' });
                workerOptions.syncPageSize = (offlineUserConfig === null || offlineUserConfig === void 0 ? void 0 : offlineUserConfig.syncPageSize) || 10000;
                workerOptions.currentCacheCount = _self.loggedInUserService.getUserConfigItem('currentCacheCount');
            }
            else {
                syncWorker = new Worker('./assets/offline-sync-js.worker.js', { type: 'module' });
            }
            syncWorker.postMessage(workerOptions);
            syncWorker.onmessage = onMessage;
            function onMessage({ data }) {
                logger$m.log(`page got message: ${data === null || data === void 0 ? void 0 : data.messageType}`);
                if ((data === null || data === void 0 ? void 0 : data.messageType) === WebWorkerMessageType.ADD_TO_MAX_CACHE_COUNT) {
                    // count is sent in data.value by OfflineStatusWebWorkerService
                    _self.offlineStatusService.setMaxCacheCount(data.value);
                }
                if ((data === null || data === void 0 ? void 0 : data.messageType) === WebWorkerMessageType.ADD_TO_CURRENT_CACHE_COUNT) {
                    _self.offlineStatusService.offlineStatus.currentCacheCount += (data.value);
                    _self.loggedInUserService.setUserConfigItem('currentCacheCount', _self.offlineStatusService.offlineStatus.currentCacheCount);
                }
                if ((data === null || data === void 0 ? void 0 : data.messageType) === WebWorkerMessageType.SET_PAGE_DOWNLOAD_PROGRESS) {
                    if (data.value && data.value.loaded) {
                        _self.offlineStatusService.pageDownloadProgress.loaded = data.value.loaded;
                    }
                    if (data.value && data.value.total) {
                        _self.offlineStatusService.pageDownloadProgress.total = data.value.total;
                    }
                    if (data.value && data.value.pageNumber) {
                        _self.offlineStatusService.pageDownloadProgress.pageNumber = data.value.pageNumber;
                    }
                }
                if ((data === null || data === void 0 ? void 0 : data.messageType) === WebWorkerMessageType.SET_INITIAL_CACHE_QUERY_SUCCESS) {
                    _self.offlineStatusService.initialCacheQuerySuccess(data.value);
                }
                if ((data === null || data === void 0 ? void 0 : data.messageType) === WebWorkerMessageType.SET_LAST_SYNC_START_TIME) {
                    _self.offlineStatusService.setLastSyncDate(data.value);
                }
                if ((data === null || data === void 0 ? void 0 : data.messageType) === WebWorkerMessageType.INITIAL_CACHE_COMPLETED) {
                    if (data.value.error) {
                        reject(data.value.error);
                    }
                    else {
                        resolve(data.value.result);
                    }
                }
            }
            ;
        });
    }
    processCachingDocuments(documents, isInitialize, updateOfflineStatus) {
        const _self = this;
        return new Promise((resolve, reject) => {
            const promises = [];
            if (isInitialize === true) {
                promises.push(_self.indexedDBService.doCacheBulkDocuments(documents, updateOfflineStatus));
            }
            else {
                for (let i = 0; i < documents.length; i++) {
                    const document = documents[i];
                    if (isInitialize === false) {
                        promises.push(_self.indexedDBService.cachePushedDocument(document, [document], updateOfflineStatus));
                    }
                    else {
                        promises.push(_self.indexedDBService.doCacheDocument(document, updateOfflineStatus));
                    }
                }
            }
            Promise.all(promises).then(function successFunction(results) {
                resolve(results);
            }, function errorFunction(err) {
                _self.indexedDBService.checkQuotaExceededError(err);
                reject(new Error(err.message));
            });
        });
    }
    getIndexedDBIndexes() {
        let indexedDBIndexes = [];
        //getting indexKey from user account group config document as well
        let accountConfig = this.loggedInUserService.getUserAccountGroupConfigDocument();
        if (accountConfig && accountConfig.indexedDBIndexes && accountConfig.indexedDBIndexes.length > 0) {
            indexedDBIndexes = accountConfig.indexedDBIndexes;
        }
        const options = {
            filter: {
                query: {
                    'bool': {
                        'must': [
                            {
                                'exists': {
                                    'field': 'indexedDBIndexes'
                                }
                            },
                            {
                                'term': {
                                    'systemHeader.systemType': 'template'
                                }
                            }
                        ]
                    }
                }
            },
            sourceFilter: 'indexedDBIndexes'
        };
        return this.search(options).then(function (results) {
            if (results.data.hits.total) {
                for (let i = 0; i < results.data.hits.hits.length; i++) {
                    const template = results.data.hits.hits[i]._source;
                    const indexes = template.indexedDBIndexes;
                    if (indexes) {
                        for (let j = 0; j < indexes.length; j++) {
                            UtilArray.add(indexedDBIndexes, indexes[j], true);
                        }
                    }
                }
            }
            return Promise.resolve(indexedDBIndexes);
        }, function (error) {
            return Promise.reject(error);
        });
    }
    getCachedDocumentCount(syncStartTime) {
        const fullUrl = serverRoutes$3.syncDocumentsCountURL + '/' + syncStartTime;
        return this.http.get(fullUrl);
    }
    loadCachedDocuments(syncStartTime, pageNo, isInitialize) {
        logger$m.info('Loading cached documents for page: ' + pageNo);
        const _self = this;
        const fullUrl = serverRoutes$3.syncDocuments + '/' + syncStartTime + '/' + pageNo;
        // ensures request is processed only when ui turned idle
        this.offlineStatusService.pageDownloadProgress.pageNumber = pageNo;
        const getParams = {
            // Them TT check request progress
            eventHandlers: {
                progress: function (e) {
                    if (e.lengthComputable) {
                        _self.offlineStatusService.pageDownloadProgress.loaded = e.loaded;
                        _self.offlineStatusService.pageDownloadProgress.total = e.total;
                    }
                }
            }
        };
        return new Promise((resolve, reject) => {
            _self.http.get(fullUrl).toPromise().then((res) => __awaiter(this, void 0, void 0, function* () {
                var e_1, _a;
                const data = res;
                const templates = data.templates;
                const documents = data.documents;
                try {
                    for (var _b = __asyncValues(res.templates), _c; _c = yield _b.next(), !_c.done;) {
                        const template = _c.value;
                        _self.indexedDBService.setIndexedDBIndexes(template);
                    }
                }
                catch (e_1_1) { e_1 = { error: e_1_1 }; }
                finally {
                    try {
                        if (_c && !_c.done && (_a = _b.return)) yield _a.call(_b);
                    }
                    finally { if (e_1) throw e_1.error; }
                }
                // Cache templates first to register indexes, no need to update offline count.
                // It will be set in the full list of offline documents in the next step
                _self.processCachingDocuments(templates, isInitialize, false).then(function cachedOk() {
                    // Then caching document to make sure the corresponding template is ready to use
                    _self.processCachingDocuments(documents, isInitialize).then(function successFunction(results) {
                        resolve(results);
                    }, function errorFunction(err) {
                        reject(new Error(err.message));
                    });
                }, function cachedFailed(err) {
                    logger$m.warn('Some templates could not be cached successfully: ' + err);
                    _self.processCachingDocuments(documents, isInitialize).then(function successFunction(results) {
                        resolve(results);
                    }, function errorFunction(error) {
                        reject(new Error(error.message));
                    });
                });
            })).catch(err => {
                const msg = 'Error caching data. Network data will be used: ' + err.status;
                logger$m.error(msg);
                reject(err);
            });
        });
    }
    // get full rest url from a url without the host and webapp section, so a url of 'cases' will become
    // {hostname}:{port}/{webappname}/cases. eg localhost:8090/focus-rest/cases
    getFullRestURL(url, id) {
        let fullUrl = url;
        if (UtilType.hasValue(id)) {
            fullUrl += '/' + id;
        }
        return fullUrl;
    }
    /**
     * As per 10539, we send the new document directly to the server and let it run jsonPatch.
     * For simplicity, other parameters from the old deep Update was
     * removed because the server can resolve them with just the newDocument value.
     *
     * @param newDocument the new document to update
     * @returns promise|*|jQuery.promise the promise
     */
    jsonPatchUpdate(newDocument, options) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const updateUrl = SharedUrlRoutes.serverRoutes.documentDeepDiffUpdate + '/' + newDocument.documentId;
                const body = this.setupRequestBody(newDocument, options);
                return yield this.http.put(updateUrl, body).toPromise();
            }
            catch (err) {
                console.log(err);
                const error = err ? new Error(err.error) : new Error('Something went wrong when updating document.');
                throw error;
            }
        });
    }
    /*
     insert function
     parameters:
     bean - the bean to insert
     url - the url for the restful web service. This will also be used as a key in the database
     successFunction - a function to execute on success to do post save steps
     cachingRequired - whether to save the record to the local database. Records that never need
     to be loaded in the app on their own, such as events, can have this set to false
     */
    insert(bean, url) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const fullUrl = this.getFullRestURL(url, bean.id);
                const body = this.setupRequestBody(bean);
                return yield this.http.post(fullUrl, body).toPromise();
            }
            catch (err) {
                console.log(err);
                const error = err ? new Error(err.error) : new Error('Something went wrong when inserting document.');
                throw error;
            }
        });
    }
    setupRequestBody(document, options) {
        if (!options) {
            options = {};
        }
        if (!options.documentSession) {
            options.documentSession = this.documentSessionService.getDocumentSession(document.documentId);
        }
        return {
            document,
            options
        };
    }
    /*
     destroy function
     parameters:
     id - the id of the bean to delete
     url - the url for the restful web service
     cachingRequired - whether to save the record to the local database. Records that never need
     templateId - template used when deleting document
     versionId - version of document during deletion
     to be loaded in the app on their own, such as events, can have this set to false
     */
    destroy(id, url, cachingRequired, templateId, previousVersionId) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const fullUrl = this.getFullRestURL(url, id) + '/' + templateId + '/' + previousVersionId;
                const data = {
                    body: this.setupRequestBody({ documentId: id })
                };
                return yield this.http.delete(fullUrl, data).toPromise();
            }
            catch (err) {
                console.log(err);
                const error = err ? new Error(err.error) : new Error('Something went wrong when destroying document.');
                throw error;
            }
        });
    }
    generatePit() {
        return this.http.get('api/generatePit');
    }
    aggregate(query) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return yield this.http.post(SharedUrlRoutes.serverRoutes.aggregate, { query }, {
                    headers: { 'Content-Type': 'application/json' }
                }).toPromise();
            }
            catch (err) {
                throw new Error('Error on aggregate: ' + err.error);
            }
        });
    }
    search(options) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return yield this.http.post('api/documentSearch', options, {
                    headers: { 'Content-Type': 'application/json' }
                }).toPromise();
            }
            catch (err) {
                let msg;
                if (err && err.error) {
                    msg = err.error || err.message;
                }
                else {
                    msg = 'Something went wrong when searching document. Status: ' + err.status;
                }
                if (err && err.status === UtilHttpStatus.SERVER_UNRESPONSIVE) {
                    throw UtilError.createError(msg, UtilError.SERVER_UNRESPONSE_ERROR);
                }
                else {
                    throw new Error(msg);
                }
            }
        });
    }
    loadVersions(versionIds) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!versionIds || !versionIds.length) {
                return null;
            }
            try {
                const url = SharedUrlRoutes.serverRoutes.versionFetch;
                const options = {
                    versionIds: versionIds,
                    'includeAllVersions': true
                };
                return yield this.http.post(url, options).toPromise();
            }
            catch (err) {
                const msg = 'Error requesting versions. Status: ' + err.status;
                logger$m.error(err.message);
                throw new Error(msg);
            }
        });
    }
    /*
     undestroy function
     parameters:
     id - the id of the bean to undelete
     url - the url for the restful web service
     */
    undestroy(id, url) {
        return __awaiter(this, void 0, void 0, function* () {
            const fullUrl = this.getFullRestURL(url, id);
            try {
                return yield this.http.put(fullUrl, null).toPromise();
            }
            catch (err) {
                const error = err ? new Error(err.error) : new Error('Something went wrong when undestroying document.');
                throw error;
            }
        });
    }
    /*
    bulkUploadDocuments function
    parameters:
    documents - the array of documents to upload.
    url - the url for the restful web service
    */
    bulkUploadDocuments(documents, url) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const fullUrl = this.getFullRestURL(url);
                return yield this.http.post(fullUrl, documents).toPromise();
            }
            catch (err) {
                const error = err ? new Error(err) : new Error('Something went wrong when uploading documents.');
                throw error;
            }
        });
    }
    executeComponentFunction(options) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return yield this.http.post('api/execute', options, { headers: { 'Content-Type': 'application/json' } }).toPromise();
            }
            catch (err) {
                let msg;
                logger$m.error(err);
                if (err && err.error) {
                    msg = err.error;
                }
                else if (err && err.message) {
                    msg = 'executing component function failed with error: ';
                    msg += err.message;
                }
                else {
                    msg = 'Something went wrong when executing component function. Status: ' + err.status;
                }
                if (err && err.status === UtilHttpStatus.SERVER_UNRESPONSIVE) {
                    throw UtilError.createError(msg, UtilError.SERVER_UNRESPONSE_ERROR);
                }
                else {
                    throw new Error(msg);
                }
            }
        });
    }
}
/** @nocollapse */ /** @nocollapse */ RestDataService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: RestDataService, deps: [{ token: i1.HttpClient }, { token: LoggedInUserService }, { token: OfflineStatusService }, { token: IndexedDBService }, { token: DocumentSessionService }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ RestDataService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: RestDataService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: RestDataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: LoggedInUserService }, { type: OfflineStatusService }, { type: IndexedDBService }, { type: DocumentSessionService }, { type: ConfigService }]; } });

const serverRoutes$2 = SharedUrlRoutes.serverRoutes;
const logger$l = console;
class DataService {
    constructor(indexedDBConnectorService, loggedInUserService, restDataService, offlineStatusService, indexedDBService, clientAccessService, documentSessionService) {
        this.indexedDBConnectorService = indexedDBConnectorService;
        this.loggedInUserService = loggedInUserService;
        this.restDataService = restDataService;
        this.offlineStatusService = offlineStatusService;
        this.indexedDBService = indexedDBService;
        this.clientAccessService = clientAccessService;
        this.documentSessionService = documentSessionService;
        if (this.user$) {
            this.user$.subscribe((data) => this.user = data);
        }
    }
    //
    // // the preferred service. This can be configured to either check the cache for data first or go to the
    // // network first. The service to call first will be set based on the ConfigService.clientConfig.preferCachedData value
    // var preferredService = null;
    //
    // // the secondaryService will be tried if the preferred service fails
    // var secondaryService = null;
    ////////////////////////////////////////
    // local service functions
    /**
     * cache a document for offline use
     * @param docInfo - the document used to load full document to cache
     * @param versions - the versions used to cache
     */
    cacheDocument(docInfo, versions) {
        const _self = this;
        if (_self.loggedInUserService.getUserConfigItem('cachingEnabled') === true) {
            if (!versions || !versions.length) {
                return _self.restDataService.loadVersions(docInfo.versionIds).then((versions) => {
                    return _self.indexedDBService.cachePushedDocument(docInfo, versions);
                }, (error) => {
                    return Promise.reject(error);
                });
            }
            return _self.indexedDBService.cachePushedDocument(docInfo, versions);
        }
        return Promise.resolve(versions);
    }
    documentExists(documentId, includeDeleted) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!documentId) {
                throw new Error('No documentId specified for check exist!');
            }
            const offlineMode = this.loggedInUserService.getUserConfigItem('cachingEnabled') === true;
            try {
                return offlineMode ? yield this.checkDocumentExistsOfflineMode(documentId)
                    : yield this.checkDocumentExistFromServer(documentId, includeDeleted);
            }
            catch (err) {
                throw UtilError.createErrorObject(err);
            }
        });
    }
    /**
     * get a document
     */
    getDocument(id, expectedDocumentType) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!id) {
                // old code reject this error as string
                // tslint:disable-next-line:no-string-throw
                throw 'No documentId specified for getDocument!';
            }
            try {
                const offlineMode = this.loggedInUserService.getUserConfigItem('cachingEnabled') === true;
                const doc = offlineMode ? yield this.getDocumentOfflineMode(id) : yield this.getDocumentFromServer(id, expectedDocumentType);
                if (doc.systemHeader && doc.systemHeader.offlineDetails) {
                    delete doc.systemHeader.offlineDetails;
                }
                return cloneDeep(doc);
            }
            catch (err) {
                let error;
                if (err.status === UtilHttpStatus.FORBIDDEN) {
                    error = UtilError.createError(err.message, UtilError.FORBIDDEN_ERROR);
                }
                else if (err.status === UtilHttpStatus.NOT_FOUND) {
                    error = UtilError.createError(err.message, UtilError.NOT_FOUND_ERROR);
                }
                else if (err.status === UtilHttpStatus.GONE) {
                    error = UtilError.createError(err.message, UtilError.DELETED_ERROR);
                }
                else {
                    error = UtilError.createErrorObject(err);
                }
                throw error;
            }
        });
    }
    getTemplateAndDocument(id, templateId) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const offlineMode = this.loggedInUserService.getUserConfigItem('cachingEnabled') === true;
                if (offlineMode) {
                    return yield this.getTemplateAndDocumentOfflineMode(id, templateId);
                }
                else {
                    return yield this.restDataService.getTemplateAndDocument(id, templateId);
                }
            }
            catch (error) {
                if (error.status <= 0) {
                    error.message = `Caught error on Network Suspension: ${error.status}: ${error.statusText}.`;
                }
                throw error;
            }
        });
    }
    /* if offline mode then retrieve the latest version of the document from offline db and name as cachedDoc
     * if the cachedDoc is not yet sent to server then return it;
     * else query the server for the latest versionId of the document
     * if versions don't match, use server's version and perform 'documentReset'
     *
     * DocumentReset will remove all versions of a document
     * and start from the beginning as a single version (that current version from the server)
     *
     * DocumentReset is there primarily there to simplify retrieving documents once there have
     * been version conflicts that have been resolved by the server: when two users modifies
     * a single document at the same time offline. If this do not occur, then documentReset will not happen.
     */
    getDocumentOfflineMode(id) {
        const _self = this;
        return new Promise((resolve, reject) => {
            _self.indexedDBService.getDocumentCurrentVersion(id, (cacheDoc) => {
                if (cacheDoc == null) { // not found in offline? retrieve from server
                    _self.getDocumentFromServer(id).then(resolve, reject);
                }
                else {
                    resolve(cacheDoc);
                }
                // Do not retrieve the server's version if this document is still unsent.
                // var unsent = cacheDoc.offlineDetails &&
                //     cacheDoc.offlineDetails.status === IndexedDBConstants.OFFLINE_STATUS_PENDING;
                // if (unsent) {
                //     deferred.resolve(cachedObject);
                //     return;
                // }
                // //let's see if we have conflicts from the server:
                // getDocumentFromServer(id).then((doc) => {
                //     var identicalDocuments = doc.systemHeader.versionId === cachedDoc.systemHeader.versionId;
                //     if (!identicalDocuments) {//version conflict! see 10049 & 10539
                //         IndexedDBService.documentReset(doc.documentId);
                //     //doc here is the latest version from the document that have been resolved by the server from conflict
                //         cacheDocument(doc);
                //     }
                //
                //     deferred.resolve(doc);
                //
                // }, function err(statusCode){
                //     //we could not retrieve from the server due to some error, return the cachedDoc instead
                //     deferred.resolve(cachedDoc);
                //
                // });
            });
        });
    }
    getDocumentFromServer(id, expectedDocumentType) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return yield this.restDataService.getDocument(id, expectedDocumentType);
            }
            catch (error) {
                const offlineMode = this.loggedInUserService.getUserConfigItem('cachingEnabled') === true;
                if (offlineMode && error && error.status === UtilHttpStatus.SERVER_UNRESPONSIVE) {
                    error.status = UtilHttpStatus.NOT_FOUND;
                }
                throw error;
            }
        });
    }
    checkDocumentExistsOfflineMode(id) {
        return __awaiter(this, void 0, void 0, function* () {
            const _self = this;
            let found = yield new Promise((resolve) => {
                _self.indexedDBService.getDocumentCurrentVersion(id, cacheDoc => {
                    resolve(!!cacheDoc);
                });
            });
            try {
                if (!found) {
                    found = !!(yield _self.checkDocumentExistFromServer(id));
                }
            }
            catch (err) {
                if ([UtilHttpStatus.INTERNAL_SERVER_ERROR, UtilHttpStatus.SERVER_UNRESPONSIVE, UtilHttpStatus.NOT_FOUND].indexOf(err.status) !== -1) {
                    found = false;
                }
                else {
                    throw err;
                }
            }
            return found;
        });
    }
    checkDocumentExistFromServer(id, includeDeleted) {
        return __awaiter(this, void 0, void 0, function* () {
            const _self = this;
            try {
                return yield _self.restDataService.checkDocumentExists(serverRoutes$2.documentExists, id, includeDeleted);
            }
            catch (error) {
                const offlineMode = _self.loggedInUserService.getUserConfigItem('cachingEnabled') === true;
                if (offlineMode && error && error.status === UtilHttpStatus.SERVER_UNRESPONSIVE) {
                    error.status = UtilHttpStatus.NOT_FOUND;
                    error.message = 'Network connection lost. Could not retrieve.';
                }
                throw error;
            }
        });
    }
    findByElastic(query, searchContext) {
        const options = {
            filter: query
        };
        return this.search(options, searchContext);
    }
    generatePit() {
        return this.restDataService.generatePit();
    }
    aggregate(query) {
        return this.restDataService.aggregate(query);
    }
    search(options, searchContext) {
        const _self = this;
        return new Promise((resolve, reject) => {
            if (searchContext) {
                const keys = Object.keys(searchContext);
                if (keys && keys.length) {
                    for (const key of keys) {
                        options[key] = searchContext[key];
                    }
                }
            }
            const offlineMode = _self.loggedInUserService.getUserConfigItem('cachingEnabled') === true;
            const searchOffline = () => {
                _self.indexedDBService.search(options).then((data2) => {
                    resolve(data2);
                }, (err) => {
                    const msg = 'Search by indexedDB failed with error: ' + err;
                    logger$l.error(msg);
                    reject(new Error(msg));
                });
            };
            if (options.text || options.filter) {
                if (options.document) {
                    const doc = {
                        document: options.document,
                        account: _self.user.account
                    };
                    if (!UtilType.isString(options.filter)) {
                        options.filter = JSON.stringify(options.filter);
                    }
                    if (!(options === null || options === void 0 ? void 0 : options.disableHandlebars)) {
                        const hbTemplate = Handlebars.compile(options.filter);
                        options.filter = hbTemplate(doc);
                    }
                    delete options.document;
                }
                if (options.searchOfflineOnly) {
                    if (!offlineMode) {
                        reject(new Error('Please enable caching before performing a search configured as offline only'));
                    }
                    else {
                        searchOffline();
                    }
                }
                else {
                    _self.restDataService.search(options).then((data) => {
                        if (!data.statusCode && data.statusCode !== UtilHttpStatus.OK && offlineMode) {
                            searchOffline();
                        }
                        else {
                            resolve(data);
                        }
                    }, (err) => {
                        if (offlineMode) {
                            searchOffline();
                        }
                        else {
                            reject(err);
                        }
                    });
                }
            }
            else if (options.searchOptions && options.searchOptions.dexieSearchFunction) {
                searchOffline();
            }
            else {
                const er = UtilError.createError('Undefined search text or query!');
                reject(er);
            }
        });
    }
    /** initialise the service passed in and return a promise for initialisation completion
     *
     * @param service - the service to initialise
     */
    initialiseService(service) {
        if (service && service.initialise) {
            return service.initialise();
        }
        // no initialise function in service
        return Promise.resolve();
    }
    terminateService(service) {
        if (service && service.terminateService) {
            return service.terminateService();
        }
        // no terminateService function in service
        return Promise.resolve();
    }
    /** set the versionId and previousVersionId fields */
    setVersionFields(document) {
        var _a, _b;
        if (document && document.systemHeader) {
            if (document && document.systemHeader.versionId) {
                document.systemHeader.previousVersionId = document.systemHeader.versionId;
                document.systemHeader.versionId = UtilDocumentId.generateId();
                // update createdBy every time because when we save the doc it's a new version
                document.systemHeader.createdBy = (_b = (_a = this.user) === null || _a === void 0 ? void 0 : _a.account) === null || _b === void 0 ? void 0 : _b.documentId;
            }
        }
    }
    hasOfflineAccess(document) {
        return this.clientAccessService.hasPermission(document, SharedConstants.OPERATION_TYPE_OFFLINE);
    }
    isAccountDoc(doc) {
        return doc.systemHeader && (doc.systemHeader.systemType === SharedConstants.SYSTEM_TYPE_ACCOUNT ||
            doc.systemHeader.systemType === SharedConstants.SYSTEM_TYPE_ACCOUNT_SECURITY ||
            doc.systemHeader.systemType === SharedConstants.SYSTEM_TYPE_ACCOUNT_CONTROL);
    }
    jsonPatchUpdate(originalBean, newBean, updateOption) {
        return __awaiter(this, void 0, void 0, function* () {
            // set the details like the versionId and previousVersionId
            this.setVersionFields(newBean);
            // set the systemHeader.createdDate in the new version. The systemHeader.createdDate is the created date of the
            // version, rather than the document, so each version needs to have a new createdDate. See Mantis 10816 for further
            // details
            newBean.systemHeader.createdDate = Date.now();
            // always use the Database Service if caching is enabled, so that the items will be placed in the
            // queue to be sent to the server, otherwise items can be saved in the wrong
            // order if there is anything already in the queue
            const updates = yield DocumentUpdater.getUpdates(originalBean, newBean);
            const options = {
                updates: updates,
                isUpdateFromPreviousVersion: UtilDocument.isPreviousVersion(newBean),
                overrideTemplateId: updateOption ? updateOption.overrideTemplateId : null,
                template: updateOption ? updateOption.templateId : null,
                makeCurrentVersion: updateOption ? updateOption.makeCurrentVersion : null,
                importPreviousVersion: updateOption ? updateOption.importPreviousVersion : null,
                documentSession: this.documentSessionService.getDocumentSession(originalBean.documentId)
            };
            if (this.loggedInUserService.getUserConfigItem('cachingEnabled') === true
                && this.hasOfflineAccess(originalBean) && !this.isAccountDoc(newBean)) {
                return this.indexedDBService.jsonPatchUpdate(newBean, originalBean, options);
            }
            return this.restDataService.jsonPatchUpdate(newBean, options);
        });
    }
    /**
     * Called in CacheLoadService.enable cache to re-arrange primary and secondary data service
     */
    initialise() {
        const _self = this;
        const offlineMode = _self.loggedInUserService.getUserConfigItem('cachingEnabled') === true;
        _self.preferredService = offlineMode ? _self.indexedDBService : _self.restDataService;
        _self.secondaryService = offlineMode ? _self.restDataService : null;
        return _self.initialiseService(_self.preferredService).then(() => {
            return _self.initialiseService(_self.secondaryService);
        });
    }
    // terminateService DataServices on logout. See InitialisationService.terminateDefaultServices
    reset() {
        const _self = this;
        return _self.terminateService(_self.preferredService).then(() => {
            return _self.terminateService(_self.secondaryService);
        }).then(() => {
            // RestDataService is necessary for login.
            // Make RestDataService as the preferred service and reset.
            _self.preferredService = _self.restDataService;
            return _self.initialiseService(_self.preferredService);
        });
    }
    /*
    jsonPatchUpdate function
    parameters:
    bean - the bean to update
    cachingRequired - whether to save the record to the local database. Records that never need
    to be loaded in the app on their own, such as events, can have this set to false
    */
    deepDiffUpdate(originalBean, newBean, options) {
        return this.jsonPatchUpdate(originalBean, newBean, options);
    }
    /*
     insert function
     parameters:
     bean - the bean to insert
     successFunction - a function to execute on success to do post save steps
     cachingRequired - whether to save the record to the local database. Records that never need
     to be loaded in the app on their own, such as events, can have this set to false
     */
    insert(bean, cachingRequired) {
        return __awaiter(this, void 0, void 0, function* () {
            // set the details like the versionId and previousVersionId
            this.setVersionFields(bean);
            // a different version of the data service will be used depending on whether offline is enabled.
            // assign the service that is to be used rather than the function because assigning a function that is a member of
            // a class means the this object of the function will not be defined
            let dataSaveService;
            let hasOfflineAccess = true;
            try {
                if (bean.systemHeader && bean.systemHeader.templateId) {
                    const template = yield this.getDocument(bean.systemHeader.templateId, SharedConstants.SYSTEM_TYPE_TEMPLATE);
                    hasOfflineAccess = this.hasOfflineAccess(template);
                }
                // always use the Database Service if caching is enabled, so that the items will be placed in the
                // queue to be sent to the server, otherwise items can be saved in the wrong
                // order if there is anything already in the queue
                if (this.loggedInUserService.getUserConfigItem('cachingEnabled') === true
                    && hasOfflineAccess && !this.isAccountDoc(bean)) {
                    dataSaveService = this.indexedDBService;
                }
                else {
                    dataSaveService = this.restDataService;
                }
                const fullUrl = UtilService.getDataUrl(serverRoutes$2.document);
                return yield dataSaveService.insert(bean, fullUrl, cachingRequired);
            }
            catch (error) {
                throw error;
            }
        });
    }
    destroy(id, templateId, versionId) {
        return __awaiter(this, void 0, void 0, function* () {
            const document = yield this.getDocument(id);
            try {
                if (this.loggedInUserService.getUserConfigItem('cachingEnabled') === true
                    && this.hasOfflineAccess(document)) {
                    return yield this.indexedDBService.destroy(id);
                }
                else {
                    return yield this.restDataService.destroy(id, serverRoutes$2.document, null, templateId, versionId);
                }
            }
            catch (err) {
                logger$l.log('Delete failed with error: ' + err);
                throw new Error(err);
            }
        });
    }
    /**
     * load unsynced and cache documents into indexedDB
     * @param isInitialize - whether in the case the documents are cached in the first time
     */
    workerLoadDataCache(isInitialize) {
        const _self = this;
        if (!_self.loggedInUserService.getUserConfigItem('cachingEnabled') === true || this.isRunningPreviousSync) {
            return Promise.resolve();
        }
        let syncStartTime = _self.offlineStatusService.getLastSyncDate();
        if (!syncStartTime) {
            syncStartTime = 0;
            logger$l.log('Loading data cache from beginning of time');
        }
        else {
            logger$l.log('Loading data cache from time: ' + syncStartTime);
        }
        this.isRunningPreviousSync = true;
        _self.loggedInUserService.setUserConfigItem('initialCacheCompleted', false);
        this.offlineStatusService.offlineStatus.cacheLoading = true;
        return _self.restDataService.workerLoadDataCache(syncStartTime, isInitialize).then((results) => __awaiter(this, void 0, void 0, function* () {
            logger$l.log('Done caching offline documents. Adding any search indexes from indexedDBIndexes.');
            yield _self.loadFileAttachmentCache();
            _self.loggedInUserService.setUserConfigItem('ErrorCachingAttachedFiles', this.offlineStatusService.offlineStatus.errorCacheAttachedFile);
            _self.loggedInUserService.setUserConfigItem('initialCacheCompleted', true);
            // registers search indexes
            return _self.indexedDBConnectorService.upgradeIndexedDB().then(() => {
                return Promise.resolve(results);
            }, (err) => {
                return Promise.reject(err);
            });
        }), (err) => {
            logger$l.error('Error caching offline documents.');
            logger$l.error(err);
            this.isRunningPreviousSync = false;
            return Promise.reject(err);
        }).then((results) => {
            logger$l.log('Done registering search indexes.');
            this.isRunningPreviousSync = false;
            return Promise.resolve(results);
        }, (err) => {
            logger$l.error('Error in registering search indexes.');
            logger$l.error(err);
            this.isRunningPreviousSync = false;
            return Promise.reject(err);
        });
    }
    /**
     * load unsynced and cache documents into indexedDB
     * @param isInitialize - whether in the case the documents are cached in the first time
     */
    loadDataCache(isInitialize) {
        const _self = this;
        if (_self.loggedInUserService.getUserConfigItem('initialCacheCompleted') === false) {
            _self.workerLoadDataCache(true);
            return Promise.resolve();
        }
        if (!_self.loggedInUserService.getUserConfigItem('cachingEnabled') === true || this.isRunningPreviousSync) {
            return Promise.resolve();
        }
        let syncStartTime = _self.offlineStatusService.getLastSyncDate();
        if (!syncStartTime) {
            syncStartTime = 0;
            logger$l.log('Loading data cache from beginning of time');
        }
        else {
            logger$l.log('Loading data cache from time: ' + syncStartTime);
        }
        this.isRunningPreviousSync = true;
        return _self.restDataService.loadDataCache(syncStartTime, isInitialize).then((results) => {
            logger$l.log('Done caching offline documents. Adding any search indexes from indexedDBIndexes.');
            // registers search indexes
            return _self.indexedDBConnectorService.upgradeIndexedDB().then(() => {
                return Promise.resolve(results);
            }, (err) => {
                return Promise.reject(err);
            });
        }, (err) => {
            logger$l.error('Error caching offline documents.');
            logger$l.error(err);
            this.isRunningPreviousSync = false;
            return Promise.reject(err);
        }).then((results) => {
            logger$l.log('Done registering search indexes.');
            this.isRunningPreviousSync = false;
            return Promise.resolve(results);
        }, (err) => {
            logger$l.error('Error in registering search indexes.');
            logger$l.error(err);
            this.isRunningPreviousSync = false;
            return Promise.reject(err);
        });
    }
    clearData() {
        return __awaiter(this, void 0, void 0, function* () {
            const _self = this;
            if (_self.loggedInUserService.getUserConfigItem('cachingEnabled') === true) {
                yield _self.indexedDBService.clearData();
                yield _self.indexedDBConnectorService.closeDatabase();
                return Promise.resolve(true);
            }
            else {
                return Promise.reject(new Error('You did not enable caching.'));
            }
        });
    }
    backupData(isFirstTime) {
        const _self = this;
        return new Promise((resolve, reject) => {
            if (_self.loggedInUserService.getUserConfigItem('cachingEnabled') === true) {
                _self.indexedDBService.backupData().then(resolve, reject);
            }
            else {
                reject(new Error('You did not enable caching.'));
            }
        });
    }
    getIndexedDBIndexes() {
        return this.restDataService.getIndexedDBIndexes();
    }
    undestroy(id) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.loggedInUserService.getUserConfigItem('cachingEnabled') === true) {
                const errMsg = 'You need to be online to undelete a document. ';
                logger$l.error(errMsg);
                throw new Error(errMsg);
            }
            else {
                const url = 'api/undelete';
                try {
                    return yield this.restDataService.undestroy(id, url);
                }
                catch (err) {
                    logger$l.error('Delete failed with error: ' + err);
                    throw new Error(err);
                }
            }
        });
    }
    bulkUploadDocuments(documents) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.loggedInUserService.getUserConfigItem('cachingEnabled') === true) {
                const errMsg = 'You need to be online to upload multiple documents. ';
                console.error(errMsg);
                throw new Error(errMsg);
            }
            else {
                const url = 'api/bulkUploadDocuments';
                try {
                    return yield this.restDataService.bulkUploadDocuments(documents, url);
                }
                catch (err) {
                    console.error('Upload documents failed with error: ' + err);
                    throw new Error(err);
                }
            }
        });
    }
    executeComponentFunction(options) {
        const _self = this;
        return new Promise((resolve, reject) => {
            const offlineMode = _self.loggedInUserService.getUserConfigItem('cachingEnabled') === true;
            if (options.componentWebServiceName) {
                const searchOffline = () => {
                    _self.indexedDBService.executeComponentFunction(options).then((data2) => {
                        resolve(data2);
                    }, (err) => {
                        reject(err);
                    });
                };
                if (options.searchOfflineOnly) {
                    if (!offlineMode) {
                        reject(new Error('Please enable caching before performing a search configured as offline only'));
                    }
                    else {
                        searchOffline();
                    }
                }
                else {
                    _self.restDataService.executeComponentFunction(options).then((data) => {
                        if (!data.statusCode && data.statusCode !== UtilHttpStatus.OK && offlineMode) {
                            searchOffline();
                        }
                        else {
                            resolve(data);
                        }
                    }, (err) => {
                        if (offlineMode) {
                            searchOffline();
                        }
                        else {
                            reject(err);
                        }
                    });
                }
            }
            else {
                const er = UtilError.createError('Undefined executeComponentFunction componentWebServiceName and functionParameters!');
                reject(er);
            }
        });
    }
    getTemplateAndDocumentOfflineMode(id, templateId) {
        const _self = this;
        return new Promise((resolve, reject) => {
            _self.indexedDBService.getTemplateAndDocument(id, templateId, (err, cacheDoc) => __awaiter(this, void 0, void 0, function* () {
                if (cacheDoc == null) { // not found in offline? retrieve from server
                    try {
                        const result = yield this.restDataService.getTemplateAndDocument(id, templateId);
                        resolve(result);
                    }
                    catch (error) {
                        const offlineMode = this.loggedInUserService.getUserConfigItem('cachingEnabled') === true;
                        if (offlineMode && error && error.status === UtilHttpStatus.SERVER_UNRESPONSIVE && err) {
                            error.status = UtilHttpStatus.NOT_FOUND;
                            error.message = err.message;
                        }
                        reject(error);
                    }
                }
                else {
                    resolve(cacheDoc);
                }
            }));
        });
    }
    loadFileAttachmentCache() {
        return __awaiter(this, void 0, void 0, function* () {
            const tbl = IndexedDBConstants.DOCUMENT_TABLE_NAME;
            const index = IndexedDBConstants.DOCUMENT_INDEX_SYSTEM_TYPE;
            const searchValue = 'fileReference';
            return this.indexedDBService.getEntries(tbl, index, searchValue, (documents) => {
                documents.forEach((doc) => __awaiter(this, void 0, void 0, function* () {
                    return yield this.indexedDBService.cacheAttachedFilesOffline(doc);
                }));
            });
        });
    }
}
/** @nocollapse */ /** @nocollapse */ DataService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DataService, deps: [{ token: IndexedDBConnectorService }, { token: LoggedInUserService }, { token: RestDataService }, { token: OfflineStatusService }, { token: IndexedDBService }, { token: ClientAccessService }, { token: DocumentSessionService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ DataService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DataService, providedIn: 'root' });
__decorate([
    select(['userState', 'user']),
    __metadata("design:type", Observable)
], DataService.prototype, "user$", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: IndexedDBConnectorService }, { type: LoggedInUserService }, { type: RestDataService }, { type: OfflineStatusService }, { type: IndexedDBService }, { type: ClientAccessService }, { type: DocumentSessionService }]; }, propDecorators: { user$: [] } });

class CurrentDocumentInfoService {
    constructor(appStore) {
        this.appStore = appStore;
    }
    getStoreDocumentInfo() {
        return this.appStore.getState().formState.documentInfo;
    }
    getDocumentData(unsavedDocumentListId, selectedDocumentId, overrideTemplateId) {
        const documentData = {};
        if (unsavedDocumentListId) {
            const storeDocInfo = this.getStoreDocumentInfo();
            const documentInfo = storeDocInfo[unsavedDocumentListId] ? storeDocInfo[unsavedDocumentListId] : {};
            const documentInfoItem = documentInfo[selectedDocumentId];
            if (documentInfoItem) {
                documentData.documentId = selectedDocumentId;
                documentData.document = cloneDeep(this.appStore.getState().formState.documents[selectedDocumentId]);
                const templateId = overrideTemplateId ? overrideTemplateId : documentInfoItem.templateId;
                documentData.templateId = templateId;
                documentData.template = cloneDeep(this.appStore.getState().formState.templates[templateId]);
                documentData.isNew = documentInfoItem.isNew;
                documentData.isMainDoc = documentInfoItem.isMainDoc;
            }
        }
        return documentData;
    }
    getMainDocumentListId() {
        const documentInfo = this.appStore.getState().formState.documentInfo;
        const unsavedDocumentListIds = Object.keys(documentInfo);
        return unsavedDocumentListIds.filter((unsavedDocumentListId) => {
            return this.getMainDocumentIdFromList(unsavedDocumentListId) !== null;
        })[0];
    }
    getMainDocumentId() {
        const mainDocumentListId = this.getMainDocumentId();
        return this.getMainDocumentIdFromList(mainDocumentListId);
    }
    getMainDocumentIdFromList(unsavedDocumentListId) {
        const documentListInfo = this.appStore.getState().formState.documentInfo[unsavedDocumentListId];
        if (documentListInfo) {
            const documentIds = Object.keys(documentListInfo);
            const filteredIds = documentIds.filter((documentId) => {
                var _a;
                return (_a = documentListInfo[documentId]) === null || _a === void 0 ? void 0 : _a.isMainDoc;
            });
            if (filteredIds.length) {
                return filteredIds[0];
            }
        }
        return null;
    }
}
/** @nocollapse */ /** @nocollapse */ CurrentDocumentInfoService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CurrentDocumentInfoService, deps: [{ token: AppStore }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ CurrentDocumentInfoService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CurrentDocumentInfoService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CurrentDocumentInfoService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: AppStore }]; } });

const logger$k = console;
class UnsavedDocumentService {
    constructor(appStore, currentDocumentInfoService) {
        this.appStore = appStore;
        this.currentDocumentInfoService = currentDocumentInfoService;
    }
    /**
     * create a new unsaved document list. The unsaved document list is a list of documents that will be saved in a single
     * operation. This allows saving of documents independently of the nav bar save button. Eg. If you open up a dialog with a
     * template with a child doc then you need to save several documents on dialog close without saving the main document loaded
     * in the form. We can't put parameters in the save function to handle special cases like whether the save is for a dialog
     * because we would have to keep adding more special cases for saving from different locations.
     */
    createUnsavedDocumentList(isNewMainDocList, existingUnsavedDocumentListId) {
        // generate an unsaved document list id, this will be used by the caller to keep track of this list. It will be passed
        // to the save function to trigger the save of the documents in that list
        const unsavedDocumentListId = existingUnsavedDocumentListId ? existingUnsavedDocumentListId : UtilDocumentId.generateId();
        this.appStore.dispatch(formSetupDocumentList(isNewMainDocList, unsavedDocumentListId));
        return unsavedDocumentListId;
    }
    getDocumentInfo(documentListId) {
        if (!documentListId) {
            throw new Error('No documentListId provided in getDocumentInfo');
        }
        return cloneDeep$1(this.appStore.getState().formState.documentInfo[documentListId]);
    }
    /**
     * Get document information for each document inside a list. The result should be:
    {
      documentId: 'document-id',
      templateId: 'template-id',
      hierarchyInfo: {
        parentDocumentId: 'parent-document-id',
        childDocumentId: 'child-document-id'
      },
      options: {}
    }
     * @param documentListId
     * @param documentId
     * @returns
     */
    getDocumentInfoItem(documentListId, documentId) {
        if (!documentListId) {
            throw new Error('No documentListId or documentId provided in getDocumentInfo item');
        }
        const storedDocInfo = this.appStore.getState().formState.documentInfo[documentListId];
        let documentInfoItem = storedDocInfo ? storedDocInfo[documentId] : null;
        if (!documentInfoItem) {
            documentInfoItem = {
                documentListId,
                documentId,
                hierarchyInfo: {}
            };
        }
        else {
            documentInfoItem = cloneDeep$1(documentInfoItem);
        }
        return documentInfoItem;
    }
    getUnsavedDocumentInfo(unsavedDocumentListId) {
        return cloneDeep$1(this.appStore.getState().formState.documentInfo[unsavedDocumentListId]);
    }
    /**
     * add a document to the list, so it will be included in the list of unsaved documents
     * @param document the document
     * @param unsavedDocumentListId the unsavedDocumentListId
     */
    addDocumentToUnsavedList(document, unsavedDocumentListId) {
        if (!document || !document.documentId) {
            throw new Error('No document or documentId provided in addDocumentToUnsavedList');
        }
        else if (!unsavedDocumentListId) {
            throw new Error('No unsavedDocumentListId provided in addDocumentToUnsavedList');
        }
        const docInfo = {
            documentId: document.documentId,
        };
        this.appStore.dispatch(formSetDocumentInfo(unsavedDocumentListId, docInfo));
        this.appStore.dispatch(formSetDocument(document));
    }
    /**
     * add a template to the list
     * @param template the template obj
     * @param unsavedDocumentListId the unsaved list
     * @param orgTemplateId the original template id
     */
    addTemplateToUnsavedList(document, template, unsavedDocumentListId, orgTemplateId) {
        if (!template || !template.documentId) {
            throw new Error('No template provided in addTemplateToUnsavedList');
        }
        else if (!unsavedDocumentListId) {
            throw new Error('No unsavedDocumentListId provided in addTemplateToUnsavedList');
        }
        const docInfo = {
            documentId: document.documentId
        };
        const templateId = template.documentId;
        // in case alt template
        if (orgTemplateId && templateId !== orgTemplateId) {
            docInfo.templateId = orgTemplateId;
            docInfo.altTemplateId = templateId;
        }
        else {
            docInfo.templateId = templateId;
        }
        this.appStore.dispatch(formSetDocumentInfo(unsavedDocumentListId, docInfo));
    }
    /**
     * get the document from the unsaved document list. The document will be set in the document parameter rather than returned
     * so that the document directly references the value in the unsaved list
     * @param documentResult - the document will be placed in the document parameter of documentResult. It needs to be done this
     *                         way because if you assign to an object directly, the reference that the object points at changes
     * @param unsavedDocumentListId the unsaved list Id
     * @param documentId the document Id
     */
    getDocument(documentResult, unsavedDocumentListId, documentId) {
        const formState = this.appStore.getState().formState;
        const documents = formState.documents;
        const docInfo = formState.documentInfo;
        if (!documentResult) {
            throw new Error('The documentResult must be passed as an object to getDocument so the document can be set under it');
        }
        else if (!unsavedDocumentListId) {
            throw new Error('Unsaved document list id not provided in getDocument');
        }
        else if (!docInfo[unsavedDocumentListId]) {
            throw new Error('GetDocument: Unsaved document list has not been initialised with id: ' + unsavedDocumentListId);
        }
        else if (!docInfo[unsavedDocumentListId][documentId]) {
            throw new Error('Unsaved document list does not contain document: ' + documentId);
        }
        // set the document from loaded docs in the document parameter
        documentResult.document = cloneDeep$1(documents[documentId]);
    }
    /**
     * get the template from the unsaved document list. The template will be set in the template result parameter
     * rather than returned so that the template directly references the value in the unsaved list
     * @param documentResult - the template will be placed in the template parameter of documentResult. It needs to
     * be done this way because if you assign to an object directly, the reference that the object points at changes
     * @param unsavedDocumentListId the unsaved list Id
     * @param templateId the template Id
     */
    getTemplate(documentResult, unsavedDocumentListId, templateId, documentId) {
        const formState = this.appStore.getState().formState;
        const templates = formState.templates;
        const docInfo = formState.documentInfo;
        if (!documentResult) {
            throw new Error('The documentResult must be passed as an object to getTemplate so the template can be set under it');
        }
        else if (!unsavedDocumentListId) {
            throw new Error('Unsaved document list id not provided in getTemplate');
        }
        else if (!docInfo[unsavedDocumentListId]) {
            throw new Error('GetTemplate: Unsaved document list has not been initialised with id: ' + unsavedDocumentListId);
        }
        else if (!docInfo[unsavedDocumentListId][documentId]) {
            throw new Error('Unsaved document list does not contain document: ' + documentId);
        }
        const docInfoItem = docInfo[unsavedDocumentListId][documentId];
        if (docInfoItem.templateId === templateId || docInfoItem.altTemplateId === templateId) {
            // set the template from loaded templates in the document parameter
            documentResult.template = cloneDeep$1(templates[templateId]);
        }
        if (!templateId) {
            throw new Error('Unsaved document list does not contain template: ' + templateId);
        }
    }
    setDocument(unsavedDocumentListId, document) {
        if (!unsavedDocumentListId) {
            throw new Error('Unsaved document list id not provided in setDocument');
        }
        const docInfo = {
            documentId: document.documentId
        };
        this.appStore.dispatch(formSetDocumentInfo(unsavedDocumentListId, docInfo));
        this.appStore.dispatch(formSetDocument(document));
    }
    setTemplate(unsavedDocumentListId, documentId, template) {
        if (!unsavedDocumentListId) {
            throw new Error('Unsaved document list id not provided in setTemplate');
        }
        else if (!template || !template.documentId) {
            throw new Error('Template not provided in setTemplate');
        }
        const docInfo = {
            templateId: template.documentId,
            documentId
        };
        this.appStore.dispatch(formSetDocumentInfo(unsavedDocumentListId, docInfo));
        this.appStore.dispatch(formSetDocument(template));
    }
    /**
     * get the document from a unsaved document list.
     * @param documentId the document Id
     */
    getDocumentOnly(documentId) {
        return cloneDeep$1(this.appStore.getState().formState.documents[documentId]);
    }
    /**
     * get the template from the unsaved document list.
     * @param templateId the template Id
     */
    getTemplateOnly(templateId) {
        return this.getDocumentOnly(templateId);
    }
    setDocumentListOptions(unsavedDocumentListId, documentId, key, value) {
        if (!unsavedDocumentListId) {
            throw new Error('Unsaved document list id not provided in getDocumentOptions');
        }
        const docInfo = {
            documentId,
            options: {
                [key]: value
            }
        };
        this.appStore.dispatch(formSetDocumentInfo(unsavedDocumentListId, docInfo));
    }
    getDocumentListOptions(unsavedDocumentListId, documentId) {
        const docInfo = this.appStore.getState().formState.documentInfo;
        if (!unsavedDocumentListId) {
            throw new Error('Unsaved document list id not provided in getDocumentOptions');
        }
        else if (!docInfo[unsavedDocumentListId] || !docInfo[unsavedDocumentListId][documentId] || !docInfo[unsavedDocumentListId][documentId].options) {
            throw new Error(`getDocumentOptions: Unsaved document list has not been initialised with id: ${unsavedDocumentListId} and docId: ${documentId}`);
        }
        return docInfo[unsavedDocumentListId][documentId].options;
    }
    getDocumentOptionKey(unsavedDocumentListId, documentId, optionKey) {
        const options = this.getDocumentListOptions(unsavedDocumentListId, documentId);
        return options[optionKey];
    }
    /**
     * Get the list of pair of document and template for each form. The result should be:
    [{
      document: document1,
      template: template1
    },
    {
      document: document2,
      template: template2
    }
    ]
     * @param unsavedDocumentListId
     * @returns
     */
    getUnsavedDocumentDatas(unsavedDocumentListId) {
        const documentDatas = [];
        const documentInfo = this.appStore.getState().formState.documentInfo[unsavedDocumentListId];
        const documents = this.appStore.getState().formState.documents;
        const templates = this.appStore.getState().formState.templates;
        const documentIds = Object.keys(documentInfo);
        documentIds.forEach(documentId => {
            const documentInfoItem = documentInfo[documentId];
            if (documentInfoItem === null || documentInfoItem === void 0 ? void 0 : documentInfoItem.loaded) {
                const document = cloneDeep$1(documents[documentId]);
                const templateId = documentInfoItem.overrideTemplateId ? documentInfoItem.overrideTemplateId : documentInfoItem.templateId;
                const template = cloneDeep$1(templates[templateId]);
                documentDatas.push({
                    document,
                    template
                });
            }
        });
        return documentDatas;
    }
    /**
     * get all the unsaved documents inside the provided document list
     * @param unsavedDocumentListId - the id of the list to get the loaded docs for
     */
    getUnsavedDocuments(unsavedDocumentListId) {
        const documentInfo = this.appStore.getState().formState.documentInfo[unsavedDocumentListId];
        const documents = this.appStore.getState().formState.documents;
        const unsavedDocuments = [];
        const documentIds = Object.keys(documentInfo);
        documentIds.forEach(documentId => {
            const doc = cloneDeep$1(documents[documentId]);
            unsavedDocuments.push(doc);
        });
        return unsavedDocuments;
    }
    getUnsavedDocumentId(unsavedDocumentListId) {
        const docInfo = this.appStore.getState().formState.documentInfo;
        if (!unsavedDocumentListId) {
            throw new Error('Unsaved document list id not specified when getting loaded documents');
        }
        else if (!docInfo[unsavedDocumentListId]) {
            throw new Error('Unsaved documents not found with id ' + unsavedDocumentListId);
        }
        return this.currentDocumentInfoService.getMainDocumentIdFromList(unsavedDocumentListId);
    }
    /**
     * get all the unsaved templates inside the privided document list
     * @param unsavedDocumentListId - the id of the document list to get all the loaded templates
     */
    getUnsavedTemplates(unsavedDocumentListId) {
        const documentInfo = this.appStore.getState().formState.documentInfo[unsavedDocumentListId];
        const unsavedTemplates = [];
        const templates = this.appStore.getState().formState.templates;
        const documentIds = Object.keys(documentInfo);
        documentIds.forEach(documentId => {
            const documentInfoItem = documentInfo[documentId];
            const templateId = documentInfoItem.templateId;
            const doc = cloneDeep$1(templates[templateId]);
            unsavedTemplates.push(doc);
        });
        return unsavedTemplates;
    }
    getUnsavedTemplate(documentId, unsavedDocumentListId) {
        const docInfo = this.appStore.getState().formState.documentInfo;
        if (!docInfo) {
            console.warn('Document infor in app state seems not initialized yet.');
            return null;
        }
        if (!unsavedDocumentListId) {
            console.warn('Unsaved document list id not specified when getting unsaved template');
            return null;
        }
        if (!documentId) {
            console.warn('Unsaved document id not specified when getting unsaved template');
            return null;
        }
        if (!docInfo[unsavedDocumentListId] || !docInfo[unsavedDocumentListId][documentId]) {
            console.warn(`Unsaved template not found with document list id: ${unsavedDocumentListId} and documentId: ${documentId}`);
            return null;
        }
        const overrideTemplateId = docInfo[unsavedDocumentListId][documentId].overrideTemplateId;
        const templateId = overrideTemplateId ? overrideTemplateId : docInfo[unsavedDocumentListId][documentId].templateId;
        return cloneDeep$1(this.appStore.getState().formState.templates[templateId]);
    }
    removeDocumentList(unsavedDocumentListId) {
        this.appStore.dispatch(formResetDocumentInfo(unsavedDocumentListId));
    }
    /**
     * Get parent document and template
     * @param unsavedDocumentListId
     * @param childDocumentId
     */
    getParentData(unsavedDocumentListId, childDocumentId) {
        var _a;
        if (!unsavedDocumentListId) {
            throw new Error('Unsaved document list id not specified when getting unsaved parent data');
        }
        if (!childDocumentId) {
            throw new Error('Unsaved child document id not specified when getting unsaved parent data');
        }
        const documents = this.appStore.getState().formState.documents;
        const templates = this.appStore.getState().formState.templates;
        const docInfo = this.appStore.getState().formState.documentInfo[unsavedDocumentListId];
        if (docInfo && docInfo[childDocumentId]) {
            const parentDocId = (_a = docInfo[childDocumentId].hierarchyInfo) === null || _a === void 0 ? void 0 : _a.parentDocumentId;
            if (parentDocId) {
                const parentDoc = cloneDeep$1(documents[parentDocId]);
                const parentTplId = docInfo[parentDocId].overrideTemplateId ? docInfo[parentDocId].overrideTemplateId : docInfo[parentDocId].templateId;
                const parentTpl = cloneDeep$1(templates[parentTplId]);
                return {
                    parentDoc,
                    parentTpl
                };
            }
        }
        return {};
    }
    setParentInfo(documentContext) {
        const hierarchyInfo = documentContext.formParameters.hierarchyInfo;
        const parentDocumentId = hierarchyInfo === null || hierarchyInfo === void 0 ? void 0 : hierarchyInfo.parentDocumentId;
        const parentDocument = this.appStore.getState().formState.documents[parentDocumentId];
        documentContext.parentDocument = parentDocument;
        if (parentDocument) {
            documentContext.parentTemplate = this.appStore.getState().formState.templates[parentDocument.systemHeader.templateId];
        }
    }
    setChildInfo(documentContext) {
        const hierarchyInfo = documentContext.formParameters.hierarchyInfo;
        const childDocumentId = hierarchyInfo === null || hierarchyInfo === void 0 ? void 0 : hierarchyInfo.childDocumentId;
        const childDocument = this.appStore.getState().formState.documents[childDocumentId];
        documentContext.childDocument = childDocument;
        if (childDocument) {
            documentContext.childTemplate = this.appStore.getState().formState.templates[childDocument.systemHeader.templateId];
        }
    }
    /**
     * Get child document and template
     * @param unsavedDocumentListId
     * @param documentId
     */
    getChildData(unsavedDocumentListId, documentId) {
        var _a;
        if (!unsavedDocumentListId) {
            throw new Error('Unsaved document list id not specified when getting unsaved child data');
        }
        if (!documentId) {
            throw new Error('Unsaved child document id not specified when getting unsaved child data');
        }
        const documents = this.appStore.getState().formState.documents;
        const templates = this.appStore.getState().formState.templates;
        const docInfo = this.appStore.getState().formState.documentInfo[unsavedDocumentListId];
        if (docInfo && docInfo[documentId]) {
            const childDocId = (_a = docInfo[documentId].hierarchyInfo) === null || _a === void 0 ? void 0 : _a.childDocumentId;
            if (childDocId) {
                const childDoc = cloneDeep$1(documents[childDocId]);
                const childTplId = docInfo[childDocId].overrideTemplateId ? docInfo[childDocId].overrideTemplateId : docInfo[childDocId].templateId;
                const childTpl = cloneDeep$1(templates[childTplId]);
                return {
                    childDoc,
                    childTpl
                };
            }
        }
        return {};
    }
    /**
       * store a document hierarchy info to a list. This will be needed when retrieve information to load a child
       * document from a parent one
       * @param hierarchyInfo (parentDocumentId and childDocumentId)
       * @param unsavedDocumentListId the unsaved list Id
       */
    updateDocumentListInfo(documentInfo, unsavedDocumentListId) {
        this.appStore.dispatch(formSetDocumentInfo(unsavedDocumentListId, documentInfo));
    }
    clearDocuments() {
        // this.documents = {};
    }
    getAllLoadedDocs(excludedOptions) {
        const docInfo = this.appStore.getState().formState.documentInfo;
        const documents = this.appStore.getState().formState.documents;
        const docIds = Object.keys(documents);
        const loadedDocs = [];
        const unsavedDocumentListIds = Object.keys(docInfo);
        unsavedDocumentListIds.forEach(function (unsavedDocumentListId) {
            const documentIds = Object.keys(docInfo[unsavedDocumentListId]);
            documentIds.forEach(documentId => {
                const options = docInfo[unsavedDocumentListId][documentId].options;
                docIds.forEach(function (docId) {
                    const doc = cloneDeep$1(documents[docId]);
                    if (excludedOptions) {
                        if (!UtilDocument.hasIntersect(options[docId], excludedOptions)) {
                            loadedDocs.push(doc);
                        }
                    }
                    else {
                        loadedDocs.push(doc);
                    }
                });
            });
        });
        return loadedDocs;
    }
    documentIsLoaded(documentId) {
        const docInfo = this.appStore.getState().formState.documentInfo;
        const documents = this.appStore.getState().formState.documents;
        let result = false;
        const unsavedDocumentListIds = Object.keys(docInfo);
        unsavedDocumentListIds.forEach(function (unsavedDocumentListId) {
            if (documents[documentId] && docInfo[unsavedDocumentListId][documentId] && docInfo[unsavedDocumentListId][documentId].loaded) {
                result = true;
            }
        });
        return result;
    }
    isDocumentsDefined(unsavedDocumentListId) {
        return !!this.appStore.getState().formState.documentInfo[unsavedDocumentListId];
    }
    getHierarchyInfo(unsavedDocumentListId, documentId) {
        const documentInfo = this.appStore.getState().formState.documentInfo;
        if (documentInfo && documentInfo[unsavedDocumentListId] && documentInfo[unsavedDocumentListId][documentId]) {
            return cloneDeep$1(documentInfo[unsavedDocumentListId][documentId].hierarchyInfo);
        }
        return null;
    }
    resetToOrgDocument(documentId, unsavedDocumentListId) {
        this.appStore.dispatch(formResetToOrgDocument(documentId, unsavedDocumentListId));
    }
    resetDocument(documentId, unsavedDocumentListId) {
        this.resetToOrgDocument(documentId, unsavedDocumentListId);
    }
    resetTemplateComponent(templateId, unsavedDocumentListId) {
        this.appStore.dispatch(formResetTemplateComponentDefinition(templateId, unsavedDocumentListId));
    }
}
/** @nocollapse */ /** @nocollapse */ UnsavedDocumentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: UnsavedDocumentService, deps: [{ token: AppStore }, { token: CurrentDocumentInfoService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ UnsavedDocumentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: UnsavedDocumentService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: UnsavedDocumentService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: AppStore }, { type: CurrentDocumentInfoService }]; } });

const logger$j = console;
class DocumentService {
    constructor(dataService, clientAccessService, unsavedDocumentService, localStorageService, appStore, keyValueStorageService) {
        this.dataService = dataService;
        this.clientAccessService = clientAccessService;
        this.unsavedDocumentService = unsavedDocumentService;
        this.localStorageService = localStorageService;
        this.appStore = appStore;
        this.keyValueStorageService = keyValueStorageService;
    }
    /**
     * Return the cached document that can be the document getting from DB or successful created document on form
     * @param documentId the document Id
     * @returns the existing document
     */
    getExistingDocument(documentId) {
        return cloneDeep(this.appStore.getState().formState.orgDocuments[documentId]);
    }
    callbackResult(callback, result) {
        if (typeof callback === 'function') {
            callback(result);
        }
    }
    execFindDocument(document, callback) {
        return __awaiter(this, void 0, void 0, function* () {
            let hasAccess = this.clientAccessService.checkAccess(document, SharedConstants.OPERATION_TYPE_READ);
            if (hasAccess) {
                // return the cached document in a callback and promise
                this.callbackResult(callback, document);
                return document;
            }
            else {
                if (document.systemHeader.systemType === SharedConstants.SYSTEM_TYPE_TEMPLATE
                    && document.altTemplateIds && document.altTemplateIds.length !== 0) {
                    const docIds = [];
                    for (let i = 0; i < document.altTemplateIds.length; i++) {
                        docIds.push(document.altTemplateIds[i]);
                    }
                    const query = {
                        'query': {
                            'bool': {
                                'must': [
                                    { 'terms': { 'documentId': docIds } }
                                ]
                            }
                        }
                    };
                    const results = yield this.dataService.findByElastic(query);
                    let accessibleDocument;
                    const templates = results.data.hits.hits;
                    for (let i = 0; i < templates.length; i++) {
                        const result = templates[i]._source;
                        hasAccess = this.clientAccessService.checkAccess(result, SharedConstants.OPERATION_TYPE_READ);
                        if (hasAccess) {
                            logger$j.info('Returning Alt Template');
                            accessibleDocument = result;
                            break;
                        }
                    }
                    if (accessibleDocument) {
                        this.callbackResult(callback, accessibleDocument);
                        return Promise.resolve(accessibleDocument);
                    }
                }
                return Promise.reject(UtilError.createForbiddenError(document, SharedConstants.OPERATION_TYPE_READ));
            }
        });
    }
    /**
     * return the document using a promise. This will be used to load documents into the data that is bound to,
     * before determining whether the document is a template or other document
     * @param documentId
     * @param options
     * @param callback
     */
    getDocumentOnly(documentId, options, callback) {
        return __awaiter(this, void 0, void 0, function* () {
            // if the document is already in the cache, return the document from the cache, otherwise get it from the
            // data service
            const document = this.getExistingDocument(documentId);
            if (document) {
                if (document.systemHeader && document.systemHeader.deleted) {
                    const deletedMsg = 'Document has been deleted';
                    return Promise.reject(UtilError.createError(deletedMsg, UtilError.DELETED_ERROR));
                }
                else {
                    return yield this.execFindDocument(document, callback);
                }
            }
            else {
                const data = yield this.dataService.getDocument(documentId);
                if (data.systemHeader && data.systemHeader.deleted) {
                    const deletedMsg = 'Document has been deleted';
                    return Promise.reject(UtilError.createError(deletedMsg, UtilError.DELETED_ERROR));
                }
                else {
                    this.appStore.dispatch(formSetOrgDocument(data));
                    return yield this.execFindDocument(data, callback);
                }
            }
        });
    }
    getLoadedDocument(documentId) {
        return cloneDeep(this.appStore.getState().formState.documents[documentId]);
    }
    getLoadedTemplate(templateId) {
        return cloneDeep(this.appStore.getState().formState.templates[templateId]);
    }
    getAltTemplate(documentId) {
        const templateId = this.appStore.getState().formState.altTemplateInfo[documentId];
        return this.getLoadedTemplate(templateId);
    }
    getloadedTemplateForDoc(document, formParameters) {
        const state = this.appStore.getState().formState;
        const documentId = document.documentId;
        let templateId;
        if (formParameters && formParameters.overrideTemplateId) {
            templateId = formParameters.overrideTemplateId;
        }
        else {
            const documentInfoItem = state.documentInfo[formParameters.unsavedDocumentListId];
            const docInfo = documentInfoItem[documentId];
            templateId = docInfo === null || docInfo === void 0 ? void 0 : docInfo.templateId;
        }
        if (!templateId) {
            templateId = document.systemHeader.templateId;
        }
        if (templateId) {
            return cloneDeep(state.templates[templateId]);
        }
    }
    /**
     * get the document
     * @param documentId the document Id
     * @param documentData - the result. The result will be put under a data element in documentData so it can
     * be bound to
     * @returns promise
     */
    getDocument(documentId, documentData) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!documentData || typeof documentData !== 'object') {
                const msg = 'The documentData needs to be an object so data can be set under it, which allows it to be bound to';
                throw UtilError.createError(msg);
            }
            yield this.getDocumentOnly(documentId);
            let document = this.getExistingDocument(documentId);
            if (document) {
                documentData.document = document;
            }
            else {
                document = yield this.dataService.getDocument(documentId);
                this.appStore.dispatch(formSetDocument(document));
                documentData.document = document;
            }
            return document;
        });
    }
    setInitialValues(document, template) {
        const _self = this;
        let hasInitialized = false;
        if (template && template.components && template.components.length) {
            if (!document) {
                document = {};
            }
            const documentId = UtilDocument.isNew(document) ? template.documentId : document.documentId;
            const initialDataKey = 'initialData:' + documentId;
            let initialData = null;
            try {
                initialData = JSON.parse(this.localStorageService.getItem(initialDataKey));
                if (initialData && initialData.documentId && UtilDocument.isNew(document)) {
                    document.documentId = initialData.documentId;
                }
                _self.restoreWIPDocument = initialData;
            }
            catch (e) {
            }
            localStorage.removeItem(initialDataKey);
            template.components.forEach(function (component) {
                if (!component.name) {
                    return;
                }
                if (!UtilType.hasValue(document[component.name])) {
                    if (UtilType.hasValue(component.defaultValue)) {
                        document[component.name] = component.defaultValue;
                    }
                }
            });
        }
        return hasInitialized;
    }
    /**
     * initialise a new document. The documentId will be set straight away so it can be passed through to child
     * docs without waiting for the main document to be saved. This allows the save to be performed in one operation
     * and reduces the chance of the child doc being saved without having the parent id set
     * @param documentData the result inside documentData object
     */
    initialiseNewDocument(documentData, template, formParameters) {
        if (documentData && template) {
            // set the isNew value to indicate that the document is a new document being created
            documentData.isNew = true;
            const newDocument = this.setInitialDocument(template);
            if (!newDocument.documentId) {
                newDocument.documentId = UtilDocumentId.generateId();
            }
            if (documentData.document) {
                extend(newDocument, documentData.document);
            }
            this.updateDocumentData(documentData, newDocument, template, formParameters);
            logger$j.info('Initialised new document: ' + newDocument.documentId);
        }
        else {
            logger$j.error('Template not provided when initialising new document');
        }
    }
    /**
     * set the document in unsaved loaded docs and set the document to documentData.document. This will be used
     * to check for modifications and on saving the documents
     * @param document the data
     * @param unsavedDocumentListId the unsaved document list Id
     */
    setInLoadedDocs(document, unsavedDocumentListId) {
        if (document && document.documentId) {
            // copy the document and set to original documents list so that the loaded doc on form isn't a reference to the original
            this.unsavedDocumentService.addDocumentToUnsavedList(document, unsavedDocumentListId);
            this.setOriginalDocumentOnly(cloneDeep(document));
        }
        else {
            logger$j.error('Attempting to add null or undefined document to loaded docs');
        }
    }
    /**
     * set the template in unsaved loaded templates and set the template to documentData.template.This will be
     * used to store modified version of the template by rules
     * associate to its document.
     * @param template the data
     * @param unsavedDocumentListId the unsaved document list Id
     * @param orgTemplateId the org template
     */
    setInLoadedTemplates(document, template, unsavedDocumentListId, orgTemplateId) {
        if (template && template.documentId) {
            // copy the template and set to original documents list so that the loaded template isn't a reference to the original
            this.unsavedDocumentService.addTemplateToUnsavedList(document, template, unsavedDocumentListId, orgTemplateId);
        }
        else {
            logger$j.error('Attempting to add null or undefined template to loaded templates');
        }
    }
    updateDocumentData(documentData, document, template, formParameters) {
        documentData.template = cloneDeep(template);
        documentData.templateId = template.documentId;
        if (formParameters.overrideTemplateId) {
            // set the override template id in the current document if specified, so that the override
            // template will be used in the save
            documentData.overrideTemplateId = formParameters.overrideTemplateId;
        }
        documentData.document = cloneDeep(document);
        documentData.documentId = document.documentId;
    }
    updateDocumentInfoProperties(unsavedDocumentListId, documentData, formParameters) {
        const docInfo = {
            documentListId: unsavedDocumentListId,
            documentId: documentData.document.documentId,
            isMainDoc: formParameters.isMainDoc,
            isNew: documentData.isNew
        };
        // set the override template id in the current document if specified, so that the override
        // template will be used in the save
        if (formParameters.overrideTemplateId) {
            docInfo.overrideTemplateId = formParameters.overrideTemplateId;
        }
        this.appStore.dispatch(formSetDocumentInfo(unsavedDocumentListId, docInfo));
    }
    getTemplateForDocumentErrorDetails(templateReturnStatus, templateId, templateError) {
        if (templateReturnStatus === UtilHttpStatus.NOT_FOUND) {
            return UtilError.createError('Template for document not found: ' + templateId);
        }
        else if (templateReturnStatus === UtilHttpStatus.FORBIDDEN) {
            return UtilError.createError(templateError.message);
        }
        else if ((templateError === null || templateError === void 0 ? void 0 : templateError.name) === UtilError.DELETED_ERROR || templateReturnStatus === UtilHttpStatus.GONE || (templateError === null || templateError === void 0 ? void 0 : templateError.type) === SharedConstants.ERROR_TYPE_GONE) {
            return UtilError.createError('Template for document has been deleted: ' + templateId);
        }
        else if (templateError) {
            return UtilError.createError('Error getting template: ' + templateError.message ? templateError.message : templateError);
        }
        else {
            return UtilError.createError('Error getting template for document : ' + templateId);
        }
    }
    // get the template for the document. If it is a template, return the document itself, otherwise return the
    // template matching the systemHeader.templateId of the document
    getTemplateForDocument(formParameters, documentData) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const documentId = formParameters.selectedDocumentId;
            const unsavedDocumentListId = formParameters.unsavedDocumentListId;
            const overrideTemplateId = formParameters.overrideTemplateId;
            try {
                this.restoreWIPDocument = null;
                if (!documentData || typeof documentData !== 'object') {
                    const msg = 'documentData needs to be an object so data can be set under it, which allows it to be bound to';
                    logger$j.error(msg);
                    throw UtilError.createError(msg);
                }
                let document = this.getLoadedDocument(documentId);
                let templateId = overrideTemplateId;
                if (!templateId && document && document.systemHeader && document.systemHeader.templateId) {
                    templateId = document.systemHeader.templateId;
                }
                else if (!templateId) {
                    templateId = documentId;
                }
                let template = this.getLoadedTemplate(templateId);
                if (!template) {
                    template = this.getAltTemplate(documentId);
                }
                if (!document && !template) {
                    const returnObj = yield this.dataService.getTemplateAndDocument(documentId, overrideTemplateId);
                    document = returnObj.document;
                    template = returnObj.template;
                    if (returnObj.templateReturnStatus !== UtilHttpStatus.OK) {
                        let templateId = overrideTemplateId;
                        if (!templateId && document && document.systemHeader && document.systemHeader.templateId) {
                            templateId = document.systemHeader.templateId;
                        }
                        throw this.getTemplateForDocumentErrorDetails(returnObj.templateReturnStatus, templateId, returnObj.templateError);
                    }
                }
                if (!overrideTemplateId && ((_a = document === null || document === void 0 ? void 0 : document.systemHeader) === null || _a === void 0 ? void 0 : _a.systemType) === SharedConstants.SYSTEM_TYPE_TEMPLATE) {
                    template = document;
                    document = null;
                }
                if (document) {
                    this.appStore.dispatch(formSetOrgDocument(document));
                }
                if (template) {
                    this.appStore.dispatch(formSetOrgDocument(template));
                }
                template = this.updateDisableSaveTemplate(documentData.isNew, template, document);
                if (!document) {
                    documentData.isNew = true;
                    // initialise the document as a new document and set into loaded docs
                    // Set document into documentData as well
                    this.initialiseNewDocument(documentData, template, formParameters);
                    // add the document to loaded docs in store, so it will be saved and have dirty checking applied
                    this.setInLoadedDocs(cloneDeep(documentData.document), unsavedDocumentListId);
                    // add the template to loaded docs into store, so it will be modified and have field mandatory/visible/disable checking applied
                    this.setInLoadedTemplates(documentData.document, documentData.template, unsavedDocumentListId);
                }
                else {
                    documentData.isNew = false;
                    document = cloneDeep(document);
                    // now set template into loaded templates and assign the its data to the documentData.template
                    this.setInLoadedTemplates(document, template, unsavedDocumentListId);
                    // update static values from template into existing document
                    this.setInitialValues(document, template);
                    // add the document to the unsaved loaded docs so that it can be compared with the
                    // original in this DocumentService.document.docs when saving
                    this.setInLoadedDocs(document, unsavedDocumentListId);
                    this.updateDocumentData(documentData, document, template, formParameters);
                }
                this.updateDocumentInfoProperties(unsavedDocumentListId, documentData, formParameters);
                return Promise.resolve({ "restoreWIPDocument": this.restoreWIPDocument });
            }
            catch (err) {
                let errorMsg;
                if (err.name === UtilError.NOT_FOUND_ERROR) {
                    errorMsg = 'Document not found: ' + documentId;
                }
                else if (err.name === UtilError.FORBIDDEN_ERROR) {
                    errorMsg = err.message;
                }
                else if (err.name === UtilError.DELETED_ERROR) {
                    errorMsg = 'Document was deleted: ' + documentId;
                }
                else {
                    errorMsg = 'Error getting template or document: ' + err.message ? err.message : err;
                }
                logger$j.error(errorMsg);
                return Promise.reject(UtilError.createError(errorMsg, err.name));
                // throw new Error("Error getting template and document: " + err.message);
            }
        });
    }
    getTemplateForDocument_OLD(formParameters, documentData) {
        return __awaiter(this, void 0, void 0, function* () {
            const targetDocumentId = formParameters.selectedDocumentId;
            const unsavedDocumentListId = formParameters.unsavedDocumentListId;
            const overrideTemplateId = formParameters.overrideTemplateId;
            const _self = this;
            if (!documentData || typeof documentData !== 'object') {
                const msg = 'documentData needs to be an object so data can be set under it, which allows it to be bound to';
                logger$j.error(msg);
                return Promise.reject(UtilError.createError(msg));
            }
            else {
                try {
                    const options = {};
                    const data = cloneDeep(yield _self.getDocumentOnly(targetDocumentId, options));
                    if (data) {
                        if (data.systemHeader && data.systemHeader.systemType === SharedConstants.SYSTEM_TYPE_TEMPLATE && !overrideTemplateId) {
                            documentData.isNew = true;
                            const template = _self.updateDisableSaveTemplate(documentData.isNew, data, documentData.document);
                            // initialise the document as a new document and set into loaded docs
                            // Set document into documentData as well
                            _self.initialiseNewDocument(documentData, template, formParameters);
                            // add the document to loaded docs in store, so it will be saved and have dirty checking applied
                            _self.setInLoadedDocs(cloneDeep(documentData.document), unsavedDocumentListId);
                            // add the template to loaded docs into store, so it will be modified and have field mandatory/visible/disable checking applied
                            _self.setInLoadedTemplates(documentData.document, documentData.template, unsavedDocumentListId);
                            _self.updateDocumentInfoProperties(unsavedDocumentListId, documentData, formParameters);
                            return Promise.resolve();
                        }
                        else {
                            // use the override template id if specified, otherwise get the templateId from the document systemHeader
                            let templateId = overrideTemplateId;
                            if (!templateId && data.systemHeader && data.systemHeader.templateId) {
                                templateId = data.systemHeader.templateId;
                            }
                            if (templateId) {
                                try {
                                    documentData.isNew = false;
                                    // get the template for the document
                                    let template = yield _self.getDocumentOnly(templateId, options);
                                    template = _self.updateDisableSaveTemplate(documentData.isNew, template, data);
                                    const document = data;
                                    // update static values from template into existing document
                                    _self.setInitialValues(document, template);
                                    // add the document to the unsaved loaded docs so that it can be compared with the
                                    // original in this DocumentService.document.docs when saving
                                    _self.setInLoadedDocs(document, unsavedDocumentListId);
                                    // now set template into loaded templates and assign the its data to the documentData.template
                                    _self.setInLoadedTemplates(document, template, unsavedDocumentListId, templateId);
                                    _self.updateDocumentData(documentData, document, template, formParameters);
                                    _self.updateDocumentInfoProperties(unsavedDocumentListId, documentData, formParameters);
                                    return Promise.resolve();
                                }
                                catch (err) {
                                    let errorMsg;
                                    if (err.name === UtilError.NOT_FOUND_ERROR) {
                                        errorMsg = 'Template for document not found: ' + templateId;
                                    }
                                    else if (err.name === UtilError.FORBIDDEN_ERROR) {
                                        errorMsg = err.message;
                                    }
                                    else if (err.name === UtilError.DELETED_ERROR) {
                                        errorMsg = 'Template for document has been deleted: ' + templateId;
                                    }
                                    else {
                                        errorMsg = 'Error getting template: ' + err.message ? err.message : err;
                                    }
                                    logger$j.error(errorMsg);
                                    return Promise.reject(UtilError.createError(errorMsg, err.name));
                                }
                            }
                            else {
                                const errorMsg = 'No Template for document found by override template id or ' +
                                    'systemHeader.templateId: ' + targetDocumentId;
                                logger$j.error(errorMsg);
                                return Promise.reject(UtilError.createError(errorMsg));
                            }
                        }
                    }
                    else {
                        const message = 'Document not found: ' + targetDocumentId;
                        logger$j.error(message);
                        return Promise.reject(UtilError.createError(message));
                    }
                }
                catch (err) {
                    let error;
                    if (err.name === UtilError.NOT_FOUND_ERROR) {
                        error = UtilError.createError('Document not found: ' + targetDocumentId, err.name);
                    }
                    else if (err.name === UtilError.FORBIDDEN_ERROR) {
                        error = err;
                    }
                    else if (err.name === UtilError.DELETED_ERROR) {
                        error = UtilError.createError('Document was deleted: ' + targetDocumentId, err.name);
                    }
                    else {
                        error = UtilError.createError('Error getting document to determine template: ' +
                            err.message ? err.message : err, err.name);
                        if (err.statusText) {
                            error.status = err.status;
                            error.statusText = err.statusText;
                        }
                    }
                    return Promise.reject(error);
                }
            }
        });
    }
    updateDisableSaveTemplate(isNew, template, document) {
        const canCreate = this.clientAccessService.checkAccess(template, SharedConstants.OPERATION_TYPE_CREATE);
        const canUpdate = this.clientAccessService.checkAccess(document, SharedConstants.OPERATION_TYPE_UPDATE);
        template = cloneDeep(template);
        if ((isNew && !canCreate) || (!isNew && !canUpdate)) {
            if (template.components) {
                for (let i = 0; i < template.components.length; i++) {
                    if (!template.components[i].disableSave) {
                        template.components[i].enabled = false;
                    }
                }
            }
        }
        this.setLoadedTemplateOnly(template);
        return template;
    }
    /**
     * return the template for the document passed in
     * @param doc - the document to get the template for
     * @returns the template object
     */
    getTemplateFromDoc(doc) {
        if (doc && doc.systemHeader && doc.systemHeader.templateId) {
            return this.getLoadedTemplate(doc.systemHeader.templateId);
        }
        else {
            logger$j.warn('No template specified for document in systemHeader');
        }
        return null;
    }
    setLoadedTemplateOnly(tpl, versionId) {
        this.appStore.dispatch(formSetTemplate(tpl, versionId));
    }
    setAltTemplateInfo(documentId, templateId) {
        this.appStore.dispatch(formSetAltTemplateInfo(documentId, templateId));
    }
    setLoadedDocumentOnly(doc, versionId) {
        this.appStore.dispatch(formSetDocument(doc, versionId));
    }
    setOriginalDocumentOnly(doc, versionId) {
        this.appStore.dispatch(formSetOrgDocument(doc, versionId));
    }
    /**
     * reset original doc to their clean state, with the latest copy of the original doc, so that any changes in
     * updates can be detected, also put new document after created successful on form.
     * @param doc - the doc to reset
     */
    resetOriginalDocument(doc) {
        if (doc) {
            this.setLoadedDocumentOnly(doc);
            this.setOriginalDocumentOnly(doc);
        }
    }
    resetLoadedTemplate(templateId) {
        const template = this.appStore.getState().formState.orgDocuments[templateId];
        if (template) {
            this.setLoadedTemplateOnly(template);
        }
    }
    /**
     * reset original version to their clean state, with the latest copy of the original doc, so that any changes in
     * updates can be detected, also put new document after created successful on form.
     * @param version - the version to reset
     */
    resetOriginalVersion(version) {
        if (version) {
            this.setLoadedDocumentOnly(version, version.systemHeader.versionId);
            this.setOriginalDocumentOnly(version, version.systemHeader.versionId);
        }
    }
    clearDocuments() {
        this.appStore.dispatch(formClearDocuments());
    }
    allDocuments() {
        const storedDocuments = this.appStore.getState().formState.documents;
        const documents = [];
        const keys = Object.keys(storedDocuments);
        keys.forEach(key => {
            documents.push(cloneDeep(storedDocuments[key]));
        });
        return documents;
    }
    getDocuments() {
        return this.allDocuments();
    }
    getOrgDocuments() {
        return this.appStore.getState().formState.orgDocuments;
    }
    setInitialDocument(template) {
        const systemType = template.systemTypeOverride ? template.systemTypeOverride : SharedConstants.SYSTEM_TYPE_DOCUMENT;
        const newDocument = {
            systemHeader: {
                templateId: template.documentId,
                systemType: systemType
            }
        };
        this.setInitialValues(newDocument, template);
        return newDocument;
    }
    setDisableSaveFieldDocument(document, template) {
        const key = document.documentId;
        const value = {};
        if (template && template.components && template.components.length) {
            template.components.forEach(tplItem => {
                if (tplItem.disableSave === true) {
                    value[tplItem.name] = document[tplItem.name];
                }
            });
        }
        this.appStore.dispatch(formSetDisableSaveFieldDocument(key, value));
    }
    getDisableSaveFieldDocument(documentId) {
        const disabledSaveFields = this.appStore.getState().formState.recordedDisableSaveFieldValues[documentId];
        return disabledSaveFields ? cloneDeep(disabledSaveFields) : {};
    }
    resetDisableSaveFieldToDoc(doc) {
        const disabledSaveFields = this.getDisableSaveFieldDocument(doc.documentId);
        const fieldNames = Object.keys(disabledSaveFields);
        fieldNames.forEach(fieldName => {
            doc[fieldName] = disabledSaveFields[fieldName];
        });
    }
}
/** @nocollapse */ /** @nocollapse */ DocumentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DocumentService, deps: [{ token: DataService }, { token: ClientAccessService }, { token: UnsavedDocumentService }, { token: LocalStorageService }, { token: AppStore }, { token: KeyValueStorageService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ DocumentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DocumentService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DocumentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: DataService }, { type: ClientAccessService }, { type: UnsavedDocumentService }, { type: LocalStorageService }, { type: AppStore }, { type: KeyValueStorageService }]; } });

class ClientSystemHeaderService {
    constructor(documentService) {
        this.documentService = documentService;
    }
    getTemplate(templateId) {
        return this.documentService.getDocumentOnly(templateId);
    }
    ;
    updateSystemHeader(bean, template, originalBean) {
        const _self = this;
        return new Promise((resolve, reject) => {
            if (originalBean && originalBean.systemHeader) {
                if (!template) {
                    if (!originalBean.systemHeader.templateId) {
                        // no template to write system header with, so don't write the summary values
                        resolve(bean);
                    }
                    else {
                        _self.getTemplate(originalBean.systemHeader.templateId).then(function successFunc(template) {
                            UtilSystemHeader.writeSystemHeaderSummary(bean, template, originalBean);
                            resolve(bean);
                        }, function errorFunc(err) {
                            resolve(bean);
                        });
                    }
                }
                else {
                    UtilSystemHeader.writeSystemHeaderSummary(bean, template, originalBean);
                    resolve(bean);
                }
            }
            else {
                resolve(bean);
            }
        });
    }
    ;
}
/** @nocollapse */ /** @nocollapse */ ClientSystemHeaderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ClientSystemHeaderService, deps: [{ token: DocumentService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ ClientSystemHeaderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ClientSystemHeaderService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ClientSystemHeaderService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: DocumentService }]; } });

/**
 * This service stores the document data for the document on the attached main template.
 * It will be retrieved to navigate to document after creating from template, command bar buttons or left/right sidebar visibility...
 */
class CurrentDocumentService {
    constructor(appStore, currentDocumentInfoService, documentService) {
        this.appStore = appStore;
        this.currentDocumentInfoService = currentDocumentInfoService;
        this.documentService = documentService;
        this.documentData = {
            document: null,
            template: null
        };
        this.documentDataSubject = new Subject();
        this.documentData$ = this.documentDataSubject.asObservable();
        this.documents$.subscribe(documents => {
            this.updateDocumentData();
        });
        this.documentInfo$.subscribe(documentInfos => {
            this.updateDocumentData();
        });
    }
    updateDocumentData() {
        let changed = false;
        const mainDocInfo = this.appStore.getState().formState.documentInfo[this.mainUnsavedDocumentListId];
        if (mainDocInfo && mainDocInfo[this.documentId]) {
            const docInfo = mainDocInfo[this.documentId];
            this.documentData.documentId = docInfo.documentId;
            this.documentData.templateId = docInfo.templateId;
            this.documentData.options = docInfo.options;
            this.documentData.overrideTemplateId = docInfo.overrideTemplateId;
            this.documentData.showFormAfterCreated = docInfo.showFormAfterCreated;
            this.documentData.hierarchyInfo = docInfo.hierarchyInfo;
            this.documentData.altTemplateId = docInfo.altTemplateId;
            this.documentData.isNew = docInfo.isNew;
            this.documentData.isMainDoc = docInfo.isMainDoc;
            changed = true;
        }
        const documents = this.appStore.getState().formState.documents;
        const templates = this.appStore.getState().formState.templates;
        if (documents && Object.keys(documents)) {
            const document = documents[this.documentData.documentId];
            if (document) {
                this.documentData.document = cloneDeep$1(document);
                changed = true;
            }
            const template = templates[this.documentData.templateId];
            if (template) {
                this.documentData.template = cloneDeep$1(template);
                changed = true;
            }
        }
        if (changed) {
            this.documentDataSubject.next(this.documentData);
        }
    }
    /**
     * Set main document list and the attached main document
     * @param documentListId
     * @param documentId
     */
    setMainDocumentListId(documentListId, documentId) {
        this.mainUnsavedDocumentListId = documentListId;
        this.documentId = documentId;
    }
    getMainDocumentListId() {
        return this.mainUnsavedDocumentListId;
    }
    getMainDocumentId() {
        return this.documentId;
    }
    getDocumentData() {
        return this.currentDocumentInfoService.getDocumentData(this.mainUnsavedDocumentListId, this.documentId);
    }
    /**
     * get the document from the main document - which is the document that the object nav bar is linked to
     */
    getDocument() {
        return this.getDocumentData().document;
    }
    setDocument(document) {
        this.documentService.setLoadedDocumentOnly(document);
    }
    /**
     * get the template from the main document - which is the document that the object nav bar is linked to
     */
    getTemplate() {
        var _a;
        return (_a = this.getDocumentData()) === null || _a === void 0 ? void 0 : _a.template;
    }
    setTemplate(template) {
        this.documentService.setLoadedDocumentOnly(template);
    }
    resetDocumentData(unsavedDocumentListId) {
        this.appStore.dispatch(formResetDocumentInfo(unsavedDocumentListId));
    }
    resetUndoRedo() {
        this.appStore.dispatch(resetUndoRedoDocument());
    }
    resetInitDocumentData() {
        this.appStore.dispatch(formResetInitDocumentData(this.mainUnsavedDocumentListId, this.documentId));
    }
}
/** @nocollapse */ /** @nocollapse */ CurrentDocumentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CurrentDocumentService, deps: [{ token: AppStore }, { token: CurrentDocumentInfoService }, { token: DocumentService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ CurrentDocumentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CurrentDocumentService, providedIn: 'root' });
__decorate([
    select(['formState', 'documents']),
    __metadata("design:type", Observable)
], CurrentDocumentService.prototype, "documents$", void 0);
__decorate([
    select(['formState', 'documentInfo']),
    __metadata("design:type", Observable)
], CurrentDocumentService.prototype, "documentInfo$", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CurrentDocumentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: AppStore }, { type: CurrentDocumentInfoService }, { type: DocumentService }]; }, propDecorators: { documents$: [], documentInfo$: [] } });

class TemplateService {
    constructor(currentDocumentService, documentService, dataService) {
        this.currentDocumentService = currentDocumentService;
        this.documentService = documentService;
        this.dataService = dataService;
        this.sub = this.currentDocumentService.documentData$.subscribe(data => {
            this.documentData = data;
        });
    }
    ngOnInit() {
    }
    ngOnDestroy() {
        this.sub.unsubscribe();
    }
    loadTemplate(doc, unsavedTemplates) {
        return __awaiter(this, void 0, void 0, function* () {
            let tpl;
            let templateId = this.documentData.overrideTemplateId;
            if (templateId && this.documentData.document.documentId === doc.documentId) {
                tpl = this.documentService.getExistingDocument(templateId);
            }
            else {
                templateId = doc.systemHeader.templateId;
                const filteredTemplates = unsavedTemplates === null || unsavedTemplates === void 0 ? void 0 : unsavedTemplates.filter(template => template.documentId === templateId);
                if (filteredTemplates.length) {
                    tpl = filteredTemplates[0];
                }
            }
            if (tpl) {
                return Promise.resolve(tpl);
            }
            // template not loaded. This can happen in circumstances like editing in the template editor.
            // Get the template from the server
            return yield this.dataService.getDocument(templateId);
        });
    }
}
/** @nocollapse */ /** @nocollapse */ TemplateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: TemplateService, deps: [{ token: CurrentDocumentService }, { token: DocumentService }, { token: DataService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ TemplateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: TemplateService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: TemplateService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CurrentDocumentService }, { type: DocumentService }, { type: DataService }]; } });

class SessionStorageService extends SynchronousStorageService {
    constructor(urlBasePathService) {
        super(urlBasePathService);
        this.urlBasePathService = urlBasePathService;
        this.storage = sessionStorage;
    }
}
/** @nocollapse */ /** @nocollapse */ SessionStorageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: SessionStorageService, deps: [{ token: UrlBasePathService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ SessionStorageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: SessionStorageService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: SessionStorageService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: UrlBasePathService }]; } });

function getDefaultFormNewState() {
    return {
        canUndo: false,
        canRedo: false,
        resetDocument: null,
        documents: {},
        templates: {},
        orgDocuments: {},
        documentInfo: {},
        documentChangedInfo: null,
        recordedStateChanges: null,
        recordedComponentTemplateChanges: {},
        recordedDisableSaveFieldValues: {},
        documentSession: {},
        rulesProcessingData: {},
        altTemplateInfo: {}
    };
}

function formDocumentReducer(state = getDefaultFormNewState(), action) {
    var _a;
    // let shouldRecordPatches = false;
    // set the changes to state from reducer so if any changes they should be subscribed instead of listening whole state.
    const recordedPatches = {
        patches: null
    };
    // let recordedUndoRedo = false;
    const changedSourceInfo = {};
    switch (action.type) {
        case FORM_SETUP_DOCUMENT_LIST: {
            const typedAction = action;
            const documentListId = typedAction.payload.documentListId;
            const isNewMainDocList = typedAction.payload.isNewMainDocList;
            const documentInfo = Object.assign({}, state.documentInfo);
            if (isNewMainDocList) {
                // Remove the old main document that was attached to navbar and other places
                let oldMainDocumentListId;
                const documentListIds = Object.keys(documentInfo);
                for (let i = 0; i < documentListIds.length; i++) {
                    const documentListId = documentListIds[i];
                    const documentIds = Object.keys(documentInfo[documentListId]);
                    for (let j = 0; j < documentIds.length && !oldMainDocumentListId; j++) {
                        const documentId = documentIds[j];
                        if (documentInfo[documentListId][documentId].isMainDoc) {
                            oldMainDocumentListId = documentListId;
                        }
                    }
                }
                if (oldMainDocumentListId) {
                    delete documentInfo[oldMainDocumentListId];
                }
            }
            if (!documentInfo[documentListId]) {
                documentInfo[documentListId] = {};
            }
            return Object.assign(Object.assign({}, state), { documentInfo: documentInfo });
        }
        case FORM_SET_ORG_DOCUMENT: {
            const typedAction = action;
            const document = typedAction.payload.document;
            const orgDocuments = Object.assign({}, state.orgDocuments);
            orgDocuments[document.documentId] = document;
            return Object.assign(Object.assign({}, state), { orgDocuments: orgDocuments });
        }
        case FORM_SET_DOCUMENT: {
            const typedAction = action;
            const document = typedAction.payload.document;
            if (typedAction.payload.versionId || document) {
                const key = typedAction.payload.versionId ? typedAction.payload.versionId : document.documentId;
                const documents = Object.assign({}, state.documents);
                documents[key] = document;
                changedSourceInfo.documentId = key;
                changedSourceInfo.resetDocument = true;
                recordedPatches.changedSourceInfo = changedSourceInfo;
                state = Object.assign(Object.assign({}, state), { recordedStateChanges: recordedPatches });
                return Object.assign(Object.assign({}, state), { documents: documents, recordedStateChanges: recordedPatches });
            }
            return state;
        }
        case FORM_SET_ALT_TEMPLATE_INFO: {
            const typedAction = action;
            const documentId = typedAction.payload.documentId;
            const templateId = typedAction.payload.templateId;
            const stateAltTemplateInfo = Object.assign({}, state.altTemplateInfo);
            stateAltTemplateInfo[documentId] = templateId;
            return Object.assign(Object.assign({}, state), { altTemplateInfo: stateAltTemplateInfo });
        }
        case FORM_SET_TEMPLATE: {
            const typedAction = action;
            const document = typedAction.payload.document;
            if (typedAction.payload.versionId || document) {
                const key = typedAction.payload.versionId ? typedAction.payload.versionId : document.documentId;
                const templates = Object.assign({}, state.templates);
                templates[key] = document;
                return Object.assign(Object.assign({}, state), { templates: templates });
            }
            return state;
        }
        case FORM_UPDATE_COMPONENT_DEFINITION: {
            const typedAction = action;
            const templateId = typedAction.payload.templateId;
            const fieldName = typedAction.payload.fieldName;
            const key = typedAction.payload.key;
            const compDef = typedAction.payload.componentDef;
            const templates = Object.assign({}, state.templates);
            const components = templates[templateId].components;
            const recordedComponentTemplateChanges = Object.assign({}, state.recordedComponentTemplateChanges);
            if (!recordedComponentTemplateChanges[templateId]) {
                recordedComponentTemplateChanges[templateId] = {};
            }
            if (!recordedComponentTemplateChanges[templateId][fieldName]) {
                recordedComponentTemplateChanges[templateId][fieldName] = {};
            }
            let hasChanged = false;
            if (components[key] && components[key].name && components[key].name === fieldName) {
                components[key] = compDef;
                recordedComponentTemplateChanges[templateId][fieldName].key = key;
                recordedComponentTemplateChanges[templateId][fieldName].componentDef = compDef;
                hasChanged = true;
            }
            // shouldRecordPatches = true;
            return hasChanged ? Object.assign(Object.assign({}, state), { templates: templates, recordedComponentTemplateChanges: recordedComponentTemplateChanges }) : state;
        }
        case FORM_SET_COMPONENT_PROPS_TEMPLATE: {
            const typedAction = action;
            const fieldName = typedAction.payload.fieldName;
            const componentName = typedAction.payload.componentName;
            const propertyValue = typedAction.payload.propertyValue;
            const templateId = typedAction.payload.templateId;
            const templates = Object.assign({}, state.templates);
            const recordedComponentTemplateChanges = Object.assign({}, state.recordedComponentTemplateChanges);
            recordedComponentTemplateChanges[templateId] = {};
            recordedComponentTemplateChanges[templateId][fieldName] = {};
            let hasChanged = false;
            templates[templateId].components.forEach((tplItem, idx) => {
                if (tplItem.name && tplItem.name === fieldName && (!componentName || tplItem.componentName === componentName)) {
                    const keys = Object.keys(propertyValue);
                    keys.forEach(key => {
                        tplItem[key] = propertyValue[key];
                    });
                    tplItem = Object.assign(Object.assign({}, tplItem), propertyValue);
                    recordedComponentTemplateChanges[templateId][fieldName].componentDef = tplItem;
                    recordedComponentTemplateChanges[templateId][fieldName].key = idx;
                    recordedComponentTemplateChanges[templateId][fieldName].propertyChanges = propertyValue;
                    if (componentName) {
                        recordedComponentTemplateChanges[templateId][fieldName].componentName = componentName;
                    }
                    hasChanged = true;
                }
            });
            return hasChanged ? Object.assign(Object.assign({}, state), { recordedComponentTemplateChanges: recordedComponentTemplateChanges, templates: templates }) : state;
        }
        case FORM_SET_COMPONENT_FLAG_TEMPLATE: {
            const typedAction = action;
            const flagName = typedAction.payload.flagName;
            const flagValue = typedAction.payload.flagValue;
            const fieldName = typedAction.payload.fieldName;
            const templateId = typedAction.payload.templateId;
            const templates = Object.assign({}, state.templates);
            const recordedComponentTemplateChanges = Object.assign({}, state.recordedComponentTemplateChanges);
            // if (!recordedComponentTemplateChanges[templateId]) {
            recordedComponentTemplateChanges[templateId] = {};
            // }
            // if (!recordedComponentTemplateChanges[templateId][fieldName]) {
            recordedComponentTemplateChanges[templateId][fieldName] = {};
            // }
            let hasChanged = false;
            templates[templateId].components.forEach((tplItem, idx) => {
                if (tplItem.name && tplItem.name === fieldName) {
                    tplItem[flagName] = flagValue;
                    if (!isEqual(recordedComponentTemplateChanges[templateId][fieldName][flagName], flagValue)) {
                        recordedComponentTemplateChanges[templateId][fieldName][flagName] = flagValue;
                        // shouldRecordPatches = true;
                        hasChanged = true;
                    }
                    recordedComponentTemplateChanges[templateId][fieldName].componentDef = tplItem;
                    recordedComponentTemplateChanges[templateId][fieldName].key = idx;
                }
            });
            return hasChanged ? Object.assign(Object.assign({}, state), { recordedComponentTemplateChanges: recordedComponentTemplateChanges, templates: templates }) : state;
        }
        case FORM_SET_LIST_COMPONENTS_FLAG_TEMPLATE: {
            const typedAction = action;
            const fieldNames = typedAction.payload.fieldNames;
            const flagName = typedAction.payload.flagName;
            const flagValue = typedAction.payload.flagValue;
            const templateId = typedAction.payload.templateId;
            const templates = state.templates;
            const template = templates[templateId];
            const recordedComponentTemplateChanges = Object.assign({}, state.recordedComponentTemplateChanges);
            if (!recordedComponentTemplateChanges[templateId]) {
                recordedComponentTemplateChanges[templateId] = {};
            }
            let hasChanged = false;
            template.components.forEach((tplItem, idx) => {
                const fieldName = tplItem.name;
                if (fieldNames.indexOf(fieldName) !== -1) {
                    tplItem[flagName] = flagValue;
                    if (!recordedComponentTemplateChanges[templateId][fieldName]) {
                        recordedComponentTemplateChanges[templateId][fieldName] = {};
                    }
                    if (!isEqual(recordedComponentTemplateChanges[templateId][fieldName][flagName], flagValue)) {
                        recordedComponentTemplateChanges[templateId][fieldName][flagName] = flagValue;
                        hasChanged = true;
                    }
                    recordedComponentTemplateChanges[templateId][fieldName].componentDef = tplItem;
                    recordedComponentTemplateChanges[templateId][fieldName].key = idx;
                }
            });
            return hasChanged ? Object.assign(Object.assign({}, state), { recordedComponentTemplateChanges: recordedComponentTemplateChanges }) : state;
        }
        case FORM_RESET_TEMPLATE_COMPONENT_DEFINITION: {
            const typedAction = action;
            const templateId = typedAction.payload.templateId;
            const recordedComponentTemplateChanges = Object.assign({}, state.recordedComponentTemplateChanges);
            recordedComponentTemplateChanges[templateId] = {};
            return Object.assign(Object.assign({}, state), { recordedComponentTemplateChanges: recordedComponentTemplateChanges });
        }
        case FORM_CLEAR_DOCUMENT: {
            const documentIds = Object.keys(state.documents);
            const documents = Object.assign({}, state.documents);
            const templates = Object.assign({}, state.templates);
            documentIds.forEach(docId => {
                // remove document from store
                delete documents[docId];
                if (documents[docId].systemHeader.systemType === SharedConstants.SYSTEM_TYPE_TEMPLATE) {
                    // remove templates from store
                    delete templates[docId];
                }
            });
            return Object.assign(Object.assign({}, state), { documents: documents, templates: templates });
        }
        case FORM_DELETE_DOCUMENT: {
            const typedAction = action;
            const documentId = typedAction.payload.documentId;
            const documents = Object.assign({}, state.documents);
            const templates = Object.assign({}, state.templates);
            const orgDocuments = Object.assign({}, state.orgDocuments);
            delete documents[documentId];
            delete templates[documentId];
            delete orgDocuments[documentId];
            return Object.assign(Object.assign({}, state), { documents: documents, templates: templates, orgDocuments: orgDocuments });
        }
        case FORM_RESET_TO_ORG_DOCUMENT: {
            const typedAction = action;
            const documentId = typedAction.payload.documentId;
            const document = state.orgDocuments[documentId];
            const documents = Object.assign({}, state.documents);
            if (documents[documentId]) {
                documents[documentId] = document;
                state = Object.assign(Object.assign({}, state), { documents: documents });
            }
            if (((_a = document === null || document === void 0 ? void 0 : document.systemHeader) === null || _a === void 0 ? void 0 : _a.systemType) === SharedConstants.SYSTEM_TYPE_TEMPLATE) {
                const templates = Object.assign({}, state.templates);
                templates[documentId] = document;
                state = Object.assign(Object.assign({}, state), { templates: templates });
            }
            return state;
        }
        case FORM_SET_DOCUMENT_INFO: {
            // DocumentInfo Item Data Structure
            // state.documentInfo[documentListId] = {
            // isMainDoc: isMainDoc;
            // documentId = null;
            // templateId = null;
            // altTemplateId = null;
            // parentDocumentId = null;
            // childDocumentId = null;
            // options = {};
            // initDocData = {
            //   processedFields: [], // processedFields: stores processed fields from components
            //   completedFields: []  // stores the fields have been initialized. It will be set once the RESET action is dispatched.
            //   // It will be used to compare to the processedFields in order to trigger Onload
            // }
            // };
            const typedAction = action;
            const documentInfo = typedAction.payload.documentInfo;
            const unsavedDocListId = typedAction.payload.unsavedDocumentListId;
            const documentId = documentInfo.documentId;
            const documentInfor = Object.assign({}, state.documentInfo);
            const storeDocumentInfo = documentInfor[unsavedDocListId];
            if (!storeDocumentInfo[documentId]) {
                storeDocumentInfo[documentId] = {};
            }
            const documentInfoItem = storeDocumentInfo[documentId];
            if (!documentInfoItem.options) {
                documentInfoItem.options = {};
            }
            if (!documentInfoItem.hierarchyInfo) {
                documentInfoItem.hierarchyInfo = {};
            }
            const keys = Object.keys(documentInfo);
            keys.forEach(key => {
                if (key === 'options') {
                    const optKeys = Object.keys(documentInfo.options);
                    optKeys.forEach(key2 => {
                        documentInfoItem.options[key2] = documentInfo.options[key2];
                    });
                }
                else {
                    documentInfoItem[key] = documentInfo[key];
                }
            });
            const parentDocumentId = documentInfoItem.hierarchyInfo.parentDocumentId;
            if (parentDocumentId) {
                const parentDocInfo = storeDocumentInfo[parentDocumentId];
                parentDocInfo.hierarchyInfo.childDocumentId = documentId;
            }
            return Object.assign(Object.assign({}, state), { documentInfo: documentInfor });
        }
        case FORM_RESET_DOCUMENT_INFO: {
            const typedAction = action;
            const documentInfo = Object.assign({}, state.documentInfo);
            documentInfo[typedAction.payload.unsavedDocumentListId] = {};
            return Object.assign(Object.assign({}, state), { documentInfo: documentInfo });
        }
        case FORM_UPDATE_INIT_DOCUMENT_DATA: {
            const typedAction = action;
            const unsavedDocListId = typedAction.payload.unsavedDocumentListId;
            const documentId = typedAction.payload.documentId;
            if (unsavedDocListId) {
                const fieldName = typedAction.payload.fieldName;
                const fieldValue = typedAction.payload.fieldValue;
                let hasChanged = false;
                const documentInfo = Object.assign({}, state.documentInfo);
                const documentInfoItem = documentInfo[unsavedDocListId][documentId];
                if (!documentInfoItem.initDocData) {
                    documentInfoItem.initDocData = {
                        processedFields: [],
                        completedFields: []
                    };
                    hasChanged = true;
                }
                const initDocData = documentInfoItem.initDocData;
                if (!initDocData.processedFields.includes(fieldName)) {
                    initDocData.processedFields.push(fieldName);
                    hasChanged = true;
                }
                if (hasChanged) {
                    state = Object.assign(Object.assign({}, state), { documentInfo: documentInfo });
                }
                const documents = Object.assign({}, state.documents);
                const document = documents[documentInfoItem.documentId];
                if (fieldValue == null) {
                    delete document[fieldName];
                }
                else {
                    document[fieldName] = fieldValue;
                }
                state = Object.assign(Object.assign({}, state), { documents: documents });
            }
            return state;
        }
        case FORM_RESET_INIT_DOCUMENT_DATA: {
            const typedAction = action;
            const unsavedDocListId = typedAction.payload.unsavedDocumentListId;
            const documentId = typedAction.payload.documentId;
            let hasChanged = false;
            const documentInfo = Object.assign({}, state.documentInfo);
            const documentInfoItem = documentInfo[unsavedDocListId][documentId];
            if (!documentInfoItem.initDocData) {
                documentInfoItem.initDocData = {
                    processedFields: [],
                    completedFields: []
                };
                hasChanged = true;
            }
            const initDocData = documentInfoItem.initDocData;
            if (initDocData.processedFields.length) {
                initDocData.completedFields = [...initDocData.processedFields];
                initDocData.processedFields = [];
                hasChanged = true;
            }
            return hasChanged ? Object.assign(Object.assign({}, state), { documentInfo: documentInfo }) : state;
        }
        case FORM_DOCUMENT_LOADED: {
            const typedAction = action;
            const documentId = typedAction.payload.documentId;
            const unsavedDocListId = typedAction.payload.unsavedDocumentListId;
            const isDocumentLoaded = typedAction.payload.isLoaded;
            const documentInfo = Object.assign({}, state.documentInfo);
            const docInfo = documentInfo[unsavedDocListId];
            if (docInfo && docInfo[documentId]) {
                if (!isDocumentLoaded) {
                    delete docInfo[documentId];
                }
                else {
                    docInfo[documentId].loaded = isDocumentLoaded;
                }
            }
            state = Object.assign(Object.assign({}, state), { documentInfo: documentInfo });
            if (!isDocumentLoaded) {
                const documents = Object.assign({}, state.documents);
                const orgDocuments = Object.assign({}, state.orgDocuments);
                // const document = documents[documentId];
                delete documents[documentId];
                // delete orgDocuments[documentId];
                state = Object.assign(Object.assign({}, state), { documents: documents, orgDocuments: orgDocuments });
            }
            return state;
        }
        case FORM_CHANGE_DOCUMENT: {
            const typedAction = action;
            const changedInfo = typedAction.payload.documentChangedInfo;
            const documentId = changedInfo.documentId;
            const documents = Object.assign({}, state.documents);
            const document = documents[documentId];
            const fieldName = changedInfo.fieldName;
            changedInfo.oldValue = changedInfo.oldValue !== undefined ? changedInfo.oldValue : document[fieldName];
            if (!isEqual(document[fieldName], changedInfo.newValue)) {
                if (document[fieldName] !== undefined && changedInfo.newValue === undefined) {
                    delete document[fieldName];
                }
                else {
                    document[fieldName] = changedInfo.newValue;
                }
                state = Object.assign(Object.assign({}, state), { documents: documents });
            }
            let documentChangedInfo = Object.assign({}, state.documentChangedInfo);
            if (!changedInfo.isInitValue) {
                documentChangedInfo = changedInfo;
                state = Object.assign(Object.assign({}, state), { documentChangedInfo: documentChangedInfo });
            }
            changedSourceInfo.documentId = documentId;
            changedSourceInfo.fieldName = fieldName;
            changedSourceInfo.type = changedInfo.sourceType;
            changedSourceInfo.controlName = changedInfo.controlName;
            recordedPatches.changedSourceInfo = changedSourceInfo;
            state = Object.assign(Object.assign({}, state), { recordedStateChanges: recordedPatches });
            const recordedDisableSaveFieldValues = Object.assign({}, state.recordedDisableSaveFieldValues);
            const template = state.templates[document.systemHeader.templateId];
            let hasDisableSave = false;
            if (template && template.components) {
                const tplItem = template.components.filter(item => {
                    return item.name && item.name === fieldName;
                })[0];
                if ((tplItem === null || tplItem === void 0 ? void 0 : tplItem.disableSave) === true) {
                    if (!recordedDisableSaveFieldValues[documentId]) {
                        recordedDisableSaveFieldValues[documentId] = {};
                    }
                    recordedDisableSaveFieldValues[documentId][fieldName] = document[fieldName];
                    hasDisableSave = true;
                }
            }
            if (hasDisableSave) {
                state = Object.assign(Object.assign({}, state), { recordedDisableSaveFieldValues: recordedDisableSaveFieldValues });
            }
            return state;
        }
        case FORM_RESET_CHANGE_DOCUMENT: {
            return Object.assign(Object.assign({}, state), { documentChangedInfo: undefined });
        }
        case FORM_UNDO: {
            // shouldRecordPatches = true;
            // recordedUndoRedo = true;
            // const newState = <IFormNewState> applyPatches(state, changes[currentVersion--].undo);
            // return produce(
            //   newState,
            //   newDraft => {
            //     newstate.canUndo = changes.hasOwnProperty(currentVersion);
            //     newstate.canRedo = true;
            //   }
            // );
            return state;
        }
        case FORM_REDO: {
            // shouldRecordPatches = true;
            // recordedUndoRedo = true;
            // const newState = <IFormNewState> applyPatches(state, changes[++currentVersion].redo);
            // return produce(
            //   newState,
            //   newDraft => {
            //     newstate.canUndo = true;
            //     newstate.canRedo = changes.hasOwnProperty(currentVersion + 1);
            //   }
            // );
            return state;
        }
        case FORM_RESET_UNDO_REDO: {
            // currentVersion = -1;
            // changes = {};
            state.canRedo = false;
            state.canUndo = false;
            return state;
        }
        case FORM_SET_DISABLE_SAVE_FIELD_DOCUMENT: {
            const typedAction = action;
            const documentId = typedAction.payload.documentId;
            const value = typedAction.payload.disabledFields;
            const recordedDisableSaveFieldValues = Object.assign({}, state.recordedDisableSaveFieldValues);
            if (Object.keys(value).length) {
                recordedDisableSaveFieldValues[documentId] = value;
            }
            else {
                delete recordedDisableSaveFieldValues[documentId];
            }
            return Object.assign(Object.assign({}, state), { recordedDisableSaveFieldValues: recordedDisableSaveFieldValues });
        }
        case FORM_SET_DOCUMENT_SESSION: {
            const typedAction = action;
            const document = typedAction.payload.documentSession;
            const key = typedAction.payload.documentId;
            const documentSession = Object.assign({}, state.documentSession);
            documentSession[key] = document;
            return Object.assign(Object.assign({}, state), { documentSession: documentSession });
        }
        case FORM_SET_RULES_STATUS: {
            const typedAction = action;
            const templateId = typedAction.payload.templateId;
            const status = typedAction.payload.status;
            const rulesProcessingData = Object.assign({}, state.rulesProcessingData);
            if (!rulesProcessingData[templateId]) {
                rulesProcessingData[templateId] = {};
            }
            rulesProcessingData[templateId].status = status;
            return Object.assign(Object.assign({}, state), { rulesProcessingData: rulesProcessingData });
        }
        default: {
            return state;
        }
    }
}

function getDefaultUserState() {
    return {
        user: {
            welcomeMsg: '',
            account: null,
            filteredAccount: null,
            menu: null,
            publicAccount: false,
            accountPublicURL: null,
            accountControlDocument: null,
            accountGroupConfigDocument: null
        }
    };
}

function userReducer(state = getDefaultUserState(), action) {
    switch (action.type) {
        case USER_SET_USER: {
            const typedAction = action;
            return Object.assign(Object.assign({}, state), { user: typedAction.payload.user });
        }
        default: {
            return state;
        }
    }
}

function getDefaultOfflineState() {
    return {
        offlineStatus: {
            unsyncedCount: 0,
            connected: true,
            connectionText: '',
            incomingCacheCount: 0,
            maxCacheCount: 0,
            currentCacheCount: 0,
            cacheLoading: false,
            pendingDocumentCount: 0,
            errorCacheCount: 0,
            initialCachingComplete: false,
            cachingApp: false,
            currentStaticResourceCount: 0,
            totalStaticResourceCount: 0,
            tileServerCacheCount: 0,
            tileServerMaxCacheCount: 0,
            errorCacheAttachedFile: [],
            errorOfflineChangesSyncDocs: [],
            maxCacheAttachedFileCount: 0,
            currentCacheAttachedFileCount: 0
        }
    };
}

function offlineReducer(state = getDefaultOfflineState(), action) {
    switch (action.type) {
        case OFFLINE_SET_OFFLINE_STATUS: {
            const typedAction = action;
            return Object.assign(Object.assign({}, state), { offlineStatus: typedAction.payload.offlineStatus });
        }
        default: {
            return state;
        }
    }
}

const rootReducer = combineReducers({
    offlineReducer: offlineReducer,
    formState: formDocumentReducer,
    userState: userReducer
});

function getStore() {
    const globalWindow = window;
    if (!globalWindow.formbirdStore) {
        // Create a Redux store holding the state of your app.
        // Store it on the window object so it is accessible from custom elements
        globalWindow.formbirdStore = createStore(rootReducer, globalWindow.__REDUX_DEVTOOLS_EXTENSION__ ? globalWindow.__REDUX_DEVTOOLS_EXTENSION__() :
            globalWindow.REDUX_DEVTOOLS_EXTENSION ? globalWindow.REDUX_DEVTOOLS_EXTENSION() : f => f);
    }
    return globalWindow.formbirdStore;
}

class ReduxStoreService {
    getStore() {
        return getStore();
    }
}
/** @nocollapse */ /** @nocollapse */ ReduxStoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ReduxStoreService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ ReduxStoreService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ReduxStoreService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ReduxStoreService, decorators: [{
            type: Injectable
        }] });

class SearchFilterService {
    constructor(localStorageService) {
        this.localStorageService = localStorageService;
        this.searchFilter = {
            filter: false,
            queryToBeFiltered: null
        };
        this.searchFilterSubject = new Subject();
        this.searchFilter$ = this.searchFilterSubject.asObservable();
    }
    setQueryToBeFiltered(query) {
        this.localStorageService.setItem(ClientConstants.FILTERED_QUERY, query);
        this.localStorageService.setItem(ClientConstants.FILTER, 'true');
        this.searchFilter.filter = true;
        this.searchFilter.queryToBeFiltered = query;
        this.searchFilterSubject.next(this.searchFilter);
    }
    removeFilter() {
        this.searchFilter.filter = false;
        this.searchFilter.queryToBeFiltered = null;
        this.localStorageService.removeItem(ClientConstants.FILTER);
        this.localStorageService.removeItem(ClientConstants.FILTERED_QUERY);
        this.searchFilterSubject.next(this.searchFilter);
    }
}
/** @nocollapse */ /** @nocollapse */ SearchFilterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: SearchFilterService, deps: [{ token: LocalStorageService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ SearchFilterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: SearchFilterService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: SearchFilterService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: LocalStorageService }]; } });

const logger$i = console;
class SearchService {
    constructor(dataService, configService) {
        this.dataService = dataService;
        this.configService = configService;
        this.results = { data: [] };
    }
    getInstance() {
        return this;
    }
    /**
     * Do Elastic search on server side. If the browser is offline, do searching indexedDB.
     * @param options consists of text, filter query to search
     * @param searchContext an object consists of additional params
     * @returns Promise
     */
    search(options, searchContext) {
        const _self = this;
        return new Promise((resolve, reject) => {
            const exceedsMaxQueryLength = query => {
                const maxSearchQueryLength = _self.configService.clientConfig().maxSearchQueryLength ||
                    SharedConstants.DEFAULT_MAX_SEARCH_QUERY_LENGTH;
                if (!query) {
                    return false;
                }
                else {
                    return JSON.stringify(query).length > maxSearchQueryLength;
                }
            };
            if (exceedsMaxQueryLength(options.filter)) {
                const message = 'The maximum length of a search query has been exceeded.';
                logger$i.error(message);
                reject(new Error(message));
                return;
            }
            _self.dataService.search(options, searchContext).then(function successFunc(returnData) {
                if (returnData.statusCode !== UtilHttpStatus.OK) {
                    let msg = 'Search failed with status - ' + UtilHttpStatus.getStatusText(returnData.errorCode);
                    if (returnData.message.status === UtilHttpStatus.BAD_REQUEST + '') {
                        msg = 'Search failed with status - ' + 'filter is incorrectly set up';
                    }
                    reject(msg);
                }
                else {
                    _self.results = returnData;
                    resolve(returnData);
                }
            }, function errorFunc(err) {
                let message;
                if (err) {
                    if (err.message) {
                        message = 'Error in search: ' + err.message;
                    }
                    else {
                        if (err.errorCode) {
                            message = 'Search failed with error: ' + err.errorCode;
                        }
                        else {
                            message = 'Error in search. Please see an administrator';
                        }
                    }
                }
                else {
                    message = 'Error in search. Please see an administrator';
                }
                reject(new Error(message));
            });
        });
    }
    generatePit() {
        return this.dataService.generatePit();
    }
}
/** @nocollapse */ /** @nocollapse */ SearchService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: SearchService, deps: [{ token: DataService }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ SearchService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: SearchService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: SearchService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: DataService }, { type: ConfigService }]; } });

class ControlStateSaveService {
    constructor(sessionStorageService) {
        this.sessionStorageService = sessionStorageService;
    }
    setControlState(stateName, value) {
        this.sessionStorageService.setItem(stateName, JSON.stringify(value));
    }
    getControlState(stateName) {
        const value = this.sessionStorageService.getItem(stateName);
        return JSON.parse(value);
    }
    removeControlState(stateName) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.sessionStorageService.removeItem(stateName);
        });
    }
}
/** @nocollapse */ /** @nocollapse */ ControlStateSaveService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ControlStateSaveService, deps: [{ token: SessionStorageService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ ControlStateSaveService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ControlStateSaveService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ControlStateSaveService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: SessionStorageService }]; } });

class VendorLibraryService {
    constructor() { }
    getVendorLibrary(name) {
        return __awaiter(this, void 0, void 0, function* () {
            const clientResourcesReq = new Request('api/loadVendorLibrary/' + name, { credentials: 'include' });
            try {
                const response = yield fetch(clientResourcesReq);
                return response.json();
            }
            catch (err) {
                return null;
            }
        });
    }
    getVendorLibs() {
        return __awaiter(this, void 0, void 0, function* () {
            const clientResourcesReq = new Request('api/loadVendorLibraries', { credentials: 'include' });
            try {
                const response = yield fetch(clientResourcesReq);
                return response.json();
            }
            catch (err) {
                return null;
            }
        });
    }
}
/** @nocollapse */ /** @nocollapse */ VendorLibraryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: VendorLibraryService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ VendorLibraryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: VendorLibraryService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: VendorLibraryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/**
 * Angular: Load External JavaScript/Stylesheet File Dynamically
 */
class LazyScriptLoaderService {
    constructor(document, vendorLibraryService) {
        this.document = document;
        this.vendorLibraryService = vendorLibraryService;
        this.loadedLibraries = {};
    }
    loadScript(url) {
        const _self = this;
        if (_self.loadedLibraries[url]) {
            return _self.loadedLibraries[url].toPromise();
        }
        _self.loadedLibraries[url] = new ReplaySubject();
        const script = _self.document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = url;
        script.onload = () => {
            _self.loadedLibraries[url].next();
            _self.loadedLibraries[url].complete();
        };
        _self.document.body.appendChild(script);
        return _self.loadedLibraries[url].toPromise();
    }
    loadStyle(url) {
        const _self = this;
        if (_self.loadedLibraries[url]) {
            return _self.loadedLibraries[url].toPromise();
        }
        _self.loadedLibraries[url] = new ReplaySubject();
        const style = _self.document.createElement('link');
        style.type = 'text/css';
        style.href = url;
        style.rel = 'stylesheet';
        style.onload = () => {
            _self.loadedLibraries[url].next();
            _self.loadedLibraries[url].complete();
        };
        const head = document.getElementsByTagName('head')[0];
        head.appendChild(style);
        return _self.loadedLibraries[url].toPromise();
    }
    load(dependencies) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!dependencies || (!dependencies.name && !dependencies.documentId)) {
                return Promise.reject(new Error('Dependencies not specified correctly in lazyLoader.load'));
            }
            let files = [];
            if (!dependencies.files) {
                let vendorLib;
                if (dependencies.documentId) {
                    vendorLib = yield this.vendorLibraryService.getVendorLibrary(dependencies.documentId);
                }
                else {
                    vendorLib = yield this.vendorLibraryService.getVendorLibrary(dependencies.name);
                }
                if (vendorLib) {
                    if (vendorLib.fileName) {
                        files.push(vendorLib.fileName);
                    }
                    if (vendorLib.fileNames) {
                        files = files.concat(vendorLib.fileNames);
                    }
                }
            }
            else {
                files = dependencies.files;
            }
            if (!files.length) {
                return Promise.reject(new Error(`Dependencies specified in lazyLoader.load not found!`));
            }
            return yield loadjs(files, {
                returnPromise: true
            });
        });
    }
}
/** @nocollapse */ /** @nocollapse */ LazyScriptLoaderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: LazyScriptLoaderService, deps: [{ token: DOCUMENT }, { token: VendorLibraryService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ LazyScriptLoaderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: LazyScriptLoaderService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: LazyScriptLoaderService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [DOCUMENT]
                    }] }, { type: VendorLibraryService }];
    } });

const logger$h = console;
class ModifiedFieldService {
    constructor(documentService, clientAccessService, unsavedDocumentService, currentDocumentService, broadcastService) {
        this.documentService = documentService;
        this.clientAccessService = clientAccessService;
        this.unsavedDocumentService = unsavedDocumentService;
        this.currentDocumentService = currentDocumentService;
        this.broadcastService = broadcastService;
        // list of modified fields in a document
        this.modifiedFields = {};
        // controls the dirty checking
        this.formDirty = {
            showSpinner: false,
            isSaving: false,
            dirty: false,
            updateAllowed: false,
            validating: null
        };
        this.user$.subscribe((user) => {
            this.user = user;
        });
    }
    canUpdateDoc(targetDocument) {
        if (!targetDocument) {
            return false;
        }
        // if the document is new
        if (targetDocument.systemHeader && !targetDocument.systemHeader.createdDate) {
            // check if user can create
            return this.clientAccessService.checkAccess(targetDocument, SharedConstants.OPERATION_TYPE_CREATE);
        }
        else {
            // check if user can update
            return this.clientAccessService.checkAccess(targetDocument, SharedConstants.OPERATION_TYPE_UPDATE);
        }
    }
    /**
     * Check if the current form is dirty, that make the save button appear and blink or display unsaved changes dialog
     */
    isDirty() {
        return this.formDirty.dirty;
    }
    shouldApplyFlags(documentListId) {
        // Should apply flag on the document list that was attached to command bar
        return !documentListId || documentListId === this.currentDocumentService.getMainDocumentListId();
    }
    /**
     * Set current form to be dirty, that make the save button appear and blink
     * @param isDirty is dirty
     * @param documentId the document id
     */
    setDirty(isDirty, documentId, unsavedDocumentListId) {
        if (!this.shouldApplyFlags(unsavedDocumentListId)) {
            return;
        }
        if (isDirty !== this.formDirty.dirty) {
            // don't set the dirty value if save is in progress. Any change in a presave server rule will go into the save. The
            // isSaving flag should have been cleared by the time of a postSave rule so that the save button will appear if the
            // document is changed in the postSave rule
            if (isDirty) {
                if (!this.formDirty.isSaving) {
                    if (this.formDirty.updateAllowed) {
                        this.formDirty.dirty = isDirty;
                    }
                    else {
                        const targetDoc = this.documentService.getExistingDocument(documentId);
                        if (this.canUpdateDoc(targetDoc)) {
                            this.formDirty.dirty = isDirty;
                        }
                    }
                }
            }
            else {
                this.formDirty.dirty = false;
            }
        }
        this.broadcastService.broadcast(ClientConstants.FORM_DIRTY_UPDATED, this.formDirty);
    }
    setShowSpinner(showSpinner, unsavedDocumentListId) {
        if (!this.shouldApplyFlags(unsavedDocumentListId)) {
            return;
        }
        this.formDirty.showSpinner = showSpinner;
        this.broadcastService.broadcast(ClientConstants.SPINNER_UPDATED, showSpinner);
    }
    /**
     * Allow this form to be updatable.
     * @param isAllowedUpdate is allowed update
     */
    setUpdateAllowed(isAllowedUpdate) {
        this.formDirty.updateAllowed = isAllowedUpdate;
    }
    /**
     * Reset the current form to its un-interacted state.
     */
    resetFormDirty() {
        this.setDirty(false);
    }
    /**
     * Sets the current form to be updatable depending on the current user's privileges.
     * @param docData the doc data object
     */
    setFormAccess(docData) {
        this.setUpdateAllowed(false); // default to false
        const userAccount = this.user ? this.user.account : null;
        // do not allow update
        if (!docData || !userAccount) {
            return;
        }
        if (this.canUpdateDoc(docData)) {
            this.setUpdateAllowed(true);
        }
    }
    isSaving() {
        return this.formDirty.isSaving;
    }
    setSaveFlag(isSaving, unsavedDocumentListId) {
        if (!this.shouldApplyFlags(unsavedDocumentListId)) {
            return;
        }
        this.formDirty.isSaving = isSaving;
    }
    /*
     * This method is used to add modified field by document id.
     * Before adding should check whether the version of the document has changed from the original because
     * there is a case reset current document from the one returned from server once it is saved.
     * @returns: true if adding successfully, otherwise: false
     */
    addModifiedField(documentId, changedFieldName, documentListId, shouldCompareDocuments, overrideTemplateId) {
        if (this.formDirty.isSaving) {
            logger$h.info('Document is saving. Ignore adding modified field.');
            return;
        }
        let result = false;
        if (!documentListId) {
            logger$h.error('Document list id not provided in addModifiedField');
        }
        else if (!documentId) {
            logger$h.error('Document id not provided in addModifiedField');
        }
        else if (!changedFieldName) {
            logger$h.error('Changed field name not provided in addModifiedField');
        }
        else if (!this.unsavedDocumentService.isDocumentsDefined(documentListId)) {
            logger$h.info('Unsaved Document List is not found');
        }
        else {
            let shouldAdd = false;
            if (shouldCompareDocuments === undefined || shouldCompareDocuments === true) {
                shouldAdd = this.isChangedField(documentId, changedFieldName, documentListId, overrideTemplateId);
            }
            else {
                shouldAdd = true;
            }
            if (shouldAdd) {
                if (!this.modifiedFields[documentId]) {
                    this.modifiedFields[documentId] = {};
                }
                this.modifiedFields[documentId][changedFieldName] = true;
                this.modifiedFields[documentId][ClientConstants.CHANGED_DOCUMENT_LIST_ID_KEY] = documentListId;
                this.setDirty(true, documentId, documentListId);
                logger$h.info('Adding new modified field. Doc: ' + documentId + ' - field: ' + changedFieldName);
                result = true;
            }
        }
        return result;
    }
    resetModifiedFields() {
        logger$h.info('Resetting all modified fields...');
        this.modifiedFields = {};
        this.broadcastService.broadcast(ClientConstants.FORM_RESET);
    }
    // Remove all changed fields of a document
    removeModifiedDocumentFields(documentId) {
        logger$h.info('Resetting all modified fields of a document...');
        delete this.modifiedFields[documentId];
    }
    // Remove all changed fields of a template
    removeTemplateFields(templateId) {
        logger$h.info('Removing changed fields of template: ' + templateId);
        delete this.modifiedFields[templateId];
    }
    removeChangedField(documentId, changedFieldName) {
        logger$h.warn('DEPRECATED: Please call ModifiedFieldService.removeModifiedField ' +
            'instead of ModifiedFieldService.removeChangedField');
        this.removeModifiedField(documentId, changedFieldName);
    }
    removeModifiedField(documentId, changedFieldName) {
        if (this.modifiedFields[documentId]) {
            logger$h.info('Removing changed field: ' + changedFieldName + ' of document: ' + documentId);
            delete this.modifiedFields[documentId][changedFieldName];
        }
        if (!this.hasAnyChanged()) {
            this.setDirty(false);
        }
    }
    isChangedField(documentId, changedFieldName, documentListId, overrideTemplateId) {
        try {
            const currentDoc = this.unsavedDocumentService.getDocumentOnly(documentId);
            const currentOriginalDoc = this.documentService.getExistingDocument(documentId);
            if (currentDoc && !currentOriginalDoc || // create doc mode
                (currentDoc && currentOriginalDoc && // modify doc && component editor mode
                    !isEqual(currentDoc[changedFieldName], currentOriginalDoc[changedFieldName])) ||
                (overrideTemplateId && !isEqual(currentDoc, currentOriginalDoc))) { // override template
                return true;
            }
        }
        catch (e) {
        }
        return false;
    }
    /**
     * whether the document has unsaved changes
     * @param documentId the document id
     * @returns the result
     */
    hasChanged(documentId) {
        logger$h.info('Checking changed document. Stored modified fields: ' + JSON.stringify(this.modifiedFields));
        const changes = this.getModifiedFieldsInDoc(documentId);
        return changes.length > 0;
    }
    /**
     * whether any documents have been changed
     * @returns the result
     */
    hasAnyChanged() {
        const documentIds = Object.keys(this.modifiedFields);
        if (documentIds) {
            for (let id = 0; id < documentIds.length; id++) {
                const infos = Object.keys(this.modifiedFields[documentIds[id]]);
                for (let i = 0; i < infos.length; i++) {
                    const key = infos[i];
                    if (key !== ClientConstants.CHANGED_DOCUMENT_LIST_ID_KEY) {
                        return true;
                    }
                }
            }
        }
        return false;
    }
    /**
     * get the unsaved documents for the unsaved document list. This needs to be separate from UnsavedDocumentService to avoid
     * a circular dependency of Angular service injections
     * @param unsavedDocumentListId - the id of the list to get the changed docs for
     */
    getModifiedDocuments(unsavedDocumentListId) {
        if (!unsavedDocumentListId) {
            throw new Error('Unsaved document list id not specified when getting loaded documents');
        }
        const self = this;
        const loadedDocs = this.unsavedDocumentService.getUnsavedDocuments(unsavedDocumentListId);
        // only return documents that have unsaved changes. Load the modified docs into unsavedDocs
        const unsavedDocs = [];
        if (loadedDocs) {
            loadedDocs.forEach(function (loadedDoc) {
                if (self.hasChanged(loadedDoc.documentId)) {
                    unsavedDocs.push(loadedDoc);
                }
            });
        }
        return unsavedDocs;
    }
    getChangedDocumentListId(documentId) {
        return this.modifiedFields[documentId] ?
            this.modifiedFields[documentId][ClientConstants.CHANGED_DOCUMENT_LIST_ID_KEY] : null;
    }
    getAllChangedDocumentListIds() {
        const res = [];
        const documentIds = Object.keys(this.modifiedFields);
        for (let i = 0; i < documentIds.length; i++) {
            const unsavedDocumentListId = this.modifiedFields[documentIds[i]][ClientConstants.CHANGED_DOCUMENT_LIST_ID_KEY];
            if (unsavedDocumentListId) {
                res.push(unsavedDocumentListId);
            }
        }
        return res;
    }
    /**
     * Get all the changed fields of the provided document.
     * @param documentId
     * @returns
     */
    getModifiedFieldsInDoc(documentId) {
        const changedFieldNames = [];
        const info = this.modifiedFields[documentId];
        if (info) {
            const keys = Object.keys(info);
            for (let i = 0; i < keys.length; i++) {
                const key = keys[i];
                if (key !== ClientConstants.CHANGED_DOCUMENT_LIST_ID_KEY) {
                    changedFieldNames.push(key);
                }
            }
        }
        return changedFieldNames;
    }
}
/** @nocollapse */ /** @nocollapse */ ModifiedFieldService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ModifiedFieldService, deps: [{ token: DocumentService }, { token: ClientAccessService }, { token: UnsavedDocumentService }, { token: CurrentDocumentService }, { token: BroadcastService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ ModifiedFieldService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ModifiedFieldService, providedIn: 'root' });
__decorate([
    select(['userState', 'user']),
    __metadata("design:type", Observable)
], ModifiedFieldService.prototype, "user$", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ModifiedFieldService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: DocumentService }, { type: ClientAccessService }, { type: UnsavedDocumentService }, { type: CurrentDocumentService }, { type: BroadcastService }]; }, propDecorators: { user$: [] } });

// Get window.URL object
const URL = window.URL; /*|| window.webkitURL;*/
const logger$g = console;
class FileProviderService {
    constructor(loggedInUserService, indexedDBService, offlineStatusService, httpClient, dataService, configService, clientAccessService, appStore, modifiedFieldService) {
        this.loggedInUserService = loggedInUserService;
        this.indexedDBService = indexedDBService;
        this.offlineStatusService = offlineStatusService;
        this.httpClient = httpClient;
        this.dataService = dataService;
        this.configService = configService;
        this.clientAccessService = clientAccessService;
        this.appStore = appStore;
        this.modifiedFieldService = modifiedFieldService;
    }
    /**
     * Retrieves the file from offline storage first, if unsuccessful retrieve from server.
     */
    getFileResources(file) {
        const _self = this;
        return new Promise((resolve, reject) => {
            const fileNo = file.fileNo;
            const fileName = file.fileName || "";
            const resources = {
                url: SharedUrlRoutes.serverRoutes.loadFile + '/' + fileNo + '/' + fileName.replace(/ /g, '-'),
                base64strData: null
            };
            if (!_self.loggedInUserService.isUserOfflineMode()) {
                resolve(resources);
                return;
            }
            const offlineFileNo = file.fileNo;
            _self.indexedDBService.getFile(offlineFileNo, function (record) {
                return __awaiter(this, void 0, void 0, function* () {
                    const data = record && record.uploadConfig && record.uploadConfig.data ? record.uploadConfig.data : null;
                    if (data) {
                        resources.base64strData = data.uploadedFile;
                    }
                    if (!resources.base64strData && data && data.file) {
                        resources.base64strData = yield _self.indexedDBService.toBase64Str(data.file);
                    }
                    resolve(resources);
                });
            });
        });
    }
    /**
     * Sends the file directly to server.
     */
    onlineFileUpload(uploadConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!uploadConfig || !uploadConfig.data) {
                throw new Error('No file details passed in file upload.');
            }
            const fileData = uploadConfig.data;
            const type = fileData.mimetype;
            const file = new File([fileData.file], fileData.originalname, { type: type });
            const options = { isOnline: true };
            options.fileReferenceDocumentId = fileData.fileReferenceDocumentId;
            let response = yield this.uploadFileToServer(fileData.fileNo, file, fileData.documentId, fileData.documentName, options)
                .toPromise();
            const uploaded = response.body;
            uploadConfig.fileNo = uploaded.fileNo;
            uploadConfig.basePath = uploaded.basePath;
            uploadConfig.fileType = uploaded.fileType;
            yield this.updateFileReferenceDocument(uploadConfig, uploaded);
            return ({ data: uploaded });
        });
    }
    uploadFileToServer(fileNo, file, documentId, documentName, options) {
        const maxUploadSize = parseInt(this.configService.clientConfig().maxUploadSize) || SharedConstants.DEFAULT_MAX_UPLOAD_SIZE;
        if (file && file.size && file.size > (maxUploadSize * 1024 * 1024)) {
            const errMsg = 'The file is too large. Allowed maximum size is ' + maxUploadSize + "mb";
            return from(Promise.reject(new Error(errMsg)));
        }
        else {
            const postUrl = SharedUrlRoutes.serverRoutes.uploadFiles;
            if (!options) {
                options = {};
            }
            if (this.loggedInUserService.isUserOfflineMode() && !options.isOnline) {
                let uploadConfig = {
                    data: {
                        fileNo: fileNo, file: file, originalname: file.name, mimetype: file.type,
                        documentId: documentId, documentName: documentName
                    }, isCommandBarUpload: options.isCommandBarUpload,
                    docFieldName: options.docFieldName,
                    options
                };
                return from(this.offlineFileUpload(uploadConfig));
            }
            else {
                const myFormData = new FormData();
                myFormData.append('fileNo', fileNo);
                myFormData.append('file', file);
                myFormData.append('documentId', documentId);
                myFormData.append('documentName', documentName);
                myFormData.append('fileReferenceTemplateId', options === null || options === void 0 ? void 0 : options.fileReferenceTemplateId);
                if (options.fileReferenceDocumentId) {
                    myFormData.append('isCreatedFileReference', 'true');
                }
                const config = new HttpRequest('POST', postUrl, myFormData, {
                    reportProgress: true
                });
                return this.httpClient.request(config);
            }
        }
    }
    createFileReferenceDocument(uploadConfig, fileUploaderConfig) {
        return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            try {
                const fileData = uploadConfig.data;
                const mimetype = fileData.mimetype;
                const fileType = mime.extension(mimetype).toUpperCase();
                const fileReferenceTemplateId = ((_a = uploadConfig === null || uploadConfig === void 0 ? void 0 : uploadConfig.options) === null || _a === void 0 ? void 0 : _a.fileReferenceTemplateId) || (fileUploaderConfig === null || fileUploaderConfig === void 0 ? void 0 : fileUploaderConfig.defaultTemplateId);
                if (!UtilUploads.whiteListed(fileType, fileUploaderConfig)) {
                    throw new Error('File type not allowed.');
                }
                if (!fileReferenceTemplateId) {
                    throw new Error('Invalid configuration values for generating reference document.');
                }
                const fileName = fileData.originalname;
                const fileNo = fileData.fileNo;
                //prepare data for creating file reference
                const meta = {
                    file: {
                        fileNo,
                        fileName: fileName,
                        fileType: fileType
                    },
                    parent: {
                        documentId: fileData.documentId,
                        name: fileData.documentName
                    }
                };
                if (mimetype) {
                    meta.file.ext = fileName.substring(fileName.lastIndexOf('.') + 1, fileName.length);
                    meta.file.mimeDetailsObj = {
                        contentType: mimetype
                    };
                }
                //create file reference
                const frTemplate = yield this.dataService.getDocument(fileReferenceTemplateId);
                UtilFileReference.constructFileReferenceDocument(meta.file, meta.parent, (_b = uploadConfig === null || uploadConfig === void 0 ? void 0 : uploadConfig.options) === null || _b === void 0 ? void 0 : _b.fileReferenceTemplateId, fileUploaderConfig, (err, fileRefDoc) => __awaiter(this, void 0, void 0, function* () {
                    if (err) {
                        reject(err);
                    }
                    else {
                        if (!fileRefDoc.documentId) {
                            fileRefDoc.documentId = UtilDocumentId.generateId();
                        }
                        this.clientAccessService.writeKeysToDocument(fileRefDoc, frTemplate);
                        this.dataService.insert(fileRefDoc)
                            .then(function successFunc(data) {
                            logger$g.info('Created reference document ' + data.documentId + ' for ' + fileNo + '.');
                            resolve(data);
                        }, function errorFunc(err) {
                            var msg = 'Failed to create reference document for ' + fileNo + ' to ' + fileData.documentId + '.';
                            logger$g.error(err.message);
                            reject(new Error(msg));
                        });
                    }
                }));
            }
            catch (err) {
                reject(err);
            }
        }));
    }
    updateFileReferenceDocument(uploadConfig, uploadedData) {
        return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            try {
                const fileData = uploadConfig.data;
                //updating offline fileReference document with uploaded fileNo.
                if (fileData.fileReferenceDocumentId) {
                    //updating fileReference document with server fileNo .
                    this.indexedDBService.getDocumentCurrentVersion(fileData.fileReferenceDocumentId, refDoc => {
                        refDoc[fileData.fileUploaderField][0].fileNo = uploadedData.fileNo;
                        refDoc[fileData.fileUploaderField][0].basePath = uploadedData.basePath;
                        this.indexedDBService.saveEntry(IndexedDBConstants.DOCUMENT_TABLE_NAME, refDoc);
                    });
                    let originalFRDoc;
                    originalFRDoc = yield this.dataService.getDocument(fileData.fileReferenceDocumentId);
                    if (originalFRDoc) {
                        const frDocument = cloneDeep(originalFRDoc);
                        frDocument[fileData.fileUploaderField][0].fileNo = uploadedData.fileNo;
                        frDocument[fileData.fileUploaderField][0].basePath = uploadedData.basePath;
                        yield this.dataService.deepDiffUpdate(originalFRDoc, frDocument);
                    }
                    resolve(true);
                }
            }
            catch (err) {
                logger$g.error("Error on offline file reference document update: " + JSON.stringify(err));
                reject(err);
            }
        }));
    }
    /**
     * Saves to offline database. The OfflinePoller sends it to the server later
     */
    offlineFileUpload(uploadConfig) {
        const _self = this;
        return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            const record = {
                fileNo: uploadConfig.data.fileNo,
                uploadConfig: uploadConfig,
                status: IndexedDBConstants.OFFLINE_STATUS_PENDING // 'UPLOADED' or 'PENDING'
            };
            try {
                const clientConfig = this.configService.clientConfig();
                const fileUploaderConfig = clientConfig.fileUploader || {};
                const fileRefDoc = yield this.createFileReferenceDocument(uploadConfig, fileUploaderConfig);
                uploadConfig.data.fileReferenceDocumentId = fileRefDoc.documentId;
                uploadConfig.data.fileUploaderField = fileUploaderConfig.fileUploaderField;
                const reader = new FileReader();
                reader.onloadend = function (e) {
                    const blob = new Blob([e.target.result], { type: "image/jpeg" });
                    uploadConfig.data.file = blob;
                    _self.indexedDBService.saveFile(record);
                    resolve({
                        body: { fileNo: uploadConfig.data.fileNo },
                        status: 200,
                        data: 'OK'
                    });
                };
                reader.readAsArrayBuffer(uploadConfig.data.file);
            }
            catch (err) {
                reject(err);
            }
        }));
    }
    downloadFile(file) {
        if (!file.resources.base64strData) {
            window.open(file.resources.url);
            return;
        }
        const objectURL = URL.createObjectURL(this.dataURLtoFile(file.resources.base64strData, file.fileName));
        const anchor = document.createElement('a');
        anchor.download = file.fileName;
        anchor.href = objectURL;
        anchor.click();
        URL.revokeObjectURL(objectURL);
    }
    // helpers
    dataURLtoFile(dataurl, filename) {
        const arr = dataurl.split(','), mime = arr[0].match(/:(.*?);/)[1], bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    }
    uploadFile(uploadConfig) {
        if (this.loggedInUserService.isUserOfflineMode()) {
            uploadConfig.isOnline = false;
        }
        else {
            uploadConfig.isOnline = true;
        }
        return this.uploadFileToServer(uploadConfig.fileNo, uploadConfig.file, uploadConfig.documentId, uploadConfig.documentName, uploadConfig);
    }
    updateFileInfo(document, uploadConfig) {
        const _self = this;
        return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            if (uploadConfig.isCommandBarUpload) {
                const DOCUMENT_FIELD_NAME = SharedConstants.DOCUMENT_FIELD_NAME_UPLOAD_FILE;
                // save as the template's image on navbar
                document.systemHeader[DOCUMENT_FIELD_NAME] = uploadConfig.fileNo;
            }
            else {
                this.indexedDBService.loadCachedDocByVersion(document.documentId, document.systemHeader.previousVersionId, function (cacheObject) {
                    const oldFieldValues = cacheObject ? cacheObject[uploadConfig.docFieldName] : null;
                    _self.updateFileInfoFieldValue(document, uploadConfig, oldFieldValues);
                    resolve(true);
                });
            }
        }));
    }
    updateFileInfoFieldValue(document, uploadConfig, oldFieldValues) {
        if (uploadConfig.docFieldName) {
            const fileRecord = {
                fileNo: uploadConfig.fileNo,
                fileName: uploadConfig.data.originalname,
                fileType: uploadConfig.fileType,
                basePath: uploadConfig.basePath,
            };
            let isExists = false;
            let documentFiles = document[uploadConfig.docFieldName];
            if (documentFiles && documentFiles.length > 0) {
                for (let i = 0; i < documentFiles.length; i++) {
                    const item = documentFiles[i];
                    if (item.fileNo === uploadConfig.data.fileNo) {
                        item.basePath = uploadConfig.basePath;
                        if (uploadConfig.fileNo) {
                            item.fileNo = uploadConfig.fileNo;
                            item.fileType = uploadConfig.fileType;
                        }
                        isExists = true;
                    }
                    else if (oldFieldValues) {
                        oldFieldValues.map(obj => {
                            if (obj.fileNo === item.fileNo) {
                                item.basePath = obj.basePath ? obj.basePath : item.basePath;
                                item.fileType = obj.fileType ? obj.fileType : item.fileType;
                            }
                        });
                    }
                }
            }
            else {
                documentFiles = [];
            }
            if (!isExists) {
                document[uploadConfig.docFieldName] = [...documentFiles, fileRecord];
            }
        }
    }
    updateSentFileToOfflineDoc(docId, processedFiles) {
        const _self = this;
        return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            const indexValue = docId;
            if (indexValue) {
                try {
                    const originalDoc = yield this.dataService.getDocument(docId);
                    //used for new document
                    let currentDoc = cloneDeep(this.appStore.getState().formState.documents[docId]);
                    const docInfo = this.appStore.getState().formState.documentInfo;
                    const unsavedDocumentListIds = Object.keys(docInfo);
                    let unsavedDocListId = null;
                    unsavedDocumentListIds.forEach(function (unsavedDocumentListId) {
                        if (docInfo[unsavedDocumentListId][docId]) {
                            unsavedDocListId = unsavedDocumentListId;
                        }
                    });
                    const cachedObject = currentDoc ? cloneDeep(currentDoc) : originalDoc;
                    let fieldName;
                    if (cachedObject) {
                        for (let i = 0; i < processedFiles.length; i++) {
                            fieldName = processedFiles[i].uploadConfig.docFieldName;
                            yield this.updateFileInfo(cachedObject, processedFiles[i].uploadConfig);
                        }
                        if (unsavedDocListId && this.modifiedFieldService.isDirty()) {
                            const formParameters = {};
                            formParameters.unsavedDocumentListId = unsavedDocListId;
                            const documentChangedInfo = {
                                documentId: docId,
                                fieldName: fieldName,
                                newValue: cachedObject[fieldName],
                                formParameters: formParameters
                            };
                            this.appStore.dispatch(formDocumentChanged(documentChangedInfo));
                        }
                        else {
                            const updatedDoc = yield this.dataService.deepDiffUpdate(originalDoc, cachedObject);
                            yield this.doUpdateCachedObject(updatedDoc);
                        }
                        resolve();
                    }
                    else {
                        logger$g.error("Error on updating send file details to the offline document");
                        reject();
                    }
                }
                catch (err) {
                    logger$g.error(err);
                    logger$g.error("Error on updating send file details to the offline document");
                    reject();
                }
            }
            else {
                resolve();
            }
        }));
    }
    doUpdateCachedObject(cachedObject) {
        const _self = this;
        return new Promise((resolve, reject) => {
            _self.indexedDBService.saveEntry(IndexedDBConstants.DOCUMENT_TABLE_NAME, cachedObject, function (err) {
                if (!err) {
                    resolve();
                }
                else {
                    reject();
                }
            });
        });
    }
}
/** @nocollapse */ /** @nocollapse */ FileProviderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: FileProviderService, deps: [{ token: LoggedInUserService }, { token: IndexedDBService }, { token: OfflineStatusService }, { token: i1.HttpClient }, { token: DataService }, { token: ConfigService }, { token: ClientAccessService }, { token: AppStore }, { token: ModifiedFieldService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ FileProviderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: FileProviderService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: FileProviderService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: LoggedInUserService }, { type: IndexedDBService }, { type: OfflineStatusService }, { type: i1.HttpClient }, { type: DataService }, { type: ConfigService }, { type: ClientAccessService }, { type: AppStore }, { type: ModifiedFieldService }]; } });

const serverRoutes$1 = SharedUrlRoutes.serverRoutes;
const logger$f = console;
class IndexedDBQueueService {
    constructor(http, indexedDBConnectorService, indexedDBService, offlineStatusService, broadcastService, fileProviderService, loggedInUserService) {
        this.http = http;
        this.indexedDBConnectorService = indexedDBConnectorService;
        this.indexedDBService = indexedDBService;
        this.offlineStatusService = offlineStatusService;
        this.broadcastService = broadcastService;
        this.fileProviderService = fileProviderService;
        this.loggedInUserService = loggedInUserService;
    }
    /**
     * send offline data that is saved in IndexedDB queue
     * @param cachedObject - the document to update
     * @param offlineDetails - details of the operation
     */
    sendOfflineData(cachedObject, offlineDetails) {
        if (!cachedObject) {
            return Promise.reject(new Error('Document not provided in sendOflineData'));
        }
        if (!offlineDetails) {
            return Promise.reject(new Error('Offline details not provided in sendOfflineData'));
        }
        const document = cloneDeep(cachedObject); // only send data and avoid updating on passed data
        let pushedData = document;
        let url = null;
        const operationType = offlineDetails.type;
        switch (operationType) {
            case ClientConstants.OPERATION_TYPE_POST:
                url = serverRoutes$1.document;
                if (document.systemHeader.currentVersion === false) {
                    document.systemHeader.currentVersion = true;
                }
                break;
            case ClientConstants.OPERATION_TYPE_PUT:
                // allows sending an old version into the server to replace the current version there
                // it will later then be overrideen when newer versions are submitted.
                // This assumes that updates are submitted only after the previous request succeeded.
                document.systemHeader.currentVersion = true;
                url = serverRoutes$1.documentDeepDiffUpdate + '/' + document.documentId;
                const options = {
                    updates: offlineDetails.updates,
                    isUpdateFromPreviousVersion: offlineDetails.isUpdateFromPreviousVersion,
                    makeCurrentVersion: offlineDetails.makeCurrentVersion,
                    importPreviousVersion: offlineDetails.importPreviousVersion
                };
                pushedData = {
                    document: document,
                    options: options
                };
                break;
            case ClientConstants.OPERATION_TYPE_DELETE:
                url = serverRoutes$1.document +
                    '/' + document.documentId +
                    '/' + document.systemHeader.templateId +
                    '/' + document.systemHeader.versionId;
                break;
            default:
                logger$f.error('Operation not yet implemented.');
                break;
        }
        const req = new HttpRequest(operationType, url, pushedData);
        return this.http.request(req).toPromise();
    }
    doUpdateCachedObject(cachedObject) {
        const _self = this;
        return new Promise((resolve, reject) => {
            _self.indexedDBService.saveEntry(IndexedDBConstants.DOCUMENT_TABLE_NAME, cachedObject, function (err) {
                if (!err) {
                    resolve();
                }
                else {
                    reject();
                }
            });
        });
    }
    updateSentFileToOfflineDocs(processedFiles) {
        return new Promise((resolve, reject) => {
            const promises = [];
            const self = this;
            const docMap = {};
            processedFiles.forEach(function (processedFile) {
                const docId = processedFile.uploadConfig ? processedFile.uploadConfig.data.documentId : null;
                if (docId) {
                    if (!docMap[docId]) {
                        docMap[docId] = [];
                    }
                    docMap[docId].push(processedFile);
                }
            });
            const docIds = Object.keys(docMap);
            docIds.forEach(function (docId) {
                promises.push(self.fileProviderService.updateSentFileToOfflineDoc(docId, docMap[docId]));
            });
            Promise.all(promises).then(function successFunc() {
                resolve(true);
            }, function errorFunc() {
                reject();
            });
        });
    }
    /*
     * @param onItemProgress - Function to call for every progress of an item regardless if it failed or not
     * This will be used to update the poller's lastActive status and make the poller active
     * @return {promise}
     */
    sendUnsyncedObjects(onItemProgress) {
        const _self = this;
        return new Promise((resolve, reject) => {
            let documentUploadDone = false; // flag
            let abort = false; // used to abort the sequence if client is in offline mode or server responds with status -1
            _self.indexedDBService.getUnsentOfflineFilesInQueue(function (files) {
                if (files.length === 0) {
                    sendUnProcessedDocs();
                }
                else {
                    logger$f.info('Sending unsent offline files...');
                    const processedFiles = [];
                    let processedCount = 0;
                    async.eachSeries(files, (file, next) => {
                        _self.processOfflineUpload(file, onItemProgress, function onItemProcessed(status, fileResult) {
                            processedCount++;
                            if (fileResult || fileResult === false) {
                                processedFiles.push(fileResult);
                                _self.broadcastService.broadcast(ClientConstants.FILE_SENT_EVENT_NAME, fileResult);
                            }
                            else if (status === _self.indexedDBService.constants.OFFLINE_STATUS_PENDING) {
                                documentUploadDone = true;
                                terminatePromise();
                            }
                            if (processedCount === files.length) {
                                logger$f.info('All unsent offline files have been sent. Do update unprocessed docs.');
                                _self.updateSentFileToOfflineDocs(processedFiles).then(function sendOfflineDocs() {
                                    logger$f.info('All uploaded files are put into unprocessed docs. Sent these docs.');
                                    sendUnProcessedDocs();
                                }, function error() {
                                    documentUploadDone = true;
                                    terminatePromise();
                                });
                            }
                            next();
                        });
                    }, function (errorMsg) {
                        if (errorMsg) {
                            logger$f.error(errorMsg);
                            documentUploadDone = true;
                            terminatePromise();
                        }
                    });
                }
            });
            function sendUnProcessedDocs() {
                _self.indexedDBService.getUnprocessedDocs(function (cachedObjects) {
                    _self.offlineStatusService.setPendingDocumentCount(cachedObjects.length);
                    if (cachedObjects.length === 0) {
                        documentUploadDone = true;
                        terminatePromise();
                        return;
                    }
                    logger$f.info('Sending unprocessed documents..');
                    let documentProcessed = 0;
                    async.whilst(hasMoreDocuments, iterator, callbackFn);
                    function hasMoreDocuments() {
                        return !abort && documentProcessed < cachedObjects.length;
                    }
                    function iterator(callback) {
                        const cachedObject = cachedObjects[documentProcessed];
                        const docId = cachedObject.documentId;
                        const docVersionId = cachedObject.systemHeader.versionId;
                        _self.indexedDBService.loadCachedDocByVersion(docId, docVersionId, function (dbCachedObject) {
                            const deleteOnComplete = dbCachedObject.systemHeader.offlineDetails.deleteOnComplete;
                            // do not send the offlineDetails to the server because the server does not use these values. The
                            // updates can cause ElasticSearch mapping conflicts
                            const offlineDetails = dbCachedObject.systemHeader.offlineDetails;
                            delete dbCachedObject.systemHeader.offlineDetails;
                            _self.sendOfflineData(dbCachedObject, offlineDetails).then(function success(response) {
                                return __awaiter(this, void 0, void 0, function* () {
                                    _self.offlineStatusService.setConnected();
                                    onItemProgress(); // record activity
                                    documentProcessed++;
                                    yield _self.cacheVendorLibs(response.data || response.body);
                                    let versionConflict = false;
                                    const thisDcVersionId = dbCachedObject.systemHeader.versionId;
                                    if (response.data != null && response.data._id != null) {
                                        const expectedVersion = thisDcVersionId;
                                        const retrievedVersion = response.data.systemHeader.versionId;
                                        versionConflict = expectedVersion !== retrievedVersion;
                                        /**
                                         * update the cache with the actual document in server
                                         * if there are conflicts as stated in issue 10049 (versionConflict === true),
                                         * the document response.data will not be identical with the sent document.
                                         * Whatever the server sent back, response.data in this case, will always be
                                         * the current document.
                                         */
                                        dbCachedObject = response.data;
                                    }
                                    const tbl = _self.indexedDBService.constants.DOCUMENT_TABLE_NAME;
                                    if (deleteOnComplete) {
                                        const index = _self.indexedDBService.constants.DOCUMENT_INDEX_VERSION_ID;
                                        // will delete only a single entry since versionID is our primary key
                                        _self.indexedDBService.deleteEntries(tbl, index, thisDcVersionId, null);
                                    }
                                    else {
                                        // update the doc - the offlineDetails need to be put back into the document before updating
                                        dbCachedObject.systemHeader.offlineDetails = offlineDetails;
                                        dbCachedObject.systemHeader.offlineDetails.status =
                                            _self.indexedDBService.constants.OFFLINE_STATUS_UPLOADED;
                                        dbCachedObject.systemHeader.offlineDetails.tries += 1;
                                        _self.indexedDBService.saveEntry(tbl, dbCachedObject);
                                    }
                                    _self.offlineStatusService.decPendingDocumentCount(); // update status bar
                                    if (versionConflict) {
                                        // Remove all versions of this document to retain only the
                                        // most recent curent version from the server.
                                        _self.indexedDBService.documentReset(dbCachedObject.documentId);
                                        logger$f.info('A new version is available for document ' + dbCachedObject.documentId);
                                        // TODO: inform a service to reload the current document offline safe
                                    }
                                    // The save from server always returns current version
                                    // (even the offline version is non-current), then the document is cached. It causes
                                    // many current versions of one document in indexedDB.
                                    // This will load all other uploaded current versions and update them to
                                    // non-current version.
                                    _self.indexedDBService.getProcessedCurrentVersionDocs(function (savedEntries) {
                                        if (savedEntries && savedEntries.length) {
                                            for (let i = 0; i < savedEntries.length; i++) {
                                                const objectData = savedEntries[i];
                                                if (objectData
                                                    && objectData.documentId === dbCachedObject.documentId
                                                    && objectData.systemHeader
                                                    && objectData.systemHeader.currentVersion
                                                    && objectData.systemHeader.versionId !== thisDcVersionId) {
                                                    objectData.systemHeader.currentVersion = false;
                                                    logger$f.info('Set other cached current processed versions of the current ' +
                                                        'document to non current version: ' + objectData.systemHeader.versionId);
                                                    _self.indexedDBService.saveEntry(tbl, savedEntries[i]);
                                                }
                                            }
                                        }
                                        callback();
                                    });
                                });
                            }, function err(response) {
                                onItemProgress();
                                offlineDetails.tries += 1;
                                offlineDetails.lastRequestStatus = UtilHttpStatus.getStatusText(response.status);
                                if (response.status === UtilHttpStatus.SERVER_UNRESPONSIVE) { // item will be retrieved
                                    _self.offlineStatusService.setDisconnected();
                                    abort = true;
                                }
                                else { // other errors will not be retried
                                    documentProcessed++;
                                    offlineDetails.status = _self.indexedDBService.constants.OFFLINE_STATUS_FAILED;
                                    const errMsg = 'Error sending document. Server responded with status: ' + response.status;
                                    logger$f.error(errMsg);
                                    const syncErrorMsg = `sync document : ${docId} - error with status: ${response.status} - ${response.error}`;
                                    _self.offlineStatusService.offlineStatus.errorOfflineChangesSyncDocs.push(syncErrorMsg);
                                    _self.loggedInUserService.setUserConfigItem('ErrorOfflineChangesSyncDocs', _self.offlineStatusService.offlineStatus.errorOfflineChangesSyncDocs);
                                    _self.offlineStatusService.decPendingDocumentCount();
                                }
                                // put the offlineDetails back into the document before saving to IndexedDB
                                dbCachedObject.systemHeader.offlineDetails = offlineDetails;
                                _self.indexedDBService.saveEntry(_self.indexedDBService.constants.DOCUMENT_TABLE_NAME, dbCachedObject);
                                // abort only if error can be retried, else continue on the next item
                                callback(abort ? abort : null);
                            });
                        });
                    }
                    function callbackFn(error) {
                        documentUploadDone = true;
                        terminatePromise();
                    }
                });
            }
            // either the document upload or the file upload will terminate this process, depending on who completes last
            function terminatePromise() {
                if (!documentUploadDone) { // wait for docs to finish uploading
                    return;
                }
                // Document upload is sequential so it has to abort if one item fails with a retriable error
                // But files can be uploaded anytime, if the upload fails it will just be retried. Unless
                // the error is a fatal one, which will not be retried and marked as OFFLINE_STATUS_FAILED
                if (abort) {
                    reject(new Error('Client is offline.'));
                }
                else {
                    resolve();
                }
            }
        });
    }
    // moved into a function to isolate it and avoid context issues
    processOfflineUpload(record, onItemProgress, onItemProcessed) {
        const _self = this;
        _self.fileProviderService.onlineFileUpload(record.uploadConfig).then(successF, errorF);
        function successF(response) {
            onItemProgress(); // keep alive poller
            const result = response.data;
            if (!result) {
                onItemProcessed(null, false); // false to ignore this file when changing its info in document
                logger$f.info('Error in File Upload. Did not receive appropriate response.');
                return;
            }
            // update the offline entry status to UPLOADED status
            record.status = _self.indexedDBService.constants.OFFLINE_STATUS_UPLOADED;
            _self.indexedDBService.saveFile(record);
            logger$f.info('File ' + record.fileNo + ' successfully uploaded via offline poller.');
            onItemProcessed(null, record);
        }
        function errorF(response) {
            onItemProgress(); // keep alive poller
            let entryStatus;
            if (response.status === UtilHttpStatus.SERVER_UNRESPONSIVE) { // ignore these errors retry sending again
                entryStatus = _self.indexedDBService.constants.OFFLINE_STATUS_PENDING;
            }
            else { // else shall never be retried
                entryStatus = _self.indexedDBService.constants.OFFLINE_STATUS_FAILED;
            }
            record.status = entryStatus;
            // update the offline entry
            const errMsg = response.status + ': ' + UtilHttpStatus.getStatusText(response.status);
            const errCount = record.errorCount == null ? 1 : record.errorCount + 1;
            _self.indexedDBService.saveFile(record, errMsg, errCount);
            onItemProcessed(entryStatus);
        }
        function progressF(response) {
            // TODO: add progress indicator later
            onItemProgress();
        }
    }
    cacheVendorLibs(document) {
        return __awaiter(this, void 0, void 0, function* () {
            const docType = [
                SharedConstants.SYSTEM_TYPE_TEMPLATE,
                SharedConstants.SYSTEM_TYPE_COMPONENT,
                SharedConstants.SYSTEM_TYPE_VENDOR_LIBRARY
            ];
            if (document && document.systemHeader && docType.indexOf(document.systemHeader.systemType) !== -1) {
                if (document.systemHeader.systemType === SharedConstants.SYSTEM_TYPE_VENDOR_LIBRARY) {
                    yield loadjs([document.fileName], {
                        returnPromise: true
                    });
                }
                else if (document.vendorLibrariesRel) {
                    for (const vendorLibrariesRel of document.vendorLibrariesRel) {
                        if (vendorLibrariesRel.fileName) {
                            yield loadjs([vendorLibrariesRel.fileName], {
                                returnPromise: true
                            });
                        }
                    }
                }
                else {
                    return Promise.resolve(true);
                }
            }
        });
    }
}
/** @nocollapse */ /** @nocollapse */ IndexedDBQueueService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: IndexedDBQueueService, deps: [{ token: i1.HttpClient }, { token: IndexedDBConnectorService }, { token: IndexedDBService }, { token: OfflineStatusService }, { token: BroadcastService }, { token: FileProviderService }, { token: LoggedInUserService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ IndexedDBQueueService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: IndexedDBQueueService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: IndexedDBQueueService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: IndexedDBConnectorService }, { type: IndexedDBService }, { type: OfflineStatusService }, { type: BroadcastService }, { type: FileProviderService }, { type: LoggedInUserService }]; } });

const logger$e = console;
class IndexedDBPropertiesService {
    constructor(loggedInUserService, indexedDBService) {
        this.loggedInUserService = loggedInUserService;
        this.indexedDBService = indexedDBService;
    }
    setItem(key, value) {
        if (!this.loggedInUserService.getUserConfigItem('cachingEnabled') === true) {
            logger$e.error('Attempting to use IndexedDBPropertiesService while caching disabled.');
            return Promise.reject(new Error('Offline is disabled.'));
        }
        const _self = this;
        const record = {
            key: key,
            value: value
        };
        return new Promise((resolve, reject) => {
            _self.indexedDBService.saveEntry(IndexedDBConstants.FT_PROPERTIES_TABLE_NAME, record, function (err) {
                if (err) {
                    if (err.name === "OpenFailedError") {
                        reject(new Error('Offline is not supported in this browser. If using Private Mode, you may not be able to use it in this browser.'));
                    }
                    else {
                        reject(new Error('Error in setting IDBProperties key ' + key));
                    }
                }
                else {
                    resolve(record);
                }
            });
        });
    }
    getItem(key) {
        const _self = this;
        if (!this.loggedInUserService.getUserConfigItem('cachingEnabled') === true) {
            logger$e.error('Attempting to use IndexedDBPropertiesService while caching disabled.');
            return Promise.reject('Offline is disabled.');
        }
        return new Promise((resolve, reject) => {
            const tbl = IndexedDBConstants.FT_PROPERTIES_TABLE_NAME;
            const index = IndexedDBConstants.FT_PROPERTIES_TABLE_INDEX_KEY;
            _self.indexedDBService.getEntries(tbl, index, key, function (entries) {
                if (entries.length > 0) {
                    resolve(entries[0]); // get first item.
                }
                else {
                    reject(new Error('Error in getting IDBProperties key ' + key));
                }
            });
        });
    }
    removeItem(key) {
        const _self = this;
        return new Promise((resolve, reject) => {
            const tbl = IndexedDBConstants.FT_PROPERTIES_TABLE_NAME;
            const index = IndexedDBConstants.FT_PROPERTIES_TABLE_INDEX_KEY;
            _self.indexedDBService.deleteEntries(tbl, index, key, function (err, count) {
                if (count === 0) {
                    logger$e.info('No entry to remove for key: ' + key);
                }
                resolve(true);
            });
        });
    }
}
/** @nocollapse */ /** @nocollapse */ IndexedDBPropertiesService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: IndexedDBPropertiesService, deps: [{ token: LoggedInUserService }, { token: IndexedDBService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ IndexedDBPropertiesService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: IndexedDBPropertiesService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: IndexedDBPropertiesService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: LoggedInUserService }, { type: IndexedDBService }]; } });

class UserSettingsService {
    constructor(localStorageService) {
        this.localStorageService = localStorageService;
    }
    fillSettingsObject(userSettings, templateId, name, settings, value) {
        if (!userSettings[templateId]) {
            userSettings[templateId] = {};
        }
        if (!userSettings[templateId][name]) {
            userSettings[templateId][name] = {};
        }
        userSettings[templateId][name][settings] = value;
        return userSettings;
    }
    saveSettings(templateId, name, settings, value) {
        let userSettings = this.localStorageService.getItem(LocalStorageKeys.USER_SETTINGS);
        if (userSettings) {
            try {
                userSettings = JSON.parse(userSettings);
            }
            catch (e) {
                userSettings = {};
            }
            finally {
                userSettings = this.fillSettingsObject(userSettings, templateId, name, settings, value);
            }
        }
        else {
            userSettings = {};
            userSettings = this.fillSettingsObject(userSettings, templateId, name, settings, value);
        }
        this.localStorageService.setItem(LocalStorageKeys.USER_SETTINGS, JSON.stringify(userSettings));
    }
    getSettings(templateId, name, settings) {
        let userSettings = this.localStorageService.getItem(LocalStorageKeys.USER_SETTINGS);
        if (userSettings) {
            try {
                userSettings = JSON.parse(userSettings);
                if (userSettings[templateId]
                    && userSettings[templateId][name]
                    && userSettings[templateId][name][settings]) {
                    return userSettings[templateId][name][settings];
                }
            }
            catch (e) { }
        }
        return null;
    }
}
/** @nocollapse */ /** @nocollapse */ UserSettingsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: UserSettingsService, deps: [{ token: LocalStorageService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ UserSettingsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: UserSettingsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: UserSettingsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: LocalStorageService }]; } });

const logger$d = console;
class OfflineUtilService {
    constructor(offlineStatusService) {
        this.offlineStatusService = offlineStatusService;
    }
    getNextId(url) {
        return UtilDocumentId.generateId();
    }
    startOfflinePoller() {
        const _self = this;
        const offlinePollerWorker = new Worker('OfflinePoller.js');
        const pollerOptions = {};
        pollerOptions.offlinePollerStart = true;
        offlinePollerWorker.postMessage(pollerOptions);
        offlinePollerWorker.onmessage = onMessage;
        function onMessage({ data }) {
            if ((data === null || data === void 0 ? void 0 : data.messageType) === WebWorkerMessageType.PEINDING_DOCUMENT_COUNT) {
                _self.offlineStatusService.setPendingDocumentCount(data.value);
            }
        }
    }
}
/** @nocollapse */ /** @nocollapse */ OfflineUtilService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: OfflineUtilService, deps: [{ token: OfflineStatusService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ OfflineUtilService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: OfflineUtilService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: OfflineUtilService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: OfflineStatusService }]; } });

const logger$c = console;
class RuleContext {
    constructor(superObj, eventName, processDoc, documentContext) {
        this.superObj = superObj;
        this.currentEventName = eventName;
        this.currentDocument = processDoc;
        this.currentTemplate = documentContext.template;
        this.currentDocumentContext = documentContext;
    }
    showField(field, flag) {
        this.setFieldFlag(field, 'visible', flag);
    }
    showFields(fields, flag) {
        this.setFieldFlags(fields, 'visible', flag);
    }
    enableField(field, flag) {
        this.setFieldFlag(field, 'enabled', flag);
    }
    enableFields(fields, flag) {
        this.setFieldFlags(fields, 'enabled', flag);
    }
    filterAndSetAffectedDoc(fields) {
        const res = [];
        for (let i = 0; i < fields.length; i++) {
            let examDoc = this.determineAffectedDoc(fields[i]);
            if (this.affectedDoc) {
                const field = examDoc.docField;
                let tplItem = this.loadTplItem(field);
                if (!tplItem) {
                    logger$c.warn('Template does not contain field: ' + field + ' to mandate.');
                }
                else {
                    res.push(field);
                }
            }
        }
        return res;
    }
    mandateField(field, flag) {
        const examDoc = this.determineAffectedDoc(field);
        if (this.affectedDoc) {
            if (flag !== undefined) {
                const tplItem = this.loadTplItem(examDoc.docField);
                if (!tplItem) {
                    logger$c.warn('Template does not contain field: ' + field + ' to mandate.');
                    return;
                }
                const result = (flag === 'yes' || flag === 'true' || flag === true);
                this.superObj.dispatchStoreAction(formSetComponentFlagTemplate(this.affectedTpl.documentId, examDoc.docField, 'mandatory', result));
                if (!result) {
                    this.superObj.setValidationValue(this.affectedDoc, examDoc.docField, 'required', false);
                }
            }
        }
    }
    mandateFields(fields, flag) {
        const resFields = this.filterAndSetAffectedDoc(fields);
        if (resFields.length) {
            const result = (flag === 'yes' || flag === 'true' || flag === true);
            this.superObj.dispatchStoreAction(formSetListComponentsFlagTemplate(this.affectedTpl.documentId, resFields, 'mandatory', result));
            if (!result) {
                for (let i = 0; i < resFields.length; i++) {
                    this.superObj.setValidationValue(this.affectedDoc, resFields[i], 'required', false);
                }
            }
        }
    }
    setFieldValue(field, value, tmp) {
        const examDoc = this.determineAffectedDoc(field);
        if (this.affectedDoc) {
            if (tmp) {
                logger$c.warn('Please use function setFieldValue(field, value) instead of setFieldValue(doc, field, value)');
                this.affectedDoc[value] = tmp;
            }
            else {
                this.affectedDoc[examDoc.docField] = value;
            }
        }
    }
    getFieldValue(field, tmp) {
        const examDoc = this.determineAffectedDoc(field);
        if (this.affectedDoc) {
            if (tmp) {
                logger$c.warn('Please use function getFieldValue(field) instead of getFieldValue(doc, field)');
                return this.affectedDoc[tmp];
            }
            else {
                return this.affectedDoc[examDoc.docField];
            }
        }
    }
    checkChecklistItem(field, optionName, checked) {
        const examDoc = this.determineAffectedDoc(field);
        let fieldValues = this.affectedDoc[examDoc.docField];
        if (fieldValues) {
            const matchIndex = UtilArray.containsIndex(fieldValues, optionName);
            if (checked === 'yes' || checked === true) {
                if (matchIndex === -1) {
                    fieldValues.push(optionName);
                }
            }
            else {
                if (matchIndex > -1) {
                    fieldValues.splice(matchIndex, 1);
                }
            }
        }
        else {
            if (checked === 'yes' || checked === true) {
                fieldValues = [];
                fieldValues.push(optionName);
            }
        }
        this.affectedDoc[examDoc.docField] = fieldValues;
        this.superObj.eventList[SharedConstants.ON_CHANGE_FILTER_EVENT_NAME] = {};
        this.superObj.eventList[SharedConstants.ON_CHANGE_FILTER_EVENT_NAME][SharedConstants.RULESET_RESULT_FLAG] = true;
        this.superObj.eventList[SharedConstants.ON_CHANGE_FILTER_EVENT_NAME][SharedConstants.RULESET_AFFECTED_FIELD_NAME] = examDoc.docField;
        this.superObj.eventList[SharedConstants.ON_CHANGE_FILTER_EVENT_NAME][SharedConstants.RULESET_AFFECTED_TEMPLATE_ID] = this.affectedTpl.documentId;
        this.superObj.eventList[SharedConstants.ON_CHANGE_FILTER_EVENT_NAME][SharedConstants.RULESET_AFFECTED_DOCUMENT_ID] = this.affectedDoc.documentId;
        this.superObj.broadcastEvent(SharedConstants.EVENT_LIST_NAME, this.superObj.eventList);
    }
    getFilter(field) {
        return this.getGridFilter(field);
    }
    getGridFilter(field) {
        const examDoc = this.determineAffectedDoc(field);
        if (this.affectedDoc) {
            const tplItem = this.loadTplItem(examDoc.docField);
            if (tplItem) {
                return tplItem.filter;
            }
        }
    }
    setGridFilter(field, newFilter) {
        this.setFilter(field, newFilter);
    }
    setFilter(field, newFilter) {
        const examDoc = this.determineAffectedDoc(field);
        const tplItem = this.loadTplItem(examDoc.docField);
        if (!tplItem) {
            logger$c.warn('Template does not contain field: ' + field + ' to set filter.');
            return;
        }
        this.superObj.dispatchStoreAction(formSetComponentFlagTemplate(this.affectedTpl.documentId, examDoc.docField, 'filter', newFilter));
        this.superObj.eventList[SharedConstants.ON_CHANGE_FILTER_EVENT_NAME] = {};
        this.superObj.eventList[SharedConstants.ON_CHANGE_FILTER_EVENT_NAME][SharedConstants.RULESET_NEW_FILTER] = newFilter;
        this.superObj.eventList[SharedConstants.ON_CHANGE_FILTER_EVENT_NAME][SharedConstants.RULESET_AFFECTED_FIELD_NAME] = examDoc.docField;
        this.superObj.eventList[SharedConstants.ON_CHANGE_FILTER_EVENT_NAME][SharedConstants.RULESET_AFFECTED_TEMPLATE_ID] = this.affectedTpl.documentId;
        this.superObj.eventList[SharedConstants.ON_CHANGE_FILTER_EVENT_NAME][SharedConstants.RULESET_AFFECTED_DOCUMENT_ID] = this.affectedDoc.documentId;
        this.superObj.eventList[SharedConstants.ON_CHANGE_FILTER_EVENT_NAME][SharedConstants.RULESET_EVENT_NAME] = this.currentEventName;
        this.superObj.broadcastEvent(SharedConstants.EVENT_LIST_NAME, this.superObj.eventList);
    }
    openNewDocument(templateId, initialData, newWindow, callback) {
        this.superObj.tabService.openNewDocument(templateId, initialData, newWindow, callback);
    }
    markDocumentChanged() {
        this.superObj.modifiedFieldService.setDirty(true);
    }
    isNew(doc) {
        return UtilDocument.isNew(doc ? doc : this.affectedDoc);
    }
    determineAffectedDoc(field) {
        const result = {};
        if (field && field.indexOf('.') !== -1) {
            const temp = field.split('.');
            result.docPrefix = temp[0]; // Must be 'parentDoc' OR 'childDoc'
            result.docField = temp[1];
        }
        else {
            result.docField = field;
        }
        this.setAffectedItems(result.docPrefix);
        return result;
    }
    /**
     * Set affected items: document, templateId, template item
     */
    setAffectedItems(docPrefix) {
        if (docPrefix === SharedConstants.RULESET_PARENT_DOCUMENT) {
            this.affectedDoc = this.superObj.getDocumentFromStore(this.currentDocumentContext.parentDocument.documentId);
            this.affectedTpl = this.superObj.getTemplateFromStore(this.currentDocumentContext.parentTemplate.documentId);
        }
        else if (docPrefix === SharedConstants.RULESET_CHILD_DOCUMENT) {
            this.affectedDoc = this.superObj.getDocumentFromStore(this.currentDocumentContext.childDocument.documentId);
            this.affectedTpl = this.superObj.getTemplateFromStore(this.currentDocumentContext.childTemplate.documentId);
        }
        else {
            this.affectedDoc = this.superObj.getDocumentFromStore(this.currentDocument.documentId);
            this.affectedTpl = this.superObj.getTemplateFromStore(this.currentTemplate.documentId);
        }
    }
    setComponentProps(field, componentName, propertyValue) {
        const examDoc = this.determineAffectedDoc(field);
        if (this.affectedDoc) {
            const tplItem = this.loadTplItem(examDoc.docField);
            if (!tplItem) {
                logger$c.warn('Template does not contain field: ' + field + ' to show/enable.');
                return;
            }
            this.superObj.dispatchStoreAction(formSetComponentPropsTemplate(this.affectedTpl.documentId, examDoc.docField, componentName, propertyValue));
        }
        return false;
    }
    setFieldFlag(field, fieldFlag, flag) {
        const examDoc = this.determineAffectedDoc(field);
        if (this.affectedDoc) {
            if ('enabled' === fieldFlag) {
                if (!this.superObj.clientAccessService.hasPermission(this.affectedDoc, 'Update,Create')) {
                    return;
                }
            }
            if (flag !== undefined) {
                const tplItem = this.loadTplItem(examDoc.docField);
                if (!tplItem) {
                    logger$c.warn('Template does not contain field: ' + field + ' to show/enable.');
                    return;
                }
                const result = (flag === 'yes' || flag === 'true' || flag === true);
                this.superObj.dispatchStoreAction(formSetComponentFlagTemplate(this.affectedTpl.documentId, examDoc.docField, fieldFlag, result));
                return result;
            }
        }
        return false;
    }
    setFieldFlags(fields, fieldFlagName, flag) {
        const resFields = this.filterAndSetAffectedDoc(fields);
        if (resFields.length) {
            if (flag !== undefined) {
                const result = (flag === 'yes' || flag === 'true' || flag === true);
                this.superObj.dispatchStoreAction(formSetListComponentsFlagTemplate(this.affectedTpl.documentId, resFields, fieldFlagName, result));
                return result;
            }
        }
        return false;
    }
    loadTplItem(fieldName) {
        if (this.affectedTpl && this.affectedTpl.components) {
            for (let i = 0; i < this.affectedTpl.components.length; i++) {
                const tplItem = this.affectedTpl.components[i];
                if (tplItem.name === fieldName) {
                    return tplItem;
                }
            }
        }
        return null;
    }
}

const logger$b = console;
class RulesetContext {
    constructor() {
    }
    loadRecords(collectionName, callback) {
        logger$b.error('LoadRecords regards collectionName is not valid function.');
    }
    sanitizeHtml(html) {
        return this.sanitizer.sanitize(SecurityContext.HTML, html);
    }
}
/** @nocollapse */ /** @nocollapse */ RulesetContext.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: RulesetContext, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ RulesetContext.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: RulesetContext, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: RulesetContext, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

class IncludeDocumentDataService {
    constructor(loggedInUserService, indexedDBConnectorService, http) {
        this.loggedInUserService = loggedInUserService;
        this.indexedDBConnectorService = indexedDBConnectorService;
        this.http = http;
    }
    getIncludeDocuments(includeNames) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const offlineMode = this.loggedInUserService.getUserConfigItem('cachingEnabled') === true;
                if (offlineMode) {
                    return yield this.getIncludeDocumentsFromIndexedDB(includeNames);
                }
                else {
                    return yield this.getIncludeDocumentsFromServer(SharedUrlRoutes.serverRoutes.loadIncludeDocs, includeNames);
                }
            }
            catch (err) {
                console.error('Get ruleset include documents failed with error: ' + err);
                throw new Error(err);
            }
        });
    }
    getIncludeDocumentsFromIndexedDB(includeNames) {
        const _self = this;
        return new Promise((resolve, reject) => {
            this.indexedDBConnectorService.getDatabase().then(function (db) {
                db.documents.where('systemHeader.systemType').equals(SharedConstants.SYSTEM_TYPE_RULESET_INCLUDE).toArray(function (documents) {
                    try {
                        var results = documents.filter(function (document) {
                            return document.systemHeader.currentVersion === true &&
                                includeNames.includes(document.name);
                        });
                        resolve(results);
                    }
                    catch (err) {
                        reject(err);
                    }
                });
            }, (error) => {
                reject(error);
            });
        });
    }
    getIncludeDocumentsFromServer(url, includeNames) {
        const _self = this;
        return new Promise((resolve, reject) => {
            _self.http.post(url, { includeNames: includeNames }).subscribe((results) => {
                resolve(results);
            }, function (err) {
                const msg = err ? err.message : 'Something went wrong when getting include documents.';
                const error = new FtError(msg);
                if (err) {
                    error.status = err.status;
                    error.statusText = err.statusText;
                    error.message = err.error;
                }
                reject(error);
            });
        });
    }
}
/** @nocollapse */ /** @nocollapse */ IncludeDocumentDataService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: IncludeDocumentDataService, deps: [{ token: LoggedInUserService }, { token: IndexedDBConnectorService }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ IncludeDocumentDataService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: IncludeDocumentDataService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: IncludeDocumentDataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: LoggedInUserService }, { type: IndexedDBConnectorService }, { type: i1.HttpClient }]; } });

const logger$a = console;
class ClientIncludeRuleSetService {
    constructor(configService, includeDocumentDataService, notificationService) {
        this.configService = configService;
        this.includeDocumentDataService = includeDocumentDataService;
        this.notificationService = notificationService;
    }
    loadIncludeDocs(includeNames, callback) {
        this.includeDocumentDataService.getIncludeDocuments(includeNames).then((results) => {
            let docs = results ? results : [];
            const tmpData = { data: docs, total: docs.length };
            callback(null, tmpData);
        }, (err) => {
            logger$a.warn(err);
            callback(err);
        });
    }
    /*
     * Including ruleset processing.
     * Include ruleset syntax: #include "rulesetName";
     */
    addIncludesToRuleset(ruleFlow, callback) {
        const includeNames = UtilIncludeRuleSet.getIncludeNames(ruleFlow);
        if (includeNames && includeNames.length) {
            const remainIncludeNames = [];
            // remove commented #include
            for (let i = 0; i < includeNames.length; i++) {
                const beforeLength = ruleFlow.length;
                const commentLineExp = '//[\t ]*#include[\t ]+[\'"]' + includeNames[i] + '[\'"][\t ]*.*';
                ruleFlow = ruleFlow.replace(new RegExp(commentLineExp, 'g'), '');
                if (beforeLength === ruleFlow.length) {
                    // Keep not commented include name
                    remainIncludeNames.push(includeNames[i]);
                }
            }
            if (remainIncludeNames.length) {
                this.loadIncludeDocs(remainIncludeNames, (error, result) => {
                    if (error) {
                        callback({
                            status: SharedConstants.STATUS_ERROR,
                            message: 'Error loading include ruleset: ' + error.message
                        });
                    }
                    else if (result && result.total && result.total >= remainIncludeNames.length) {
                        const duplicates = result.data.map(value => value.name).filter((value, index, self) => {
                            return self.indexOf(value) !== index;
                        });
                        if (duplicates.length)
                            this.notificationService.warning(`Duplicate ruleset include: ${duplicates}`);
                        for (let id = 0; id < result.total; id++) {
                            const includeDoc = result.data[id];
                            // replace #include by script for JS
                            const exp = '#include[\t ]+[\'"]' + includeDoc.name + '[\'"][\t ]*,';
                            ruleFlow = ruleFlow.replace(new RegExp(exp, 'g'), includeDoc.ruleSetSnippet + ',');
                        }
                        callback({
                            status: SharedConstants.STATUS_SUCCESS,
                            ruleFlow: ruleFlow
                        });
                    }
                    else {
                        let missedIncludes;
                        if (result && result.total) {
                            missedIncludes = [];
                            for (let i = 0; i < remainIncludeNames.length; i++) {
                                let res = false;
                                for (let j = 0; j < result.total; j++) {
                                    if (result.data[j].name === remainIncludeNames[i]) {
                                        res = true;
                                        break;
                                    }
                                }
                                if (!res) {
                                    missedIncludes.push(remainIncludeNames[i]);
                                }
                            }
                        }
                        else {
                            missedIncludes = remainIncludeNames;
                        }
                        logger$a.error('There are missing ruleset includes for this document: ' + missedIncludes +
                            '. Please make sure the ruleset include documents exist and ' +
                            'the correct access keys are configured for them.');
                        let errMsg = 'Ruleset Include(s) not found: ';
                        for (let i = 0; i < missedIncludes.length; i++) {
                            errMsg += missedIncludes[i];
                            if (i < missedIncludes.length - 1) {
                                errMsg += ', ';
                            }
                        }
                        callback({
                            status: SharedConstants.STATUS_ERROR,
                            message: errMsg
                        });
                    }
                });
            }
            else {
                callback({
                    status: SharedConstants.STATUS_SUCCESS,
                    ruleFlow: ruleFlow
                });
            }
        }
        else {
            callback({
                status: SharedConstants.STATUS_SUCCESS,
                ruleFlow: ruleFlow
            });
        }
    }
}
/** @nocollapse */ /** @nocollapse */ ClientIncludeRuleSetService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ClientIncludeRuleSetService, deps: [{ token: ConfigService }, { token: IncludeDocumentDataService }, { token: NotificationService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ ClientIncludeRuleSetService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ClientIncludeRuleSetService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ClientIncludeRuleSetService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: ConfigService }, { type: IncludeDocumentDataService }, { type: NotificationService }]; } });

const logger$9 = console;
class SclSearchClientConstantService {
}
SclSearchClientConstantService.operator = {
    equals: 'eq',
    notEquals: 'neq',
    contains: 'contains'
};
class SearchClientService {
    constructor(httpClient, searchService, configService, loggedInUserService, dataService) {
        this.httpClient = httpClient;
        this.searchService = searchService;
        this.configService = configService;
        this.loggedInUserService = loggedInUserService;
        this.dataService = dataService;
        this.processDexieResults = (options, results, resolve) => __awaiter(this, void 0, void 0, function* () {
            let docs = [];
            let filterDocs = results.data;
            if (filterDocs && filterDocs.length > 0) {
                filterDocs = yield this.filterResults(options, filterDocs);
                if (filterDocs && filterDocs.length > 0) {
                    filterDocs = this.sortResults(options, filterDocs);
                    const size = options.size || options.pageSize || SharedConstants.DEFAULT_PAGE_SIZE;
                    let pageFrom;
                    if (!options.page && options.from && options.from > 0 && options.size > 0) {
                        pageFrom = options.from / options.size;
                    }
                    const page = options.page || pageFrom || SharedConstants.DEFAULT_PAGE;
                    const docsArray = chunk(filterDocs, size);
                    docs = nth(docsArray, page);
                }
            }
            results.total = filterDocs.length;
            results.data = docs;
            resolve(results);
        });
        this.executeComponentFunction = (options, resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            const params = {
                functionName: options.functionName ? options.functionName : 'searchByQueryName',
                componentWebServiceName: options.componentWebServiceName ? options.componentWebServiceName : 'ws-search-service',
                functionParameters: [options.queryName, options.dataSource, options.document, options],
                offlineComponentLibraryName: options.offlineComponentLibraryName ? options.offlineComponentLibraryName : options.queryName
            };
            let docs = [];
            let isExists = false;
            if (!isEmpty(options.queryName) && options.dataSource === 'elastic') {
                try {
                    isExists = yield this.isQueryDocumentExists(options.queryName, options.dataSource);
                }
                catch (err) {
                    // the error will be ignored so the filter can be used
                }
            }
            if (!isExists) {
                this.searchService.search(options).then((results) => {
                    if (results.searchProvier === 'dexie') {
                        this.processDexieResults(options, results, resolve);
                    }
                    else {
                        resolve(results);
                    }
                }, (err) => {
                    reject(err);
                });
            }
            else {
                this.dataService.executeComponentFunction(params)
                    .then((results) => {
                    let data;
                    if (results.data && results.data.hits && results.data.hits.hits) {
                        data = results.data.hits.hits;
                    }
                    else {
                        data = results;
                        results.total = data.length;
                        results.searchProvier = options.dataSource;
                        results.statusCode = 200;
                    }
                    if (data && data.length > 0) {
                        const size = options.size || options.pageSize || SharedConstants.DEFAULT_PAGE_SIZE;
                        let pageFrom;
                        if (!options.page && options.from && options.from > 0 && options.size > 0) {
                            pageFrom = options.from / options.size;
                        }
                        const page = options.page || pageFrom || SharedConstants.DEFAULT_PAGE;
                        const docsArray = chunk(data, size);
                        docs = nth(docsArray, page);
                    }
                    if (results.data && results.data.hits && results.data.hits.hits) {
                        results.data.hits.hits = docs;
                    }
                    else {
                        results.data = docs;
                    }
                    resolve(results);
                }, (error) => {
                    reject(error);
                });
            }
        });
    }
    search(options, searchContext) {
        return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            try {
                const offlineMode = this.loggedInUserService.getUserConfigItem('cachingEnabled') === true;
                if ((options === null || options === void 0 ? void 0 : options.searchOfflineOnly) && !offlineMode) {
                    reject(new Error('Please enable caching before performing a search configured as offline only'));
                }
                else {
                    let isExists = false;
                    if (!isEmpty(options.queryName) && options.dataSource === 'elastic') {
                        try {
                            isExists = yield this.isQueryDocumentExists(options.queryName, options.dataSource);
                        }
                        catch (err) {
                            // the error will be ignored so the filter can be used
                        }
                    }
                    if (!isEmpty(options.queryName) && options.dataSource && isExists) {
                        this.executeComponentFunction(options, resolve, reject);
                    }
                    else {
                        if (!isEmpty(options.queryName) && (options.dataSource === 'dexie' || offlineMode)) {
                            options.searchOptions = options.searchOptions ? options.searchOptions : {};
                            options.searchOptions.dexieSearchFunction = yield this.getDexieSearchFunction(options);
                        }
                        else if (options.offlineFilter) {
                            options.searchOptions = options.searchOptions ? options.searchOptions : {};
                            options.searchOptions.dexieSearchFunction = yield this.parseOfflineFilter(options);
                        }
                        this.searchService.search(options, searchContext).then((results) => {
                            if (results.searchProvier === 'dexie') {
                                this.processDexieResults(options, results, resolve);
                            }
                            else {
                                resolve(results);
                            }
                        }, (err) => {
                            reject(err);
                        });
                    }
                }
            }
            catch (error) {
                reject(error);
            }
        }));
    }
    getDexieSearchFunction(options) {
        const self = this;
        return new Promise((resolve, reject) => {
            this.getDexieQuery(options.queryName, function (err, queryDocument) {
                if (err) {
                    reject(err);
                }
                else if (queryDocument && !isEmpty(queryDocument.filter)) {
                    let fun;
                    try {
                        let funcStr = queryDocument.filter;
                        if (options.processHandlebars) {
                            funcStr = options.processHandlebars(queryDocument.filter, null, options.handlebarsParams);
                        }
                        // tslint:disable-next-line:no-eval
                        fun = eval('(' + funcStr + ')');
                        resolve(fun);
                    }
                    catch (err) {
                        reject(err);
                    }
                }
                else {
                    resolve();
                }
            });
        });
    }
    getDexieQuery(queryName, callback) {
        const offlineMode = this.loggedInUserService.getUserConfigItem('cachingEnabled') === true;
        if (!offlineMode) {
            callback(new Error("Request for Dexie query without offline enabled. Please enable caching before querying Dexie"));
        }
        else {
            const queryDocument = (db, callback) => __awaiter(this, void 0, void 0, function* () {
                try {
                    yield db.documents.where('name').equals(queryName).and((document) => {
                        return document.systemHeader.systemType === 'query'
                            && toLower(document.searchProvider) === 'dexie'
                            && document.systemHeader.currentVersion === true;
                    }).toArray((documents) => {
                        callback(null, documents);
                    });
                }
                catch (err) {
                    logger$9.error(err);
                    callback(err);
                }
            });
            const options = { "searchOptions": { "dexieSearchFunction": queryDocument } };
            this.searchService.search(options).then((results) => {
                let qryDoc;
                if (results && results.data && results.data.length > 0) {
                    qryDoc = results.data[0];
                }
                callback(null, qryDoc);
            }, (err) => {
                logger$9.error(err);
                callback(err);
            });
        }
    }
    filterResults(options, documents) {
        return __awaiter(this, void 0, void 0, function* () {
            const self = this;
            return new Promise((resolve) => __awaiter(this, void 0, void 0, function* () {
                let query = [];
                let filterObj = options.filter;
                if (typeof filterObj === 'string') {
                    filterObj = filterObj.replace(/'/g, '"');
                    filterObj = JSON.parse(filterObj);
                }
                if (filterObj && !isEmpty(filterObj.dexieQuery) && isArray(filterObj.dexieQuery) && filterObj.dexieQuery.length > 0) {
                    query = cloneDeep(filterObj.dexieQuery);
                }
                const currentVerFilter = { field: 'systemHeader.currentVersion', value: true,
                    operator: SclSearchClientConstantService.operator.equals };
                query.push(currentVerFilter);
                const results = yield documents.filter(function (document) {
                    let isFound = false;
                    for (let i = 0; i < query.length; i++) {
                        const queryObj = query[i];
                        if (queryObj && queryObj.field && queryObj.value) {
                            const fldValue = get(document, queryObj.field);
                            if (isEmpty(queryObj.operator) || queryObj.operator === SclSearchClientConstantService.operator.contains) {
                                isFound = includes(toLower(fldValue), queryObj.value);
                            }
                            else if (queryObj.operator === SclSearchClientConstantService.operator.equals) {
                                isFound = isEqual(fldValue, queryObj.value) || isEqual(toLower(fldValue), queryObj.value);
                            }
                            else if (queryObj.operator === SclSearchClientConstantService.operator.notEquals) {
                                isFound = !isEqual(toLower(fldValue), queryObj.value);
                            }
                        }
                        if (!isFound) {
                            break;
                        }
                    }
                    return isFound;
                });
                resolve(results);
            }));
        });
    }
    sortResults(options, documents) {
        let filterObj = options.filter;
        if (typeof filterObj === 'string') {
            filterObj = filterObj.replace(/'/g, '"');
            filterObj = JSON.parse(filterObj);
        }
        if (filterObj && filterObj.dexieSort && isPlainObject(filterObj.dexieSort)) {
            let fieldArr;
            fieldArr = [];
            let valueArr;
            valueArr = [];
            map$1(filterObj.dexieSort, function (value, key) {
                fieldArr.push(key);
                valueArr.push(value);
            });
            documents = orderBy(documents, fieldArr, valueArr);
        }
        return documents;
    }
    getDefaultSearchProvider(context) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        const offlineMode = this.loggedInUserService.getUserConfigItem('cachingEnabled') === true;
        const clientConfig = this.configService.clientConfig();
        this.contextParams = {
            document: context.document,
            doc: context.document,
            account: context.account,
            tpl: context.template,
            template: context.template,
            tplItem: context.componentDefinition,
            componentDefinition: context.componentDefinition // the current components configuration details
        };
        if (!isEmpty((_a = context.componentDefinition) === null || _a === void 0 ? void 0 : _a.queryName) && offlineMode && ((_b = context.componentDefinition) === null || _b === void 0 ? void 0 : _b.searchOfflineOnly)) {
            return 'dexie';
        }
        else if (((_c = context.componentDefinition) === null || _c === void 0 ? void 0 : _c.searchProvider) || ((_d = context.template) === null || _d === void 0 ? void 0 : _d.searchProvider)) {
            return ((_e = context.componentDefinition) === null || _e === void 0 ? void 0 : _e.searchProvider) || ((_f = context.template) === null || _f === void 0 ? void 0 : _f.searchProvider);
        }
        else if (((_g = context.componentDefinition) === null || _g === void 0 ? void 0 : _g.defaultSearchProvider) || (clientConfig === null || clientConfig === void 0 ? void 0 : clientConfig.defaultSearchProvider)) {
            return ((_h = context.componentDefinition) === null || _h === void 0 ? void 0 : _h.defaultSearchProvider) || (clientConfig === null || clientConfig === void 0 ? void 0 : clientConfig.defaultSearchProvider);
        }
        else if (!isEmpty((_j = context.componentDefinition) === null || _j === void 0 ? void 0 : _j.queryName)) {
            return 'elastic';
        }
        else {
            return null;
        }
    }
    isDexieQuery(context, queryName) {
        const offlineMode = this.loggedInUserService.getUserConfigItem('cachingEnabled') === true;
        return ((!isEmpty(context.componentDefinition.queryName) || queryName) &&
            (offlineMode || context.componentDefinition.searchOfflineOnly ||
                (context.componentDefinition.searchOptions && context.componentDefinition.searchOptions.searchOfflineOnly)));
    }
    isQueryDocumentExists(queryName, dataSource) {
        return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            const options = {
                filter: `{"query":{"bool":{"filter":[{"term":{"systemHeader.systemType":"query"}},{"term":{"name":"${queryName}"}},` +
                    `{"term":{"searchProvider":{"value": "${dataSource}", "case_insensitive": true}}}]}}}`
            };
            try {
                const results = yield this.searchService.search(options);
                SearchResultsProcessor.processSearchResults(results, (itemData, sort) => {
                }, (customData, totalHits, lastSort) => {
                    resolve(totalHits > 0);
                });
            }
            catch (err) {
                logger$9.error(err);
                resolve(false);
            }
        }));
    }
    parseOfflineFilter(options) {
        const self = this;
        return new Promise((resolve, reject) => {
            let funcStr = options.offlineFilter;
            if (!isEmpty(funcStr) && isString(funcStr)) {
                let fun;
                try {
                    if (options.processHandlebars) {
                        funcStr = options.processHandlebars(funcStr, null, options.handlebarsParams);
                    }
                    // tslint:disable-next-line:no-eval
                    fun = eval('(' + funcStr + ')');
                    resolve(fun);
                }
                catch (err) {
                    reject(err);
                }
            }
            else if (isFunction(funcStr)) {
                resolve(funcStr);
            }
            else {
                resolve();
            }
        });
    }
}
/** @nocollapse */ /** @nocollapse */ SearchClientService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: SearchClientService, deps: [{ token: i1.HttpClient }, { token: SearchService }, { token: ConfigService }, { token: LoggedInUserService }, { token: DataService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ SearchClientService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: SearchClientService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: SearchClientService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: SearchService }, { type: ConfigService }, { type: LoggedInUserService }, { type: DataService }]; } });

const logger$8 = console;
class ClientRulesService {
    constructor(dataService, loggedInUserService, clientIncludeRuleSetService, documentService, offlineStatusService, http, lazyScriptLoaderService, searchService, notificationService, modifiedFieldService, clientAccessService, sanitizer, searchClientService) {
        this.dataService = dataService;
        this.loggedInUserService = loggedInUserService;
        this.clientIncludeRuleSetService = clientIncludeRuleSetService;
        this.documentService = documentService;
        this.offlineStatusService = offlineStatusService;
        this.http = http;
        this.lazyScriptLoaderService = lazyScriptLoaderService;
        this.searchService = searchService;
        this.notificationService = notificationService;
        this.modifiedFieldService = modifiedFieldService;
        this.clientAccessService = clientAccessService;
        this.sanitizer = sanitizer;
        this.searchClientService = searchClientService;
        this.sharedValues = {};
        this.user$.subscribe((data) => this.user = data);
    }
    expose() {
        return __awaiter(this, void 0, void 0, function* () {
            const rulesetContext = new RulesetContext();
            rulesetContext.user = this.user;
            rulesetContext.sharedValues = this.sharedValues;
            rulesetContext.modalService = this.modalService;
            rulesetContext.dataService = this.dataService;
            rulesetContext.DataService = this.dataService; // used in rules for the old code convention
            rulesetContext.ModifiedFieldService = this.modifiedFieldService;
            rulesetContext.lazyLoader = this.lazyScriptLoaderService;
            rulesetContext.loggedInUserService = this.loggedInUserService;
            rulesetContext.clientIncludeRuleSetService = this.clientIncludeRuleSetService;
            rulesetContext.documentService = this.documentService;
            rulesetContext.offlineStatusService = this.offlineStatusService;
            rulesetContext.http = this.http;
            rulesetContext.searchService = this.searchService;
            rulesetContext.notificationService = this.notificationService;
            rulesetContext.q = Promise;
            rulesetContext.logger = logger$8;
            rulesetContext.ModalService = this.modalService;
            rulesetContext.hasDuplicate = this.hasDuplicate;
            rulesetContext.showNotification = this.showNotification;
            /**
             * returns a boolean indicating whether the device is connected
             */
            rulesetContext.isOnline = this.offlineStatusService.isConnected;
            rulesetContext.updateDocument = this.updateDocument;
            rulesetContext.deleteDocument = this.deleteDocument;
            rulesetContext.findDocuments = this.findDocuments;
            rulesetContext.findDocumentsByElastic = this.findDocumentsByElastic;
            rulesetContext.findDocumentsBySearch = this.findDocumentsBySearch;
            /*
                * Including ruleset processing.
                * Include ruleset syntax = #include 'rulesetName';
                */
            rulesetContext.addIncludesToRuleset = this.addIncludesToRuleset;
            rulesetContext.createDocumentFromTemplate = this.createDocumentFromTemplate;
            rulesetContext.setSharedValue = this.setSharedValue;
            rulesetContext.getSharedValue = this.getSharedValue;
            rulesetContext.processSearchResults = SearchResultsProcessor.processSearchResults;
            rulesetContext.showDialog = this.showDialog;
            rulesetContext.sanitizer = this.sanitizer;
            rulesetContext.RulesetError = RulesetError;
            rulesetContext.clientAccessService = this.clientAccessService;
            rulesetContext.searchClientService = this.searchClientService;
            return rulesetContext;
        });
    }
    hasDuplicate(templateId, duplicateCondition, IdToExclude) {
        const _self = this;
        const query = ESQueryUtils.prepareESQuery(duplicateCondition);
        query.query.bool.must.push({ 'systemHeader.templateId': templateId });
        // Execute query
        return new Promise((resolve, reject) => {
            _self.dataService.findByElastic(query).then(function successFunc(results) {
                // Check excluded ID
                let result = false;
                for (let i = 0; i < results.data.hits.total; i++) {
                    if (results.data.hits.hits[i]._source.documentId !== IdToExclude) {
                        result = true;
                        break;
                    }
                }
                resolve(result);
            }, function errorFunc(err) {
                reject(err);
            });
        });
    }
    showNotification(message, status) {
        if (!status) {
            status = 'info';
        }
        status = status.toLowerCase();
        this.notificationService.printMessage(message, status, null, { x: 'right', y: 'top' });
    }
    updateDocument(id, change, userId, callback) {
        const _self = this;
        function convertToErrorObject(error) {
            let err = error;
            if (typeof err === 'string') {
                err = new Error(err);
            }
            return err;
        }
        function doUpdateDoc(orgDoc) {
            try {
                const clonedDoc = cloneDeep(orgDoc);
                DocumentUpdater.applyUpdatesSimple(clonedDoc, change);
                _self.dataService.jsonPatchUpdate(orgDoc, clonedDoc).then(function successFunc(doc) {
                    callback(null, doc);
                }, function errorFunc(err) {
                    const errObj = convertToErrorObject(err);
                    callback(errObj);
                });
            }
            catch (error) {
                const errObj = convertToErrorObject(error);
                callback(errObj);
            }
        }
        const originalDoc = _self.documentService.getExistingDocument(id);
        if (!originalDoc) {
            _self.dataService.getDocument(id).then(function successFunc(orgDoc) {
                doUpdateDoc(orgDoc);
            }, function errorFunc(err) {
                callback(err);
            });
        }
        else {
            doUpdateDoc(originalDoc);
        }
    }
    deleteDocument(id, options, callback) {
        const _self = this;
        if (isFunction(options)) {
            callback = options;
            options = null;
        }
        if (id) {
            _self.dataService.destroy(id).then(function successFunc(doc) {
                callback();
            }, function errorFunc(err) {
                callback(err);
            });
        }
    }
    findDocuments(query, userId, callback) {
        logger$8.warn('===== Deprecated function: findDocuments. Use findDocumentsByElastic instead. =====');
        logger$8.warn('Passing userId to findDocuments is ignored. The logged in user will be used');
        const queryString = '?q=' + JSON.stringify(query);
        const url = SharedUrlRoutes.serverRoutes.loadDocsForRulesBaseUrl + '/' + userId + queryString;
        this.http.get(url).subscribe((response) => {
            callback(null, response);
        }, (err) => {
            callback(err);
        });
    }
    addIncludesToRuleset(ruleFlow, callback) {
        this.clientIncludeRuleSetService.addIncludesToRuleset(ruleFlow, callback);
    }
    createDocumentFromTemplate(docInfo, templateId, userId, callback) {
        const _self = this;
        const loggedInUserId = this.user.account.documentId;
        if (userId && userId !== loggedInUserId) {
            logger$8.warn('Logged in user id will be used rather than the user id parameter ' +
                'passed to createDocumentFromTemplate');
        }
        if (!docInfo.documentId) {
            docInfo.documentId = UtilDocumentId.generateId();
        }
        if (!docInfo.systemHeader) {
            docInfo.systemHeader = {};
        }
        if (!docInfo.systemHeader.systemType) {
            docInfo.systemHeader.systemType = SharedConstants.SYSTEM_TYPE_DOCUMENT;
        }
        docInfo.systemHeader.templateId = templateId;
        this.dataService.getDocument(templateId, SharedConstants.SYSTEM_TYPE_TEMPLATE).then(function successFunc(template) {
            _self.clientAccessService.writeKeysToDocument(docInfo, template);
            _self.dataService.insert(docInfo).then(function successFunc(result) {
                callback(null, result);
            }, function errorFunc(err) {
                callback(err);
            });
        }, function errorFunc(err) {
            callback(err);
        });
    }
    setSharedValue(key, value) {
        this.sharedValues[key] = value;
    }
    getSharedValue(key) {
        return this.sharedValues[key];
    }
    findDocumentsBySearch(query, userId, searchContext, options, callback) {
        const _self = this;
        if (isFunction(searchContext)) {
            callback = searchContext;
            searchContext = {};
        }
        if (isFunction(options)) {
            callback = options;
            options = {};
        }
        if (userId) {
            logger$8.warn('Passing userId to findDocumentsBySearch is ignored. The logged in user will be used');
        }
        options = isObject(options) ? options : {};
        options.filter = query;
        _self.searchClientService.search(options, searchContext).then(function successFunc(data) {
            callback(null, data);
        }, function errorFunc(err) {
            _self.notificationService.printMessage(err.message, 'error');
            callback(err);
        });
    }
    findDocumentsByElastic(query, userId, searchContext, options, callback) {
        this.findDocumentsBySearch(query, userId, searchContext, options, callback);
    }
    showDialog(message, errorType, succFunc, errorFunc, discardFunc) {
        this.modalService.showDialog(message, errorType, succFunc, errorFunc, discardFunc);
    }
    /** set the modal service. This is set from the application importing the @formbird/services
     *  library so that a native app can set its own implementation of ModalService
     */
    setModalService(modalService) {
        this.modalService = modalService;
    }
}
/** @nocollapse */ /** @nocollapse */ ClientRulesService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ClientRulesService, deps: [{ token: DataService }, { token: LoggedInUserService }, { token: ClientIncludeRuleSetService }, { token: DocumentService }, { token: OfflineStatusService }, { token: i1.HttpClient }, { token: LazyScriptLoaderService }, { token: SearchService }, { token: NotificationService }, { token: ModifiedFieldService }, { token: ClientAccessService }, { token: i12.DomSanitizer }, { token: SearchClientService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ ClientRulesService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ClientRulesService, providedIn: 'root' });
__decorate([
    select(['userState', 'user']),
    __metadata("design:type", Observable)
], ClientRulesService.prototype, "user$", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ClientRulesService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: DataService }, { type: LoggedInUserService }, { type: ClientIncludeRuleSetService }, { type: DocumentService }, { type: OfflineStatusService }, { type: i1.HttpClient }, { type: LazyScriptLoaderService }, { type: SearchService }, { type: NotificationService }, { type: ModifiedFieldService }, { type: ClientAccessService }, { type: i12.DomSanitizer }, { type: SearchClientService }]; }, propDecorators: { user$: [] } });

class RuleCacheService {
    // loadedRulesetIncludes: RulesetCache = {
    // }
    constructor() {
        this.loadedRulesets = {};
    }
    setRuleset(documentId, rulesetScript) {
        if (documentId && rulesetScript) {
            let rulesetInfo = {
                rulesetId: documentId,
                rulesetScriptFunc: rulesetScript
            };
            this.loadedRulesets[documentId] = rulesetInfo;
        }
    }
    // setRulesetInclude(documentId: string, rulesetIncludeScript: string) {
    //   if (documentId && rulesetScript) {
    //     let rulesetInfo: RulesetCacheInfo = {
    //       rulesetScript: rulesetIncludeScript
    //     };
    //     this.loadedRulesetIncludes[documentId] = rulesetInfo;
    //   }
    // }
    getRuleset(documentId) {
        return documentId ? this.loadedRulesets[documentId] : null;
    }
}
/** @nocollapse */ /** @nocollapse */ RuleCacheService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: RuleCacheService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ RuleCacheService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: RuleCacheService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: RuleCacheService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

class LatestDocumentService {
    constructor() {
        this.latestDocuments = {};
    }
    getLatestDocument(documentId) {
        return this.latestDocuments[documentId];
    }
}
/** @nocollapse */ /** @nocollapse */ LatestDocumentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: LatestDocumentService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ LatestDocumentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: LatestDocumentService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: LatestDocumentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

const logger$7 = console;
class RuleEngineService {
    constructor(clientRulesService, configService, localStorageService, documentSessionService, reduxStoreService, ruleCacheService, latestDocumentService, offlineStatusService, loggedInUserService, modifiedFieldService, unsavedDocumentService) {
        this.clientRulesService = clientRulesService;
        this.configService = configService;
        this.localStorageService = localStorageService;
        this.documentSessionService = documentSessionService;
        this.reduxStoreService = reduxStoreService;
        this.ruleCacheService = ruleCacheService;
        this.latestDocumentService = latestDocumentService;
        this.offlineStatusService = offlineStatusService;
        this.loggedInUserService = loggedInUserService;
        this.modifiedFieldService = modifiedFieldService;
        this.unsavedDocumentService = unsavedDocumentService;
        this.setProxyForDocument = (originalDoc, documentContext) => {
            const _self = this;
            const nestedObjectHandler = function (fldName, oldValue) {
                return {
                    get: function (obj, prop) {
                        if (['[object Object]', '[object Array]'].indexOf(Object.prototype.toString.call(obj[prop])) > -1) {
                            return new Proxy(obj[prop], nestedObjectHandler(fldName, oldValue));
                        }
                        return obj[prop];
                    },
                    set: function (obj, prop, value) {
                        obj[prop] = value;
                        if (Array.isArray(obj) && prop === 'length') {
                            return true; // ignore inside variable of array
                        }
                        const newValue = originalDoc[fldName];
                        if (!isEqual(oldValue, newValue)) {
                            _self.attachChangedField(originalDoc, fldName, newValue, oldValue, documentContext);
                            console.log(`Setting value for field: ${fldName} - value: ${newValue}`);
                        }
                        return true;
                    }
                };
            };
            const fieldsChangedByRuleset = {
                set: function (document, fldName, newValue) {
                    _self.attachChangedField(document, fldName, newValue, document[fldName], documentContext);
                    Reflect.set(document, fldName, newValue);
                    console.log(`Setting value for field: ${fldName} - value: ${newValue}`);
                    return true;
                },
                deleteProperty(document, fldName) {
                    _self.attachChangedField(document, fldName, undefined, document[fldName], documentContext);
                    Reflect.deleteProperty(document, fldName);
                    return true;
                },
                defineProperty: function (document, fldName, descriptor) {
                    _self.attachChangedField(document, fldName, descriptor.value, document[fldName], documentContext);
                    Reflect.defineProperty(document, fldName, descriptor);
                    return document;
                },
                get: function (document, fldName) {
                    if ((['[object Object]', '[object Array]'].indexOf(Object.prototype.toString.call(document[fldName])) > -1) &&
                        document[fldName] !== null) {
                        const doc = _self.latestDocumentService.latestDocuments && _self.latestDocumentService.latestDocuments[originalDoc === null || originalDoc === void 0 ? void 0 : originalDoc.documentId]
                            ? _self.latestDocumentService.latestDocuments[originalDoc === null || originalDoc === void 0 ? void 0 : originalDoc.documentId] : originalDoc;
                        let oldValue;
                        if (doc && doc[fldName]) {
                            oldValue = cloneDeep(doc[fldName]);
                        }
                        return new Proxy(document[fldName], nestedObjectHandler(fldName, oldValue));
                    }
                    else {
                        return document[fldName];
                    }
                }
            };
            return new Proxy(originalDoc, fieldsChangedByRuleset);
        };
    }
    /*
     * Process Rule flow string
     * @document - document object
     * @documentContext - additional objects will be used in rules
     * @ruleFlowInfo - rules flow as an object contains rulesetId and its script
     * @scope - additional scope objects will be plugged in to rules scope
     */
    processRuleFlow(document, documentContext, ruleFlowInfo, ruleScope, callback) {
        return __awaiter(this, void 0, void 0, function* () {
            const _self = this;
            const thisScope = yield this.clientRulesService.expose();
            if (ruleScope) {
                Object.keys(thisScope).forEach(function (key) {
                    ruleScope[key] = thisScope[key];
                });
                if (!documentContext) {
                    documentContext = {};
                }
                const documentId = document.documentId;
                documentContext.logger = logger$7;
                documentContext.appVersion = _self.localStorageService.getItem(LocalStorageKeys.APP_VERSION);
                documentContext.databaseMode = _self.configService.clientConfig().defaultSearchProvider;
                documentContext.documentSession = this.documentSessionService.getDocumentSession(documentId);
                documentContext.isOnline = () => { return _self.offlineStatusService.offlineStatus.connected; };
                documentContext.isCachingEnabled = () => { return _self.loggedInUserService.getUserConfigItem('cachingEnabled'); };
                function process(rulesetScriptFunc) {
                    if (rulesetScriptFunc) {
                        //defining proxy for document to get the changed fields from the ruleset.
                        const proxyDocument = _self.setProxyForDocument(document, documentContext);
                        RuleHelper.processJsonRuleset(proxyDocument, documentContext, rulesetScriptFunc, ruleScope, function (result) {
                            _self.documentSessionService.setDocumentSession(documentId, documentContext.documentSession);
                            callback(result);
                        });
                    }
                }
                let rulesetCacheInfo = this.ruleCacheService.getRuleset(ruleFlowInfo.rulesetId);
                if (!rulesetCacheInfo || !(rulesetCacheInfo === null || rulesetCacheInfo === void 0 ? void 0 : rulesetCacheInfo.rulesetScriptFunc)) {
                    _self.clientRulesService.addIncludesToRuleset(ruleFlowInfo.ruleSetDSL, function (processedRuleFlowResult) {
                        if (processedRuleFlowResult.status === SharedConstants.STATUS_ERROR) {
                            callback(processedRuleFlowResult);
                        }
                        else {
                            const processedRuleFlow = processedRuleFlowResult.ruleFlow;
                            if (processedRuleFlow) {
                                // Parse the rulesetScript into a JSON object of functions
                                const ruleset = Function('return (' + processedRuleFlow + ')')();
                                _self.ruleCacheService.setRuleset(ruleFlowInfo.rulesetId, ruleset);
                                process(ruleset);
                            }
                            else {
                                callback();
                            }
                        }
                    });
                }
                else {
                    process(rulesetCacheInfo.rulesetScriptFunc);
                }
            }
        });
    }
    dispatchChangedAction(dispatchedChangedActions) {
        const fieldNames = Object.keys(dispatchedChangedActions);
        if (fieldNames.length) {
            fieldNames.forEach(fieldName => {
                const action = dispatchedChangedActions[fieldName];
                console.log('Dispatching onfieldchange for field: ' + fieldName + ' - value: ' + action.payload.documentChangedInfo.newValue);
                this.reduxStoreService.getStore().dispatch(action);
            });
        }
    }
    // attachChangedField is passed to setProxyForDocument
    attachChangedField(document, fldName, newValue, oldValue, documentContext) {
        var _a, _b;
        const documentId = document.documentId;
        if (this.isChangedTemplateFieldValue(fldName, documentContext.template)) {
            const currentChangedFields = documentContext.changedFields;
            const changedFields = documentContext.changedFields ? cloneDeep(currentChangedFields) : [];
            const sourceType = documentContext.changeSourceType;
            let shouldTrackChangeDoc = true; // 15618: change to false in order to change value in store without firing onfieldchange when setting document values from non-onfieldchange rules
            const isDocLoaded = this.unsavedDocumentService.documentIsLoaded(documentId);
            if (!isDocLoaded) {
                shouldTrackChangeDoc = false;
            }
            else {
                const eventName = documentContext.eventName;
                const onPreRenderName = SharedConstants.RULESET_RELATIONSHIPS.PRERENDER_EVENT_NAME.name;
                const onLoadName = SharedConstants.RULESET_RELATIONSHIPS.ONLOAD_EVENT_NAME.name;
                const onFieldChangeName = SharedConstants.RULESET_RELATIONSHIPS.ON_FIELD_CHANGE_EVENT_NAME.name;
                const isFromOnFieldChange = eventName === onFieldChangeName && documentContext.fieldChanged;
                const isFromHTMLEventListener = !sourceType && changedFields.length === 0 && (eventName === onPreRenderName || eventName === onLoadName);
                if (!isFromHTMLEventListener && !isFromOnFieldChange) {
                    shouldTrackChangeDoc = false;
                }
            }
            if (sourceType === ChangeSourceType.RULESET && (changedFields.includes(fldName) || documentContext.fieldChanged === fldName)) {
                //Stop the looping OnFieldChange on the current field
                return;
            }
            if (sourceType === ChangeSourceType.COMPONENT) {
                changedFields.push(documentContext.fieldChanged);
            }
            changedFields.push(fldName);
            console.log('Dispatching onfieldchange for field: ' + fldName + ' - value: ' + newValue);
            const documentChangedInfo = {
                documentId,
                fieldName: fldName,
                newValue: newValue,
                oldValue: oldValue,
                isInitValue: !shouldTrackChangeDoc,
                formParameters: documentContext.formParameters,
                sourceType: ChangeSourceType.RULESET,
                changedFields: changedFields
            };
            this.reduxStoreService.getStore().dispatch(formDocumentChanged(documentChangedInfo));
        }
        else {
            const documentChangedInfo = {
                documentId,
                fieldName: fldName,
                newValue: newValue,
                isInitValue: true,
                formParameters: documentContext.formParameters,
                sourceType: ChangeSourceType.RULESET,
                changedFields: documentContext.changedFields
            };
            this.reduxStoreService.getStore().dispatch(formDocumentChanged(documentChangedInfo));
        }
        if (this.unsavedDocumentService.documentIsLoaded(documentId)) {
            const component = ((_b = (_a = documentContext.template) === null || _a === void 0 ? void 0 : _a.components) === null || _b === void 0 ? void 0 : _b.filter(comp => (comp === null || comp === void 0 ? void 0 : comp.name) === fldName)[0]) || {};
            const unsavedDocumentListId = documentContext.formParameters.unsavedDocumentListId;
            if (Object.keys(component).length && !component.disableSave) {
                this.modifiedFieldService.addModifiedField(documentId, fldName, unsavedDocumentListId);
            }
        }
    }
    isChangedTemplateFieldValue(field, template) {
        let flag = false;
        for (let i = 0; i < template.components.length; i++) {
            if (template.components[i].name === field) {
                flag = true;
                break;
            }
        }
        return flag;
    }
}
/** @nocollapse */ /** @nocollapse */ RuleEngineService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: RuleEngineService, deps: [{ token: ClientRulesService }, { token: ConfigService }, { token: LocalStorageService }, { token: DocumentSessionService }, { token: ReduxStoreService }, { token: RuleCacheService }, { token: LatestDocumentService }, { token: OfflineStatusService }, { token: LoggedInUserService }, { token: ModifiedFieldService }, { token: UnsavedDocumentService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ RuleEngineService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: RuleEngineService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: RuleEngineService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: ClientRulesService }, { type: ConfigService }, { type: LocalStorageService }, { type: DocumentSessionService }, { type: ReduxStoreService }, { type: RuleCacheService }, { type: LatestDocumentService }, { type: OfflineStatusService }, { type: LoggedInUserService }, { type: ModifiedFieldService }, { type: UnsavedDocumentService }]; } });

class TabService {
    constructor() {
    }
    openNewDocument(templateId, initialData, newTab, callback) {
        if (!callback) {
            callback = function () {
            };
        }
        let target = '_blank';
        if ((newTab && newTab !== null && newTab === 'no') || newTab === false) {
            target = '_self';
        }
        const initialDataKey = 'initialData:' + templateId;
        localStorage.setItem(UrlBasePathUtil.basePath + initialDataKey, JSON.stringify(initialData));
        callback();
        window.open('form/' + templateId, target);
    }
}
/** @nocollapse */ /** @nocollapse */ TabService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: TabService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ TabService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: TabService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: TabService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

const logger$6 = console;
class ValidationService {
    constructor(documentService, unsavedDocumentService, notificationService, modifiedFieldService, ruleEngineService, loggedInUserService, currentDocumentService, configService, broadcastService, tabService, // do not remove. Used in ClientRuleContext
    clientAccessService, // do not remove. Used in ClientRuleContext
    appStore, keyValueStorageService) {
        this.documentService = documentService;
        this.unsavedDocumentService = unsavedDocumentService;
        this.notificationService = notificationService;
        this.modifiedFieldService = modifiedFieldService;
        this.ruleEngineService = ruleEngineService;
        this.loggedInUserService = loggedInUserService;
        this.currentDocumentService = currentDocumentService;
        this.configService = configService;
        this.broadcastService = broadcastService;
        this.tabService = tabService;
        this.clientAccessService = clientAccessService;
        this.appStore = appStore;
        this.keyValueStorageService = keyValueStorageService;
        this.subs = new Subscription();
        /***------------------- Exported data -------------------------***/
        this.validationCtrlValues = {};
        /***------------------- Local data ----------------------------***/
        this.eventList = {};
        this.pendingSave = null;
        this.queue = new Subject();
        /***------------------- End Rule context -------------------****/
        this.validateFormFields = (value) => {
            this.broadcastEvent(ClientConstants.VALIDATE_FORM_FIELDS, value);
        };
        const subDocData = this.currentDocumentService.documentData$.subscribe(data => {
            this.documentData = data;
        });
        const subUser = this.user$.subscribe((user) => this.user = user);
        const documentChangedSub = this.documentChangedInfo$.subscribe((documentChangedData) => {
            this.handleOnFieldChange(documentChangedData);
        });
        const results = this.queue.pipe(concatMap((rulesData) => {
            return this.executeDocumentEventPromise(rulesData);
        }));
        const ruleSub = results.subscribe((data) => {
            var _a, _b;
            if (data.rulesetCount) {
                logger$6.info(`%c${data === null || data === void 0 ? void 0 : data.rulesetCount} ${data === null || data === void 0 ? void 0 : data.eventName} rules done for document: ${(_a = data === null || data === void 0 ? void 0 : data.document) === null || _a === void 0 ? void 0 : _a.documentId}`, `color:green`);
            }
            this.appStore.dispatch(formSetRulesStatus((_b = data === null || data === void 0 ? void 0 : data.documentContext) === null || _b === void 0 ? void 0 : _b.templateId, ClientConstants.RULES_STATUS_DONE));
            data.callback(data.error, data.results);
        });
        const destroyedSub = broadcastService.on(ClientConstants.DOCUMENT_DESTROYED).subscribe(data => {
            this.destroyedDocumentData = data;
        });
        this.subs.add(subDocData);
        this.subs.add(subUser);
        this.subs.add(documentChangedSub);
        this.subs.add(ruleSub);
        this.subs.add(destroyedSub);
    }
    ngOnDestroy() {
        this.subs.unsubscribe();
    }
    /***------------------- Exported functions --------------------***/
    loadValidators(tplItem, fieldName) {
        const validators = [];
        const required = {
            'name': ClientConstants.VALIDATION_REQUIRED_KEY,
            'text': ClientConstants.VALIDATION_REQUIRED_MESSAGE
        };
        validators.push(required);
        const minLength = {
            'name': ClientConstants.VALIDATION_MIN_LENGTH_KEY,
            'text': ClientConstants.VALIDATION_MIN_LENGTH_MESSAGE.replace(/%s/g, tplItem.minLength)
        };
        validators.push(minLength);
        const maxLength = {
            'name': ClientConstants.VALIDATION_MAX_LENGTH_KEY,
            'text': ClientConstants.VALIDATION_MAX_LENGTH_MESSAGE.replace(/%s/g, tplItem.maxLength)
        };
        validators.push(maxLength);
        return validators;
    }
    waitPendingEvents(save) {
        logger$6.info('ModifiedFieldService.formDirty.validating ' + this.modifiedFieldService.formDirty.validating);
        if (this.modifiedFieldService.formDirty.validating) {
            this.modifiedFieldService.setShowSpinner(true);
            this.modifiedFieldService.setDirty(false);
            this.pendingSave = save;
        }
        else {
            save();
        }
    }
    updateDocumentToStore(prevDocument, document, ruleContext) {
        const diffs = UtilConflict.getDiffs(prevDocument, document);
        if (diffs && diffs.length) {
            this.dispatchStoreAction(formSetDocument(document));
        }
    }
    handleOnFieldChange(documentChangedData) {
        var _a;
        if (documentChangedData) {
            const state = this.appStore.getState().formState;
            const documentId = documentChangedData.documentId;
            const document = cloneDeep(state.documents[documentId]);
            const fieldName = documentChangedData.fieldName;
            const formParameters = documentChangedData.formParameters;
            const template = this.documentService.getloadedTemplateForDoc(document, formParameters);
            const component = ((_a = template === null || template === void 0 ? void 0 : template.components) === null || _a === void 0 ? void 0 : _a.filter(comp => comp.name === fieldName)[0]) || {};
            if (documentChangedData.shouldCompareValues !== false) {
                if (isEqual(documentChangedData.newValue, documentChangedData.oldValue) ||
                    (!UtilValidation.hasValue(documentChangedData.oldValue) &&
                        !UtilValidation.hasValue(documentChangedData.newValue))) { // first run.
                    return;
                }
                const isChanged = this.modifiedFieldService.isChangedField(documentId, fieldName, formParameters.unsavedDocumentListId);
                if (!isChanged && isEqual(documentChangedData.newValue, documentChangedData.oldValue)) {
                    return;
                }
            }
            if (Object.keys(component).length && !component.disableSave) {
                this.modifiedFieldService.addModifiedField(documentId, fieldName, formParameters.unsavedDocumentListId);
            }
            const orgValue = this.appStore.getState().formState.orgDocuments[documentId][fieldName];
            const docContext = {};
            docContext.fieldChanged = fieldName;
            docContext.originValue = orgValue;
            docContext.newValue = documentChangedData.newValue;
            docContext.oldValue = documentChangedData.oldValue;
            docContext.templateId = template.documentId;
            docContext.formParameters = formParameters;
            docContext.template = template;
            docContext.databaseMode = this.configService.clientConfig().defaultSearchProvider;
            docContext.changeSourceType = documentChangedData.sourceType;
            docContext.changedFields = documentChangedData.changedFields;
            this.validateChangedField(component, document, template, documentChangedData.newValue, docContext);
        }
    }
    executeOnFieldChangeWithNoChanges(documentChangedInfo) {
        this.handleOnFieldChange(documentChangedInfo);
    }
    executeOnFieldChange(document, docContext) {
        if (this.modifiedFieldService.formDirty.dirty) {
            const key = `FB-CORE-WIP-${document.documentId}`;
            this.keyValueStorageService.setItem(key, { document, updated: new Date() });
        }
        const _self = this;
        const currentValidating = new Date();
        _self.modifiedFieldService.formDirty.validating = currentValidating;
        _self.executeDocumentEvent(SharedConstants.RULESET_RELATIONSHIPS.ON_FIELD_CHANGE_EVENT_NAME.name, document, docContext, function (error, result) {
            if (result) {
                _self.broadcastValidationEvent();
            }
            if (_self.modifiedFieldService.formDirty.validating === currentValidating) {
                if (_self.pendingSave !== null) {
                    _self.pendingSave();
                }
                _self.modifiedFieldService.formDirty.validating = null;
                _self.pendingSave = null;
            }
        });
    }
    validateChangedField(component, document, template, newValue, docContext) {
        const fieldName = component.name;
        this.initFieldValidator(document, template, component, fieldName, newValue);
        this.executeOnFieldChange(document, docContext);
    }
    validateChangedFieldValues(scope, originValue, newValue, oldValue) {
        const tplField = scope.tplItem;
        const fieldName = scope.fieldName;
        const document = scope.doc;
        const template = scope.tpl;
        this.initFieldValidator(document, template, tplField, fieldName, newValue);
        const docContext = {};
        docContext.fieldChanged = fieldName;
        docContext.originValue = originValue;
        docContext.newValue = newValue;
        docContext.oldValue = oldValue;
        docContext.templateId = scope.tplId;
        docContext.formParameters = scope.formParameters;
        docContext.template = scope.tpl;
        docContext.databaseMode = this.configService.clientConfig().defaultSearchProvider;
        this.executeOnFieldChange(scope.doc, docContext);
    }
    preRenderDocument(document, docContext, callback) {
        this.executeDocumentEvent(SharedConstants.RULESET_RELATIONSHIPS.PRERENDER_EVENT_NAME.name, document, docContext, callback);
    }
    onLoadDocument(document, docContext, callback) {
        const template = docContext.template;
        // reset validation values due to template/document OnLoad
        this.validationCtrlValues[document.documentId] = {
            templateId: template.documentId
        };
        this.executeDocumentEvent(SharedConstants.RULESET_RELATIONSHIPS.ONLOAD_EVENT_NAME.name, document, docContext, callback);
    }
    doAuthRules(ruleName, document, docContext, callback) {
        var _a, _b;
        docContext = docContext || {};
        const templateId = (_a = document === null || document === void 0 ? void 0 : document.systemHeader) === null || _a === void 0 ? void 0 : _a.templateId;
        if (!templateId) {
            callback(ruleName + " no template for " + document.documentId);
        }
        docContext.templateId = templateId;
        docContext.formParameters = { unsavedDocumentListId: this.unsavedDocumentService.createUnsavedDocumentList() };
        this.documentService.getDocumentOnly((_b = document === null || document === void 0 ? void 0 : document.systemHeader) === null || _b === void 0 ? void 0 : _b.templateId).then(template => {
            docContext.template = template;
            this.executeDocumentEvent(ruleName, document, docContext, callback);
        }).catch(error => {
            var _a;
            if (error.name === UtilError.FORBIDDEN_ERROR) {
                logger$6.warn('Current user doesn\'t have access to account template to execute login rules. Will skip execution.');
                callback();
                return;
            }
            if (error.name === UtilError.NOT_FOUND_ERROR) {
                logger$6.warn(`Template ${(_a = document === null || document === void 0 ? void 0 : document.systemHeader) === null || _a === void 0 ? void 0 : _a.templateId} not found to execute login rules. Will skip execution.`);
                callback();
                return;
            }
            callback("Unable to run " + ruleName + " due to error: " + error.message);
        });
    }
    doPostLoginClient(document, docContext, callback) {
        this.doAuthRules(SharedConstants.RULESET_RELATIONSHIPS.POSTLOGIN_CLIENT_EVENT_NAME.name, document, docContext, callback);
    }
    doPreLogoutClient(document, docContext, callback) {
        this.doAuthRules(SharedConstants.RULESET_RELATIONSHIPS.PRELOGOUT_CLIENT_EVENT_NAME.name, document, docContext, callback);
    }
    onPreSaveDocument(document, docContext, callback) {
        if (!docContext.formParameters) {
            const unsavedDocumentListId = docContext.unsavedDocumentListId;
            const hierarchyInfo = this.unsavedDocumentService.getHierarchyInfo(unsavedDocumentListId, document.documentId);
            docContext.formParameters = {};
            docContext.formParameters.hierarchyInfo = hierarchyInfo;
            docContext.formParameters.unsavedDocumentListId = unsavedDocumentListId;
        }
        this.executeDocumentEvent(SharedConstants.RULESET_RELATIONSHIPS.PRESAVE_EVENT_NAME.name, document, docContext, callback);
    }
    onPostSaveClientDocument(document, docContext, callback) {
        if (!docContext.formParameters) {
            const unsavedDocumentListId = docContext.unsavedDocumentListId;
            const hierarchyInfo = this.unsavedDocumentService.getHierarchyInfo(unsavedDocumentListId, document.documentId);
            docContext.formParameters = {};
            docContext.formParameters.hierarchyInfo = hierarchyInfo;
            docContext.formParameters.unsavedDocumentListId = unsavedDocumentListId;
        }
        this.executeDocumentEvent(SharedConstants.RULESET_RELATIONSHIPS.POSTSAVE_CLIENT_EVENT_NAME.name, document, docContext, callback);
    }
    broadcastValidationEvent() {
        this.broadcastEvent(SharedConstants.EVENT_VALIDATION_VALUES_NAME, this.validationCtrlValues);
    }
    broadcastEvent(eventName, eventValue) {
        this.broadcastService.broadcast(eventName, eventValue);
    }
    dispatchStoreAction(action) {
        this.appStore.dispatch(action);
    }
    getTemplateFromStore(templateId) {
        return cloneDeep(this.appStore.getState().formState.templates[templateId]);
    }
    getDocumentFromStore(documentId) {
        return cloneDeep(this.appStore.getState().formState.documents[documentId]);
    }
    /***---------------------- Helper functions -----------------***/
    initFieldValidator(document, template, tplField, fieldName, fieldValue) {
        this.initValidationValue(document, template, fieldName);
        if (tplField.mandatory === true) {
            this.setValidationValue(document, fieldName, 'required', !UtilValidation.hasValue(fieldValue));
        }
        if (tplField.minLength) {
            this.setValidationValue(document, fieldName, 'hasMinLength', !UtilValidation.validateMinLength(fieldValue, tplField.minLength));
        }
        if (tplField.maxLength) {
            this.setValidationValue(document, fieldName, 'hasMaxLength', !UtilValidation.validateMaxLength(fieldValue, tplField.maxLength));
        }
    }
    executeRuleSet(eventName, processDoc, documentContext, eventRule) {
        return __awaiter(this, void 0, void 0, function* () {
            const _self = this;
            return new Promise((resolve, reject) => {
                documentContext.eventName = eventName;
                const ruleContext = new RuleContext(_self, eventName, processDoc, documentContext);
                _self.ruleEngineService.processRuleFlow(processDoc, documentContext, eventRule, ruleContext, function (result) {
                    const msg = result ? result.message : null;
                    const status = result ? result.status : SharedConstants.STATUS_SUCCESS;
                    if (msg) {
                        _self.notificationService.printMessage(msg, status, null, 'toast-top-right', false);
                    }
                    if (status && status.toLowerCase() === SharedConstants.STATUS_ERROR) {
                        reject(result);
                    }
                    else {
                        switch (eventName) {
                            case SharedConstants.RULESET_RELATIONSHIPS.ONLOAD_EVENT_NAME.name:
                                _self.broadcastEvent(SharedConstants.ONLOAD_DONE_RULES_EVENT_NAME, true);
                                break;
                        }
                        // _self.updateDocumentToStore(prevDocument, processDoc, documentContext);
                        resolve(result);
                    }
                });
            });
        });
    }
    shouldRunRules(rulesData) {
        var _a, _b, _c, _d;
        const _self = this;
        const eventName = rulesData.eventName;
        const documentContext = rulesData.documentContext;
        if (SharedConstants.RULESET_RELATIONSHIPS.ON_FIELD_CHANGE_EVENT_NAME.name === eventName && !this.isExistsChangedFieldInRulesetTriggeredOn(eventName, documentContext)) {
            return false;
        }
        const document = rulesData.document;
        const unsavedDocumentListId = (_b = (_a = rulesData.documentContext) === null || _a === void 0 ? void 0 : _a.formParameters) === null || _b === void 0 ? void 0 : _b.unsavedDocumentListId;
        if (((_c = _self.destroyedDocumentData) === null || _c === void 0 ? void 0 : _c.documentId) === document.documentId && ((_d = _self.destroyedDocumentData) === null || _d === void 0 ? void 0 : _d.unsavedDocumentListId) === unsavedDocumentListId) {
            _self.destroyedDocumentData = null;
            const msg = `%cDocument: ${document.documentId} has been unloaded on form. Terminate processing ${eventName} rules.`;
            logger$6.info(msg, `color:green`);
            return false;
        }
        return true;
    }
    executeDocumentEventPromise(rulesData) {
        var _a, _b;
        const _self = this;
        const eventName = rulesData.eventName;
        let document = rulesData.document ? rulesData.document : {};
        const documentContext = rulesData.documentContext;
        const changedField = documentContext.fieldChanged;
        const newValue = documentContext.newValue;
        const template = documentContext.template;
        const templateId = documentContext.templateId;
        if (documentContext.isNew === undefined) {
            const unsavedDocumentListId = documentContext.unsavedDocumentListId ? documentContext.unsavedDocumentListId : (_a = documentContext.formParameters) === null || _a === void 0 ? void 0 : _a.unsavedDocumentListId;
            if (((_b = document.systemHeader) === null || _b === void 0 ? void 0 : _b.serverUpdatedDate) && eventName !== SharedConstants.RULESET_RELATIONSHIPS.POSTSAVE_CLIENT_EVENT_NAME.name) {
                documentContext.isNew = false;
            }
            else if (unsavedDocumentListId) {
                const documentInfoItem = _self.unsavedDocumentService.getDocumentInfoItem(unsavedDocumentListId, document === null || document === void 0 ? void 0 : document.documentId);
                documentContext.isNew = (documentInfoItem === null || documentInfoItem === void 0 ? void 0 : documentInfoItem.isNew) === true;
            }
            else if (documentContext.documentData) {
                documentContext.isNew = documentContext.documentData.isNew;
            }
        }
        const changedFieldMsg = ` - changed field: ${changedField} - value: ${newValue}`;
        const msg = `%cChecking for and processing ${eventName} rules for document: ${document.documentId} ${changedField ? changedFieldMsg : ''}`;
        logger$6.info(msg, `color:green`);
        return new Promise((resolve) => {
            if (_self.shouldRunRules(rulesData) === false) {
                resolve(rulesData);
                return;
            }
            this.appStore.dispatch(formSetRulesStatus(templateId, ClientConstants.RULES_STATUS_STARTED));
            _self.loadRule(eventName, template).then(function successFunc(rulesets) {
                rulesData.rulesetCount = rulesets.length;
                if (!rulesets.length) {
                    resolve(rulesData);
                    return;
                }
                function processEvent(user, accountControl) {
                    documentContext.user = user ? user : {};
                    documentContext.accountControl = accountControl;
                    _self.unsavedDocumentService.setParentInfo(documentContext);
                    _self.unsavedDocumentService.setChildInfo(documentContext);
                    function asyncJob(ruleset) {
                        if (ruleset) {
                            const state = _self.appStore.getState().formState;
                            document = state.documents[document.documentId] ? state.documents[document.documentId] : document;
                            return _self.executeRuleSet(eventName, document, documentContext, ruleset);
                        }
                    }
                    UtilSequenceProcess.processList(rulesets, asyncJob).then(function allPromiseSuccessFunc(results) {
                        rulesData.results = results;
                        resolve(rulesData);
                    }, function errorFunc(err) {
                        rulesData.error = err;
                        resolve(rulesData);
                        if (eventName === SharedConstants.RULESET_RELATIONSHIPS.PRESAVE_EVENT_NAME.name) {
                            _self.broadcastEvent(ClientConstants.PRESAVE_DOCUMENT_FORM_RESET, {
                                document: document,
                                unsavedDocumentListId: documentContext.unsavedDocumentListId
                            });
                        }
                    });
                }
                processEvent(_self.user.account, _self.user.accountControlDocument);
            }, function errorFunc(err) {
                logger$6.error('Load rule error: ' + err);
                rulesData.error = err;
                resolve(rulesData);
            });
        });
    }
    executeDocumentEvent(eventName, document, documentContext, callback) {
        var _a, _b;
        documentContext.isChildDoc = !!((_b = (_a = documentContext.formParameters) === null || _a === void 0 ? void 0 : _a.hierarchyInfo) === null || _b === void 0 ? void 0 : _b.parentDocumentId);
        const rulesData = {
            eventName,
            document,
            documentContext,
            callback
        };
        this.queue.next(rulesData);
    }
    setParentTplDoc(documentContext, document) {
        let unsavedDocumentListId = documentContext.formParameters.unsavedDocumentListId;
        if (!unsavedDocumentListId) {
            unsavedDocumentListId = documentContext.unsavedDocumentListId;
        }
        const result = this.unsavedDocumentService.getParentData(unsavedDocumentListId, document.documentId);
        documentContext.parentDocument = result.parentDoc;
        documentContext.parentTemplate = result.parentTpl;
    }
    setChildTplDoc(documentContext, document) {
        let unsavedDocumentListId = documentContext.formParameters.unsavedDocumentListId;
        if (!unsavedDocumentListId) {
            unsavedDocumentListId = documentContext.unsavedDocumentListId;
        }
        const result = this.unsavedDocumentService.getChildData(unsavedDocumentListId, document.documentId);
        documentContext.childDocument = result.childDoc;
        documentContext.childTemplate = result.childTpl;
    }
    loadRulesetById(docId, ruleName) {
        const _self = this;
        return new Promise((resolve, reject) => {
            _self.documentService.getDocumentOnly(docId).then(function successFunc(doc) {
                resolve({
                    rulesetId: docId,
                    ruleSetDSL: doc.ruleSetDSL
                });
            }, function errorFunc(err) {
                let msg = ruleName + ' ruleset: ' + docId + ' not found due to error: ';
                msg += err && err.message ? err.message : err;
                if (ruleName === SharedConstants.RULESET_RELATIONSHIPS.ONLOAD_EVENT_NAME.name ||
                    ruleName === SharedConstants.RULESET_RELATIONSHIPS.PRESAVE_EVENT_NAME.name) {
                    reject(new Error(msg));
                }
                else {
                    _self.notificationService.printMessage(msg, SharedConstants.STATUS_WARNING, null, { x: 'right', y: 'top' });
                    // do not break the chain in order to load the next ruleset document
                    resolve(true);
                }
            });
        });
    }
    loadRule(ruleName, template) {
        const _self = this;
        return new Promise((resolve, reject) => {
            if (template) {
                const relName = UtilRules.getRelatedName(ruleName);
                const rulesetRelValues = template[relName];
                if (rulesetRelValues) {
                    logger$6.info('Using number of related rulesets: ' + rulesetRelValues.length);
                    const promises = [];
                    rulesetRelValues.forEach(function (obj) {
                        promises.push(_self.loadRulesetById(obj.documentId, ruleName));
                    });
                    Promise.all(promises).then(function successFunc(ruleFlows) {
                        logger$6.info('Found related rulesets: ' + ruleFlows.length);
                        resolve(ruleFlows);
                    }, function errorFunc(err) {
                        logger$6.warn('Not found related rulesets.');
                        reject(err);
                    });
                }
                else {
                    const rules = template.rules;
                    const foundRuleset = [];
                    if (rules && rules.length) {
                        for (let rId = 0; rId < rules.length; rId++) {
                            if (rules[rId].name === ruleName) {
                                logger$6.warn('DEPRECATED: please use the ruleset related document structure ' +
                                    'in the rules definition instead of having the ruleFlow script directly ' +
                                    'in template: ' + template.documentId);
                                foundRuleset.push(rules[rId].ruleFlow);
                            }
                        }
                    }
                    resolve(foundRuleset);
                }
            }
            else {
                reject("No template provided for loadRule.");
            }
        });
    }
    initValidationValue(document, template, field) {
        const affectedDocId = document.documentId;
        if (!this.validationCtrlValues[affectedDocId]) {
            this.validationCtrlValues[affectedDocId] = {
                templateId: template.documentId
            };
        }
        if (!this.validationCtrlValues[affectedDocId][field]) {
            this.validationCtrlValues[affectedDocId][field] = {};
        }
    }
    setValidationValue(affectedDoc, field, name, flag) {
        if (flag !== undefined) {
            // use string values of "yes" and "true" so that they can be used inside the rules.
            const result = (flag === 'yes' || flag === 'true' || flag === true);
            if (this.validationCtrlValues[affectedDoc.documentId] &&
                this.validationCtrlValues[affectedDoc.documentId][field]) {
                if (this.validationCtrlValues[affectedDoc.documentId][field][name] !== result) {
                    this.validationCtrlValues[affectedDoc.documentId][field][name] = result;
                    this.validateFormFields({
                        documentId: affectedDoc.documentId,
                        fieldName: field,
                        validateName: name
                    });
                }
            }
        }
    }
    isExistsChangedFieldInRulesetTriggeredOn(eventName, documentContext) {
        let isChangedFieldFound = false;
        if (SharedConstants.RULESET_RELATIONSHIPS.ON_FIELD_CHANGE_EVENT_NAME.name === eventName) {
            const rulesetRelValues = documentContext.template[SharedConstants.RULESET_RELATIONSHIPS.ON_FIELD_CHANGE_EVENT_NAME.relName];
            if (rulesetRelValues && rulesetRelValues.length > 0) {
                for (let i = 0; i < rulesetRelValues.length; i++) {
                    const ruleObj = rulesetRelValues[i];
                    isChangedFieldFound = !ruleObj.triggeredOn || includes(ruleObj.triggeredOn, documentContext.fieldChanged);
                    if (isChangedFieldFound) {
                        break;
                    }
                }
            }
        }
        return isChangedFieldFound;
    }
}
/** @nocollapse */ /** @nocollapse */ ValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ValidationService, deps: [{ token: DocumentService }, { token: UnsavedDocumentService }, { token: NotificationService }, { token: ModifiedFieldService }, { token: RuleEngineService }, { token: LoggedInUserService }, { token: CurrentDocumentService }, { token: ConfigService }, { token: BroadcastService }, { token: TabService }, { token: ClientAccessService }, { token: AppStore }, { token: KeyValueStorageService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ ValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ValidationService, providedIn: 'root' });
__decorate([
    select(['formState', 'documentChangedInfo']),
    __metadata("design:type", Observable)
], ValidationService.prototype, "documentChangedInfo$", void 0);
__decorate([
    select(['userState', 'user']),
    __metadata("design:type", Observable)
], ValidationService.prototype, "user$", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: DocumentService }, { type: UnsavedDocumentService }, { type: NotificationService }, { type: ModifiedFieldService }, { type: RuleEngineService }, { type: LoggedInUserService }, { type: CurrentDocumentService }, { type: ConfigService }, { type: BroadcastService }, { type: TabService }, { type: ClientAccessService }, { type: AppStore }, { type: KeyValueStorageService }]; }, propDecorators: { documentChangedInfo$: [], user$: [] } });

class ChangedDocumentService {
    constructor(appStore, validationService, latestDocumentService) {
        this.appStore = appStore;
        this.validationService = validationService;
        this.latestDocumentService = latestDocumentService;
    }
    dispatchAction(action) {
        this.appStore.dispatch(action);
    }
    /**
     * Provide the change information for the specified document field that the component binds to
     * @param component the component binds to the field
     * @param newValue the changed/new value
     * @param controlName the registered control manages the field value
     * @param options the options
     */
    valueChanged(component, newValue, controlName, options) {
        var _a;
        const fieldName = component === null || component === void 0 ? void 0 : component.fieldName;
        if (!fieldName) {
            const msg = `Name not specified in component definition of component ${(_a = component === null || component === void 0 ? void 0 : component.componentDefinition) === null || _a === void 0 ? void 0 : _a.componentName} for value: ${JSON.stringify(newValue)}. Please ensure the components entry has a name in the component definition.`;
            console.error(msg);
            throw new Error(msg);
        }
        const documentChangedInfo = {
            documentId: component.document.documentId,
            fieldName: fieldName,
            newValue: newValue,
            formParameters: component.formParameters,
            // default source value change is made from the component
            sourceType: (options === null || options === void 0 ? void 0 : options.sourceType) ? options === null || options === void 0 ? void 0 : options.sourceType : ChangeSourceType.COMPONENT,
            controlName: controlName
        };
        this.appStore.dispatch(formDocumentChanged(documentChangedInfo));
    }
    executeOnFieldChangeWithNoChanges(component) {
        const documentChangedInfo = {
            documentId: component.document.documentId,
            fieldName: component.fieldName,
            newValue: null,
            formParameters: component.formParameters,
            // default source value change is made from the component
            sourceType: ChangeSourceType.COMPONENT,
            shouldCompareValues: false
        };
        this.validationService.executeOnFieldChangeWithNoChanges(documentChangedInfo);
    }
    resetChangeDocument() {
        this.appStore.dispatch(formResetDocumentChanged());
    }
    notifyIntializedField(component, shouldDispatchEmptyFieldValue) {
        // const fieldName = component?.fieldName;
        // if (!fieldName) {
        //   return;
        // }
        // const unsavedDocumentListId = component?.formParameters?.unsavedDocumentListId;
        // const documentId = component?.document?.documentId;
        // const fieldValue = shouldDispatchEmptyFieldValue !== false ? component?.fieldValue : undefined;
        // this.appStore.dispatch(formUpdateInitDocumentData(unsavedDocumentListId, documentId, fieldName, fieldValue));
    }
    isChangedFromTheSameSource(stateChanges, fieldName, documentId, controlName) {
        const changedSourceInfo = stateChanges === null || stateChanges === void 0 ? void 0 : stateChanges.changedSourceInfo;
        return changedSourceInfo
            && changedSourceInfo.type === ChangeSourceType.COMPONENT
            && changedSourceInfo.fieldName === fieldName
            && changedSourceInfo.documentId === documentId
            && changedSourceInfo.controlName === controlName;
    }
    /**
     * Listen value changes on a specified document field
     * @param fieldName the field name of document
     * @param documentId the document id
     * @param controlName the registered control that holds the field value in this component. It is used to check on the source of change
     * to avoid applying changes itself and also applying the value from the components that share the field name.
     * @param options provides more information for the watch.
     */
    watch(fieldName, documentId, controlName, options) {
        return this.recordedStateChanges$.pipe(filter((stateChanges) => {
            var _a, _b;
            const docExists = this.appStore.getState().formState.documents[documentId] && ((_a = stateChanges === null || stateChanges === void 0 ? void 0 : stateChanges.changedSourceInfo) === null || _a === void 0 ? void 0 : _a.documentId) === documentId;
            const con1 = (_b = stateChanges === null || stateChanges === void 0 ? void 0 : stateChanges.changedSourceInfo) === null || _b === void 0 ? void 0 : _b.resetDocument;
            const con2 = fieldName === (stateChanges === null || stateChanges === void 0 ? void 0 : stateChanges.changedSourceInfo.fieldName)
                && (!this.isChangedFromTheSameSource(stateChanges, fieldName, documentId) || (options === null || options === void 0 ? void 0 : options.forceWatch));
            return docExists && (con1 || con2);
        }), map((stateChanges) => {
            return this.appStore.getState().formState.documents[documentId][fieldName];
        }));
    }
    watchComponentTemplate(templateId) {
        return this.rulesProcessingData$.pipe(filter((rulesProcessingData) => {
            return this.appStore.getState().formState.templates[templateId] &&
                rulesProcessingData[templateId] && rulesProcessingData[templateId].status === ClientConstants.RULES_STATUS_DONE;
        }), map((rulesProcessingData) => {
            return this.appStore.getState().formState.templates[templateId].components;
        }));
    }
    watchComponentDef(fieldName, templateId, componentName) {
        return this.recordedComponentTemplateChanges$.pipe(filter((componentDefChanges) => {
            const hasChangedFieldName = componentDefChanges[templateId] && componentDefChanges[templateId][fieldName];
            return !!componentName ? componentDefChanges[templateId][fieldName].componentName === componentName : hasChangedFieldName;
        }), map((componentDefChanges) => {
            const components = this.appStore.getState().formState.templates[templateId].components;
            for (let i = 0; i < components.length; i++) {
                if (components[i].name === fieldName && (!componentName || componentName === components[i].componentName)) {
                    return components[i];
                }
            }
        }));
    }
    watchDocument(documentId) {
        return this.recordedStateChanges$.pipe(filter((stateChanges) => {
            return this.appStore.getState().formState.documents[documentId] &&
                (stateChanges === null || stateChanges === void 0 ? void 0 : stateChanges.changedSourceInfo.documentId) === documentId;
        }), map((stateChanges) => {
            const document = this.appStore.getState().formState.documents[documentId];
            if (document === null || document === void 0 ? void 0 : document.documentId) {
                this.latestDocumentService.latestDocuments[document.documentId] = document;
            }
            return document;
        }));
    }
    watchStoreChanges() {
        return this.recordedStateChanges$;
    }
    watchComponentStateChanges(templateId) {
        return this.rulesProcessingData$.pipe(filter((rulesProcessingData) => {
            return rulesProcessingData[templateId] && rulesProcessingData[templateId].status === ClientConstants.RULES_STATUS_DONE;
        }), map((rulesProcessingData) => {
            return this.appStore.getState().formState.recordedComponentTemplateChanges;
        }));
    }
    notifyDocumentChanged(documentChangedInfo) {
        this.dispatchAction(formDocumentChanged(documentChangedInfo));
    }
    notifyComponentDefinitionUpdated(templateId, fieldName, key, componentDefinition) {
        this.dispatchAction(formUpdateComponentDefinition(templateId, fieldName, key, componentDefinition));
    }
    setOrgDocument(document) {
        this.dispatchAction(formSetOrgDocument(document));
    }
    setDocumentInfo(unsavedDocumentListId, docInfo) {
        this.dispatchAction(formSetDocumentInfo(unsavedDocumentListId, docInfo));
    }
    setDocument(document) {
        this.dispatchAction(formSetDocument(document));
    }
    setComponentFlagTemplate(templateId, fieldName, flagName, flagValue) {
        this.dispatchAction(formSetComponentFlagTemplate(templateId, fieldName, flagName, flagValue));
    }
    documentLoaded(documentId, unsavedDocumentListId, isLoaded) {
        this.dispatchAction(formDocumentLoaded(documentId, unsavedDocumentListId, isLoaded));
    }
    isLoadedDocument(documentId, unsavedDocumentListId) {
        var _a;
        const docInfo = (_a = this.appStore.getState().formState) === null || _a === void 0 ? void 0 : _a.documentInfo[unsavedDocumentListId];
        return docInfo && docInfo[documentId] && docInfo[documentId].loaded;
    }
}
/** @nocollapse */ /** @nocollapse */ ChangedDocumentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ChangedDocumentService, deps: [{ token: AppStore }, { token: ValidationService }, { token: LatestDocumentService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ ChangedDocumentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ChangedDocumentService, providedIn: 'root' });
__decorate([
    select(['formState', 'resetDocument']),
    __metadata("design:type", Observable)
], ChangedDocumentService.prototype, "resetDocument$", void 0);
__decorate([
    select(['formState', 'documents']),
    __metadata("design:type", Observable)
], ChangedDocumentService.prototype, "documents$", void 0);
__decorate([
    select(['formState', 'recordedStateChanges']),
    __metadata("design:type", Observable)
], ChangedDocumentService.prototype, "recordedStateChanges$", void 0);
__decorate([
    select(['formState', 'recordedComponentTemplateChanges']),
    __metadata("design:type", Observable)
], ChangedDocumentService.prototype, "recordedComponentTemplateChanges$", void 0);
__decorate([
    select(['formState', 'rulesProcessingData']),
    __metadata("design:type", Observable)
], ChangedDocumentService.prototype, "rulesProcessingData$", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ChangedDocumentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: AppStore }, { type: ValidationService }, { type: LatestDocumentService }]; }, propDecorators: { resetDocument$: [], documents$: [], recordedStateChanges$: [], recordedComponentTemplateChanges$: [], rulesProcessingData$: [] } });

class CurrentChildDocumentService {
    constructor(appStore, documentService) {
        this.appStore = appStore;
        this.documentService = documentService;
    }
}
/** @nocollapse */ /** @nocollapse */ CurrentChildDocumentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CurrentChildDocumentService, deps: [{ token: AppStore }, { token: DocumentService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ CurrentChildDocumentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CurrentChildDocumentService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CurrentChildDocumentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: AppStore }, { type: DocumentService }]; } });

const logger$5 = console;
class TemplateHtmlService {
    constructor(factoryResolver) {
        this.factoryResolver = factoryResolver;
    }
    /**
     * add the component to the view as a custom element
     */
    appendComponent(componentDefinition, documentData, formParameters, user, key, vcr, dynamicContainerComponent) {
        if (componentDefinition.componentName) {
            const factory = this.factoryResolver.resolveComponentFactory(dynamicContainerComponent);
            const component = factory.create(vcr.parentInjector);
            component.instance.formParameters = formParameters;
            component.instance.tplItem = componentDefinition;
            component.instance.documentData = documentData;
            component.instance.key = key;
            component.instance.user = user.filteredAccount;
            vcr.insert(component.hostView);
            // render a not found component if the component does not exist
            // const componentName = componentDefinition.componentName
            // const customElement = renderer.createElement(componentName);
            //
            // // pass the parameters of the component to the custom element
            // customElement.componentDefinition = componentDefinition;
            // customElement.document = documentData.document;
            // customElement.template = documentData.template;
            // customElement.formParameters = formParameters;
            // customElement.key = key;
            // customElement.account = user.filteredAccount;
            // customElement.fieldName = componentDefinition.name;
            // if (documentData.document) {
            //     customElement.fieldValue = documentData.document[componentDefinition.name];
            // }
            //
            // // insertBefore element to container
            // renderer.appendChild(el.nativeElement, customElement);
        }
    }
}
/** @nocollapse */ /** @nocollapse */ TemplateHtmlService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: TemplateHtmlService, deps: [{ token: ComponentFactoryResolver }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ TemplateHtmlService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: TemplateHtmlService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: TemplateHtmlService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [ComponentFactoryResolver]
                    }] }];
    } });

class ComponentService {
    constructor(http, notificationService, templateHtmlService, configService, loggedInUserService) {
        this.http = http;
        this.notificationService = notificationService;
        this.templateHtmlService = templateHtmlService;
        this.configService = configService;
        this.loggedInUserService = loggedInUserService;
        this.loadedComponentDocs = {};
        this.loadedComponents = {};
        this.loadedVendorLibs = {};
    }
    loadVendorLibs(vendorLibs) {
        return __awaiter(this, void 0, void 0, function* () {
            const vendorLibraryBasePath = this.configService.clientConfig().vendorLibraryBasePath || '';
            return new Promise((resolve, reject) => {
                async.eachSeries(vendorLibs, (fileName, next) => {
                    if (!this.loadedVendorLibs[fileName]) {
                        const item = vendorLibs[fileName];
                        fileName = fileName.replace('${vendorLibraryBasePath}', vendorLibraryBasePath);
                        loadjs([fileName], {
                            returnPromise: true
                        }).then(() => {
                            this.loadedVendorLibs[fileName] = true;
                            next(null);
                        }, (err) => {
                            next();
                            let offlineMsg = "";
                            if (this.loggedInUserService.getUserConfigItem('cachingEnabled') === true) {
                                offlineMsg = ' Please ensure that the library is configured for offline use ' +
                                    'by setting offline keys on the vendor library document';
                            }
                            const msg = `Error loading component vendorLibrary: ${fileName}.${offlineMsg}`;
                            console.log(msg);
                            this.notificationService.error(msg);
                        });
                    }
                    else {
                        next();
                    }
                }, function (errorMsg) {
                    if (errorMsg) {
                        reject(new Error(errorMsg));
                    }
                    resolve();
                });
            });
        });
    }
    loadTemplateVendorLibs(template) {
        return __awaiter(this, void 0, void 0, function* () {
            if (template && template.vendorLibrariesRel) {
                const templateVendorLibs = this.getFileNamesFromVendorLib(template.vendorLibrariesRel);
                yield this.loadVendorLibs(templateVendorLibs);
            }
        });
    }
    registerComponents(componentDefs, documentData, formParameters, user, vcr, dynamicContainerComponent) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!componentDefs) {
                return Promise.resolve();
            }
            const _self = this;
            let key = 0;
            async.eachSeries(componentDefs, (componentDef, next) => {
                const directiveName = componentDef.componentName;
                if (directiveName && !startsWith(directiveName, 'ft-') && !startsWith(directiveName, 'cc-')) {
                    const componentName = componentDef.componentName;
                    const promise = _self.loadComponentDocAndVendorLibs(componentName).then(function successFunc() {
                        componentDef.componentName = _self.componentDocExists(componentDef.componentName) ?
                            componentDef.componentName : 'ft-component-error';
                        _self.templateHtmlService.appendComponent(componentDef, documentData, formParameters, user, key, vcr, dynamicContainerComponent);
                        key++;
                        next();
                    });
                }
            }, function (errorMsg) {
                if (errorMsg) {
                    return Promise.reject(new Error(errorMsg));
                }
                return Promise.resolve();
            });
        });
    }
    getFileNamesFromVendorLib(vendorLibrariesRel) {
        const fileNames = [];
        if (vendorLibrariesRel && vendorLibrariesRel.length) {
            vendorLibrariesRel.forEach((vendorLib) => {
                if (vendorLib.fileName) {
                    const fileName = vendorLib.fileName;
                    if (!isEmpty(fileName) && !this.loadedVendorLibs[fileName]) {
                        fileNames.push(fileName);
                    }
                }
                else if (vendorLib.fileNames) {
                    fileNames.push(...vendorLib.fileNames);
                }
            });
        }
        return fileNames;
    }
    /**
     * Load component document
     * @param directiveName the directive name
     * @returns the promise of component doc
     */
    loadComponentDoc(directiveName) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = 'api/loadComponentDoc/' + directiveName;
            try {
                const result = yield this.http.get(url).toPromise();
                if (result === 404) {
                    throw new Error(`Component ${directiveName} not found.`);
                }
                return result;
            }
            catch (err) {
                throw new Error(err.message || 'Something went wrong when getting custom component document.');
            }
        });
    }
    /**
     * whether the component has been loaded
     * @param directiveName the directive name
     * @returns the result
     */
    componentExists(directiveName) {
        return this.loadedComponents[directiveName];
    }
    /**
     * whether the component doc has been loaded
     * @param directiveName the directive name
     * @returns the result
     */
    componentDocExists(directiveName) {
        return !!this.loadedComponentDocs[directiveName];
    }
    loadComponentDocAndVendorLibs(componentName) {
        const _self = this;
        return new Promise((resolve, reject) => {
            if (!_self.componentDocExists(componentName)) {
                _self.loadComponentDoc(componentName).then(function successFunc(componentDoc) {
                    _self.loadedComponentDocs[componentName] = componentDoc;
                    const compVendorLibRels = componentDoc.vendorLibrariesRel;
                    if (compVendorLibRels && compVendorLibRels.length) {
                        const compVendorLibRel = componentDoc.vendorLibrariesRel;
                        const compVendorLibs = _self.getFileNamesFromVendorLib(compVendorLibRel);
                        _self.loadVendorLibs(compVendorLibs).then(function sFunc() {
                            resolve(componentDoc);
                        }, function eFunc() {
                            resolve(componentDoc);
                        });
                    }
                    else {
                        resolve(componentDoc);
                    }
                }, function error(err) {
                    _self.loadedComponentDocs[componentName] = null;
                    // do not break the chain in order to load the next component document
                    resolve(null);
                });
            }
            else {
                resolve(_self.loadedComponentDocs[componentName]);
            }
        });
    }
    createComponent(document, template, formParameters, account, compDef, key, isloadedComponentDocAndVendorLibs) {
        return __awaiter(this, void 0, void 0, function* () {
            const COMPONENT_ERROR_NAME = 'ft-component-error';
            if (!isloadedComponentDocAndVendorLibs) {
                yield this.loadComponentDocAndVendorLibs(compDef.componentName);
            }
            let componentName;
            if (template.loadComponentDocumentDependencies === false) {
                componentName = compDef.componentName;
            }
            else {
                const componentDocExists = this.componentDocExists(compDef.componentName);
                if (componentDocExists) {
                    componentName = compDef.componentName;
                }
                else {
                    compDef = Object.assign({ errorMessage: `Component not found: ${compDef.componentName}` }, compDef);
                    componentName = COMPONENT_ERROR_NAME;
                }
            }
            const anyWindow = window;
            const componentNameCamelCase = lodashString.camelCase(componentName);
            const isFunctionComponent = anyWindow.FormbirdServiceInjector.componentFunctions
                && anyWindow.FormbirdServiceInjector.componentFunctions[componentNameCamelCase];
            let customElement;
            const hasRegistered = customElements.get(componentName);
            if (!hasRegistered && !isFunctionComponent) {
                customElement = window.document.createElement(COMPONENT_ERROR_NAME);
                compDef = Object.assign({ errorMessage: `Component "${componentName}" has not registered a custom element.
                  Check that the component package has been installed correctly` }, compDef);
            }
            else {
                customElement = window.document.createElement(componentName);
            }
            // pass the parameters of the component to the custom element
            customElement.componentDefinition = compDef;
            customElement.document = document;
            // add a class that can be used to identify custom elements that have been rendered by the formbird core from the
            // components array
            customElement.className = "ft-form-component";
            customElement.template = template;
            customElement.formParameters = formParameters;
            customElement.key = key;
            customElement.account = account;
            customElement.fieldName = compDef.name;
            if (document) {
                customElement.fieldValue = document[compDef.name];
            }
            return customElement;
        });
    }
}
/** @nocollapse */ /** @nocollapse */ ComponentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ComponentService, deps: [{ token: i1.HttpClient }, { token: NotificationService }, { token: TemplateHtmlService }, { token: ConfigService }, { token: LoggedInUserService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ ComponentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ComponentService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ComponentService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: NotificationService }, { type: TemplateHtmlService }, { type: ConfigService }, { type: LoggedInUserService }]; } });

class FormColorService {
    constructor() {
        this.color = {
            background: ''
        };
        this.colorSubject = new Subject();
        this.color$ = this.colorSubject.asObservable();
    }
    setColor(formColor) {
        this.color.background = formColor;
        this.colorSubject.next(this.color);
    }
    setBackgroundColor() {
        this.setColor(FormColorService.COLOR_WHITE);
    }
}
FormColorService.COLOR_WHITE = '#ffffff';
/** @nocollapse */ /** @nocollapse */ FormColorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: FormColorService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ FormColorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: FormColorService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: FormColorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

class FormbirdInjectorService {
    constructor() {
        // get the injector. If running with an AngularJS core the injector will be the AngularJS injector. If running with an
        // Angular core, the injector is exposed globally in a window.FormbirdServiceInjector object. Once we have moved all customers
        // to an Angular core, we can remove the searching for an AngularJS injector
        // first try getting the global formbird service injector which will be defined if running with an Angular only core
        this.injector = window.FormbirdServiceInjector;
    }
    get(serviceName) {
        // first try getting the global formbird service injector which will be defined if running with an Angular only core
        return this.injector.get(serviceName);
    }
}
/** @nocollapse */ /** @nocollapse */ FormbirdInjectorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: FormbirdInjectorService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ FormbirdInjectorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: FormbirdInjectorService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: FormbirdInjectorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

class RouterService {
    constructor(injector) {
        this.injector = injector;
        this.service = injector.get('RouterService');
    }
    getActivatedRouteSnapshot() {
        return this.service.getActivatedRouteSnapshot();
    }
    navigate(params) {
        return this.service.navigate(params);
    }
    navigateByUrl(url, target) {
        return this.service.navigateByUrl(url, target);
    }
}
/** @nocollapse */ /** @nocollapse */ RouterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: RouterService, deps: [{ token: FormbirdInjectorService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ RouterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: RouterService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: RouterService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: FormbirdInjectorService }]; } });

const logger$4 = console;
class DefaultStateService {
    constructor(loggedInUserService, router, configService) {
        this.loggedInUserService = loggedInUserService;
        this.router = router;
        this.configService = configService;
        this.user$.subscribe((user) => this.user = user);
    }
    isPathAccountDefault() {
        let userBean;
        if (this.user) {
            userBean = this.user.account;
        }
        const pathArray = this.router.url.split('/');
        return userBean && userBean.defaultURL === pathArray[2];
    }
    changeToDefaultState(nextState, userBean) {
        logger$4.info('Changing to default URL');
        const defaultURL = this.configService.clientConfig().defaultURL;
        const user = this.user.account;
        let publicAccount = false;
        let accountPublicURL;
        if (user && user.publicAccount) {
            publicAccount = true;
            accountPublicURL = user.accountPublicURL;
        }
        if (!userBean) {
            userBean = this.user.account;
        }
        let id = '';
        if (defaultURL && defaultURL !== '') {
            id = defaultURL;
        }
        if (userBean && userBean.defaultURL && userBean.defaultURL !== '') {
            id = userBean.defaultURL;
        }
        if (nextState) {
            if (id && id !== '') {
                const locationParams = {};
                if (publicAccount) {
                    locationParams.replaceUrl = false;
                }
                this.router.navigate([nextState, id], locationParams);
            }
            else {
                this.router.navigate([nextState]);
            }
        }
        else {
            if (publicAccount) {
                this.router.navigate([`/${accountPublicURL}`, 'form', id]);
            }
            else if (id !== '') {
                this.router.navigate(['/form', id]);
            }
            else {
                this.router.navigate(['/form']);
            }
        }
    }
}
/** @nocollapse */ /** @nocollapse */ DefaultStateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DefaultStateService, deps: [{ token: LoggedInUserService }, { token: i2.Router }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ DefaultStateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DefaultStateService, providedIn: 'root' });
__decorate([
    select(['userState', 'user']),
    __metadata("design:type", Observable)
], DefaultStateService.prototype, "user$", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DefaultStateService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: LoggedInUserService }, { type: i2.Router }, { type: ConfigService }]; }, propDecorators: { user$: [] } });

class NavigationService {
    constructor(loggedInUserService, defaultStateService) {
        this.loggedInUserService = loggedInUserService;
        this.defaultStateService = defaultStateService;
        this.subs = new Subscription();
        this.menusSubject = new Subject();
        this.menus$ = this.menusSubject.asObservable();
        this.imagesSubject = new Subject();
        this.images$ = this.imagesSubject.asObservable();
        this.reset();
        const sub = this.user$.subscribe((user) => this.user = user);
        this.subs.add(sub);
    }
    ngOnInit() {
    }
    ngOnDestroy() {
        this.subs.unsubscribe();
    }
    reset() {
        this.menus = {
            template: null,
            user: null,
            sideMenuOpen: false
        };
        this.images = {
            homeButtonImage: null,
            homeButtonImages: null
        };
        this.menusSubject.next(this.menus);
        this.imagesSubject.next(this.images);
    }
    setCurrentTemplate(template) {
        if (template) {
            // setting home Button Image
            if (template.homeButtonImage) {
                this.images.homeButtonImage = template.homeButtonImage;
            }
            if (template.homeButtonImages) {
                this.images.homeButtonImages = template.homeButtonImages;
            }
            // setting menu
            this.menus.user = this.user.menu;
            // assign template menu if default path regardless of undefined or not
            if (template.menu || this.defaultStateService.isPathAccountDefault()) {
                this.menus.template = template.menu;
            }
            this.menusSubject.next(this.menus);
            this.imagesSubject.next(this.images);
        }
    }
}
/** @nocollapse */ /** @nocollapse */ NavigationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: NavigationService, deps: [{ token: LoggedInUserService }, { token: DefaultStateService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ NavigationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: NavigationService, providedIn: 'root' });
__decorate([
    select(['userState', 'user']),
    __metadata("design:type", Observable)
], NavigationService.prototype, "user$", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: NavigationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: LoggedInUserService }, { type: DefaultStateService }]; }, propDecorators: { user$: [] } });

class ModifiedCSSService {
    constructor(localStorageService) {
        this.localStorageService = localStorageService;
        this.dynamicCSSObj = {
            commandBarCSS: null
        };
    }
    setCommandBarCSS(templateId, commandBarCSS) {
        this.dynamicCSSObj.commandBarCSS = commandBarCSS;
        const fromStorage = this.localStorageService.getItem(LocalStorageKeys.COMMAND_BAR_CSS);
        const entry = fromStorage ? JSON.parse(fromStorage) : {};
        entry[templateId] = commandBarCSS;
        this.localStorageService.setItem('commandBarCSS', JSON.stringify(entry));
    }
    getStoredCommandBarCSS(templateId) {
        const fromStorage = this.localStorageService.getItem(LocalStorageKeys.COMMAND_BAR_CSS);
        const entry = fromStorage ? JSON.parse(fromStorage) : {};
        return entry[templateId];
    }
}
/** @nocollapse */ /** @nocollapse */ ModifiedCSSService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ModifiedCSSService, deps: [{ token: LocalStorageService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ ModifiedCSSService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ModifiedCSSService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ModifiedCSSService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: LocalStorageService }]; } });

const logger$3 = console;
class CustomFieldPreprocessorService {
    constructor(componentService) {
        this.componentService = componentService;
    }
    preProcess(field, document) {
        const _self = this;
        return new Promise((resolve, reject) => {
            _self.componentService.loadComponentDoc(field.componentName).then(function foundFunc(component) {
                try {
                    const customFunc = component.clientPreprocessorFunction;
                    if (UtilValidation.hasValue(customFunc)) {
                        const clientPreprocessorFunc = Function('return (' + customFunc + ')')();
                        if (component.preprocessorIsAsynchronous) {
                            clientPreprocessorFunc(field, document).then(function (retVal) {
                                resolve(retVal);
                            }, function (error) {
                                reject(error);
                            });
                        }
                        else {
                            const retVal = clientPreprocessorFunc(field, document);
                            resolve(retVal);
                        }
                    }
                    else {
                        resolve(document);
                    }
                }
                catch (err) {
                    const errMsg = ('Error in running custom component preprocessor function : ' + err);
                    logger$3.debug(errMsg);
                    reject(errMsg);
                }
            }, function notFoundFunc(err) {
                reject(err);
            });
        });
    }
}
/** @nocollapse */ /** @nocollapse */ CustomFieldPreprocessorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CustomFieldPreprocessorService, deps: [{ token: ComponentService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ CustomFieldPreprocessorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CustomFieldPreprocessorService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CustomFieldPreprocessorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: ComponentService }]; } });

class PreProcessorFieldService {
    constructor(customFieldPreprocessorService) {
        this.customFieldPreprocessorService = customFieldPreprocessorService;
    }
    preProcessOneField(field, document) {
        this.customFieldPreprocessorService.preProcess(field, document);
    }
    preProcessField(template, document) {
        if (template && template.components) {
            for (let i = 0; i < template.components.length; i++) {
                const field = template.components[i];
                this.preProcessOneField(field, document);
            }
        }
    }
    isCustomPreProcessor(component) {
        return component.componentName && !component.componentName.startsWith('ft-');
    }
    preProcess(template, document) {
        const _self = this;
        const promises = [];
        return new Promise((resolve, reject) => {
            if (template && template.components) {
                template.components.forEach(function (component) {
                    if (_self.isCustomPreProcessor(component)) {
                        promises.push(_self.customFieldPreprocessorService.preProcess(component, document));
                    }
                });
                if (promises.length > 0) {
                    Promise.all(promises).then(function successFunc() {
                        resolve(promises.length);
                    }, function errorFunc(err) {
                        reject(err);
                    });
                }
                else {
                    resolve(0);
                }
            }
            else {
                resolve(0);
            }
        });
    }
}
/** @nocollapse */ /** @nocollapse */ PreProcessorFieldService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: PreProcessorFieldService, deps: [{ token: CustomFieldPreprocessorService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ PreProcessorFieldService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: PreProcessorFieldService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: PreProcessorFieldService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: CustomFieldPreprocessorService }]; } });

class MandatoryValueProcessor {
    static processMandatoryValues(mandatoryValuesArray, fieldValue, label) {
        return __awaiter(this, void 0, void 0, function* () {
            let mandatoryValuesLabel;
            // the mandatoryValuesArray can only be applied to an object
            if (!fieldValue) {
                return;
            }
            else if (!isObject(fieldValue)) {
                return;
            }
            // mandatoryValuesArray must be an array
            if (!mandatoryValuesArray) {
                return;
            }
            else if (!isArray(mandatoryValuesArray)) {
                return;
            }
            for (let i = 0; i < mandatoryValuesArray.length; i++) {
                try {
                    const JSONPath = window.JSONPath;
                    let matches = JSONPath.JSONPath({ path: mandatoryValuesArray[i].jsonPathQuery, json: fieldValue });
                    if (matches.length === 0) {
                        // second validation if the first one failed
                        matches = JSONPath.JSONPath({ path: mandatoryValuesArray[i].jsonPathQuery, json: [fieldValue] });
                        if (matches.length === 0) {
                            if (!mandatoryValuesLabel) {
                                mandatoryValuesLabel = label;
                            }
                            // the mandatoryValuesLabel is output to html so use, <br> for the break
                            mandatoryValuesLabel += '<br> ➡ ' + mandatoryValuesArray[i].name;
                        }
                    }
                }
                catch (err) {
                    console.error(err);
                    return err;
                }
            }
            return mandatoryValuesLabel;
        });
    }
}

class MandatoryFieldService {
    constructor(modifiedFieldService) {
        this.modifiedFieldService = modifiedFieldService;
        this.mandatoryFields = { components: [] };
        this.checkMandatoryFields = this.checkMandatoryFields.bind(this);
    }
    /**
     * @param fieldInput the input field
     * @param fld field
     * @returns the label that will be shown for mandatory fields that are missing
     */
    checkMandatoryValues(fieldInput, fld, isValidationArray) {
        return __awaiter(this, void 0, void 0, function* () {
            let label = fld.label;
            if (!fld.label || fld.label === '') {
                label = fld.name;
            }
            if (!fieldInput && fieldInput !== 0) {
                return label;
            }
            else if (isArray(fieldInput) && isEqual([], fieldInput)) {
                return label;
            }
            else if (isObject(fieldInput) && !isDate(fieldInput)) {
                if (isEqual({}, fieldInput)) {
                    return label;
                }
                else {
                    const valuesArray = isValidationArray ? fld.validationsArray : fld.mandatoryValuesArray;
                    if (valuesArray) {
                        const mandatoryValuesLabel = yield MandatoryValueProcessor.processMandatoryValues(valuesArray, fieldInput, label);
                        if (mandatoryValuesLabel) {
                            // there are some conditions that do not match the mandatory requirements, so do not do any further
                            // processing of the save
                            return mandatoryValuesLabel;
                        }
                    }
                }
            }
            return null;
        });
    }
    /**
     * @param fieldInput the input field
     * @param fld field
     * @returns the label that will be shown for mandatory fields that are missing
     */
    checkMandatoryFields(fieldInput, fld) {
        let label = fld.label;
        if (!fld.label || fld.label === '') {
            label = fld.name;
        }
        if (!fieldInput && fieldInput !== 0) {
            return label;
        }
        else if (isArray(fieldInput) && isEqual([], fieldInput)) {
            return label;
        }
        else if (isObject(fieldInput) && !isDate(fieldInput)) {
            if (isEqual({}, fieldInput)) {
                return label;
            }
            else {
                const mandatoryFields = fld.mandatoryFields || [];
                if (!mandatoryFields.length) {
                    if (fld.mandatoryValues) {
                        const keys = Object.keys(fld.mandatoryValues) || [];
                        for (let i = 0; i < keys.length; i++) {
                            const key = keys[i];
                            if (fld.mandatoryValues[key]) {
                                mandatoryFields.push(key);
                            }
                        }
                    }
                }
                if (mandatoryFields && mandatoryFields.length) {
                    const keys = Object.keys(fieldInput);
                    for (let id = 0; id < keys.length; id++) {
                        const key = keys[id];
                        const obj = fieldInput[key];
                        if (isString(obj)) {
                            if (mandatoryFields) {
                                for (let m = 0, len = mandatoryFields.length; m < len; m++) {
                                    if (mandatoryFields[m] === key) {
                                        if (obj === undefined || obj === '') {
                                            return label + ' ➡ ' + mandatoryFields[m];
                                        }
                                    }
                                }
                            }
                            else {
                                if (obj === undefined || obj === '') {
                                    return label;
                                }
                            }
                        }
                        else if (isObject(obj)) {
                            if (this.checkMandatoryFields(obj, fld)) {
                                return label;
                            }
                        }
                    }
                }
            }
        }
        return null;
    }
    isMandatoryAnswered(unsavedDocumentDatas) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!unsavedDocumentDatas) {
                return Promise.reject(new Error('Unsaved document data list not provided in mandatory field check'));
            }
            const modifiedFieldService = this.modifiedFieldService;
            const checkMandatoryValues = this.checkMandatoryValues;
            const checkMandatoryFields = this.checkMandatoryFields;
            let unansweredMandatoryFieldMsg = 'Mandatory item(s) not completed: ';
            let hasUnansweredMandatoryFields = false;
            const doCheckMandatory = function (unsavedDocumentData) {
                return __awaiter(this, void 0, void 0, function* () {
                    const bean = unsavedDocumentData.document;
                    const tpl = unsavedDocumentData.template;
                    if (modifiedFieldService.hasChanged(bean.documentId)) {
                        try {
                            for (let t = 0, len = tpl.components.length; t < len; t++) {
                                const fld = tpl.components[t];
                                const value = bean[fld.name];
                                // add the mandatory fields to a message that can be output once all mandatory fields
                                // are checked. This way all mandatory fields can be displayed in the message
                                let processMandatoryFields = null;
                                let isValidationArray = false;
                                if (fld.mandatoryValuesArray && fld.mandatory) {
                                    processMandatoryFields = checkMandatoryValues;
                                }
                                else if (fld.mandatory) {
                                    processMandatoryFields = checkMandatoryFields;
                                }
                                else if (fld.validationsArray && !isEmpty(value)) {
                                    processMandatoryFields = checkMandatoryFields;
                                    isValidationArray = true;
                                }
                                if (processMandatoryFields) {
                                    const missingField = yield processMandatoryFields(value, fld, isValidationArray);
                                    if (isError(missingField)) {
                                        return Promise.reject(missingField);
                                    }
                                    if (missingField) {
                                        if (fld.mandatoryMessage) {
                                            unansweredMandatoryFieldMsg += `<br><span class='mandatory-item'>${fld.mandatoryMessage}</span>`;
                                        }
                                        else {
                                            unansweredMandatoryFieldMsg += `<br><span class='mandatory-item'>${missingField}</span>`;
                                        }
                                        hasUnansweredMandatoryFields = true;
                                    }
                                }
                            }
                        }
                        catch (err) {
                            let msg;
                            if (err.name === UtilError.NOT_FOUND_ERROR) {
                                msg = 'Template not found';
                            }
                            else {
                                msg = 'Error getting template to determine mandatory field: ' + err.message;
                            }
                            return Promise.reject(new Error(msg));
                        }
                    }
                    else {
                        return Promise.resolve();
                    }
                });
            };
            for (let i = 0; i < unsavedDocumentDatas.length; i++) {
                yield doCheckMandatory(unsavedDocumentDatas[i]);
            }
            ;
            if (hasUnansweredMandatoryFields) {
                // reject with the the mandatory message so it can be output by the caller
                return Promise.reject(new Error(unansweredMandatoryFieldMsg));
            }
            else {
                return Promise.resolve(true);
            }
        });
    }
}
/** @nocollapse */ /** @nocollapse */ MandatoryFieldService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: MandatoryFieldService, deps: [{ token: ModifiedFieldService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ MandatoryFieldService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: MandatoryFieldService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: MandatoryFieldService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: ModifiedFieldService }]; } });

const logger$2 = console;
class DocumentUpdateService {
    constructor(documentService, modifiedFieldService, unsavedDocumentService, broadcastService, appStore) {
        this.documentService = documentService;
        this.modifiedFieldService = modifiedFieldService;
        this.unsavedDocumentService = unsavedDocumentService;
        this.broadcastService = broadcastService;
        this.appStore = appStore;
    }
    updatePrevCurrentVersion(currentVersion) {
        const _self = this;
        // in case the provided version is current one so set currentVersion of the previous one to false
        // This case for updating document from pulling documents on reconnection
        const documentId = currentVersion.documentId;
        const loadedDocs = _self.unsavedDocumentService.getAllLoadedDocs();
        const orgDocs = _self.documentService.getOrgDocuments();
        const keys = Object.keys(orgDocs);
        for (let i = 0; i < keys.length; i++) {
            const versionId = keys[i];
            let orgDoc = orgDocs[versionId];
            if (documentId === orgDoc.documentId &&
                versionId !== documentId &&
                versionId !== currentVersion.systemHeader.versionId &&
                orgDoc.systemHeader.currentVersion) {
                orgDoc = cloneDeep(orgDoc);
                orgDoc.systemHeader.currentVersion = false;
                _self.documentService.setOriginalDocumentOnly(orgDoc, orgDoc.systemHeader.versionId);
                loadedDocs.forEach(function (loadedDoc) {
                    if (loadedDoc.systemHeader.versionId === orgDoc.systemHeader.versionId) {
                        loadedDoc.systemHeader.currentVersion = false;
                        _self.documentService.setLoadedDocumentOnly(cloneDeep(loadedDoc), orgDoc.systemHeader.versionId);
                    }
                });
                return;
            }
        }
    }
    updateVersions(versions) {
        return __awaiter(this, void 0, void 0, function* () {
            const _self = this;
            if (versions && versions.length) {
                const currentVersion = versions.filter(version => version.systemHeader.currentVersion)[0];
                if (currentVersion) {
                    this.documentService.resetDisableSaveFieldToDoc(currentVersion);
                    if (versions.length === 1) {
                        _self.updatePrevCurrentVersion(currentVersion);
                    }
                    const documentId = currentVersion.documentId;
                    logger$2.info('Updating document in memory: ' + documentId);
                    const loadedDoc = _self.documentService.getLoadedDocument(documentId);
                    // use current version to update loaded document on form
                    yield _self.applyDocumentChanges(loadedDoc, currentVersion);
                    // update the loaded document in store
                    _self.documentService.setLoadedDocumentOnly(loadedDoc);
                    // use current version to update org version in store
                    _self.documentService.setOriginalDocumentOnly(currentVersion);
                    logger$2.info('Update document on form to latest one.');
                    _self.broadcastService.broadcast('document:updated', [currentVersion]);
                }
                const nonCurrentVersions = versions.filter(version => version.systemHeader.currentVersion === false);
                if (nonCurrentVersions.length) {
                    // user non current version to update loaded non-current version in store
                    nonCurrentVersions.forEach(function (version) {
                        return __awaiter(this, void 0, void 0, function* () {
                            const versionId = version.systemHeader.versionId;
                            const loadedVersion = _self.documentService.getLoadedDocument(versionId);
                            if (loadedVersion) {
                                yield _self.applyDocumentChanges(loadedVersion, version);
                                // update loaded non-current version on form
                                _self.documentService.setLoadedDocumentOnly(cloneDeep(loadedVersion), versionId);
                                // use non-current version to update its version in store
                                _self.documentService.setOriginalDocumentOnly(version, versionId);
                            }
                        });
                    });
                }
            }
        });
    }
    applyDocumentChanges(oldOrgDoc, latestDoc) {
        return __awaiter(this, void 0, void 0, function* () {
            const documentId = latestDoc.documentId;
            logger$2.info('Applying document changes for doc: ' + documentId);
            const modifiedFields = this.modifiedFieldService.getModifiedFieldsInDoc(documentId);
            var excludedOptions = {};
            excludedOptions[SharedConstants.MAKE_CURRENT_VERSION_KEY] = true;
            const loadedDocs = this.unsavedDocumentService.getAllLoadedDocs(excludedOptions);
            for (let i = 0; i < loadedDocs.length; i++) {
                const loadedDoc = cloneDeep(loadedDocs[i]);
                if (loadedDoc.documentId === documentId) {
                    const updates = yield DocumentUpdater.getUpdates(oldOrgDoc, latestDoc);
                    if (updates.updates && updates.updates.length) {
                        if (modifiedFields && modifiedFields.length) {
                            modifiedFields.forEach(function (modifiedField) {
                                modifiedField = UtilString.convertToObjectPath(modifiedField, '/');
                                updates.updates.forEach(function (update, index) {
                                    const path = UtilString.convertToObjectPath(update.path, '/');
                                    if (modifiedField === path &&
                                        !isEqual(loadedDoc[modifiedField], latestDoc[modifiedField])) {
                                        logger$2.warn('Field in pushed document: \'' +
                                            modifiedField + '\' has been changed to: \'' + JSON.stringify(update.value));
                                        delete updates.updates[index];
                                    }
                                });
                            });
                        }
                        yield DocumentUpdater.applyUpdates(oldOrgDoc, updates);
                    }
                }
            }
        });
    }
    updateFormDocument(unsavedDocuments, doc) {
        return __awaiter(this, void 0, void 0, function* () {
            if (unsavedDocuments.length) {
                // just mutate the document if so that references to this object are not cutoff
                // also update loaded docs too
                for (let j = 0; j < unsavedDocuments.length; j++) {
                    const unsavedDoc = unsavedDocuments[j];
                    if (unsavedDoc.documentId === doc.documentId) {
                        const updates = yield DocumentUpdater.getUpdates(unsavedDoc, doc);
                        yield DocumentUpdater.applyUpdates(unsavedDoc, updates);
                        this.appStore.dispatch(formSetDocument(unsavedDoc));
                        break;
                    }
                }
            }
        });
    }
}
/** @nocollapse */ /** @nocollapse */ DocumentUpdateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DocumentUpdateService, deps: [{ token: DocumentService }, { token: ModifiedFieldService }, { token: UnsavedDocumentService }, { token: BroadcastService }, { token: AppStore }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ DocumentUpdateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DocumentUpdateService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DocumentUpdateService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: DocumentService }, { type: ModifiedFieldService }, { type: UnsavedDocumentService }, { type: BroadcastService }, { type: AppStore }]; } });

class FinaliseValueFieldService {
    constructor(modifiedFieldService) {
        this.modifiedFieldService = modifiedFieldService;
        this.finaliseValueFunctions = {};
    }
    ensureTemplateExist(templateId) {
        if (!this.finaliseValueFunctions[templateId]) {
            this.finaliseValueFunctions[templateId] = {};
        }
    }
    addFinaliseValueFunction(templateId, fieldName, func) {
        this.ensureTemplateExist(templateId);
        this.finaliseValueFunctions[templateId][fieldName] = func;
    }
    getFinaliseValueFunction(templateId, fieldName) {
        this.ensureTemplateExist(templateId);
        return this.finaliseValueFunctions[templateId][fieldName];
    }
    finaliseFieldValues(document, template) {
        if (document && this.modifiedFieldService.hasChanged(document.documentId)) {
            for (let t = 0, len = template.components.length; t < len; t++) {
                const fld = template.components[t];
                const finaliseFunc = this.getFinaliseValueFunction(template.documentId, fld.name);
                if (finaliseFunc) {
                    finaliseFunc(document);
                }
            }
        }
    }
}
/** @nocollapse */ /** @nocollapse */ FinaliseValueFieldService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: FinaliseValueFieldService, deps: [{ token: ModifiedFieldService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ FinaliseValueFieldService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: FinaliseValueFieldService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: FinaliseValueFieldService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: ModifiedFieldService }]; } });

const logger$1 = console;
class DocumentSaveService {
    constructor(dataService, notificationService, documentService, unsavedDocumentService, currentDocumentService, mandatoryFieldService, modifiedFieldService, validationService, clientSystemHeaderService, documentUpdateService, configService, broadcastService, clientAccessService, changedDocumentService, finaliseValueService, preProcessorFieldService, appStore, keyValueStorageService) {
        this.dataService = dataService;
        this.notificationService = notificationService;
        this.documentService = documentService;
        this.unsavedDocumentService = unsavedDocumentService;
        this.currentDocumentService = currentDocumentService;
        this.mandatoryFieldService = mandatoryFieldService;
        this.modifiedFieldService = modifiedFieldService;
        this.validationService = validationService;
        this.clientSystemHeaderService = clientSystemHeaderService;
        this.documentUpdateService = documentUpdateService;
        this.configService = configService;
        this.broadcastService = broadcastService;
        this.clientAccessService = clientAccessService;
        this.changedDocumentService = changedDocumentService;
        this.finaliseValueService = finaliseValueService;
        this.preProcessorFieldService = preProcessorFieldService;
        this.appStore = appStore;
        this.keyValueStorageService = keyValueStorageService;
        this.documentData = {};
        this.savingEventBus = new Subject();
        this.savedEventBus = new Subject();
        this.currentDocumentService.documentData$.subscribe(data => {
            this.documentData = data;
        });
    }
    loadOverrideTemplate(document) {
        var _a;
        if (this.documentData.overrideTemplateId && ((_a = this.documentData.document) === null || _a === void 0 ? void 0 : _a.documentId) === document.documentId) {
            return this.documentData.overrideTemplateId;
        }
    }
    loadTemplate(document, unsavedDocumentListId) {
        return this.unsavedDocumentService.getUnsavedTemplate(document.documentId, unsavedDocumentListId);
    }
    doPreSaveData(document, unsavedDocumentListId) {
        return __awaiter(this, void 0, void 0, function* () {
            const docBean = cloneDeep(document);
            const template = this.loadTemplate(docBean, unsavedDocumentListId);
            yield this.preProcessorFieldService.preProcess(template, docBean);
            this.finaliseValueService.finaliseFieldValues(docBean, template);
            if (!docBean.systemHeader) {
                docBean.systemHeader = {};
            }
            if (!docBean.systemHeader.systemType) {
                docBean.systemHeader.systemType = SharedConstants.SYSTEM_TYPE_DOCUMENT;
            }
            if (UtilDocument.isNew(docBean) && !docBean.systemHeader.templateId) {
                docBean.systemHeader.templateId = template.documentId;
            }
            this.clientAccessService.writeKeysToDocument(docBean, template);
            const overrideTemplateId = this.loadOverrideTemplate(document);
            if (overrideTemplateId) {
                docBean.systemHeader.createdWith = overrideTemplateId;
            }
            else {
                docBean.systemHeader.createdWith = document.systemHeader.templateId;
            }
            const docContext = {};
            docContext.template = template;
            docContext.templateId = document.systemHeader.templateId;
            docContext.unsavedDocumentListId = unsavedDocumentListId;
            return this.preSaveValidation(docBean, docContext);
        });
    }
    doPostSaveData(document, unsavedDocumentListId) {
        const docBean = cloneDeep(document);
        const template = this.loadTemplate(docBean, unsavedDocumentListId);
        if (!template || !docBean) {
            return;
        }
        const docContext = {};
        docContext.template = template;
        docContext.templateId = document.systemHeader.templateId;
        docContext.unsavedDocumentListId = unsavedDocumentListId;
        return this.postSaveClientValidation(docBean, docContext);
    }
    preSaveValidation(docBean, docContext) {
        const _self = this;
        return new Promise((resolve, reject) => {
            _self.appStore.dispatch(formSetDocument(docBean));
            _self.validationService.onPreSaveDocument(docBean, docContext, function (error, result) {
                if (error) {
                    reject(error);
                }
                else {
                    _self.validationService.broadcastValidationEvent();
                    const latestDocument = _self.documentService.getLoadedDocument(docBean.documentId);
                    resolve(latestDocument);
                }
            });
        });
    }
    postSaveClientValidation(docBean, docContext) {
        return new Promise((resolve, reject) => {
            this.validationService.onPostSaveClientDocument(docBean, docContext, (error, result) => {
                if (error) {
                    reject(error);
                }
                else {
                    this.validationService.broadcastValidationEvent();
                    const latestDocument = this.documentService.getLoadedDocument(docBean.documentId);
                    resolve(latestDocument);
                }
            });
        });
    }
    shouldApplyFlags(documentListId) {
        // Should apply flag on the document list that was attached to command bar
        return documentListId === this.currentDocumentService.getMainDocumentListId();
    }
    resetCleanStateDocument(doc, unsavedDocumentListId) {
        this.documentService.resetDisableSaveFieldToDoc(doc);
        this.documentService.resetOriginalDocument(doc);
        if (this.shouldApplyFlags(unsavedDocumentListId)) {
            this.currentDocumentService.resetUndoRedo();
        }
        this.changedDocumentService.resetChangeDocument();
    }
    performPostSaveEach(doc, unsavedDocumentListId) {
        this.doPostSaveData(doc, unsavedDocumentListId);
        this.notificationService.success('Saved successfully');
        this.modifiedFieldService.removeModifiedDocumentFields(doc.documentId);
        this.resetCleanStateDocument(doc, unsavedDocumentListId);
        const key = `FB-CORE-WIP-${doc.documentId}`;
        this.keyValueStorageService.removeItem(key);
        // Put doc into an array due to avoid changing the handler in custom component
        this.broadcastService.broadcast('document:updated', [doc]);
    }
    processSaveEach(modifiedDocument, unsavedDocumentListId) {
        const _self = this;
        return new Promise((resolve, reject) => {
            const result = DocumentIdValidator.validateDocumentId(modifiedDocument.documentId, _self.configService.clientConfig().documentIdRules);
            if (!result.valid) {
                reject(new Error(result.reason));
            }
            _self.reloadDocumentAndApplyUpdates(modifiedDocument, unsavedDocumentListId).then(function successFunc(newModifiedDoc) {
                _self.doPreSaveData(newModifiedDoc, unsavedDocumentListId).then(function preSaveSuccessFunc(processedDoc) {
                    if (processedDoc) {
                        _self.doSaveEach(processedDoc, unsavedDocumentListId).then(function (doc) {
                            _self.performPostSaveEach(doc, unsavedDocumentListId);
                            resolve(doc);
                        }, function (err) {
                            reject(err);
                        });
                    }
                    else {
                        const msg = 'There is error in PreSave.';
                        logger$1.error(msg);
                        reject(new Error(msg));
                    }
                }, function errorFunc(error) {
                    logger$1.info('Saving data error...' + JSON.stringify(error));
                    reject(error);
                });
            }, function errorFunc(err) {
                logger$1.info('Saving data error...' + JSON.stringify(err));
                reject(err);
            });
        });
    }
    doSaveEach(docBean, unsavedDocumentListId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (docBean) {
                logger$1.info('Saving document: ' + docBean.documentId);
                const includeDeleted = true;
                const docExists = yield this.dataService.documentExists(docBean.documentId, includeDeleted);
                const tpl = this.documentService.getTemplateFromDoc(docBean);
                let originalDoc;
                if (docExists) {
                    originalDoc = this.documentService.getExistingDocument(docBean.documentId);
                    if (!originalDoc && this.configService.clientConfig().useSaveConflictResolution) {
                        originalDoc = this.documentService.getExistingDocument(docBean.systemHeader.previousVersionId);
                    }
                    else if (!originalDoc) {
                        originalDoc = this.documentService.getExistingDocument(docBean.systemHeader.versionId);
                    }
                }
                const isCreationMode = UtilDocument.isNew(docBean) || !docExists; // insert as new if not found
                if (isCreationMode) {
                    const savedDoc = yield this.dataService.insert(docBean);
                    this.savedDocs.push(savedDoc);
                    logger$1.info('Document inserted: ' + savedDoc.documentId);
                    return savedDoc;
                }
                else {
                    if (!originalDoc) { // docExists but no access
                        throw new Error('Unable to update! No access to document: ' + docBean.documentId);
                    }
                    else if (!this.clientSystemHeaderService.updateSystemHeader(docBean, tpl, originalDoc)) {
                        throw new Error('Error writing system header');
                    }
                    else {
                        const overrideTemplateId = this.loadOverrideTemplate(docBean);
                        const options = {
                            overrideTemplateId: overrideTemplateId,
                            templateId: originalDoc ? originalDoc.systemHeader.templateId : docBean.systemHeader.templateId,
                            makeCurrentVersion: this.unsavedDocumentService.getDocumentOptionKey(unsavedDocumentListId, docBean.documentId, SharedConstants.MAKE_CURRENT_VERSION_KEY),
                            importPreviousVersion: this.unsavedDocumentService.getDocumentOptionKey(unsavedDocumentListId, docBean.documentId, SharedConstants.IMPORT_PREVIOUS_VERSION)
                        };
                        const savedDoc = yield this.dataService.jsonPatchUpdate(originalDoc, docBean, options);
                        this.savedDocs.push(savedDoc);
                        logger$1.info('Document updated: ' + savedDoc.documentId);
                        return savedDoc;
                    }
                }
            }
        });
    }
    setFlags(dirtyFlag, spinnerFlag, saveFlag, unsavedDocumentListId) {
        if (spinnerFlag !== undefined) {
            this.modifiedFieldService.setShowSpinner(spinnerFlag, unsavedDocumentListId);
        }
        if (saveFlag !== undefined) {
            this.modifiedFieldService.setSaveFlag(saveFlag, unsavedDocumentListId);
        }
        if (saveFlag) {
            // Saving means the update on form is allowed,
            // so set updateAllowed flag to true in order to show the dirty again in case saving errors
            this.modifiedFieldService.setUpdateAllowed(true);
        }
        if (dirtyFlag !== undefined) {
            this.modifiedFieldService.setDirty(dirtyFlag, null, unsavedDocumentListId);
        }
    }
    doSave(unsavedDocumentListId) {
        const _self = this;
        return new Promise((resolve, reject) => {
            try {
                // check mandatory fields in all documents getUnsavedDocuments will throw an exception
                // if the unsavedDocumentListId is unknown
                const unsavedDocumentDatas = _self.unsavedDocumentService.getUnsavedDocumentDatas(unsavedDocumentListId);
                _self.mandatoryFieldService.isMandatoryAnswered(unsavedDocumentDatas).then(function successFunc() {
                    // 11003: only save modified documents. Don't save the parent if only a child has changed
                    const modifiedDocuments = _self.modifiedFieldService.getModifiedDocuments(unsavedDocumentListId);
                    if (modifiedDocuments === null || modifiedDocuments === void 0 ? void 0 : modifiedDocuments.length) {
                        _self.savedDocs = [];
                        // save documents sequentially so that all document saves aren't sent to the server at the
                        // same time. See the notes of Mantis 11213 for further details
                        UtilSequenceProcess.processList(modifiedDocuments, (modifiedDocument) => {
                            return _self.processSaveEach(modifiedDocument, unsavedDocumentListId);
                        }).then(function allPromiseSuccessFunc() {
                            if (_self.savedDocs && _self.savedDocs.length > 0) {
                                resolve(_self.savedDocs);
                            }
                            else {
                                reject(new Error('No results returned from save'));
                            }
                        }, function errorFunc(err) {
                            logger$1.info('Saving data got errors...' + JSON.stringify(err));
                            reject(err);
                        });
                    }
                    else {
                        logger$1.info('Exit saving because there is no modified document.');
                        // resolve with no document because there's no save
                        resolve(null);
                    }
                }, function errorFunc(err) {
                    reject(err);
                });
            }
            catch (e) {
                // pass through the error with no additional messages so that the error is
                // nicely formatted for display
                reject(new Error(e));
            }
        });
    }
    reloadDocumentAndApplyUpdates(modifiedDoc, unsavedDocumentListId) {
        const _self = this;
        return new Promise((resolve, reject) => {
            var isMakingCurVer = _self.unsavedDocumentService.getDocumentOptionKey(unsavedDocumentListId, modifiedDoc.documentId, SharedConstants.MAKE_CURRENT_VERSION_KEY);
            if (UtilDocument.isNew(modifiedDoc) || UtilDocument.isPreviousVersion(modifiedDoc) || isMakingCurVer) {
                resolve(modifiedDoc);
            }
            else {
                const documentId = modifiedDoc.documentId;
                let oldOriginalDoc = _self.documentService.getExistingDocument(documentId);
                if (!oldOriginalDoc) {
                    oldOriginalDoc = _self.documentService.getExistingDocument(modifiedDoc.systemHeader.versionId);
                }
                if (oldOriginalDoc) {
                    // load the latest version from DB first
                    _self.dataService.getDocument(documentId).then(function successFunc(latestDoc) {
                        if (oldOriginalDoc.systemHeader.versionId !== latestDoc.systemHeader.versionId) {
                            let diffs = UtilConflict.getDiffs(oldOriginalDoc, latestDoc);
                            if (diffs.length) {
                                // get the changed fields by rules and user on form
                                diffs = UtilConflict.getDiffs(oldOriginalDoc, modifiedDoc);
                                // update the latest version to current original version
                                _self.documentService.resetOriginalDocument(latestDoc);
                                // create a new copied version from the latest one
                                const newModifiedDoc = cloneDeep(latestDoc);
                                // apply the changes by rules and user on form to the new document
                                if (diffs.length) {
                                    diffs.forEach(function (diff) {
                                        newModifiedDoc[diff.fieldName] = modifiedDoc[diff.fieldName];
                                    });
                                }
                                const template = _self.loadTemplate(newModifiedDoc, unsavedDocumentListId);
                                if (template && template.components && template.components.length) {
                                    template.components.forEach(function (component) {
                                        if (!UtilType.isDefined(newModifiedDoc[component.name])) {
                                            if (UtilType.isDefined(component.defaultValue)) {
                                                newModifiedDoc[component.name] = component.defaultValue;
                                            }
                                        }
                                    });
                                }
                                // set the new changed version to unsaved list
                                _self.unsavedDocumentService.setDocument(unsavedDocumentListId, newModifiedDoc);
                                resolve(newModifiedDoc);
                            }
                            else {
                                resolve(modifiedDoc);
                            }
                        }
                        else {
                            resolve(modifiedDoc);
                        }
                    }, function errorFunc(err) {
                        reject(err);
                    });
                }
                else {
                    resolve(modifiedDoc);
                }
            }
        });
    }
    save(unsavedDocumentListId) {
        const _self = this;
        return new Promise((resolve, reject) => {
            if (!unsavedDocumentListId) {
                reject(new Error('Unsaved document list id not provided in save'));
            }
            else if (_self.modifiedFieldService.isSaving()) {
                reject(new Error('Save is already in progress'));
            }
            else {
                _self.setFlags(false, true, true, unsavedDocumentListId);
                const unsavedDocuments = _self.modifiedFieldService.getModifiedDocuments(unsavedDocumentListId);
                _self.doSave(unsavedDocumentListId).then(function doSaveSuccess(docs) {
                    return __awaiter(this, void 0, void 0, function* () {
                        // reset temporary array of saved documents
                        _self.savedDocs = null;
                        if (docs) {
                            for (let id = 0; id < docs.length; id++) {
                                const doc = cloneDeep(docs[id]);
                                // Remove offline info before applying the changes
                                if (doc.systemHeader) {
                                    delete doc.systemHeader.offlineDetails;
                                }
                                yield _self.documentUpdateService.updateFormDocument(unsavedDocuments, doc);
                            }
                        }
                        _self.setFlags(false, false, false, unsavedDocumentListId);
                        resolve(docs);
                    });
                }, function errorFunc(err) {
                    _self.setFlags(true, false, false, unsavedDocumentListId);
                    reject(err);
                });
            }
        });
    }
    saveDocumentLists(documentListIds) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = {
                data: null,
                error: null
            };
            const _self = this;
            this.broadcastSaving(true);
            _self.broadcastService.broadcast(ClientConstants.VALIDATE_FORM_FIELDS);
            try {
                const unsavedDocumentListIds = _self.modifiedFieldService.getAllChangedDocumentListIds();
                // save documents sequentially so that all document saves aren't sent to the server at the
                // same time. See the notes of Mantis 11213 for further details
                function asyncJob(documentListId) {
                    if (documentListId) {
                        if (UtilArray.contains(unsavedDocumentListIds, documentListId)) {
                            return _self.save(documentListId);
                        }
                    }
                }
                result.data = yield UtilSequenceProcess.processList(documentListIds, asyncJob);
                if (result.data) {
                    this.broadcastDocumentSaved(result.data.map(documentObj => documentObj === null || documentObj === void 0 ? void 0 : documentObj.documentId));
                }
            }
            catch (err) {
                result.error = err;
            }
            finally {
                this.broadcastSaving(false);
            }
            return result;
        });
    }
    broadcastSaving(data) {
        this.savingEventBus.next(data);
    }
    onDocumentSaving() {
        return this.savingEventBus.asObservable();
    }
    broadcastDocumentSaved(documentIds) {
        this.savedEventBus.next(documentIds);
    }
    onDocumentSaved() {
        return this.savedEventBus.asObservable();
    }
}
/** @nocollapse */ /** @nocollapse */ DocumentSaveService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DocumentSaveService, deps: [{ token: DataService }, { token: NotificationService }, { token: DocumentService }, { token: UnsavedDocumentService }, { token: CurrentDocumentService }, { token: MandatoryFieldService }, { token: ModifiedFieldService }, { token: ValidationService }, { token: ClientSystemHeaderService }, { token: DocumentUpdateService }, { token: ConfigService }, { token: BroadcastService }, { token: ClientAccessService }, { token: ChangedDocumentService }, { token: FinaliseValueFieldService }, { token: PreProcessorFieldService }, { token: AppStore }, { token: KeyValueStorageService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ DocumentSaveService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DocumentSaveService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DocumentSaveService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: DataService }, { type: NotificationService }, { type: DocumentService }, { type: UnsavedDocumentService }, { type: CurrentDocumentService }, { type: MandatoryFieldService }, { type: ModifiedFieldService }, { type: ValidationService }, { type: ClientSystemHeaderService }, { type: DocumentUpdateService }, { type: ConfigService }, { type: BroadcastService }, { type: ClientAccessService }, { type: ChangedDocumentService }, { type: FinaliseValueFieldService }, { type: PreProcessorFieldService }, { type: AppStore }, { type: KeyValueStorageService }]; } });

class UrlService {
    constructor() {
        this.urls = {
            external: null,
            lastEntered: null,
            lastVisited: null,
            current: null // current viewed url
        };
    }
    getUrls() {
        return this.urls;
    }
}
/** @nocollapse */ /** @nocollapse */ UrlService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: UrlService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ UrlService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: UrlService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: UrlService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

class ClientResourceService {
    constructor(http) {
        this.http = http;
    }
    getClientResources() {
        return this.http.get(SharedUrlRoutes.serverRoutes.clientResources).toPromise();
    }
    getCachedFilesCount() {
        return Promise.resolve(0);
        // Will be checked later because caches object is undefined
        // return new Promise((resolve, reject) => {
        //   if (caches) {
        //     caches.keys().then(keyList => {
        //
        //       const promises = [];
        //       for (let i = 0; i < keyList.length - 1; i++) {
        //         const key = keyList[i];
        //         promises.push(caches.open(key));
        //       }
        //
        //       return Promise.all(promises);
        //
        //     }).then(returnedCaches => {
        //       const promises = [];
        //       for (let i = 0; i < returnedCaches.length; i++) {
        //         const cache = returnedCaches[i];
        //         promises.push(cache.keys());
        //       }
        //
        //       return Promise.all(promises);
        //
        //     }).then((cachedFilesArray) => {
        //       let totalCount = 0;
        //       for (let i = 0; i < cachedFilesArray.length; i++) {
        //         totalCount += cachedFilesArray[i].length;
        //       }
        //
        //       resolve(totalCount);
        //     });
        //   } else {
        //     resolve(0);
        //   }
        // });
    }
}
/** @nocollapse */ /** @nocollapse */ ClientResourceService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ClientResourceService, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ ClientResourceService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ClientResourceService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ClientResourceService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }]; } });

let StorageEventService$;
class StorageEventService {
    constructor() {
        this.listeners = [];
        StorageEventService$ = this;
    }
    addListener(callback) {
        this.listeners.push(callback);
    }
    storage(event) {
        for (let i = 0; i < StorageEventService$.listeners.length; i++) {
            StorageEventService$.listeners[i](event);
        }
    }
    ;
    start() {
        window.addEventListener('storage', this.storage);
    }
    end() {
        this.listeners = [];
        window.removeEventListener('storage', this.storage);
    }
}
/** @nocollapse */ /** @nocollapse */ StorageEventService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: StorageEventService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ StorageEventService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: StorageEventService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: StorageEventService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/** @hidden */
const assert = (condition, message) => {
    if (!condition) {
        throw new Error(message);
    }
};

/** @hidden */
const sniffSelectorType$1 = (selector) => !selector
    ? 'nil'
    : Array.isArray(selector)
        ? 'path'
        : 'function' === typeof selector
            ? 'function'
            : 'property';
/** @hidden */
const resolver$1 = (selector) => ({
    property: (state) => state ? state[selector] : undefined,
    path: (state) => getIn$1(state, selector),
    function: selector,
    nil: (state) => state,
});
/** @hidden */
const resolveToFunctionSelector$1 = (selector) => resolver$1(selector)[sniffSelectorType$1(selector)];
/**
 * Gets a deeply-nested property value from an object, given a 'path'
 * of property names or array indices.
 *
 * @hidden
 */
function getIn$1(v, pathElems) {
    if (!v) {
        return v;
    }
    // If this is an ImmutableJS structure, use existing getIn function
    if ('function' === typeof v.getIn) {
        return v.getIn(pathElems);
    }
    const [firstElem, ...restElems] = pathElems;
    if (undefined === v[firstElem]) {
        return undefined;
    }
    if (restElems.length === 0) {
        return v[firstElem];
    }
    return getIn$1(v[firstElem], restElems);
}

/**
 * Gets a deeply-nested property value from an object, given a 'path'
 * of property names or array indices.
 *
 * @hidden
 */
function getIn(v, pathElems) {
    if (!v) {
        return v;
    }
    // If this is an ImmutableJS structure, use existing getIn function
    if ('function' === typeof v.getIn) {
        return v.getIn(pathElems);
    }
    const [firstElem, ...restElems] = pathElems;
    if (undefined === v[firstElem]) {
        return undefined;
    }
    if (restElems.length === 0) {
        return v[firstElem];
    }
    return getIn(v[firstElem], restElems);
}

/**
 * Sets a deeply-nested property value from an object, given a 'path'
 * of property names or array indices. Path elements are created if
 * not there already. Does not mutate the given object.
 *
 * @hidden
 */
const setIn = (obj, [firstElem, ...restElems], value) => 'function' === typeof (obj[firstElem] || {}).setIn
    ? Object.assign(Object.assign({}, obj), { [firstElem]: obj[firstElem].setIn(restElems, value) }) : Object.assign(Object.assign({}, obj), { [firstElem]: restElems.length === 0
        ? value
        : setIn(obj[firstElem] || {}, restElems, value) });

let reducerMap = {};
const composeReducers = (...reducers) => (state, action) => reducers.reduce((subState, reducer) => reducer(subState, action), state);
/**
 * @param rootReducer Call this on your root reducer to enable SubStore
 * functionality for pre-configured stores (e.g. using AppStore.provideStore()).
 * AppStore.configureStore
 * does it for you under the hood.
 */
function enableFractalReducers(rootReducer) {
    reducerMap = {};
    return composeReducers(rootFractalReducer, rootReducer);
}
/** @hidden */
function registerFractalReducer(basePath, localReducer) {
    const existingFractalReducer = reducerMap[JSON.stringify(basePath)];
    if (existingFractalReducer && existingFractalReducer !== localReducer) {
        throw new Error(`attempt to overwrite fractal reducer for basePath ${basePath}`);
    }
    reducerMap[JSON.stringify(basePath)] = localReducer;
}
/** @hidden */
function replaceLocalReducer(basePath, nextLocalReducer) {
    reducerMap[JSON.stringify(basePath)] = nextLocalReducer;
}
function rootFractalReducer(state = {}, action) {
    const fractalKey = action['@angular-redux::fractalkey'];
    const fractalPath = fractalKey ? JSON.parse(fractalKey) : [];
    const localReducer = reducerMap[fractalKey || ''];
    return fractalKey && localReducer
        ? setIn(state, fractalPath, localReducer(getIn(state, fractalPath), action))
        : state;
}

/** @hidden */
const sniffSelectorType = (selector) => !selector
    ? 'nil'
    : Array.isArray(selector)
        ? 'path'
        : 'function' === typeof selector
            ? 'function'
            : 'property';
/** @hidden */
const resolver = (selector) => ({
    property: (state) => state ? state[selector] : undefined,
    path: (state) => getIn(state, selector),
    function: selector,
    nil: (state) => state,
});
/** @hidden */
const resolveToFunctionSelector = (selector) => resolver(selector)[sniffSelectorType(selector)];

/** @hidden */
class SubStore {
    constructor(rootStore, basePath, localReducer) {
        this.rootStore = rootStore;
        this.basePath = basePath;
        this.dispatch = action => this.rootStore.dispatch(Object.assign(Object.assign({}, action), { '@angular-redux::fractalkey': JSON.stringify(this.basePath) }));
        this.getState = () => getIn(this.rootStore.getState(), this.basePath);
        this.configureSubStore = (basePath, localReducer) => new SubStore(this.rootStore, [...this.basePath, ...basePath], localReducer);
        this.select = (selector, comparator) => this.rootStore.select(this.basePath).pipe(map(resolveToFunctionSelector(selector)), distinctUntilChanged(comparator));
        this.subscribe = (listener) => {
            const subscription = this.select().subscribe(listener);
            return () => subscription.unsubscribe();
        };
        this.replaceReducer = (nextLocalReducer) => replaceLocalReducer(this.basePath, nextLocalReducer);
        registerFractalReducer(basePath, localReducer);
    }
}

/** @hidden */
class RootStore extends AppStore {
    constructor() {
        super();
        this.store = undefined;
        this.provideStore = (store) => {
            assert(!this.store, 'Store already configured!');
            this.setStore(store);
        };
        this.getState = () => this.store.getState();
        this.subscribe = (listener) => this.store.subscribe(listener);
        this.replaceReducer = (nextReducer) => {
            this.store.replaceReducer(nextReducer);
        };
        this.dispatch = (action) => {
            assert(!!this.store, 'Dispatch failed: did you forget to configure your store? ');
            return this.store.dispatch(action);
        };
        this.select = (selector, comparator) => this.store$.pipe(distinctUntilChanged(), map(resolveToFunctionSelector$1(selector)), distinctUntilChanged(comparator));
        this.configureSubStore = (basePath, localReducer) => new SubStore(this, basePath, localReducer);
        this.storeToObservable = (store) => new Observable((observer) => {
            observer.next(store.getState());
            const unsubscribeFromRedux = store.subscribe(() => observer.next(store.getState()));
            return () => {
                unsubscribeFromRedux();
                observer.complete();
            };
        });
        AppStore.instance = this;
        this.store$ = new BehaviorSubject(undefined).pipe(filter(n => n !== undefined), switchMap(observableStore => observableStore));
    }
    setStore(store) {
        this.store = store;
        const storeServable = this.storeToObservable(store);
        this.store$.next(storeServable);
    }
}

const operator = {
    equals: 'eq',
    notEquals: 'neq',
    contains: 'contains'
};
class SearchQueriesService {
    constructor() { }
    getElasticQuery(scope, filter, search, columns, order, dtcolumns) {
        let filterObj = filter;
        if (typeof filter === 'string') {
            filterObj = filter.replace(/'/g, '"');
            // filterObj = filterObj.replace(/ /g, "\\\\ ");
            filterObj = JSON.parse(filterObj);
        }
        if (filterObj.query.bool.should === undefined) {
            filterObj.query.bool.should = [];
        }
        if (filterObj.query.bool.must === undefined) {
            filterObj.query.bool.must = [];
        }
        // tslint:disable-next-line:variable-name
        let indexSBoxFilter = -1;
        const arr = filterObj.query.bool.must;
        for (let i = 0; i < arr.length; i++) {
            if (typeof arr[i].query_string !== 'undefined') {
                if (typeof arr[i].query_string.allow_leading_wildcard !== 'undefined') {
                    indexSBoxFilter = i;
                    break;
                }
            }
        }
        if (indexSBoxFilter !== -1) {
            filterObj.query.bool.must.splice(indexSBoxFilter, 1);
        }
        if (search.value) {
            let searchTerm = search.value.trim();
            if (!scope.searchBoxAnyCase) {
                searchTerm = search.value.toLowerCase().trim();
            }
            // eslint-disable-next-line max-len
            let sBoxFilter = `{'query_string':{'query': '*{{{search}}}*','analyze_wildcard':true, 'allow_leading_wildcard': true, default_operator: 'AND', 'analyzer: 'formbird_text_search'}}`;
            // 17334
            if (scope.componentDefinition.searchBoxFilter) {
                sBoxFilter = scope.componentDefinition.searchBoxFilter;
            }
            // end 17334
            const sBoxFilterStr = sBoxFilter.replace('{{{search}}}', searchTerm.replace(/\//g, '//')).replace(/'/g, '"');
            const sBoxFilterObj = JSON.parse(sBoxFilterStr);
            const excludeTypes = ['date', 'boolean', 'number', 'object', 'array'];
            const excludeFilters = ['dateFilter', 'arrayFilter', 'numericFilter'];
            const systemTextSearch = ['systemHeader.summaryName', 'systemHeader.summaryDescription', 'appTags'];
            let hasSystemSearch = false;
            // Assign Default Fields if no fields set in the sBox filter
            if (!sBoxFilterObj.query_string.fields) {
                const fields = [];
                dtcolumns.map((col) => {
                    if (!includes(excludeTypes, col.type) && !includes(excludeFilters, col.filter)) {
                        let fieldName = col.data;
                        if (!scope.searchBoxAnyCase && !systemTextSearch.includes(col.data)) {
                            fieldName = col.data + '.lcase';
                        }
                        if (systemTextSearch.includes(col.data)) {
                            hasSystemSearch = true;
                        }
                        else {
                            fields.push(fieldName);
                        }
                    }
                });
                if (hasSystemSearch) {
                    fields.push('system_text_search');
                }
                sBoxFilterObj.query_string.fields = fields;
            }
            // column
            filterObj.query.bool.must.push(sBoxFilterObj);
        }
        for (let i = 0; i < columns.length; i++) {
            const s = columns[i];
            let fieldTerm = s.name;
            if (fieldTerm.match(/\.\d+\./g)) {
                fieldTerm = fieldTerm.replace(/\.\d+\./g, '.');
            }
            // tslint:disable-next-line:variable-name
            let indexCBoxFilter = -1;
            // tslint:disable-next-line:no-shadowed-variable
            const arr = filterObj.query.bool.must;
            for (let j = 0; j < arr.length; j++) {
                if (typeof arr[j].wildcard !== 'undefined') {
                    if (typeof arr[j].wildcard[fieldTerm + '.lcase'] !== 'undefined') {
                        if (typeof arr[j].wildcard[fieldTerm + '.lcase'].value !== 'undefined') {
                            indexCBoxFilter = j;
                            break;
                        }
                    }
                }
            }
            if (indexCBoxFilter !== -1) {
                filterObj.query.bool.must.splice(indexCBoxFilter, 1);
            }
            if (s.search.value) {
                switch (dtcolumns[i].type) {
                    default:
                        const wc = {};
                        wc[fieldTerm + '.lcase'] = { value: '*' + s.search.value.replace(/\//g, '//').toLowerCase() + '*' };
                        filterObj.query.bool.must.push({ wildcard: wc });
                        break;
                }
            }
        }
        // Secure the sort object to be empty, avoid adding duplicate sort fields
        if (filterObj.sort === undefined || filterObj.sort.length > 0) {
            filterObj.sort = [];
        }
        order.forEach((val, i) => {
            if (val.dir !== '') {
                const sortObj = {};
                sortObj[scope.componentDefinition.gridColumns[val.column].field.replace(/\.\d+/g, '')] = val.dir;
                filterObj.sort.push(sortObj);
            }
        });
        return filterObj;
    }
    getMongoQuery(scope, filter, search, columns, order, dtcolumns) {
        let filterObj;
        let queryData;
        if (typeof filter === 'string') {
            filterObj = filter.replace(/'/g, '"');
            filterObj = JSON.parse(filterObj);
        }
        else {
            filterObj = filter;
        }
        queryData = filterObj.query ? filterObj.query : filterObj;
        if (search.value) {
            const andArray = queryData.$and;
            const regexObj = { $regex: search.value.replace(/\//g, '//').replace(/'/g, '"').toLowerCase(), $options: 'i' };
            if (andArray === undefined || !isArray(andArray)) {
                queryData['systemHeader.summaryName'] = regexObj;
            }
            else {
                queryData.$and.push({ 'systemHeader.summaryName': regexObj });
            }
        }
        for (let i = 0; i < columns.length; i++) {
            const s = columns[i];
            let fieldTerm = s.name;
            if (fieldTerm.match(/\.\d+\./g)) {
                fieldTerm = fieldTerm.replace(/\.\d+\./g, '.');
            }
            if (s.search.value) {
                switch (dtcolumns[i].type) {
                    default:
                        const andArray = filterObj.$and;
                        const regexObj = { $regex: s.search.value.replace(/\//g, '//').toLowerCase().toLowerCase(), $options: 'i' };
                        if (andArray === undefined || !isArray(andArray)) {
                            queryData[fieldTerm] = regexObj;
                        }
                        else {
                            const fldObj = {};
                            fldObj[fieldTerm] = regexObj;
                            queryData.$and.push(fldObj);
                        }
                        break;
                }
            }
        }
        if (filterObj.query) {
            filterObj.query = queryData;
        }
        else {
            filterObj = { query: queryData };
        }
        // Secure the sort object to be empty, avoid adding duplicate sort fields
        if (filterObj.sort === undefined || !isObject(filterObj.sort)) {
            filterObj.sort = {};
        }
        order.forEach((val, i) => {
            if (val.dir !== '') {
                filterObj.sort[scope.componentDefinition.gridColumns[val.column].field.replace(/\.\d+/g, '')] = val.dir === 'asc' ? 1 : -1;
            }
        });
        return filterObj;
    }
    getDexieQuery(scope, filter, search, columns, order, dtcolumns) {
        let filterObj;
        filterObj = { dexieQuery: [] };
        if (search.value) {
            filterObj.dexieQuery.push({ field: 'systemHeader.summaryName',
                value: search.value.replace(/\//g, '//').replace(/'/g, '"').toLowerCase(),
                operator: operator.contains });
        }
        for (let i = 0; i < columns.length; i++) {
            const s = columns[i];
            let fieldTerm = s.name;
            if (fieldTerm.match(/\.\d+\./g)) {
                fieldTerm = fieldTerm.replace(/\.\d+\./g, '.');
            }
            if (s.search.value) {
                switch (dtcolumns[i].type) {
                    default:
                        const fldObj = { field: fieldTerm, value: s.search.value.replace(/\//g, '//').toLowerCase(),
                            operator: operator.contains };
                        filterObj.dexieQuery.push(fldObj);
                        break;
                }
            }
        }
        // Secure the sort object to be empty, avoid adding duplicate sort fields
        if (filterObj.dexieSort === undefined || !isObject(filterObj.dexieSort)) {
            filterObj.dexieSort = {};
        }
        order.forEach((val, i) => {
            if (val.dir !== '') {
                filterObj.dexieSort[scope.componentDefinition.gridColumns[val.column].field] = val.dir;
            }
        });
        return filterObj;
    }
}
/** @nocollapse */ /** @nocollapse */ SearchQueriesService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: SearchQueriesService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ SearchQueriesService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: SearchQueriesService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: SearchQueriesService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });
class DatatablesQueriesService extends SearchQueriesService {
}
/** @nocollapse */ /** @nocollapse */ DatatablesQueriesService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DatatablesQueriesService, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ DatatablesQueriesService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DatatablesQueriesService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DatatablesQueriesService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

// TODO: add dialog to entry files in app.module.ts
function appStoreFactory() {
    return new RootStore();
}
class FormbirdServicesModule {
    constructor(appStore) {
        const store = getStore();
        appStore.provideStore(store);
    }
}
/** @nocollapse */ /** @nocollapse */ FormbirdServicesModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: FormbirdServicesModule, deps: [{ token: AppStore }], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ /** @nocollapse */ FormbirdServicesModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: FormbirdServicesModule, imports: [HttpClientModule] });
/** @nocollapse */ /** @nocollapse */ FormbirdServicesModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: FormbirdServicesModule, providers: [
        { provide: AppStore, useFactory: appStoreFactory },
        LoggedInUserService,
        LocalStorageService,
        SessionStorageService,
        DataService,
        ReduxStoreService,
        RestDataService,
        ClientAccessService,
        BroadcastService,
        DocumentService,
        TemplateService,
        ComponentService,
        CurrentDocumentService,
        CurrentChildDocumentService,
        CurrentDocumentInfoService,
        UnsavedDocumentService,
        ChangedDocumentService,
        ClientSystemHeaderService,
        SearchFilterService,
        OfflineStatusService,
        SearchService,
        ControlStateSaveService,
        SessionTestService,
        LazyScriptLoaderService,
        IndexedDBQueueService,
        IndexedDBPropertiesService,
        IndexedDBService,
        IndexedDBConnectorService,
        DexieSearchService,
        PageVisibilityService,
        UserSettingsService,
        FileProviderService,
        OfflineUtilService,
        FormColorService,
        ModifiedCSSService,
        DefaultStateService,
        NavigationService,
        ValidationService,
        CustomFieldPreprocessorService,
        PreProcessorFieldService,
        ModifiedFieldService,
        TabService,
        RuleCacheService,
        ClientRulesService,
        ClientIncludeRuleSetService,
        RuleEngineService,
        NotificationService,
        DocumentSaveService,
        DocumentUpdateService,
        MandatoryFieldService,
        TemplateHtmlService,
        UrlService,
        ClientResourceService,
        StorageEventService,
        UrlBasePathService,
        VendorLibraryService,
        FinaliseValueFieldService,
        IncludeDocumentDataService,
        RouterService,
        LatestDocumentService,
        DatatablesQueriesService,
        SearchQueriesService,
        SearchClientService
    ], imports: [[
            HttpClientModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: FormbirdServicesModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [
                        HttpClientModule,
                    ],
                    exports: [],
                    providers: [
                        { provide: AppStore, useFactory: appStoreFactory },
                        LoggedInUserService,
                        LocalStorageService,
                        SessionStorageService,
                        DataService,
                        ReduxStoreService,
                        RestDataService,
                        ClientAccessService,
                        BroadcastService,
                        DocumentService,
                        TemplateService,
                        ComponentService,
                        CurrentDocumentService,
                        CurrentChildDocumentService,
                        CurrentDocumentInfoService,
                        UnsavedDocumentService,
                        ChangedDocumentService,
                        ClientSystemHeaderService,
                        SearchFilterService,
                        OfflineStatusService,
                        SearchService,
                        ControlStateSaveService,
                        SessionTestService,
                        LazyScriptLoaderService,
                        IndexedDBQueueService,
                        IndexedDBPropertiesService,
                        IndexedDBService,
                        IndexedDBConnectorService,
                        DexieSearchService,
                        PageVisibilityService,
                        UserSettingsService,
                        FileProviderService,
                        OfflineUtilService,
                        FormColorService,
                        ModifiedCSSService,
                        DefaultStateService,
                        NavigationService,
                        ValidationService,
                        CustomFieldPreprocessorService,
                        PreProcessorFieldService,
                        ModifiedFieldService,
                        TabService,
                        RuleCacheService,
                        ClientRulesService,
                        ClientIncludeRuleSetService,
                        RuleEngineService,
                        NotificationService,
                        DocumentSaveService,
                        DocumentUpdateService,
                        MandatoryFieldService,
                        TemplateHtmlService,
                        UrlService,
                        ClientResourceService,
                        StorageEventService,
                        UrlBasePathService,
                        VendorLibraryService,
                        FinaliseValueFieldService,
                        IncludeDocumentDataService,
                        RouterService,
                        LatestDocumentService,
                        DatatablesQueriesService,
                        SearchQueriesService,
                        SearchClientService
                    ]
                }]
        }], ctorParameters: function () { return [{ type: AppStore }]; } });

class ControlStateConstants {
}
ControlStateConstants.CONTROL_STATE_STORAGE_PREFIX = 'ControlState_';
ControlStateConstants.CONTROL_STATE_SIDEBAR_TOGGLE = ControlStateConstants.CONTROL_STATE_STORAGE_PREFIX + 'SideBarToggle';
ControlStateConstants.CONTROL_STATE_SIDEBAR_TOGGLE_XS_SM = ControlStateConstants.CONTROL_STATE_STORAGE_PREFIX + 'SideBarToggleXsSm';

const cleanRelatedDocs = function (docs) {
    // remove _uiSelectChoiceLocked field
    for (let i = 0; i < docs.length; i++) {
        if ('_uiSelectChoiceLocked' in docs[i]) {
            delete docs[i]['_uiSelectChoiceLocked'];
        }
    }
};
const formatValueInDocument = function (tplItem, document) {
    const field = tplItem.name;
    const relatedFields = tplItem.relatedFields;
    if (document[field]) {
        for (let i = 0; i < document[field].length; i++) {
            const value = document[field][i];
            // if the value is in ElasticSearch format, convert it to the required related document format
            if (value._source) {
                const formattedValue = {
                    documentId: value._source.documentId,
                    name: ''
                };
                if (value._source.systemHeader.summaryName) {
                    formattedValue.name = value._source.systemHeader.summaryName;
                }
                if (relatedFields) {
                    const keys = Object.keys(relatedFields);
                    for (let idx = 0; idx < keys.length; idx++) {
                        const key = keys[idx];
                        const val = relatedFields[key];
                        if (value._source[val]) {
                            formattedValue[key] = value._source[val];
                        }
                    }
                }
                document[field][i] = formattedValue;
            }
        }
    }
};
const RelatedDocumentPreprocessor = {
    preProcess: function (field, document) {
        if (document[field.name]) {
            cleanRelatedDocs(document[field.name]);
            formatValueInDocument(field, document);
        }
    },
    formatValueInDocument: formatValueInDocument
};

const serverRoutes = SharedUrlRoutes.serverRoutes;
class DynamicMetaTagService {
    constructor(localStorageService) {
        this.localStorageService = localStorageService;
        this.metaDefaultTitle = 'Formbird Version ' + this.localStorageService.getItem(LocalStorageKeys.APP_VERSION);
        this.metaDefaultDesc = 'Copyright ' + new Date().getFullYear() + ' Formbird Pty Ltd. All rights reserved.';
        this.metaDefaultImg = '../../../../../images/formbird-logo.png';
    }
    /**
     * set the meta tags in the html head of the page. This can be used by other applications to see details about the current
     * document in the page, eg. with document unfurling in Skype or Slack
     */
    set(title, description, img) {
        this.metaTitleData = title ? title : this.metaDefaultTitle;
        this.metaDescData = description ? description : this.metaDefaultDesc;
        this.metaImgData = img ? (serverRoutes.loadFile + '/' + img) : this.metaDefaultImg;
        const metaTitleElm = document.getElementById('metaTitle');
        if (metaTitleElm) {
            metaTitleElm.setAttribute('content', this.metaTitleData);
        }
        const metaDescElm = document.getElementById('metaDesc');
        if (metaDescElm) {
            metaDescElm.setAttribute('content', this.metaDescData);
        }
        const metaImgElm = document.getElementById('metaImg');
        if (metaImgElm) {
            metaImgElm.setAttribute('content', this.metaImgData);
        }
    }
    title() {
        return this.metaTitleData;
    }
    description() {
        return this.metaDescData;
    }
    image() {
        return this.metaImgData;
    }
}
/** @nocollapse */ /** @nocollapse */ DynamicMetaTagService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DynamicMetaTagService, deps: [{ token: LocalStorageService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ DynamicMetaTagService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DynamicMetaTagService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: DynamicMetaTagService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: LocalStorageService }]; } });

const logger = console;
const urlBasePath = UrlBasePathUtil.basePathWithSlashPrefix;
const path = { path: 'socket.io' };
const WEB_SOCKET_RECONNECT_TIME = 1000;
class WebSocketService {
    constructor(notificationService, offlineStatusService, unsavedDocumentService, dataService, restDataService, loggedInUserService, documentUpdateService, configService, broadcastService) {
        this.notificationService = notificationService;
        this.offlineStatusService = offlineStatusService;
        this.unsavedDocumentService = unsavedDocumentService;
        this.dataService = dataService;
        this.restDataService = restDataService;
        this.loggedInUserService = loggedInUserService;
        this.documentUpdateService = documentUpdateService;
        this.configService = configService;
        this.broadcastService = broadcastService;
    }
    getTransports() {
        if (this.configService.clientConfig().socketIO && this.configService.clientConfig().socketIO.transports) {
            return this.configService.clientConfig().socketIO.transports;
        }
        return null;
    }
    /**
     * cache any documents saved while offline. This can either be when the browser was closed or the device was offline
     */
    loadDocumentsSavedWhileOffline() {
        const _self = this;
        logger.info('Device has come online or app has been started. Loading any data saved while offline');
        const promise = _self.dataService.loadDataCache(false);
        promise.then(updateCachedDocuments);
        function updateCachedDocuments(newDocuments) {
            return __awaiter(this, void 0, void 0, function* () {
                if (newDocuments) {
                    for (let i = 0; i < newDocuments.length; i++) {
                        let newDocument = newDocuments[i];
                        yield _self.documentUpdateService.updateVersions([newDocument]);
                    }
                }
                return newDocuments;
            });
        }
    }
    start() {
        logger.info('Starting websocket...');
        const _self = this;
        const options = {
            resource: path,
            path: null,
            transports: ["websocket"]
        };
        const transports = _self.getTransports();
        if (transports) {
            options.transports = transports;
        }
        options.path = urlBasePath + '/socket.io';
        _self.socket = io.connect('/', options);
        function startReconnect() {
            if (!_self.socket.connected) {
                setTimeout(doReconnect, WEB_SOCKET_RECONNECT_TIME);
            }
        }
        function doReconnect() {
            if (!_self.socket.connected) {
                startReconnect();
                _self.socket.connect();
            }
        }
        _self.socket.on(SharedConstants.WEB_SOCKET_MESSAGE_SAVE_DOCUMENT, function (docInfo) {
            if (_self.loggedInUserService.getUserConfigItem('cachingEnabled') === true ||
                _self.unsavedDocumentService.documentIsLoaded(docInfo.documentId)) {
                return _self.restDataService.loadVersions(docInfo.versionIds).then(function successFunc(versions) {
                    return __awaiter(this, void 0, void 0, function* () {
                        yield _self.documentUpdateService.updateVersions(versions);
                        if (_self.loggedInUserService.getUserConfigItem('cachingEnabled') === true) {
                            _self.offlineStatusService.addToMaxCacheCount(1);
                            // pass versions in. If it was not fetched already
                            _self.dataService.cacheDocument(docInfo, versions).then(function successFunc(versions) {
                                logger.info("Cached pushed document for offline use: " + docInfo.documentId);
                            }, function errorFunc(err) {
                                logger.info("Not found document from received pushed versions: " + docInfo.documentId);
                            });
                        }
                    });
                }, function errorFunc(err) {
                    logger.error("Error loading pushed document: " + err.message);
                });
            }
        });
        _self.socket.on('reconnect', function (attempt) {
            _self.loadDocumentsSavedWhileOffline();
        });
        _self.socket.on('connect', function (data) {
            logger.info('Connected to notification socket. Setting status to connected.');
            _self.offlineStatusService.setConnected();
            // _self.utilService.scopeApply(_self.$rootScope);
            _self.loadDocumentsSavedWhileOffline();
        });
        _self.socket.on('connect_error', function (data) {
            logger.info('Error in notification socket. Setting status to disconnected');
            _self.offlineStatusService.setDisconnected();
            // _self.utilService.scopeApply(_self.$rootScope);
        });
        _self.socket.on('disconnect', function (data) {
            logger.info('Disconnected from notification socket. Setting status to disconnected');
            _self.offlineStatusService.setDisconnected();
            startReconnect();
        });
        _self.socket.on(SharedConstants.WEB_SOCKET_MESSAGE_PASSWORD_RESET, function (userId) {
            _self.emit(SharedConstants.PASSWORD_RESET, userId);
        });
    }
    /**
     * end the websocket session
     */
    end() {
        // disconnect from the socket. No callback is fired on doing this. A disconnect event is fired but only if the socket is
        // currently connected
        if (this.socket) {
            this.socket.disconnect();
        }
    }
    emit(eventName, data) {
        this.socket.emit(eventName, data);
    }
    addEventListener(eventName, eventHandlerFunc) {
        this.socket.on(eventName, eventHandlerFunc);
    }
    removeEventListener(eventName) {
        this.socket.off(eventName);
    }
}
/** @nocollapse */ /** @nocollapse */ WebSocketService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: WebSocketService, deps: [{ token: NotificationService }, { token: OfflineStatusService }, { token: UnsavedDocumentService }, { token: DataService }, { token: RestDataService }, { token: LoggedInUserService }, { token: DocumentUpdateService }, { token: ConfigService }, { token: BroadcastService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ WebSocketService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: WebSocketService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: WebSocketService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: NotificationService }, { type: OfflineStatusService }, { type: UnsavedDocumentService }, { type: DataService }, { type: RestDataService }, { type: LoggedInUserService }, { type: DocumentUpdateService }, { type: ConfigService }, { type: BroadcastService }]; } });

class ModalService {
    constructor() {
        const element = document.querySelectorAll('div[ui-view]');
        const injector = angular.element(element).injector();
        this.ajsService = injector.get('ModalService');
    }
    showDialog(message, messageType, succFunc, errorFunc, discardFunc) {
        this.ajsService.showDialog(message, messageType, succFunc, errorFunc, discardFunc);
    }
}
/** @nocollapse */ /** @nocollapse */ ModalService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ModalService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ ModalService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ModalService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ModalService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

class CustomElementService {
    constructor() {
        this.convertToCustomElement = (component, selector, injector, version, options) => {
            if (!version) {
                version = 'NOT-SET';
            }
            if (!customElements.get(selector)) {
                const scComponent = createCustomElement(component, { injector: injector });
                const connectedCallback = scComponent.prototype.connectedCallback || function () { };
                scComponent.prototype.connectedCallback = function () {
                    this.setAttribute('fb-component-version', version);
                    if (options) {
                        this.setAttribute('is-static-panel', options.isStaticPanel);
                        this.setAttribute('is-collapsible-panel', options.isCollapsiblePanel);
                    }
                    connectedCallback.call(this);
                };
                customElements.define(selector, scComponent);
            }
        };
    }
}
/** @nocollapse */ /** @nocollapse */ CustomElementService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CustomElementService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ CustomElementService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CustomElementService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CustomElementService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/*
 * Public API Surface of formbird-services
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AppStore, BroadcastService, ChangedDocumentService, ClientAccessService, ClientConstants, ClientIncludeRuleSetService, ClientResourceService, ClientRulesService, ClientSystemHeaderService, ComponentService, ConfigService, ControlStateConstants, ControlStateSaveService, CurrentChildDocumentService, CurrentDocumentInfoService, CurrentDocumentService, CustomElementService, CustomFieldPreprocessorService, DataService, DatatablesQueriesService, DefaultStateService, DexieSearchService, DocumentSaveService, DocumentService, DocumentSessionService, DocumentUpdateService, DynamicMetaTagService, FORM_CHANGE_DOCUMENT, FORM_CLEAR_DOCUMENT, FORM_DELETE_DOCUMENT, FORM_DOCUMENT_LOADED, FORM_REDO, FORM_RESET_CHANGE_DOCUMENT, FORM_RESET_DOCUMENT_INFO, FORM_RESET_INIT_DOCUMENT_DATA, FORM_RESET_TEMPLATE_COMPONENT_DEFINITION, FORM_RESET_TO_ORG_DOCUMENT, FORM_RESET_UNDO_REDO, FORM_SETUP_DOCUMENT_LIST, FORM_SET_ALT_TEMPLATE_INFO, FORM_SET_COMPONENT_FLAG_TEMPLATE, FORM_SET_COMPONENT_PROPS_TEMPLATE, FORM_SET_DISABLE_SAVE_FIELD_DOCUMENT, FORM_SET_DOCUMENT, FORM_SET_DOCUMENT_INFO, FORM_SET_DOCUMENT_SESSION, FORM_SET_LIST_COMPONENTS_FLAG_TEMPLATE, FORM_SET_ORG_DOCUMENT, FORM_SET_RULES_STATUS, FORM_SET_TEMPLATE, FORM_UNDO, FORM_UPDATE_COMPONENT_DEFINITION, FORM_UPDATE_INIT_DOCUMENT_DATA, FileProviderService, FinaliseValueFieldService, FormColorService, FormbirdInjectorService, FormbirdServicesModule, IndexedDBConnectorService, IndexedDBPropertiesService, IndexedDBQueueService, IndexedDBService, KeyValueStorageService, LatestDocumentService, LazyScriptLoaderService, LocalStorageKeys, LocalStorageService, LoggedInUserService, MandatoryFieldService, MandatoryValueProcessor, ModalService, ModifiedCSSService, ModifiedFieldService, NavigationService, NotificationService, OfflineStatusService, OfflineUtilService, PageVisibilityService, PreProcessorFieldService, RelatedDocumentPreprocessor, RestDataService, RouterService, RuleCacheService, RuleContext, RuleEngineService, SclSearchClientConstantService, SearchClientService, SearchFilterService, SearchQueriesService, SearchService, SessionStorageService, SessionTestService, StorageEventService, TabService, TemplateService, UnsavedDocumentService, UrlBasePathService, UrlBasePathUtil, UrlService, UserSettingsService, ValidationService, VendorLibraryService, WebSocketService, appStoreFactory, formClearDocuments, formDeleteDocument, formDocumentChanged, formDocumentLoaded, formDocumentReducer, formRedoDocument, formResetDocumentChanged, formResetDocumentInfo, formResetInitDocumentData, formResetTemplateComponentDefinition, formResetToOrgDocument, formSetAltTemplateInfo, formSetComponentFlagTemplate, formSetComponentPropsTemplate, formSetDisableSaveFieldDocument, formSetDocument, formSetDocumentInfo, formSetDocumentSession, formSetListComponentsFlagTemplate, formSetOrgDocument, formSetRulesStatus, formSetTemplate, formSetupDocumentList, formUndoDocument, formUpdateComponentDefinition, formUpdateInitDocumentData, getDefaultFormNewState, getIn$1 as getIn, getStore, resetUndoRedoDocument, resolveToFunctionSelector$1 as resolveToFunctionSelector, resolver$1 as resolver, select, select$, sniffSelectorType$1 as sniffSelectorType };
